import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useId, lazy, Suspense } from "react";
import { Button, Dropdown, Modal, Form, Overlay, OverlayTrigger, Popover, FloatingLabel, Tooltip } from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";
import katex from "katex";
import FlowDiagramModal from "./flowDiagram/FlowDiagramModal";
import {
  CAPTION_LABELS,
  CAPTION_SIZES,
  applyCaptionSettings,
  customCaptionLabels,
  defaultCaptionKind,
  figureBodyHtml,
  readCaptionSettings,
  removeCaption,
  renumberCaptions,
  setFigureBody,
} from "./rteCaptions";

const ReferenceController = lazy(() => import("./references/ReferenceController"));

const HEADING_OPTIONS = [
  { value: "h1", label: "H1" },
  { value: "h2", label: "H2" },
  { value: "h3", label: "H3" },
  { value: "h4", label: "H4" },
  { value: "h5", label: "H5" },
  { value: "h6", label: "H6" },
];

const RTE_TEXTBOX_CLASS = "rte-textbox";
const RTE_TB_PAD = "data-rte-tb-pad";
const RTE_TB_BORDER = "data-rte-tb-border";
const RTE_TB_COLOR = "data-rte-tb-color";
const RTE_TEXTBOX_PADS = [
  { value: "0", label: "None" },
  { value: "1", label: "Small" },
  { value: "2", label: "Medium" },
  { value: "3", label: "Large" },
];
const RTE_TEXTBOX_BORDERS = [
  { value: "0", label: "None" },
  { value: "1", label: "Thin" },
  { value: "2", label: "Thick" },
];
const RTE_TEXTBOX_COLORS = [
  { value: "gray", label: "Gray" },
  { value: "blue", label: "Blue" },
  { value: "green", label: "Green" },
  { value: "yellow", label: "Yellow" },
  { value: "red", label: "Red" },
  { value: "none", label: "None" },
];
const RTE_QUOTE_STYLE_ATTR = "data-rte-quote-style";
const RTE_QUOTE_COLOR_ATTR = "data-rte-quote-color";
const RTE_QUOTE_STYLES = [
  { value: "classic", label: "Classic" },
  { value: "bar", label: "Left bar" },
  { value: "outline", label: "Outline" },
  { value: "pull", label: "Pull quote" },
  { value: "line", label: "Simple line" },
  { value: "banner", label: "Banner" },
];

const findTextboxAncestor = (node, root) => {
  const el = node?.nodeType === 1 ? node : node?.parentElement;
  if (!el?.closest) return null;
  const box = el.closest(`.${RTE_TEXTBOX_CLASS}`);
  if (!box || (root && !root.contains(box))) return null;
  return box;
};

const selectionCrossesBlocks = (range) => {
  try {
    const frag = range.cloneContents();
    return !!frag.querySelector?.(
      "p,div,h1,h2,h3,h4,h5,h6,ul,ol,li,blockquote,pre,table"
    );
  } catch {
    return false;
  }
};

/** Pull a block text box out of paragraphs, headings, and inline wrappers. */
const liftBlockTextbox = (box, root) => {
  if (!box || !root) return;
  while (box.parentElement && box.parentElement !== root) {
    const parent = box.parentElement;
    if (parent.classList?.contains(RTE_TEXTBOX_CLASS)) break;
    const tag = parent.tagName;
    const inline = /^(SPAN|FONT|A|B|STRONG|I|EM|U|S|STRIKE|SUB|SUP|MARK)$/.test(tag);
    const textBlock = /^(P|H1|H2|H3|H4|H5|H6)$/.test(tag);
    if (!inline && !textBlock) break;
    const after = parent.cloneNode(false);
    while (box.nextSibling) after.appendChild(box.nextSibling);
    parent.parentNode.insertBefore(box, parent.nextSibling);
    if (after.childNodes.length) box.after(after);
    if (!parent.childNodes.length) parent.remove();
  }
  const dropEmptySibling = (start, dir) => {
    let node = start;
    while (node && node.nodeType === 1 && /^(P|H1|H2|H3|H4|H5|H6)$/.test(node.tagName)) {
      const text = String(node.textContent || "").replace(/\u00a0/g, " ").trim();
      const keep =
        text ||
        node.querySelector("img, br, table, ul, ol, .rte-textbox, .rte-math");
      if (keep) break;
      const next = dir === "prev" ? node.previousSibling : node.nextSibling;
      node.remove();
      node = next;
    }
  };
  dropEmptySibling(box.previousSibling, "prev");
  dropEmptySibling(box.nextSibling, "next");
};

const FONT_SIZE_OPTIONS = [
  { value: "14px", label: "14px" },
  { value: "16px", label: "16px" },
  { value: "20px", label: "20px" },
  { value: "24px", label: "24px" },
  { value: "28px", label: "28px" },
  { value: "32px", label: "32px" },
  { value: "36px", label: "36px" },
  { value: "40px", label: "40px" },
];

const FONT_FAMILY_DEFAULT = "__default__";

const FONT_FAMILY_OPTIONS = [
  { value: FONT_FAMILY_DEFAULT, label: "Default" },
  { value: "Arial, Helvetica, sans-serif", label: "Arial" },
  { value: "Georgia, serif", label: "Georgia" },
  { value: "'Times New Roman', Times, serif", label: "Times New Roman" },
  { value: "'Courier New', Courier, monospace", label: "Courier New" },
  { value: "Verdana, Geneva, sans-serif", label: "Verdana" },
  { value: "Tahoma, Geneva, sans-serif", label: "Tahoma" },
  { value: "'Trebuchet MS', Helvetica, sans-serif", label: "Trebuchet MS" },
];

const normalizeFontFamilyKey = (raw) =>
  String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/\s*,\s*/g, ",")
    .replace(/\s+/g, " ");

const matchFontFamilyOption = (raw) => {
  const key = normalizeFontFamilyKey(raw);
  if (!key || key === FONT_FAMILY_DEFAULT) return FONT_FAMILY_OPTIONS[0];
  const found = FONT_FAMILY_OPTIONS.find(
    (opt) =>
      opt.value !== FONT_FAMILY_DEFAULT &&
      normalizeFontFamilyKey(opt.value) === key
  );
  if (found) return found;
  // Match by primary family name (first token)
  const primary = key.split(",")[0]?.trim();
  return (
    FONT_FAMILY_OPTIONS.find(
      (opt) =>
        opt.value !== FONT_FAMILY_DEFAULT &&
        normalizeFontFamilyKey(opt.value).split(",")[0]?.trim() === primary
    ) || null
  );
};

const TEXT_COLOR_OPTIONS = [
  { value: "#212529", label: "Dark" },
  { value: "#343a40", label: "Gray dark" },
  { value: "#6c757d", label: "Muted" },
  { value: "#adb5bd", label: "Gray light" },
  { value: "#0d6efd", label: "Blue" },
  { value: "#0a58ca", label: "Blue deep" },
  { value: "#198754", label: "Green" },
  { value: "#20c997", label: "Teal" },
  { value: "#dc3545", label: "Red" },
  { value: "#fd7e14", label: "Orange" },
  { value: "#ffc107", label: "Amber" },
  { value: "#ffcd39", label: "Yellow" },
  { value: "#0dcaf0", label: "Cyan" },
  { value: "#6f42c1", label: "Violet" },
  { value: "#d63384", label: "Pink" },
  { value: "#842029", label: "Maroon" },
  { value: "#664d03", label: "Brown" },
  { value: "#ffffff", label: "White", lightSwatch: true },
];

const HIGHLIGHT_COLOR_OPTIONS = [
  { value: "#fff3a3", apply: "rgba(255, 193, 7, 0.22)", token: "yellow", label: "Yellow" },
  { value: "#b7f7c5", apply: "rgba(40, 167, 69, 0.22)", token: "green", label: "Green" },
  { value: "#ffc9de", apply: "rgba(214, 51, 132, 0.22)", token: "pink", label: "Pink" },
  { value: "#a8d8ff", apply: "rgba(33, 150, 243, 0.22)", token: "blue", label: "Blue" },
];

const ALIGN_OPTIONS = [
  { value: "left", command: "justifyLeft", label: "Align left", icon: "bi-text-left" },
  { value: "center", command: "justifyCenter", label: "Align center", icon: "bi-text-center" },
  { value: "right", command: "justifyRight", label: "Align right", icon: "bi-text-right" },
  { value: "justify", command: "justifyFull", label: "Justify (full)", icon: "bi-justify" },
];

const cssTextAlignToKey = (raw) => {
  const v = String(raw ?? "").toLowerCase();
  if (v === "center") return "center";
  if (v === "right" || v === "end") return "right";
  if (v === "justify") return "justify";
  return "left";
};

const RTE_TIP_POPPER_CONFIG = {
  strategy: "fixed",
  modifiers: [
    {
      name: "preventOverflow",
      options: {
        boundary: "viewport",
        padding: 8,
        altAxis: true,
      },
    },
    {
      name: "flip",
      options: {
        fallbackPlacements: ["bottom", "top", "right", "left"],
        padding: 8,
      },
    },
  ],
};

function RteTip({ text, children, placement = "top" }) {
  const id = useId();
  if (text == null || text === "") return children;
  return (
    <OverlayTrigger
      placement={placement}
      trigger={["hover", "focus"]}
      delay={{ show: 400, hide: 50 }}
      container={() => document.body}
      popperConfig={RTE_TIP_POPPER_CONFIG}
      overlay={
        <Tooltip id={`rte-tip-${id}`} className="rte-tip">
          {text}
        </Tooltip>
      }
    >
      {children}
    </OverlayTrigger>
  );
}

const TABLE_GRID_MAX = 8;
const TABLE_INSERT_ACCENT = "var(--blue)";
const TABLE_FLOAT_DEL_HIDE_MS = 240;
const TABLE_EDGE_PX = 12;
const TABLE_HOVER_SHOW_MS = 500;
const TABLE_COL_RESIZE_MS = 500;
const TABLE_COL_DIVIDER_PX = 7;
const TABLE_COL_MIN_PX = 40;
const RTE_CELL_BG_ATTR = "data-rte-cell-bg";
const RTE_TABLE_BG_ATTR = "data-rte-table-bg";
const RTE_TABLE_PERIM_ATTR = "data-rte-table-perimeter";
const RTE_TABLE_INSIDE_ATTR = "data-rte-table-inside";
const RTE_BAND_BORDER_ATTR = {
  top: "data-rte-b-top",
  right: "data-rte-b-right",
  bottom: "data-rte-b-bottom",
  left: "data-rte-b-left",
};
const RTE_BAND_BORDER_COLOR_ATTR = {
  top: "data-rte-bc-top",
  right: "data-rte-bc-right",
  bottom: "data-rte-bc-bottom",
  left: "data-rte-bc-left",
};
const RTE_BORDER_COLOR_STYLE = {
  top: "borderTopColor",
  right: "borderRightColor",
  bottom: "borderBottomColor",
  left: "borderLeftColor",
};
const RTE_TABLE_PERIM_COLOR_ATTR = "data-rte-table-perim-color";
const RTE_TABLE_INSIDE_COLOR_ATTR = "data-rte-table-in-color";

const sanitizeBorderColor = (raw) => {
  const value = String(raw ?? "").trim();
  if (!value || value === "default" || value === "none") return "";
  const match = value.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!match) return "";
  if (match[1].length === 3) {
    const [r, g, b] = match[1].split("");
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return value.toLowerCase();
};
const RTE_TABLE_BORDER_STEPS = [
  { value: "0", label: "None" },
  { value: "1", label: "1px" },
  { value: "2", label: "2px" },
  { value: "3", label: "3px" },
  { value: "4", label: "4px" },
];
const RTE_BAND_BORDER_TARGETS = [
  { id: "perimeter", label: "Outer", icon: "bi-border-outer" },
  { id: "inside", label: "Inner", icon: "bi-border-inner" },
  { id: "top", label: "Top", icon: "bi-border-top" },
  { id: "bottom", label: "Bottom", icon: "bi-border-bottom" },
  { id: "left", label: "Left", icon: "bi-border-left" },
  { id: "right", label: "Right", icon: "bi-border-right" },
];

const TABLE_STYLE_OPTIONS = [
  {
    id: "grid",
    label: "Grid",
    className: "rte-table-style-grid",
    headerRow: false,
  },
  {
    id: "header",
    label: "Header",
    className: "rte-table-style-header",
    headerRow: true,
  },
  {
    id: "striped",
    label: "Striped",
    className: "rte-table-style-striped",
    headerRow: false,
  },
];

const wrapPaddingEdgeOrigin = (wrap) => {
  if (!wrap) return { left: 0, top: 0 };
  const r = wrap.getBoundingClientRect();
  const st = window.getComputedStyle(wrap);
  const bl = parseFloat(st.borderLeftWidth) || 0;
  const bt = parseFloat(st.borderTopWidth) || 0;
  return { left: r.left + bl, top: r.top + bt };
};

const TITLE_CASE_TRANSFORM = (s) =>
  s.replace(/\w\S*/g, (txt) =>
    txt.length === 0
      ? txt
      : txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase()
  );

const CARET_INSPECTOR_EMPTY = {
  active: false,
  selected: false,
  blockShort: "—",
  blockLong: "—",
  fontSize: "—",
  fontFamily: "Default",
  fontFamilyValue: "",
  colorLabel: "—",
  caseLabel: "—",
  bold: false,
  italic: false,
  underline: false,
  strikeThrough: false,
  superscript: false,
  subscript: false,
  inLink: false,
  inQuote: false,
  inCode: false,
  inTable: false,
  align: "left",
  hasBottomMargin: false,
  lineSpacing: "",
  listKind: "",
};

const compactUniqueLabels = (values, fallback = "—") => {
  const out = [];
  values.forEach((raw) => {
    const v = String(raw ?? "").trim();
    if (!v) return;
    if (!out.includes(v)) out.push(v);
  });
  if (!out.length) return fallback;
  if (out.length === 1) return out[0];
  return "—";
};

const inspectBlockForNode = (node, root) => {
  let el = node?.nodeType === Node.TEXT_NODE ? node.parentElement : node;
  while (el && el !== root) {
    if (el.nodeType === 1) {
      const tag = el.tagName.toLowerCase();
      if (/^h[1-6]$/.test(tag)) {
        const upper = tag.toUpperCase();
        return { short: upper, long: upper };
      }
      if (tag === "blockquote") {
        return { short: "QUOTE", long: "Quotation" };
      }
      if (tag === "pre" || tag === "code") {
        return { short: "CODE", long: "Code block" };
      }
      if (tag === "li") {
        const pt = el.parentElement?.tagName.toLowerCase() ?? "";
        const kind = listKindOf(el.parentElement);
        const label = RTE_LIST_KINDS.find((item) => item.id === kind)?.label || "List";
        return {
          short: pt === "ol" ? "LI·OL" : "LI·UL",
          long: label,
          listKind: kind,
        };
      }
      if (tag === "p" || tag === "div") return { short: "P", long: "Paragraph" };
    }
    el = el.parentElement;
  }
  return { short: "P", long: "Paragraph" };
};

const findLinkAncestor = (node, root) => {
  if (!node || !root) return null;
  let el = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
  while (el && el !== root) {
    if (el.nodeName === "A") return el;
    el = el.parentElement;
  }
  return null;
};

const findBlockquoteAncestor = (node, root) => {
  if (!node || !root) return null;
  let el = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
  while (el && el !== root) {
    if (el.nodeName === "BLOCKQUOTE") return el;
    el = el.parentElement;
  }
  return null;
};

const findPreAncestor = (node, root) => {
  if (!node || !root) return null;
  let el = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
  while (el && el !== root) {
    if (el.nodeName === "PRE") return el;
    el = el.parentElement;
  }
  return null;
};

const findTableAncestor = (node, root) => {
  if (!node || !root) return null;
  let el = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
  while (el && el !== root) {
    if (el.nodeName === "TABLE") return el;
    el = el.parentElement;
  }
  return null;
};

const tableRowElements = (table) => (table ? [...table.querySelectorAll("tr")] : []);

const tableRowCells = (row) =>
  row?.querySelectorAll ? [...row.querySelectorAll(":scope > th, :scope > td")] : [];

const cellsForTableBand = (table, selection) => {
  if (!table || !selection) return [];
  const rows = tableRowElements(table);
  if (selection.type === "row") return tableRowCells(rows[selection.index]);
  return rows.map((row) => tableRowCells(row)[selection.index]).filter(Boolean);
};

const cellsForBandEdge = (table, selection, edge) => {
  const rows = tableRowElements(table);
  if (!selection) return [];
  if (selection.type === "row") {
    const cells = tableRowCells(rows[selection.index]);
    if (!cells.length) return [];
    if (edge === "left") return [cells[0]];
    if (edge === "right") return [cells[cells.length - 1]];
    return cells;
  }
  const cells = rows.map((row) => tableRowCells(row)[selection.index]).filter(Boolean);
  if (!cells.length) return [];
  if (edge === "top") return [cells[0]];
  if (edge === "bottom") return [cells[cells.length - 1]];
  return cells;
};

const bandEdgesForControl = (edgeId) => {
  if (edgeId === "sides") return ["left", "right"];
  if (edgeId === "perimeter") return ["top", "right", "bottom", "left"];
  return [edgeId];
};

const writeBorderAttr = (cell, attr, width) => {
  if (!cell) return;
  if (width == null || width === "") cell.removeAttribute(attr);
  else cell.setAttribute(attr, String(width));
};

const edgeIsOuter = (table, cell, edge) => {
  const rows = tableRowElements(table);
  const row = cell?.parentElement;
  const rowIndex = rows.indexOf(row);
  const cells = tableRowCells(row);
  const colIndex = cells.indexOf(cell);
  if (rowIndex < 0 || colIndex < 0) return false;
  if (edge === "top") return rowIndex === 0;
  if (edge === "bottom") return rowIndex === rows.length - 1;
  if (edge === "left") return colIndex === 0;
  if (edge === "right") return colIndex === cells.length - 1;
  return false;
};

const effectiveBorderWidth = (table, cell, edge) => {
  const explicit = cell?.getAttribute(RTE_BAND_BORDER_ATTR[edge]);
  if (explicit != null && explicit !== "") return explicit;
  const outer = edgeIsOuter(table, cell, edge);
  if (outer && table?.hasAttribute(RTE_TABLE_PERIM_ATTR)) {
    return table.getAttribute(RTE_TABLE_PERIM_ATTR) || "1";
  }
  if (!outer && table?.hasAttribute(RTE_TABLE_INSIDE_ATTR)) {
    return table.getAttribute(RTE_TABLE_INSIDE_ATTR) || "1";
  }
  return "1";
};

const neighborAcrossEdge = (table, cell, edge) => {
  const row = cell?.parentElement;
  const rows = tableRowElements(table);
  const rowIndex = rows.indexOf(row);
  const cells = tableRowCells(row);
  const colIndex = cells.indexOf(cell);
  if (rowIndex < 0 || colIndex < 0) return null;
  if (edge === "top") return tableRowCells(rows[rowIndex - 1])[colIndex] || null;
  if (edge === "bottom") return tableRowCells(rows[rowIndex + 1])[colIndex] || null;
  if (edge === "left") return cells[colIndex - 1] || null;
  if (edge === "right") return cells[colIndex + 1] || null;
  return null;
};

const facingBorderAttr = {
  top: RTE_BAND_BORDER_ATTR.bottom,
  bottom: RTE_BAND_BORDER_ATTR.top,
  left: RTE_BAND_BORDER_ATTR.right,
  right: RTE_BAND_BORDER_ATTR.left,
};

const applyCellEdgeWidth = (table, cell, edge, width) => {
  writeBorderAttr(cell, RTE_BAND_BORDER_ATTR[edge], width);
  const neighbor = neighborAcrossEdge(table, cell, edge);
  const facing = facingBorderAttr[edge];
  if (!neighbor || !facing) return;
  const current = neighbor.getAttribute(facing);
  if (current == null || current === "0" || current === "1") neighbor.setAttribute(facing, "0");
};

const applyBandEdge = (table, selection, edgeId, width) => {
  bandEdgesForControl(edgeId).forEach((edge) => {
    cellsForBandEdge(table, selection, edge).forEach((cell) => {
      applyCellEdgeWidth(table, cell, edge, width);
    });
  });
};

const forBandInsideEdges = (table, selection, fn) => {
  const edge = selection?.type === "row" ? "left" : "top";
  cellsForTableBand(table, selection).slice(1).forEach((cell) => fn(cell, edge));
};

const applyCellEdgeColor = (table, cell, edge, color) => {
  const safe = sanitizeBorderColor(color);
  if (safe) {
    const width = effectiveBorderWidth(table, cell, edge);
    applyCellEdgeWidth(table, cell, edge, width === "0" ? "1" : width);
  }
  const attr = RTE_BAND_BORDER_COLOR_ATTR[edge];
  if (!safe) {
    cell.removeAttribute(attr);
    cell.style[RTE_BORDER_COLOR_STYLE[edge]] = "";
    return;
  }
  cell.setAttribute(attr, safe);
  cell.style[RTE_BORDER_COLOR_STYLE[edge]] = safe;
};

const applyBandEdgeColor = (table, selection, edgeId, color) => {
  bandEdgesForControl(edgeId).forEach((edge) => {
    cellsForBandEdge(table, selection, edge).forEach((cell) => {
      applyCellEdgeColor(table, cell, edge, color);
    });
  });
};

const readBandEdgeColor = (table, selection, edgeId) => {
  const values = [];
  bandEdgesForControl(edgeId).forEach((edge) => {
    cellsForBandEdge(table, selection, edge).forEach((cell) => {
      values.push(sanitizeBorderColor(cell.getAttribute(RTE_BAND_BORDER_COLOR_ATTR[edge])) || "");
    });
  });
  if (!values.length) return "";
  return values.every((value) => value === values[0]) ? values[0] : null;
};

const clearCellTableStyle = (cell) => {
  if (!cell) return;
  cell.removeAttribute(RTE_CELL_BG_ATTR);
  Object.values(RTE_BAND_BORDER_ATTR).forEach((attr) => cell.removeAttribute(attr));
  Object.values(RTE_BAND_BORDER_COLOR_ATTR).forEach((attr) => cell.removeAttribute(attr));
  Object.values(RTE_BORDER_COLOR_STYLE).forEach((prop) => {
    cell.style[prop] = "";
  });
  cell.style.removeProperty("background-color");
  if (cell.getAttribute("style") === "") cell.removeAttribute("style");
};

const resetBandToDefault = (table, selection) => {
  const cells = cellsForTableBand(table, selection);
  cells.forEach((cell) => {
    ["top", "right", "bottom", "left"].forEach((edge) => {
      const neighbor = neighborAcrossEdge(table, cell, edge);
      const facing = facingBorderAttr[edge];
      if (neighbor && neighbor.getAttribute(facing) === "0") neighbor.removeAttribute(facing);
    });
    clearCellTableStyle(cell);
  });
};

const resetTableToDefault = (table) => {
  [
    RTE_TABLE_BG_ATTR,
    RTE_TABLE_PERIM_ATTR,
    RTE_TABLE_INSIDE_ATTR,
    RTE_TABLE_PERIM_COLOR_ATTR,
    RTE_TABLE_INSIDE_COLOR_ATTR,
  ].forEach((attr) => table.removeAttribute(attr));
  table.style.removeProperty("--rte-perim-color");
  table.style.removeProperty("--rte-in-color");
  table.style.removeProperty("--rte-table-fill");
  if (table.getAttribute("style") === "") table.removeAttribute("style");
  table.querySelectorAll("td, th").forEach((cell) => {
    clearCellTableStyle(cell);
    cell.style.removeProperty("width");
    if (cell.getAttribute("style") === "") cell.removeAttribute("style");
  });
};

const rowCells = (row) =>
  row ? [...row.children].filter((el) => el.tagName === "TD" || el.tagName === "TH") : [];

const measureColumnWidths = (table) =>
  rowCells(table?.querySelector("tr")).map((cell) => cell.getBoundingClientRect().width);

const tableColumnsSized = (table) => !!rowCells(table?.querySelector("tr"))[0]?.style.width;

const applyColumnWidths = (table, pxWidths) => {
  const total = pxWidths.reduce((sum, n) => sum + n, 0);
  if (!table || !total) return;
  table.querySelectorAll("tr").forEach((tr) => {
    rowCells(tr).forEach((cell, index) => {
      if (pxWidths[index] == null) return;
      cell.style.width = `${((pxWidths[index] / total) * 100).toFixed(3)}%`;
    });
  });
};

const widthsWithInsertedColumn = (widths, insertAt) => {
  const total = widths.reduce((sum, n) => sum + n, 0);
  if (!widths.length || !total) return widths;
  const share = total / (widths.length + 1);
  const scale = (total - share) / total;
  const next = widths.map((width) => width * scale);
  next.splice(insertAt, 0, share);
  return next;
};

/** Internal vertical borders only: the line between two columns, not the table's outer edges. */
const columnDividerAtPoint = (cell, colIndex, cellCount, clientX) => {
  if (!cell || cellCount < 2) return null;
  const rect = cell.getBoundingClientRect();
  const distRight = rect.right - clientX;
  const distLeft = clientX - rect.left;
  if (colIndex < cellCount - 1 && distRight >= -1 && distRight <= TABLE_COL_DIVIDER_PX) {
    return { colIndex, key: `div:${colIndex}` };
  }
  if (colIndex > 0 && distLeft >= -1 && distLeft <= TABLE_COL_DIVIDER_PX) {
    return { colIndex: colIndex - 1, key: `div:${colIndex - 1}` };
  }
  return null;
};

const applyTableBorderColor = (table, kind, color) => {
  const safe = sanitizeBorderColor(color);
  const attr = kind === "inside" ? RTE_TABLE_INSIDE_COLOR_ATTR : RTE_TABLE_PERIM_COLOR_ATTR;
  const variable = kind === "inside" ? "--rte-in-color" : "--rte-perim-color";
  if (!safe) {
    table.removeAttribute(attr);
    table.style.removeProperty(variable);
    return;
  }
  table.setAttribute(attr, safe);
  table.style.setProperty(variable, safe);
};

function RteBorderColorChoices({ value, onChange, ariaLabel }) {
  const selected = sanitizeBorderColor(value) || "";
  const pickerValue = selected || "#6c757d";
  return (
    <div className="rte-border-color-choices">
      <button
        type="button"
        className={`rte-band-color${selected ? "" : " is-active"}`}
        title="Default"
        aria-label="Default border color"
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => onChange("")}
      >
        <span className="rte-textbox-color-dot is-none" />
      </button>
      {TEXT_COLOR_OPTIONS.map((opt) => {
        const hex = sanitizeBorderColor(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            title={opt.label}
            aria-label={opt.label}
            className={`rte-color-swatch${selected === hex ? " is-active" : ""}${
              opt.lightSwatch ? " rte-color-swatch-light-border" : ""
            }`}
            style={{ backgroundColor: opt.value }}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onChange(opt.value)}
          />
        );
      })}
      <label className="rte-border-color-custom" title="Choose a color">
        <input
          type="color"
          aria-label={ariaLabel || "Choose border color"}
          value={pickerValue}
          onMouseDown={(event) => event.stopPropagation()}
          onChange={(event) => onChange(event.target.value)}
        />
      </label>
    </div>
  );
}

const readBandEdge = (table, selection, edgeId) => {
  const values = [];
  bandEdgesForControl(edgeId).forEach((edge) => {
    cellsForBandEdge(table, selection, edge).forEach((cell) => {
      values.push(effectiveBorderWidth(table, cell, edge));
    });
  });
  if (!values.length) return "1";
  return values.every((value) => value === values[0]) ? values[0] : "";
};

const applyBandTargetWidth = (table, selection, target, width) => {
  if (target === "inside") {
    forBandInsideEdges(table, selection, (cell, edge) => {
      applyCellEdgeWidth(table, cell, edge, width);
    });
    return;
  }
  applyBandEdge(table, selection, target, width);
};

const applyBandTargetColor = (table, selection, target, color) => {
  if (target === "inside") {
    forBandInsideEdges(table, selection, (cell, edge) => {
      applyCellEdgeColor(table, cell, edge, color);
    });
    return;
  }
  applyBandEdgeColor(table, selection, target, color);
};

const readBandTargetWidth = (table, selection, target) => {
  if (target === "inside") {
    const values = [];
    forBandInsideEdges(table, selection, (cell, edge) => {
      values.push(effectiveBorderWidth(table, cell, edge));
    });
    if (!values.length) return "";
    return values.every((value) => value === values[0]) ? values[0] : "";
  }
  return readBandEdge(table, selection, target);
};

const readBandTargetColor = (table, selection, target) => {
  if (target === "inside") {
    const values = [];
    forBandInsideEdges(table, selection, (cell, edge) => {
      values.push(sanitizeBorderColor(cell.getAttribute(RTE_BAND_BORDER_COLOR_ATTR[edge])) || "");
    });
    if (!values.length) return "";
    return values.every((value) => value === values[0]) ? values[0] : null;
  }
  return readBandEdgeColor(table, selection, target);
};

const applyBandAllBorderColor = (table, selection, color) => {
  RTE_BAND_BORDER_TARGETS.forEach((target) => {
    applyBandTargetColor(table, selection, target.id, color);
  });
};

const readBandAllBorderColor = (table, selection) => {
  const values = [];
  RTE_BAND_BORDER_TARGETS.forEach((target) => {
    if (target.id === "inside") {
      let count = 0;
      forBandInsideEdges(table, selection, () => {
        count += 1;
      });
      if (!count) return;
    }
    values.push(readBandTargetColor(table, selection, target.id));
  });
  if (!values.length || values.some((value) => value == null)) return null;
  return values.every((value) => value === values[0]) ? values[0] : null;
};

const readBandColor = (table, selection) => {
  const cells = cellsForTableBand(table, selection);
  if (!cells.length) return "";
  const values = cells.map((cell) => cell.getAttribute(RTE_CELL_BG_ATTR) || "");
  return values.every((value) => value === values[0]) ? values[0] : null;
};

const paintCellFill = (cell, color) => {
  const hex = sanitizeBorderColor(color);
  if (!color || color === "none") {
    cell.removeAttribute(RTE_CELL_BG_ATTR);
    cell.style.removeProperty("background-color");
  } else if (hex) {
    cell.setAttribute(RTE_CELL_BG_ATTR, hex);
    cell.style.setProperty("background-color", hex, "important");
  } else {
    cell.setAttribute(RTE_CELL_BG_ATTR, color);
    cell.style.removeProperty("background-color");
  }
  if (cell.getAttribute("style") === "") cell.removeAttribute("style");
};

const paintTableFill = (table, color) => {
  const hex = sanitizeBorderColor(color);
  if (!color || color === "none") {
    table.removeAttribute(RTE_TABLE_BG_ATTR);
    table.style.removeProperty("--rte-table-fill");
  } else if (hex) {
    table.setAttribute(RTE_TABLE_BG_ATTR, hex);
    table.style.setProperty("--rte-table-fill", hex);
  } else {
    table.setAttribute(RTE_TABLE_BG_ATTR, color);
    table.style.removeProperty("--rte-table-fill");
  }
  if (table.getAttribute("style") === "") table.removeAttribute("style");
};

const applyBandColor = (table, selection, color) => {
  cellsForTableBand(table, selection).forEach((cell) => paintCellFill(cell, color));
};

const buildTableHoverIntent = (table, event, cell, rowIndex, colIndex, rows, cells) => {
  const rect = cell.getBoundingClientRect();
  const nearLeft = event.clientX - rect.left <= TABLE_EDGE_PX;
  const nearRight = rect.right - event.clientX <= TABLE_EDGE_PX;
  const nearTop = event.clientY - rect.top <= TABLE_EDGE_PX;
  const isLastRow = rowIndex === rows.length - 1;
  const isFirstCol = colIndex === 0;
  const isLastCol = colIndex === cells.length - 1;
  const onColumnTop = rowIndex === 0 && nearTop;
  const onRowEdge = (isFirstCol && nearLeft) || (isLastCol && nearRight);

  const colGutter = () => {
    const distLeft = event.clientX - rect.left;
    const distRight = rect.right - event.clientX;
    return {
      kind: "col",
      insertAfterCol: distLeft < distRight && colIndex > 0 ? colIndex - 1 : colIndex,
    };
  };
  const rowGutter = () => {
    if (event.clientY - rect.top <= rect.bottom - event.clientY) {
      return { kind: "row", mode: "beforeRow", rowIndex };
    }
    if (isLastRow) return { kind: "row", mode: "afterLast" };
    return { kind: "row", mode: "beforeRow", rowIndex: rowIndex + 1 };
  };

  let gutter = null;
  if (onColumnTop && onRowEdge) {
    const distTop = event.clientY - rect.top;
    const distSide =
      isFirstCol && nearLeft ? event.clientX - rect.left : rect.right - event.clientX;
    gutter = distTop <= distSide ? colGutter() : rowGutter();
  } else if (onColumnTop) {
    gutter = colGutter();
  } else if (onRowEdge) {
    gutter = rowGutter();
  }

  let preview = null;
  if (nearLeft && isFirstCol) preview = { type: "row", index: rowIndex };
  else if (nearTop && rowIndex === 0) preview = { type: "col", index: colIndex };
  if (!gutter && !preview) return null;

  const gutterKey = !gutter
    ? ""
    : gutter.kind === "col"
      ? `c:${gutter.insertAfterCol}`
      : `r:${gutter.mode}:${gutter.rowIndex ?? ""}`;
  const previewKey = preview ? `${preview.type}:${preview.index}` : "";
  return { key: `${gutterKey}|${previewKey}`, table, gutter, preview };
};

const buildRteTableHtml = (rows, cols, styleId = "grid") => {
  const r = Math.max(1, Math.min(TABLE_GRID_MAX, rows));
  const c = Math.max(1, Math.min(TABLE_GRID_MAX, cols));
  const style =
    TABLE_STYLE_OPTIONS.find((s) => s.id === styleId) || TABLE_STYLE_OPTIONS[0];
  let html = `<table class="rte-table ${style.className}"><tbody>`;
  for (let i = 0; i < r; i += 1) {
    html += "<tr>";
    for (let j = 0; j < c; j += 1) {
      const tag = style.headerRow && i === 0 ? "th" : "td";
      html += `<${tag}><br></${tag}>`;
    }
    html += "</tr>";
  }
  html += "</tbody></table><p><br></p>";
  return html;
};

const selectionIntersectsLink = (sel, root) => {
  if (!sel?.rangeCount || !root) return false;
  const range = sel.getRangeAt(0);
  if (range.collapsed) {
    return Boolean(findLinkAncestor(sel.focusNode, root));
  }
  if (findLinkAncestor(range.commonAncestorContainer, root)) return true;
  try {
    const anchors = root.querySelectorAll("a");
    for (let i = 0; i < anchors.length; i += 1) {
      if (range.intersectsNode(anchors[i])) return true;
    }
  } catch {
    /* intersectsNode can throw on detached nodes */
  }
  return false;
};

const unwrapAnchorElement = (anchor) => {
  if (!anchor?.parentNode) return;
  const parent = anchor.parentNode;
  while (anchor.firstChild) {
    parent.insertBefore(anchor.firstChild, anchor);
  }
  parent.removeChild(anchor);
};

const resolveSelectionTargets = (sel, root) => {
  const range = sel?.rangeCount ? sel.getRangeAt(0) : null;
  if (!range) return [];

  if (range.collapsed) {
    const focusNode = sel.focusNode;
    if (!focusNode || !root.contains(focusNode)) return [];
    return [focusNode.nodeType === Node.TEXT_NODE ? focusNode.parentElement : focusNode];
  }

  const targets = [];
  const seen = new Set();
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  let textNode;
  while ((textNode = walker.nextNode())) {
    if (!range.intersectsNode(textNode)) continue;
    if (!String(textNode.textContent ?? "").trim()) continue;
    const el = textNode.parentElement;
    if (!el) continue;
    if (seen.has(el)) continue;
    seen.add(el);
    targets.push(el);
  }

  if (!targets.length) {
    const fallbackNode = range.commonAncestorContainer;
    if (fallbackNode && root.contains(fallbackNode)) {
      targets.push(
        fallbackNode.nodeType === Node.TEXT_NODE
          ? fallbackNode.parentElement
          : fallbackNode
      );
    }
  }
  return targets.filter(Boolean);
};

const computedWeightIsBold = (fontWeightRaw) => {
  const w = String(fontWeightRaw ?? "").toLowerCase();
  if (w === "bold" || w === "bolder") return true;
  const n = parseInt(w, 10);
  return Number.isFinite(n) && n >= 600;
};

const cssColorToHexLabel = (cssColor) => {
  if (!cssColor || typeof cssColor !== "string") return "—";
  const s = cssColor.trim();
  const su = s.toUpperCase();
  if (su.startsWith("#"))
    return su.length <= 9 ? su : `${su.slice(0, 7)}…`;
  const mm = su.match(/^RGBA?\(([\d.%\s+,]+)\)/i);
  if (!mm) return s.slice(0, 20);
  const parts = mm[1].split(",").map((p) => parseFloat(p.trim()));
  const r = Math.round(parts[0]);
  const g = Math.round(parts[1]);
  const b = Math.round(parts[2]);
  if ([r, g, b].some((n) => Number.isNaN(n))) return "—";
  const hx = (n) =>
    Math.max(0, Math.min(255, n)).toString(16).padStart(2, "0");
  return `#${hx(r)}${hx(g)}${hx(b)}`.toUpperCase();
};

const cssTextTransformToCaseKey = (ttRaw) => {
  const tt = String(ttRaw || "none").toLowerCase();
  if (tt === "uppercase") return "uppercase";
  if (tt === "lowercase") return "lowercase";
  if (tt === "capitalize") return "capitalize";
  return "n/a";
};

const caseKeyToTitle = (caseKey) => {
  if (caseKey === "uppercase") return "Uppercase";
  if (caseKey === "lowercase") return "Lowercase";
  if (caseKey === "capitalize") return "Title Case";
  if (caseKey === "Multiple") return "Multiple";
  return "None";
};

/** Toolbar / picker label: ABC · abc · AbC · Tt (default) */
const renderCaseLabel = (caseKey) => {
  if (caseKey === "uppercase") {
    return <span className="font-monospace small fw-semibold">ABC</span>;
  }
  if (caseKey === "lowercase") {
    return <span className="font-monospace small fw-semibold">abc</span>;
  }
  if (caseKey === "capitalize") {
    return <span className="font-monospace small fw-semibold">AbC</span>;
  }
  if (caseKey === "Multiple") {
    return <span className="text-muted small">Multiple</span>;
  }
  return <span className="font-monospace small fw-semibold">Tt</span>;
};

/**
 * Heading / paragraph control: execCommand(formatBlock) often fails when "select all +
 * font size" leaves a lone root-level &lt;span&gt; without a proper block ancestor.
 */
const findStructuralBlockAncestor = (node, root) => {
  if (typeof Node === "undefined" || !root?.contains(node)) return null;
  let el = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
  while (el && el !== root) {
    const t = el.tagName.toLowerCase();
    const parent = el.parentElement;
    if (!parent) break;

    if (/^h[1-6]$/.test(t) || t === "p" || t === "blockquote" || t === "pre") {
      return el;
    }
    if (t === "li") {
      return el;
    }

    const isRootShell =
      parent === root &&
      (t === "div" || t === "span" || t === "font" || t === "section");
    if (isRootShell) {
      return el;
    }

    el = parent;
  }
  return null;
};

const replaceElementPreserveChildren = (blockEl, newTagLower) => {
  const tag = newTagLower === "p" ? "P" : String(newTagLower).toUpperCase();
  const doc = blockEl.ownerDocument;
  const neo = doc.createElement(tag);
  while (blockEl.firstChild) {
    neo.appendChild(blockEl.firstChild);
  }
  blockEl.parentNode.replaceChild(neo, blockEl);
  return neo;
};

/**
 * Whole-line / double-click selections often end at offset 0 of the next block.
 * intersectsNode is true for that next block even though it was not selected.
 */
const rangeMeaningfullyIntersectsBlock = (range, el) => {
  try {
    if (!range.intersectsNode(el)) return false;
    const br = document.createRange();
    br.selectNodeContents(el);
    // Range ends exactly at the start of this block (previous-line leak).
    if (range.compareBoundaryPoints(Range.END_TO_START, br) === 0) return false;
    // Range starts exactly at the end of this block (next-line leak).
    if (range.compareBoundaryPoints(Range.START_TO_END, br) === 0) return false;
    return true;
  } catch {
    try {
      return range.intersectsNode(el);
    } catch {
      return false;
    }
  }
};

/** Leaf structural blocks intersecting a selection (multi-paragraph tools). */
const collectIntersectingStructuralBlocks = (range, root, { includePre = true } = {}) => {
  if (!range || !root) return [];
  const selector = includePre
    ? "p, div, li, blockquote, pre, h1, h2, h3, h4, h5, h6"
    : "p, div, li, blockquote, h1, h2, h3, h4, h5, h6";
  const out = [];
  const seen = new Set();
  root.querySelectorAll(selector).forEach((el) => {
    if (seen.has(el)) return;
    if (el.tagName !== "PRE" && el.closest?.("pre")) return;
    if (!rangeMeaningfullyIntersectsBlock(range, el)) return;
    const nested = el.querySelector(selector);
    if (nested && el.tagName !== "LI") return;
    seen.add(el);
    out.push(el);
  });
  return out;
};

/** Drop empty leading headings left behind when a title heading was applied to the body. */
const unwrapLeadingEmptyHeadings = (root) => {
  if (!root?.firstElementChild) return false;
  let changed = false;
  while (root.firstElementChild) {
    const el = root.firstElementChild;
    if (!/^H[1-6]$/i.test(el.tagName)) break;
    const text = String(el.textContent || "").replace(/\u00a0/g, " ").trim();
    const meaningful = el.querySelector?.("img, table, iframe, video, hr");
    if (text || meaningful) break;
    const p = root.ownerDocument.createElement("p");
    p.innerHTML = el.innerHTML?.trim() ? el.innerHTML : "<br>";
    root.replaceChild(p, el);
    changed = true;
  }
  return changed;
};

/** Explicit font-family on node or ancestors (empty = editor default). */
const getExplicitFontFamily = (node, root) => {
  let el = node?.nodeType === Node.TEXT_NODE ? node.parentElement : node;
  while (el && el !== root) {
    if (el.nodeType === 1) {
      const styled = el.style?.fontFamily?.trim();
      if (styled) return styled;
      if (el.tagName === "FONT") {
        const face = el.getAttribute("face")?.trim();
        if (face) return face;
      }
    }
    el = el.parentElement;
  }
  return "";
};

/** Unwrap quote/code wrappers back to sibling paragraphs when they hold block children. */
const unwrapStructuralBlockToParagraphs = (block) => {
  if (!block?.parentNode || !block.ownerDocument) return null;
  const parent = block.parentNode;
  const doc = block.ownerDocument;
  const kids = [...block.childNodes];
  const hasBlockKids = kids.some(
    (k) =>
      k.nodeType === 1 &&
      /^(P|DIV|H[1-6]|UL|OL|LI|BLOCKQUOTE|PRE)$/i.test(k.tagName)
  );
  if (!hasBlockKids) {
    return replaceElementPreserveChildren(block, "p");
  }
  let last = null;
  kids.forEach((kid) => {
    if (kid.nodeType === 1 && /^(P|DIV)$/i.test(kid.tagName)) {
      parent.insertBefore(kid, block);
      last = kid;
      return;
    }
    if (kid.nodeType === 1 && /^H[1-6]$/i.test(kid.tagName)) {
      const neo = replaceElementPreserveChildren(kid, "p");
      parent.insertBefore(neo, block);
      last = neo;
      return;
    }
    if (
      kid.nodeType === 3 &&
      String(kid.textContent ?? "").replace(/\u00a0/g, " ").trim() === ""
    ) {
      return;
    }
    const p = doc.createElement("p");
    p.appendChild(kid);
    parent.insertBefore(p, block);
    last = p;
  });
  block.remove();
  return last;
};

/** Prefer &lt;p&gt; for body blocks (Chrome otherwise inserts &lt;div&gt;). */
const ensureDefaultParagraphSeparator = () => {
  if (typeof document === "undefined") return;
  try {
    document.execCommand("defaultParagraphSeparator", false, "p");
  } catch {
    /* unsupported */
  }
};

/**
 * Convert bare contentEditable &lt;div&gt; blocks to &lt;p&gt; (no class / nested blocks).
 */
/** Caret parked on the element itself (empty block) is dropped when that element is replaced. */
const collapsedCaretOffsetOnElement = (el) => {
  if (typeof window === "undefined" || !el) return null;
  const sel = window.getSelection();
  if (!sel?.rangeCount || !sel.isCollapsed || sel.focusNode !== el) return null;
  return sel.focusOffset;
};

const restoreCollapsedCaretOnElement = (el, offset) => {
  if (offset == null || !el || typeof window === "undefined") return;
  const sel = window.getSelection();
  if (!sel) return;
  try {
    const range = document.createRange();
    const max = el.childNodes.length;
    range.setStart(el, Math.max(0, Math.min(offset, max)));
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  } catch {
    /* detached */
  }
};

const normalizeDivBlocksToParagraphs = (root) => {
  if (!root?.ownerDocument) return false;
  let changed = false;
  [...root.querySelectorAll("div")].forEach((div) => {
    if (!root.contains(div) || div === root) return;
    if (div.closest("pre, table, .rte-code-gutter, figure.rte-flow, figure.rte-image")) return;
    if (div.className && String(div.className).trim()) return;
    if (
      div.querySelector(
        "ul, ol, blockquote, h1, h2, h3, h4, h5, h6, pre, table, div, section"
      )
    ) {
      return;
    }
    const caretOffset = collapsedCaretOffsetOnElement(div);
    const css = div.getAttribute("style");
    const neo = replaceElementPreserveChildren(div, "p");
    if (css) neo.setAttribute("style", css);
    if (caretOffset != null) restoreCollapsedCaretOnElement(neo, caretOffset);
    changed = true;
  });
  return changed;
};

const RTE_DOC_BASE_VAR = "--rte-doc-base";

/**
 * Headings scale from --rte-doc-base. Body text-size edits must not rewrite that
 * baseline, or every heading rescales with the selection.
 */
const refreshRteDocBaseVar = (rootEl) => {
  if (!rootEl?.style) return;
};

/** Headings, table header cells, and captions keep their own size. */
const isTableHeaderCell = (el) => {
  if (!el || el.nodeType !== 1) return false;
  const cell = el.closest?.("th, td");
  if (!cell) return false;
  if (cell.tagName === "TH") return true;
  const table = cell.closest("table");
  if (!table?.classList?.contains("rte-table-style-header")) return false;
  const firstRow = table.querySelector("tr");
  return Boolean(firstRow && cell.parentElement === firstRow);
};

/** Diagram artwork, not its caption. */
const isInsideFlowDiagramBody = (el) => {
  if (!el || el.nodeType !== 1) return false;
  const fig = el.closest?.("figure.rte-flow");
  if (!fig) return false;
  if (el.closest?.("figcaption, .rte-caption")) return false;
  return true;
};

const isFontSizeExemptElement = (el) => {
  if (!el || el.nodeType !== 1) return false;
  if (isInsideFlowDiagramBody(el)) return true;
  if (el.closest("h1, h2, h3, h4, h5, h6, figcaption, .rte-caption, [data-guides-title]")) {
    return true;
  }
  return isTableHeaderCell(el);
};

const isSkippableFontChrome = (el) =>
  isInsideFlowDiagramBody(el)
  || !!el?.closest?.(".rte-code-gutter, .rte-caption-catalog, figure.rte-image svg");

/** Keep diagram label metrics on the SVG text, so a document font cannot inherit in. */
const lockFlowDiagramTypography = (root) => {
  root?.querySelectorAll?.("figure.rte-flow").forEach((fig) => {
    fig.style?.removeProperty("font-size");
    fig.style?.removeProperty("font-family");
    fig.style?.removeProperty("line-height");
    cleanupEmptyStyleAttr(fig);
    fig.querySelectorAll("svg text, svg tspan").forEach((node) => {
      if (!node.style) return;
      const size = node.getAttribute("font-size");
      const family = node.getAttribute("font-family");
      const weight = node.getAttribute("font-weight");
      if (size) {
        const cssSize = /[a-z%]/i.test(String(size)) ? String(size) : `${size}px`;
        node.style.setProperty("font-size", cssSize, "important");
      }
      if (family) node.style.setProperty("font-family", family, "important");
      if (weight) node.style.setProperty("font-weight", weight, "important");
      const fontStyle = node.getAttribute("font-style");
      node.style.setProperty("font-style", fontStyle || "normal", "important");
      const decoration = node.getAttribute("text-decoration");
      node.style.setProperty("text-decoration", decoration || "none", "important");
      const anchor = node.getAttribute("text-anchor");
      if (anchor) node.style.setProperty("text-anchor", anchor, "important");
      node.style.setProperty("text-indent", "0", "important");
      node.style.setProperty("letter-spacing", "0px", "important");
      node.style.setProperty("word-spacing", "0px", "important");
    });
  });
};

const rangeCoversNodeContents = (range, node) => {
  if (!range || !node) return false;
  try {
    const br = document.createRange();
    br.selectNodeContents(node);
    return (
      range.compareBoundaryPoints(Range.START_TO_START, br) <= 0 &&
      range.compareBoundaryPoints(Range.END_TO_END, br) >= 0
    );
  } catch {
    return false;
  }
};

/** Citation marks and the bibliography are not part of a normal text selection. */
const isReferenceChromeNode = (node) => {
  const el = node?.nodeType === 1 ? node : node?.parentElement;
  return !!el?.closest?.(".rte-cite-marker, [data-rte-generated='bibliography']");
};

/** Keep a text selection out of citation marks and the reference list. */
const trimSelectionAroundReferences = (root) => {
  const sel = window.getSelection();
  if (!sel?.rangeCount || !root) return;
  const range = sel.getRangeAt(0);
  const ancestor = range.commonAncestorContainer;
  if (ancestor !== root && !root.contains(ancestor)) return;

  const next = range.cloneRange();
  let changed = false;
  const markerOf = (node) => {
    const el = node?.nodeType === 1 ? node : node?.parentElement;
    const marker = el?.closest?.(".rte-cite-marker");
    return marker && root.contains(marker) ? marker : null;
  };

  const bib = root.querySelector("[data-rte-generated='bibliography']");
  if (bib && root.contains(bib)) {
    let hits = false;
    try {
      hits = next.intersectsNode(bib);
    } catch {
      hits = bib.contains(next.startContainer) || bib.contains(next.endContainer);
    }
    if (hits) {
      if (bib.contains(next.startContainer)) {
        next.setStartBefore(bib);
        next.collapse(true);
        changed = true;
      } else {
        try {
          next.setEndBefore(bib);
          changed = true;
        } catch {
          /* range already ends before the list */
        }
      }
    }
  }

  const startMarker = markerOf(next.startContainer);
  const endMarker = markerOf(next.endContainer);
  if (startMarker && startMarker === endMarker) {
    next.setStartAfter(startMarker);
    next.collapse(true);
    changed = true;
  } else {
    if (startMarker) {
      next.setStartAfter(startMarker);
      changed = true;
    }
    if (endMarker && !next.collapsed) {
      next.setEndBefore(endMarker);
      changed = true;
    }
  }

  if (!changed) return;
  sel.removeAllRanges();
  sel.addRange(next);
};

const stripReferenceChromeText = (el) => {
  if (!el?.cloneNode) return "";
  const clone = el.cloneNode(true);
  clone.querySelectorAll(".rte-cite-marker, [data-rte-generated='bibliography']").forEach((node) => node.remove());
  return normalizeVisibleText(clone.innerText || clone.textContent || "");
};

/** A block counts as fully selected when only citation marks sit outside the range. */
const rangeCoversEditableBlock = (range, block) => {
  if (!range || !block) return false;
  if (rangeCoversNodeContents(range, block)) return true;
  const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  let any = false;
  while (node) {
    const value = String(node.nodeValue || "");
    if (!value.trim() || isReferenceChromeNode(node)) {
      node = walker.nextNode();
      continue;
    }
    any = true;
    let hit = false;
    try {
      hit = range.intersectsNode(node);
    } catch {
      hit = false;
    }
    if (!hit) return false;
    if (node === range.startContainer && range.startOffset > 0) return false;
    if (node === range.endContainer && range.endOffset < value.length) return false;
    node = walker.nextNode();
  }
  return any;
};

/**
 * True when the caret selection covers the whole editor (Select all).
 * A saved range is used when the toolbar click has already collapsed the live selection.
 * Tables count when the selection includes the table, even if it does not enter cell text.
 */
const selectionCoversEntireEditor = (root, savedRange) => {
  if (!root || typeof window === "undefined") return false;
  const sel = window.getSelection();
  const live = sel?.rangeCount ? sel.getRangeAt(0) : null;
  const liveOk = live && !live.collapsed && root.contains(live.commonAncestorContainer);
  let savedOk = false;
  try {
    savedOk = Boolean(
      savedRange && !savedRange.collapsed && root.contains(savedRange.commonAncestorContainer)
    );
  } catch {
    savedOk = false;
  }
  const range = liveOk ? live : savedOk ? savedRange : null;
  if (!range) return false;

  if (rangeCoversNodeContents(range, root)) return true;

  const selected = normalizeVisibleText(range.toString());
  const all = normalizeVisibleText(root.innerText);
  if (all && selected && selected === all) return true;
  const withoutReferences = stripReferenceChromeText(root);
  if (withoutReferences && selected && selected === withoutReferences) return true;

  const blocks = [...root.querySelectorAll("p, li, h1, h2, h3, h4, h5, h6, blockquote, pre, td, th, div")]
    .filter((el) => root.contains(el)
      && !isSkippableFontChrome(el)
      && !el.closest("[data-rte-generated='bibliography']")
      && normalizeVisibleText(el.innerText));
  const outside = blocks.filter((el) => !el.closest("table"));
  const tables = [...root.querySelectorAll("table")].filter((table) => root.contains(table));
  if (!outside.length && !tables.length) return false;
  if (!outside.every((block) => rangeCoversEditableBlock(range, block))) return false;
  return tables.every((table) => {
    if (rangeCoversNodeContents(range, table)) return true;
    try {
      return range.intersectsNode(table);
    } catch {
      return false;
    }
  });
};

const isHeadingOrTitleElement = (el) => {
  if (!el || el.nodeType !== 1) return false;
  if (/^H[1-6]$/i.test(el.tagName) || el.hasAttribute?.("data-guides-title")) return true;
  return !!el.closest?.("h1, h2, h3, h4, h5, h6, [data-guides-title]");
};

const clearFontPropertyInTree = (root, property) => {
  if (!root?.querySelectorAll) return;
  [root, ...root.querySelectorAll("*")].forEach((el) => {
    if (!el.style || isSkippableFontChrome(el)) return;
    if (property === "font-size" && isHeadingOrTitleElement(el)) return;
    if (property === "font-size") {
      el.style.removeProperty("font-size");
      if (el.tagName === "FONT") el.removeAttribute("size");
    } else {
      el.style.removeProperty("font-family");
      el.style.removeProperty("font");
      if (el.tagName === "FONT") el.removeAttribute("face");
    }
    cleanupEmptyStyleAttr(el);
  });
};

const paintFontFamilyOn = (el, family) => {
  if (!el?.style || isSkippableFontChrome(el)) return;
  const storesFamily = el.classList?.contains("rte-cite-marker")
    || el.matches?.("[data-rte-generated='bibliography']");
  if (family) {
    el.style.setProperty("font-family", family, "important");
    if (storesFamily) el.setAttribute("data-rte-font-family", family);
  } else {
    el.style.removeProperty("font-family");
    if (storesFamily) el.removeAttribute("data-rte-font-family");
    cleanupEmptyStyleAttr(el);
  }
};

const referenceHostBlock = (el) => {
  if (el?.closest?.("[data-rte-generated='bibliography']") || el?.matches?.("[data-rte-generated='bibliography']")) {
    return null;
  }
  return el?.closest?.("p, li, td, th, blockquote") || null;
};

/** Citation marks are not selectable. Include a marker when its paragraph or cited text is in the range. */
const referenceNodesForFont = (root, range, entire) => {
  if (!root?.querySelectorAll) return [];
  const nodes = [];
  const seen = new Set();
  const add = (el) => {
    if (!el || seen.has(el) || !root.contains(el) || isSkippableFontChrome(el)) return;
    seen.add(el);
    nodes.push(el);
  };
  const hits = (el) => {
    if (entire) return true;
    if (!range) return false;
    try {
      if (range.intersectsNode(el)) return true;
    } catch {
      /* detached */
    }
    const block = referenceHostBlock(el);
    if (!block) return false;
    try {
      return range.intersectsNode(block);
    } catch {
      return false;
    }
  };
  root.querySelectorAll(".rte-cite, .rte-cite *, .rte-cite-marker, .rte-cite-marker *, [data-rte-generated='bibliography'], [data-rte-generated='bibliography'] *").forEach((el) => {
    if (hits(el)) add(el);
  });
  if (!entire && range) {
    root.querySelectorAll(".rte-cite[data-cite-id]").forEach((cite) => {
      if (!hits(cite)) return;
      const id = cite.getAttribute("data-cite-id");
      if (!id) return;
      let marker = null;
      try {
        marker = root.querySelector(`.rte-cite-marker[data-cite-id="${CSS.escape(id)}"]`);
      } catch {
        marker = null;
      }
      add(marker);
    });
  }
  return nodes;
};

const forEachFontHost = (root, fn) => {
  if (!root?.querySelectorAll) return;
  root.querySelectorAll(
    "h1, h2, h3, h4, h5, h6, p, li, blockquote, td, th, pre, figcaption, .rte-caption-text, .rte-caption-label, div, span, font, b, strong, i, em, u, s, a"
  ).forEach((el) => {
    if (!root.contains(el) || el === root || isSkippableFontChrome(el)) return;
    fn(el);
  });
};

const applyFontSizeEverywhere = (root, size) => {
  normalizeDivBlocksToParagraphs(root);
  clearFontPropertyInTree(root, "font-size");
  const paint = (el) => {
    if (!el?.style || isSkippableFontChrome(el) || isHeadingOrTitleElement(el)) return;
    const marker = el.classList?.contains("rte-cite-marker") || !!el.closest?.(".rte-cite-marker");
    const bibliography = el.matches?.("[data-rte-generated='bibliography']")
      || !!el.closest?.("[data-rte-generated='bibliography']");
    const caption = el.tagName === "FIGCAPTION"
      || el.classList?.contains("rte-caption")
      || el.classList?.contains("rte-caption-text")
      || el.classList?.contains("rte-caption-label");
    if (marker) {
      el.style.setProperty("font-size", `calc(${size} * 0.75)`, "important");
    } else if (caption || bibliography) {
      el.style.setProperty("font-size", size, "important");
    } else {
      el.style.fontSize = size;
      el.style.lineHeight = getEffectiveLineSpacing(el);
    }
    if (caption) {
      const cap = el.closest("figcaption") || el;
      cap.setAttribute?.("data-rte-caption-size", size);
      el.closest("figure")?.setAttribute("data-rte-caption-size", size);
    }
  };
  forEachFontHost(root, paint);
  root.querySelectorAll(
    ".rte-cite, .rte-cite-marker, .rte-cite-marker *, [data-rte-generated='bibliography'], [data-rte-generated='bibliography'] *"
  ).forEach(paint);
  lockFlowDiagramTypography(root);
};

const FONT_FAMILY_BLOCK_SELECTOR = [
  "h1", "h2", "h3", "h4", "h5", "h6",
  "p", "li", "ul", "ol", "blockquote", "td", "th",
  "figcaption", ".rte-caption", ".rte-caption-text", ".rte-caption-label",
  ".rte-cite", ".rte-cite-marker",
  "[data-rte-generated='bibliography']"
].join(", ");

const applyFontFamilyEverywhere = (root, family, titleEl) => {
  normalizeDivBlocksToParagraphs(root);
  clearFontPropertyInTree(root, "font-family");
  const paint = (el) => {
    if (!el?.style || isSkippableFontChrome(el)) return;
    paintFontFamilyOn(el, family);
  };
  if (family) root.style?.setProperty("font-family", family, "important");
  else {
    root.style?.removeProperty("font-family");
    cleanupEmptyStyleAttr(root);
  }
  forEachFontHost(root, paint);
  root.querySelectorAll(FONT_FAMILY_BLOCK_SELECTOR).forEach(paint);
  if (titleEl) paint(titleEl);
  referenceNodesForFont(root, null, true).forEach((el) => paintFontFamilyOn(el, family));
  lockFlowDiagramTypography(root);
};

/** Left gutter line numbers for &lt;pre&gt; code blocks. */
const RTE_CODE_GUTTER_CLASS = "rte-code-gutter";
const RTE_CODE_GUTTER_LINE_CLASS = "rte-code-gutter-line";

/**
 * Hard lines only (Enter / \\n / <br>). Drops one trailing empty segment that
 * contentEditable often adds as a caret <br> without a real next line.
 */
const getPreHardLineTexts = (pre) => {
  const gutter = pre.querySelector(`:scope > .${RTE_CODE_GUTTER_CLASS}`);
  const prevDisplay = gutter ? gutter.style.display : "";
  if (gutter) gutter.style.display = "none";

  const text = pre.innerText ?? pre.textContent ?? "";

  if (gutter) gutter.style.display = prevDisplay;

  const parts = String(text).split("\n");
  if (parts.length > 1 && parts[parts.length - 1] === "") {
    parts.pop();
  }
  if (!parts.length) return [""];
  return parts;
};

const measurePreLineHeight = (pre, lineText, contentWidthPx) => {
  const cs = window.getComputedStyle(pre);
  const probe = document.createElement("div");
  probe.setAttribute("aria-hidden", "true");
  probe.style.cssText = [
    "position:absolute",
    "left:0",
    "top:0",
    "visibility:hidden",
    "pointer-events:none",
    `width:${Math.max(1, contentWidthPx)}px`,
    "white-space:pre-wrap",
    "overflow-wrap:break-word",
    "word-break:break-word",
    `font-family:${cs.fontFamily}`,
    `font-size:${cs.fontSize}`,
    `font-weight:${cs.fontWeight}`,
    `font-style:${cs.fontStyle}`,
    `line-height:${cs.lineHeight}`,
    `letter-spacing:${cs.letterSpacing}`,
    `tab-size:${cs.tabSize || 2}`,
    "padding:0",
    "margin:0",
    "border:0",
    "box-sizing:border-box",
  ].join(";");
  probe.textContent = lineText.length ? lineText : "\u00a0";
  pre.appendChild(probe);
  const height = Math.max(probe.offsetHeight, parseFloat(cs.lineHeight) || 0);
  probe.remove();
  return height;
};

const refreshCodeBlockLineNumbers = (rootEl) => {
  if (!rootEl?.querySelectorAll || typeof window === "undefined") return;

  rootEl.querySelectorAll("pre").forEach((pre) => {
    pre.removeAttribute("data-line-numbers");

    let gutter = pre.querySelector(`:scope > .${RTE_CODE_GUTTER_CLASS}`);
    if (!gutter) {
      gutter = document.createElement("span");
      gutter.className = RTE_CODE_GUTTER_CLASS;
      gutter.contentEditable = "false";
      gutter.setAttribute("aria-hidden", "true");
      pre.insertBefore(gutter, pre.firstChild);
    }

    const cs = window.getComputedStyle(pre);
    const padL = parseFloat(cs.paddingLeft) || 0;
    const padR = parseFloat(cs.paddingRight) || 0;
    const contentWidth = Math.max(1, pre.clientWidth - padL - padR);
    const lines = getPreHardLineTexts(pre);

    const frag = document.createDocumentFragment();
    lines.forEach((lineText, i) => {
      const row = document.createElement("span");
      row.className = RTE_CODE_GUTTER_LINE_CLASS;
      row.textContent = String(i + 1);
      row.style.height = `${measurePreLineHeight(pre, lineText, contentWidth)}px`;
      frag.appendChild(row);
    });
    gutter.replaceChildren(frag);
  });
};

const unwrapInlineElementKeepChildren = (el) => {
  const parent = el.parentNode;
  if (!parent) return;
  while (el.firstChild) parent.insertBefore(el.firstChild, el);
  parent.removeChild(el);
};

const isBareStyleSpan = (el) =>
  el &&
  el.nodeType === 1 &&
  el.tagName === "SPAN" &&
  !el.className &&
  !el.id &&
  !el.getAttribute("style")?.trim();

const declaredWeightIsBold = (value) => {
  const w = String(value || "").trim().toLowerCase();
  if (!w || w === "normal" || w === "lighter" || w === "inherit" || w === "initial" || w === "unset") {
    return false;
  }
  if (w === "bold" || w === "bolder") return true;
  const n = parseInt(w, 10);
  return Number.isFinite(n) && n >= 600;
};

/**
 * Headings already carry a weight. Nested bold uses `bolder`, which paints
 * heavier than that weight (600/700 becomes 900). Drop the extra mark so the
 * heading's own weight is the bold.
 */
const releaseInheritedHeadingBold = (heading) => {
  if (!heading || heading.nodeType !== 1 || !/^H[1-6]$/.test(heading.tagName)) return;
  const locked = (el) => Boolean(el.closest?.("[contenteditable='false']"));
  const boldEls = [...heading.querySelectorAll("b, strong")].filter((el) => !locked(el));
  for (let i = boldEls.length - 1; i >= 0; i -= 1) {
    unwrapInlineElementKeepChildren(boldEls[i]);
  }
  const styled = [...heading.querySelectorAll("[style]")].filter((el) => !locked(el));
  for (let i = styled.length - 1; i >= 0; i -= 1) {
    const el = styled[i];
    if (!el.isConnected || !declaredWeightIsBold(el.style?.fontWeight)) continue;
    el.style.removeProperty("font-weight");
    const raw = el.getAttribute("style");
    if (raw != null && !String(raw).trim()) el.removeAttribute("style");
    if (isBareStyleSpan(el)) unwrapInlineElementKeepChildren(el);
  }
};

/** Unitless so line boxes shrink/grow with font-size (esp. list items). */
const RTE_TEXT_LINE_HEIGHT = "1.35";
/** Matches Bootstrap list indent (`ul`/`ol` padding-left). */
const RTE_INDENT_STEP_REM = 2;
const RTE_INDENT_MAX_LEVEL = 8;
const RTE_LIST_MAX_DEPTH = 3;
const RTE_LIST_KIND_ATTR = "data-rte-list";
const RTE_LIST_KINDS = [
  { id: "disc", label: "Bullet", tag: "ul", icon: "bi-list-ul" },
  { id: "chevron", label: "Chevron", tag: "ul", icon: "bi-chevron-right" },
  { id: "diamond", label: "Diamond", tag: "ul", icon: "bi-diamond-fill" },
  { id: "check", label: "Check", tag: "ul", icon: "bi-check-lg" },
  { id: "circle", label: "Circle", tag: "ul", icon: "bi-circle" },
  { id: "dash", label: "Dash", tag: "ul", icon: "bi-dash-lg" },
  { id: "decimal", label: "Numbers", tag: "ol", icon: "bi-list-ol" },
  { id: "alpha", label: "Letters", tag: "ol", icon: "" },
];

const listKindOf = (list) => {
  const attr = list?.getAttribute?.(RTE_LIST_KIND_ATTR);
  if (attr && RTE_LIST_KINDS.some((kind) => kind.id === attr)) return attr;
  if (!list) return "";
  return list.tagName === "OL" ? "decimal" : "disc";
};

const blockHasVisibleText = (el) =>
  !!String(el?.textContent || "")
    .replace(/\u00a0/g, " ")
    .trim();

const htmlBody = (html) => {
  if (typeof DOMParser === "undefined") return null;
  return new DOMParser().parseFromString(String(html || ""), "text/html").body;
};

/** First-seen key wins ties, so the style introduced earliest stays the shortcut. */
const mostCommonKey = (counts, order) => {
  if (!order.length) return "";
  let best = order[0];
  let bestCount = counts.get(best) || 0;
  order.forEach((key) => {
    const n = counts.get(key) || 0;
    if (n > bestCount) {
      best = key;
      bestCount = n;
    }
  });
  return best;
};

/**
 * Selection-popover heading shortcuts.
 * An empty document offers H4 and H5. Once headings exist, the two most-used levels are shown.
 */
const selectionPopoverHeadingLevels = (html) => {
  const body = htmlBody(html);
  if (!body) return ["h4", "h5"];
  const counts = new Map();
  const order = [];
  body.querySelectorAll("h1,h2,h3,h4,h5,h6").forEach((el) => {
    if (el.closest("[data-guides-title]")) return;
    if (!blockHasVisibleText(el)) return;
    const tag = el.tagName.toLowerCase();
    if (!counts.has(tag)) order.push(tag);
    counts.set(tag, (counts.get(tag) || 0) + 1);
  });
  if (!order.length) return ["h4", "h5"];
  return [...order]
    .sort((a, b) => (counts.get(b) || 0) - (counts.get(a) || 0))
    .slice(0, 2);
};

/** One list shortcut. Empty documents use a bullet; otherwise the most-used list kind. */
const selectionPopoverListKind = (html) => {
  const body = htmlBody(html);
  if (!body) return "disc";
  const counts = new Map();
  const order = [];
  body.querySelectorAll("li").forEach((li) => {
    const list = li.parentElement;
    if (!list || (list.tagName !== "UL" && list.tagName !== "OL")) return;
    if (!blockHasVisibleText(li)) return;
    const kind = listKindOf(list);
    if (!RTE_LIST_KINDS.some((item) => item.id === kind)) return;
    if (!counts.has(kind)) order.push(kind);
    counts.set(kind, (counts.get(kind) || 0) + 1);
  });
  return mostCommonKey(counts, order) || "disc";
};

const listDepthOf = (li) => {
  let depth = 0;
  let el = li;
  while (el) {
    if (el.tagName === "LI") depth += 1;
    el = el.parentElement;
  }
  return depth;
};

const stampListKind = (list, kind) => {
  const spec = RTE_LIST_KINDS.find((item) => item.id === kind);
  if (!list || !spec) return list;
  let host = list;
  if (host.tagName.toLowerCase() !== spec.tag) {
    const neo = replaceElementPreserveChildren(host, spec.tag);
    [...host.attributes].forEach((attr) => {
      if (attr.name.toLowerCase() !== RTE_LIST_KIND_ATTR) neo.setAttribute(attr.name, attr.value);
    });
    host = neo;
  }
  host.setAttribute(RTE_LIST_KIND_ATTR, kind);
  return host;
};

/** Line spacing (line-height) for selected blocks. */
const RTE_LINE_SPACING_ATTR = "data-rte-line-spacing";
const RTE_LINE_SPACING_OPTIONS = ["1", "1.15", "1.5", "2", "2.5", "3"];

const isRteLineSpacingValue = (value) =>
  RTE_LINE_SPACING_OPTIONS.includes(String(value ?? "").trim());

const getEffectiveLineSpacing = (el) => {
  let n = el;
  while (n && n.nodeType === 1) {
    const attr = n.getAttribute?.(RTE_LINE_SPACING_ATTR);
    if (attr && isRteLineSpacingValue(attr)) return String(attr).trim();
    const lh = n.style?.lineHeight;
    if (lh && isRteLineSpacingValue(lh)) return String(lh).trim();
    n = n.parentElement;
  }
  return RTE_TEXT_LINE_HEIGHT;
};

const applyFontSizeAndLineHeight = (el, size) => {
  if (!el?.style || isFontSizeExemptElement(el)) return;
  el.style.fontSize = size;
  el.style.lineHeight = getEffectiveLineSpacing(el);
};

const isInlineSizeWrapper = (el) => {
  if (!el || el.nodeType !== 1) return false;
  const tag = el.tagName.toLowerCase();
  if (tag !== "span" && tag !== "font") return false;
  return Boolean(
    el.style?.fontSize ||
      el.style?.lineHeight ||
      (tag === "font" && el.hasAttribute("size"))
  );
};

/** Split an ancestor so `el` becomes its sibling (drops leftover large-size struts). */
const splitAncestorAroundNode = (el, ancestor) => {
  if (!el || !ancestor || el.parentNode !== ancestor) return;
  const parent = ancestor.parentNode;
  if (!parent) return;

  if (ancestor.childNodes.length === 1 && ancestor.firstChild === el) {
    ancestor.style.removeProperty("font-size");
    ancestor.style.removeProperty("line-height");
    if (ancestor.tagName === "FONT") ancestor.removeAttribute("size");
    cleanupEmptyStyleAttr(ancestor);
    if (isBareStyleSpan(ancestor)) unwrapInlineElementKeepChildren(ancestor);
    return;
  }

  const after = ancestor.cloneNode(false);
  let next = el.nextSibling;
  while (next) {
    const keep = next.nextSibling;
    after.appendChild(next);
    next = keep;
  }
  parent.insertBefore(el, ancestor.nextSibling);
  if (after.hasChildNodes()) parent.insertBefore(after, el.nextSibling);
  if (!ancestor.hasChildNodes()) ancestor.remove();
};

const liftOutOfSizedAncestors = (el, stopAt) => {
  let parent = el.parentElement;
  let guard = 0;
  while (parent && parent !== stopAt && guard < 24) {
    guard += 1;
    if (!isInlineSizeWrapper(parent)) {
      parent = parent.parentElement;
      continue;
    }
    splitAncestorAroundNode(el, parent);
    parent = el.parentElement;
  }
};

const fontSizeFullyOverriddenByDescendants = (el) => {
  if (!el?.style?.fontSize) return false;
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
  let sawText = false;
  let tn = walker.nextNode();
  while (tn) {
    if (String(tn.textContent || "").replace(/\u00a0/g, " ").trim()) {
      sawText = true;
      let p = tn.parentElement;
      let covered = false;
      while (p && p !== el) {
        if (p.style?.fontSize) {
          covered = true;
          break;
        }
        p = p.parentElement;
      }
      if (!covered) return false;
    }
    tn = walker.nextNode();
  }
  return sawText;
};

const cleanupEmptyStyleAttr = (el) => {
  if (!el?.getAttribute) return;
  const raw = el.getAttribute("style");
  if (raw != null && String(raw).trim() === "") el.removeAttribute("style");
};

const hasVisibleTextContent = (el) => {
  if (!el) return false;
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
  let node;
  while ((node = walker.nextNode())) {
    if (String(node.textContent || "").replace(/\u00a0/g, " ").trim()) return true;
  }
  return false;
};

const normalizeVisibleText = (s) =>
  String(s || "")
    .replace(/\u00a0/g, " ")
    .replace(/\r\n/g, "\n")
    .trim();

/**
 * Drop font-size/line-height that only inflate line boxes (empty wrappers, parent
 * struts after shrinking text), and re-assert unitless 1.35 on sized nodes.
 */
const explodeBlockFontSizeOntoUnsizedRuns = (block) => {
  if (!block?.style?.fontSize || !block.ownerDocument) return;
  const size = block.style.fontSize;
  const hasSizedDesc = [...block.querySelectorAll("*")].some(
    (el) => Boolean(el.style?.fontSize) && !isFontSizeExemptElement(el)
  );
  if (!hasSizedDesc) return;

  const underSizedAncestor = (node) => {
    let p = node.parentElement;
    while (p && p !== block) {
      if (p.style?.fontSize) return true;
      p = p.parentElement;
    }
    return false;
  };

  const wrapText = (tn) => {
    if (!tn?.parentNode) return;
    if (!String(tn.textContent || "").length) return;
    if (isFontSizeExemptElement(tn.parentElement)) return;
    if (underSizedAncestor(tn)) return;
    const span = block.ownerDocument.createElement("span");
    applyFontSizeAndLineHeight(span, size);
    tn.parentNode.insertBefore(span, tn);
    span.appendChild(tn);
  };

  const walk = (parent) => {
    [...parent.childNodes].forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        wrapText(child);
        return;
      }
      if (child.nodeType !== 1) return;
      if (isFontSizeExemptElement(child)) return;
      if (/^(BR|IMG|HR)$/i.test(child.tagName)) return;
      if (child.style?.fontSize) return;
      const hasNested = [...child.querySelectorAll("*")].some((el) =>
        Boolean(el.style?.fontSize)
      );
      if (hasNested) {
        walk(child);
        return;
      }
      if (underSizedAncestor(child)) return;
      if (
        child.childNodes.length === 1 &&
        child.firstChild.nodeType === Node.TEXT_NODE
      ) {
        applyFontSizeAndLineHeight(child, size);
      } else {
        walk(child);
      }
    });
  };

  walk(block);
  block.style.removeProperty("font-size");
  block.style.removeProperty("line-height");
  cleanupEmptyStyleAttr(block);
};

const normalizeBlockFontMetrics = (block) => {
  if (!block || block.nodeType !== 1) return;

  const candidates = [block, ...block.querySelectorAll("[style], span, font")];
  const seen = new Set();
  candidates.forEach((el) => {
    if (seen.has(el) || el.nodeType !== 1 || !el.style) return;
    seen.add(el);
    if (el !== block && isFontSizeExemptElement(el)) return;
    if (!el.style.fontSize && !el.style.lineHeight) return;
    if (
      el !== block &&
      (!hasVisibleTextContent(el) || fontSizeFullyOverriddenByDescendants(el))
    ) {
      el.style.removeProperty("font-size");
      el.style.removeProperty("line-height");
      if (el.tagName === "FONT") el.removeAttribute("size");
      cleanupEmptyStyleAttr(el);
      if (isBareStyleSpan(el)) unwrapInlineElementKeepChildren(el);
    }
  });

  const sizedDescendants = [...block.querySelectorAll("*")].filter(
    (el) => el.style?.fontSize && !isFontSizeExemptElement(el)
  );
  if (block.style?.fontSize && sizedDescendants.length) {
    // Keep large runs explicit, then drop the block strut so small lines
    // no longer inherit tall top/bottom spacing.
    explodeBlockFontSizeOntoUnsizedRuns(block);
  }

  const ensureSized = [block, ...block.querySelectorAll("*")];
  const lineSpacing = getEffectiveLineSpacing(block);
  ensureSized.forEach((el) => {
    if (!el.style || (el !== block && isFontSizeExemptElement(el))) return;
    if (el.style.fontSize) {
      el.style.lineHeight = lineSpacing;
    } else if (el.style.lineHeight && /px$/i.test(el.style.lineHeight)) {
      // Fixed px line-height from a prior large size keeps tall spacing
      el.style.lineHeight = lineSpacing;
    }
  });

  // Uniform size left only on inlines → promote to the block (keeps list markers in sync).
  const remainingSized = [...block.querySelectorAll("*")].filter(
    (el) => el.style?.fontSize
  );
  if (!block.style?.fontSize && remainingSized.length) {
    const sizes = [
      ...new Set(remainingSized.map((el) => el.style.fontSize.trim()).filter(Boolean)),
    ];
    if (sizes.length === 1 && hasVisibleTextContent(block)) {
      const onlySize = sizes[0];
      const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT, null);
      let tn;
      let allTextCovered = true;
      while ((tn = walker.nextNode())) {
        if (!String(tn.textContent || "").replace(/\u00a0/g, " ").trim()) continue;
        if (isFontSizeExemptElement(tn.parentElement)) continue;
        let p = tn.parentElement;
        let ok = false;
        while (p && p !== block) {
          if (p.style?.fontSize === onlySize) {
            ok = true;
            break;
          }
          p = p.parentElement;
        }
        if (!ok) {
          allTextCovered = false;
          break;
        }
      }
      if (allTextCovered) {
        clearFontSizeStylesInTree(block);
        applyFontSizeAndLineHeight(block, onlySize);
      }
    }
  }
};

const clearBlockLineSpacing = (el) => {
  if (!el) return;
  el.removeAttribute?.(RTE_LINE_SPACING_ATTR);
  if (!el.style) return;
  el.style.removeProperty("line-height");
  // Sized runs fall back to the editor default
  el.querySelectorAll?.("*").forEach((child) => {
    if (!child.style) return;
    if (child.getAttribute?.(RTE_LINE_SPACING_ATTR)) {
      child.removeAttribute(RTE_LINE_SPACING_ATTR);
    }
    if (child.style.fontSize) {
      child.style.lineHeight = RTE_TEXT_LINE_HEIGHT;
    } else if (child.style.lineHeight && isRteLineSpacingValue(child.style.lineHeight)) {
      child.style.removeProperty("line-height");
      cleanupEmptyStyleAttr(child);
    }
  });
  cleanupEmptyStyleAttr(el);
};

const applyBlockLineSpacing = (el, value) => {
  if (!el?.style) return;
  const v = String(value ?? "").trim();
  if (!isRteLineSpacingValue(v)) {
    clearBlockLineSpacing(el);
    return;
  }
  el.setAttribute(RTE_LINE_SPACING_ATTR, v);
  el.style.lineHeight = v;
  el.querySelectorAll("*").forEach((child) => {
    if (!child.style) return;
    if (child.style.fontSize || child.getAttribute?.(RTE_LINE_SPACING_ATTR)) {
      child.setAttribute(RTE_LINE_SPACING_ATTR, v);
      child.style.lineHeight = v;
    }
  });
};

const getBlockLineSpacingValue = (el) => {
  if (!el) return "";
  const attr = el.getAttribute?.(RTE_LINE_SPACING_ATTR);
  if (attr && isRteLineSpacingValue(attr)) return String(attr).trim();
  const lh = el.style?.lineHeight;
  if (lh && isRteLineSpacingValue(lh)) return String(lh).trim();
  return "";
};

/** Bottom margin after paragraphs / headings (`data-rte-mb`). */
const RTE_MB_ATTR = "data-rte-mb";
const RTE_MB_AFTER = "0.75em";

const elementMarginBottomOpen = (el) => {
  if (!el) return false;
  const attr = el.getAttribute?.(RTE_MB_ATTR);
  if (attr === "0") return false;
  if (attr === "1") return true;
  const inline = String(el.style?.marginBottom || "").trim();
  if (inline === "0" || inline === "0px" || inline === "0em") return false;
  if (inline) {
    const n = parseFloat(inline);
    if (Number.isFinite(n)) return n > 0;
  }
  if (typeof window === "undefined" || !window.getComputedStyle) return false;
  try {
    const mb = parseFloat(window.getComputedStyle(el).marginBottom);
    return Number.isFinite(mb) && mb > 0;
  } catch {
    return false;
  }
};

/** The list (or a quote that wraps it) owns the space after the last bullet. */
const marginHostForBlock = (el) => {
  if (el?.tagName !== "LI") return el;
  let list = el.parentElement;
  if (!list || !/^(UL|OL)$/i.test(list.tagName)) return el;
  while (
    list.parentElement?.tagName === "LI" &&
    /^(UL|OL)$/i.test(list.parentElement.parentElement?.tagName)
  ) {
    list = list.parentElement.parentElement;
  }
  if (list.parentElement?.tagName === "BLOCKQUOTE") return list.parentElement;
  return list;
};

/** True when the block currently has a visible bottom margin (attr, inline, or CSS default). */
const blockHasBottomMargin = (el) => {
  if (!el) return false;
  return elementMarginBottomOpen(marginHostForBlock(el));
};

const clearElementBottomMargin = (el) => {
  if (!el?.style) return;
  el.setAttribute(RTE_MB_ATTR, "0");
  el.style.marginBottom = "0";
};

const applyBlockBottomMargin = (el, enabled) => {
  const target = marginHostForBlock(el);
  if (!target?.style) return;
  if (enabled) {
    target.setAttribute(RTE_MB_ATTR, "1");
    target.style.marginBottom = RTE_MB_AFTER;
    return;
  }
  clearElementBottomMargin(target);
};

const clearBlockBottomMarginOverride = (el) => {
  if (!el) return;
  el.removeAttribute?.(RTE_MB_ATTR);
  if (!el.style) return;
  el.style.removeProperty("margin-bottom");
  cleanupEmptyStyleAttr(el);
};

/** Indent, margins/padding used for layout chrome. */
const clearBlockLayoutStyles = (el) => {
  if (!el) return;
  el.removeAttribute?.(RTE_LINE_SPACING_ATTR);
  el.removeAttribute?.(RTE_MB_ATTR);
  el.removeAttribute?.("align");
  if (!el.style) return;
  [
    "padding",
    "padding-left",
    "padding-right",
    "padding-top",
    "padding-bottom",
    "margin",
    "margin-left",
    "margin-right",
    "margin-top",
    "margin-bottom",
    "text-align",
    "line-height",
  ].forEach((prop) => el.style.removeProperty(prop));
  cleanupEmptyStyleAttr(el);
};

const getBlockIndentLevel = (el) => {
  if (!el?.style) return 0;
  const raw = String(el.style.paddingLeft || "").trim();
  if (!raw) return 0;
  if (raw.endsWith("rem")) {
    const n = parseFloat(raw);
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.round(n / RTE_INDENT_STEP_REM));
  }
  if (raw.endsWith("px")) {
    const n = parseFloat(raw);
    if (!Number.isFinite(n)) return 0;
    const stepPx =
      (typeof window !== "undefined" &&
        parseFloat(window.getComputedStyle(document.documentElement).fontSize)) ||
      16;
    return Math.max(0, Math.round(n / (RTE_INDENT_STEP_REM * stepPx)));
  }
  return 0;
};

const setBlockIndentLevel = (el, level) => {
  if (!el?.style) return;
  const next = Math.max(0, Math.min(RTE_INDENT_MAX_LEVEL, level));
  if (next <= 0) {
    el.style.removeProperty("padding-left");
  } else {
    el.style.paddingLeft = `${next * RTE_INDENT_STEP_REM}rem`;
  }
  cleanupEmptyStyleAttr(el);
};

/** Guides topic/article title heading — clear-format must not demote or strip it. */
const RTE_GUIDE_TITLE_ID = "guide-title-placeholder";

const isProtectedTitleHeading = (el, root) => {
  if (!el || !root || el.nodeType !== 1) return false;
  if (!/^H[1-6]$/i.test(el.tagName)) return false;
  if (el.id === RTE_GUIDE_TITLE_ID) return true;
  return el.getAttribute("data-guides-title") === "1";
};

const RTE_NONEMPTY_EMBED_SELECTOR = "img, table, iframe, video, hr, figure";

const rangeHasVisibleContent = (range) => {
  try {
    const text = range
      .toString()
      .replace(/\u00a0/g, " ")
      .replace(/\u200b/g, "")
      .trim();
    if (text) return true;
    const frag = range.cloneContents();
    return Boolean(
      frag.querySelector && frag.querySelector(RTE_NONEMPTY_EMBED_SELECTOR)
    );
  } catch {
    return true;
  }
};

const placeCaretAtBlockStart = (el) => {
  const sel = typeof window !== "undefined" ? window.getSelection() : null;
  if (!sel || !el) return;
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
};

/** End of the last text run. An element-end caret in a heading gets snapped to the document start. */
const placeCaretAtBlockEnd = (el) => {
  const sel = typeof window !== "undefined" ? window.getSelection() : null;
  if (!sel || !el) return;
  const range = document.createRange();
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
  let lastText = null;
  let textNode;
  while ((textNode = walker.nextNode())) {
    if (String(textNode.textContent || "").length) lastText = textNode;
  }
  if (lastText) {
    range.setStart(lastText, lastText.length);
    range.collapse(true);
  } else {
    range.selectNodeContents(el);
    range.collapse(true);
  }
  sel.removeAllRanges();
  sel.addRange(range);
};

const childNodeIndex = (parent, node) => {
  let index = 0;
  let child = parent?.firstChild;
  while (child) {
    if (child === node) return index;
    child = child.nextSibling;
    index += 1;
  }
  return -1;
};

/** Path of child indexes so a caret can be put back after innerHTML is replaced. */
const captureSelectionInRoot = (root) => {
  if (!root || typeof window === "undefined") return null;
  const sel = window.getSelection();
  if (!sel?.rangeCount) return null;
  const range = sel.getRangeAt(0);
  const pointFor = (node, offset) => {
    if (!node || (node !== root && !root.contains(node))) return null;
    const path = [];
    let current = node;
    while (current && current !== root) {
      const parent = current.parentNode;
      if (!parent) return null;
      const index = childNodeIndex(parent, current);
      if (index < 0) return null;
      path.push(index);
      current = parent;
    }
    path.reverse();
    return { path, offset };
  };
  const start = pointFor(range.startContainer, range.startOffset);
  const end = pointFor(range.endContainer, range.endOffset);
  if (!start || !end) return null;
  return { start, end };
};

const resolveSelectionPoint = (root, point) => {
  if (!root || !point) return null;
  let node = root;
  for (const index of point.path) {
    if (!node?.childNodes || index < 0 || index >= node.childNodes.length) return null;
    node = node.childNodes[index];
  }
  const max = node.nodeType === Node.TEXT_NODE ? node.length : node.childNodes.length;
  return { node, offset: Math.max(0, Math.min(point.offset, max)) };
};

const restoreSelectionInRoot = (root, saved) => {
  if (!root || !saved || typeof window === "undefined") return false;
  const start = resolveSelectionPoint(root, saved.start);
  const end = resolveSelectionPoint(root, saved.end);
  if (!start || !end) return false;
  const sel = window.getSelection();
  if (!sel) return false;
  try {
    const range = document.createRange();
    range.setStart(start.node, start.offset);
    range.setEnd(end.node, end.offset);
    sel.removeAllRanges();
    sel.addRange(range);
    return true;
  } catch {
    return false;
  }
};

const replaceEditorHtmlKeepCaret = (root, html) => {
  if (!root) return;
  const textCaret = captureLeafBlockCaret(root);
  const pathCaret = captureSelectionInRoot(root);
  root.innerHTML = html;
  if (!restoreLeafBlockCaret(root, textCaret)) {
    restoreSelectionInRoot(root, pathCaret);
  }
};

const ensureEmptyBlockHasBr = (el) => {
  if (!el?.ownerDocument) return;
  if (hasVisibleTextContent(el)) return;
  if (el.querySelector?.(RTE_NONEMPTY_EMBED_SELECTOR)) return;
  if (el.querySelector?.("br")) return;
  el.appendChild(el.ownerDocument.createElement("br"));
};

const RTE_LEAF_BLOCK_SELECTOR =
  "p, div, li, blockquote, pre, h1, h2, h3, h4, h5, h6";

const listLeafStyleBlocks = (root) => {
  if (!root?.querySelectorAll) return [];
  return [...root.querySelectorAll(RTE_LEAF_BLOCK_SELECTOR)].filter((el) => {
    if (!root.contains(el)) return false;
    if (el.closest?.(`.${RTE_CODE_GUTTER_CLASS}`)) return false;
    if (el.querySelector(RTE_LEAF_BLOCK_SELECTOR)) return false;
    return true;
  });
};

const offsetWithinBlock = (block, node, offset) => {
  if (!block || !node) return 0;
  if (block !== node && !block.contains(node)) return 0;
  try {
    const range = document.createRange();
    range.selectNodeContents(block);
    range.setEnd(node, offset);
    return range.toString().length;
  } catch {
    return 0;
  }
};

const pointWithinBlock = (block, textOffset) => {
  const target = Math.max(0, Number(textOffset) || 0);
  if (!block) return null;
  const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT, null);
  let node;
  let last = null;
  while ((node = walker.nextNode())) {
    last = node;
    try {
      const endRange = document.createRange();
      endRange.selectNodeContents(block);
      endRange.setEnd(node, node.length);
      const endLen = endRange.toString().length;
      if (target <= endLen) {
        const startRange = document.createRange();
        startRange.selectNodeContents(block);
        startRange.setEnd(node, 0);
        const startLen = startRange.toString().length;
        return {
          node,
          offset: Math.max(0, Math.min(node.length, target - startLen)),
        };
      }
    } catch {
      /* detached */
    }
  }
  if (last) return { node: last, offset: last.length };
  return { node: block, offset: Math.min(target, block.childNodes.length) };
};

/** Caret/selection anchored to block text so tag changes don't throw it to the document start. */
const captureLeafBlockCaret = (root) => {
  if (!root || typeof window === "undefined") return null;
  const sel = window.getSelection();
  if (!sel?.rangeCount) return null;
  const range = sel.getRangeAt(0);
  if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) return null;
  const blocks = listLeafStyleBlocks(root);
  const ownerOf = (node) =>
    blocks.find((block) => block === node || block.contains(node)) || null;
  const startBlock = ownerOf(range.startContainer);
  if (!startBlock) return null;
  const startText = startBlock.innerText || "";
  const occurrence = blocks
    .filter((block) => (block.innerText || "") === startText)
    .indexOf(startBlock);
  if (occurrence < 0) return null;
  const start = offsetWithinBlock(startBlock, range.startContainer, range.startOffset);
  if (range.collapsed) {
    return {
      startText,
      occurrence,
      start,
      endText: startText,
      endOccurrence: occurrence,
      end: start,
      collapsed: true,
    };
  }
  const endBlock = ownerOf(range.endContainer) || startBlock;
  const endText = endBlock.innerText || "";
  const endOccurrence = blocks
    .filter((block) => (block.innerText || "") === endText)
    .indexOf(endBlock);
  if (endOccurrence < 0) return null;
  return {
    startText,
    occurrence,
    start,
    endText,
    endOccurrence,
    end: offsetWithinBlock(endBlock, range.endContainer, range.endOffset),
    collapsed: false,
  };
};

const restoreLeafBlockCaret = (root, saved) => {
  if (!root || !saved || typeof window === "undefined") return false;
  const blocks = listLeafStyleBlocks(root);
  const findOcc = (text, occ) =>
    blocks.filter((block) => (block.innerText || "") === text)[occ] || null;
  const startBlock = findOcc(saved.startText, saved.occurrence);
  const endBlock = saved.collapsed
    ? startBlock
    : findOcc(saved.endText, saved.endOccurrence);
  if (!startBlock || !endBlock) return false;
  const startPoint = pointWithinBlock(startBlock, saved.start);
  const endPoint = saved.collapsed ? startPoint : pointWithinBlock(endBlock, saved.end);
  if (!startPoint || !endPoint) return false;
  const sel = window.getSelection();
  if (!sel) return false;
  try {
    const range = document.createRange();
    range.setStart(startPoint.node, startPoint.offset);
    range.setEnd(endPoint.node, endPoint.offset);
    sel.removeAllRanges();
    sel.addRange(range);
    return true;
  } catch {
    return false;
  }
};

const findEnclosingListItem = (node, root) => {
  let el = node?.nodeType === 1 ? node : node?.parentElement;
  while (el && el !== root) {
    if (el.tagName === "LI") return el;
    el = el.parentElement;
  }
  return null;
};

const caretAtBlockEdge = (block, range, edge) => {
  if (!block || !range?.collapsed) return false;
  try {
    const probe = document.createRange();
    probe.selectNodeContents(block);
    if (edge === "start") probe.setEnd(range.startContainer, range.startOffset);
    else probe.setStart(range.endContainer, range.endOffset);
    return !rangeHasVisibleContent(probe);
  } catch {
    return false;
  }
};

const listItemVisuallyEmpty = (li) => {
  if (!li) return false;
  if (li.querySelector?.("ul, ol")) return false;
  if (li.querySelector?.(RTE_NONEMPTY_EMBED_SELECTOR)) return false;
  return !hasVisibleTextContent(li);
};

const copyBlockPresentation = (from, to) => {
  if (!from || !to) return;
  const style = from.getAttribute?.("style");
  if (style) to.setAttribute("style", style);
  [RTE_MB_ATTR, RTE_LINE_SPACING_ATTR].forEach((name) => {
    const value = from.getAttribute?.(name);
    if (value != null) to.setAttribute(name, value);
  });
};

const siblingListItem = (li, dir) => {
  let node = dir === "prev" ? li?.previousElementSibling : li?.nextElementSibling;
  while (node && node.tagName !== "LI") {
    node = dir === "prev" ? node.previousElementSibling : node.nextElementSibling;
  }
  return node && node.tagName === "LI" ? node : null;
};

const placeCaretAtChildIndex = (parent, index) => {
  if (!parent || typeof window === "undefined") return;
  const sel = window.getSelection();
  if (!sel) return;
  const range = document.createRange();
  const prev = parent.childNodes[index - 1];
  if (prev?.nodeType === 3 && String(prev.textContent || "").length) {
    range.setStart(prev, prev.length);
    range.collapse(true);
  } else {
    const next = parent.childNodes[index];
    if (next?.nodeType === 3) {
      range.setStart(next, 0);
      range.collapse(true);
    } else {
      range.setStart(parent, Math.max(0, Math.min(index, parent.childNodes.length)));
      range.collapse(true);
    }
  }
  sel.removeAllRanges();
  sel.addRange(range);
};

const stripCaretPlaceholderBr = (el) => {
  if (!el) return;
  if (!hasVisibleTextContent(el)) {
    while (el.firstChild) el.removeChild(el.firstChild);
    return;
  }
  const last = el.lastChild;
  if (last?.nodeType === 1 && last.tagName === "BR") last.remove();
};

/** Move `source` into `target` and leave the caret where the two met. */
const mergeNodeInto = (target, source) => {
  if (!target || !source || !target.parentNode) return false;
  stripCaretPlaceholderBr(target);
  const joinIndex = target.childNodes.length;
  const trailingLists = [];
  while (source.firstChild) {
    const child = source.firstChild;
    if (
      child.nodeType === 1 &&
      /^(UL|OL)$/i.test(child.tagName) &&
      !/^(LI)$/i.test(target.tagName)
    ) {
      trailingLists.push(child);
      child.remove();
    } else {
      target.appendChild(child);
    }
  }
  source.remove();
  placeCaretAtChildIndex(target, joinIndex);
  ensureEmptyBlockHasBr(target);
  trailingLists.forEach((list) => target.after(list));
  return true;
};

/** Plain paragraph / heading only. Widgets (text boxes, figures) stay put. */
const isPlainFlowBlock = (el) => {
  if (!el || el.nodeType !== 1) return false;
  const tag = el.tagName;
  if (!/^(P|DIV|H[1-6])$/i.test(tag)) return false;
  if (tag === "DIV" && String(el.className || "").trim()) return false;
  if (el.closest?.("pre, figure")) return false;
  return true;
};

/**
 * Backspace at the start of a pasted line joins it to the previous line.
 * Doing this ourselves keeps the caret at the join. The browser merge is
 * undone when a later pass rewrites the DOM.
 */
const joinPlainBlockWithPrevious = (block) => {
  if (!isPlainFlowBlock(block)) return false;
  const prev = block.previousElementSibling;
  if (!isPlainFlowBlock(prev)) return false;
  if (block.parentNode !== prev.parentNode) return false;
  if (!hasVisibleTextContent(block)) {
    block.remove();
    placeCaretAtBlockEnd(prev);
    return true;
  }
  return mergeNodeInto(prev, block);
};

/**
 * Enter in a plain paragraph opens the next line and leaves the caret there.
 */
const splitPlainBlockOnEnter = (block, range) => {
  if (!isPlainFlowBlock(block) || !range || !block.parentNode) return false;
  if (block.closest?.("li")) return false;

  let host = block;
  let startContainer = range.startContainer;
  let startOffset = range.startOffset;
  if (block.tagName === "DIV") {
    const caretOnElement = startContainer === block;
    host = replaceElementPreserveChildren(block, "p");
    copyBlockPresentation(block, host);
    if (!host) return false;
    if (caretOnElement) {
      startContainer = host;
      startOffset = Math.min(startOffset, host.childNodes.length);
    }
  }

  const doc = host.ownerDocument;
  const neo = doc.createElement("p");
  copyBlockPresentation(host, neo);
  try {
    const tail = doc.createRange();
    tail.selectNodeContents(host);
    tail.setStart(startContainer, startOffset);
    neo.appendChild(tail.extractContents());
  } catch {
    return false;
  }
  ensureEmptyBlockHasBr(host);
  ensureEmptyBlockHasBr(neo);
  host.after(neo);
  placeCaretAtBlockStart(neo);
  return true;
};

const removeListIfEmpty = (list) => {
  if (!list?.parentNode) return;
  if ([...list.children].some((child) => child.tagName === "LI")) return;
  list.remove();
};

/**
 * Pull one list item out as its own block, splitting the list when the item
 * sits between siblings. The caret is not moved; callers place it.
 */
const liftListItemOut = (li, tagLower = "p") => {
  const list = li?.parentElement;
  if (!list || !/^(UL|OL)$/i.test(list.tagName) || !list.parentNode) return null;
  const doc = li.ownerDocument;
  const block = doc.createElement(String(tagLower || "p").toLowerCase());
  copyBlockPresentation(li, block);
  const nested = [];
  while (li.firstChild) {
    const child = li.firstChild;
    if (child.nodeType === 1 && /^(UL|OL)$/i.test(child.tagName)) {
      nested.push(child);
      child.remove();
    } else {
      block.appendChild(child);
    }
  }
  ensureEmptyBlockHasBr(block);

  const before = [];
  const after = [];
  let seen = false;
  [...list.children].forEach((child) => {
    if (child === li) {
      seen = true;
      return;
    }
    if (child.tagName !== "LI") return;
    (seen ? after : before).push(child);
  });

  const parent = list.parentNode;
  const insertNested = (afterNode) => {
    let cursor = afterNode;
    nested.forEach((listNode) => {
      cursor.after(listNode);
      cursor = listNode;
    });
  };

  if (!before.length && !after.length) {
    parent.insertBefore(block, list);
    insertNested(block);
    list.remove();
    return block;
  }

  li.remove();
  if (!before.length) {
    parent.insertBefore(block, list);
    insertNested(block);
    return block;
  }
  if (!after.length) {
    list.after(block);
    insertNested(block);
    return block;
  }

  const rest = doc.createElement(list.tagName.toLowerCase());
  [...list.attributes].forEach((attr) => rest.setAttribute(attr.name, attr.value));
  after.forEach((item) => rest.appendChild(item));
  list.after(block);
  insertNested(block);
  const anchor = nested.length ? nested[nested.length - 1] : block;
  anchor.after(rest);
  return block;
};

const outdentListItem = (li) => {
  const list = li?.parentElement;
  const parentLi = list?.parentElement;
  const grandList = parentLi?.parentElement;
  if (!list || parentLi?.tagName !== "LI" || !/^(UL|OL)$/i.test(grandList?.tagName)) {
    return false;
  }
  parentLi.after(li);
  removeListIfEmpty(list);
  return true;
};

const indentListItem = (li) => {
  if (!li || listDepthOf(li) >= RTE_LIST_MAX_DEPTH) return false;
  const prev = siblingListItem(li, "prev");
  const parentList = li.parentElement;
  if (!prev || !parentList) return false;
  const kind = listKindOf(parentList);
  let sub = [...prev.children].reverse().find((child) => /^(UL|OL)$/i.test(child.tagName));
  if (!sub) {
    sub = li.ownerDocument.createElement(parentList.tagName.toLowerCase());
    const attr = parentList.getAttribute(RTE_LIST_KIND_ATTR);
    if (attr) sub.setAttribute(RTE_LIST_KIND_ATTR, attr);
    prev.appendChild(sub);
  }
  sub.appendChild(li);
  stampListKind(sub, kind);
  placeCaretAtBlockStart(li);
  return true;
};

/** The lifted line does not add a second gap. The list above keeps its end spacing. */
const keepLiftedListLineTight = (block) => {
  if (!block?.style) return;
  block.setAttribute(RTE_MB_ATTR, "0");
  block.style.marginTop = "0";
  block.style.marginBottom = "0";
};

const clearListItem = (li) => {
  const block = liftListItemOut(li, "p");
  if (!block) return false;
  keepLiftedListLineTight(block);
  placeCaretAtBlockStart(block);
  return true;
};

const exitListItemToParagraph = (li) => {
  if (outdentListItem(li)) {
    placeCaretAtBlockStart(li);
    return true;
  }
  const block = liftListItemOut(li, "p");
  if (!block) return false;
  keepLiftedListLineTight(block);
  placeCaretAtBlockStart(block);
  return true;
};

const copyElementAttributes = (from, to, skip = []) => {
  if (!from?.attributes || !to) return;
  const skipSet = new Set(skip.map((name) => name.toLowerCase()));
  [...from.attributes].forEach((attr) => {
    if (skipSet.has(attr.name.toLowerCase())) return;
    to.setAttribute(attr.name, attr.value);
  });
};

const listsAreSameKind = (a, b) =>
  !!(
    a &&
    b &&
    /^(UL|OL)$/i.test(a.tagName) &&
    a.tagName === b.tagName &&
    listKindOf(a) === listKindOf(b)
  );

const mergeAdjacentSameLists = (list) => {
  if (!list?.parentNode) return list;
  let prev = list.previousElementSibling;
  while (listsAreSameKind(prev, list)) {
    while (prev.firstChild) list.insertBefore(prev.firstChild, list.firstChild);
    const dead = prev;
    prev = prev.previousElementSibling;
    dead.remove();
  }
  let next = list.nextElementSibling;
  while (listsAreSameKind(list, next)) {
    while (next.firstChild) list.appendChild(next.firstChild);
    const dead = next;
    next = next.nextElementSibling;
    dead.remove();
  }
  return list;
};

/** Pull a consecutive run of items out as their own list, keeping the list kind. */
const detachListRun = (items) => {
  const first = items?.[0];
  const list = first?.parentElement;
  if (!list || !/^(UL|OL)$/i.test(list.tagName) || !list.parentNode) return null;
  const doc = list.ownerDocument;
  const all = [...list.children].filter((child) => child.tagName === "LI");
  const start = all.indexOf(first);
  const end = all.indexOf(items[items.length - 1]);
  if (start < 0 || end < start) return null;
  const run = all.slice(start, end + 1);
  const before = all.slice(0, start);
  const after = all.slice(end + 1);
  if (!before.length && !after.length) return list;
  const extracted = doc.createElement(list.tagName.toLowerCase());
  copyElementAttributes(list, extracted, ["id"]);
  run.forEach((li) => extracted.appendChild(li));
  if (!before.length) {
    list.parentNode.insertBefore(extracted, list);
    return extracted;
  }
  if (!after.length) {
    list.after(extracted);
    return extracted;
  }
  const rest = doc.createElement(list.tagName.toLowerCase());
  copyElementAttributes(list, rest, ["id"]);
  after.forEach((item) => rest.appendChild(item));
  list.after(extracted);
  extracted.after(rest);
  return extracted;
};

const quoteHoldsBlocks = (quote) =>
  [...(quote?.childNodes || [])].some(
    (kid) =>
      kid.nodeType === 1 &&
      /^(P|DIV|H[1-6]|UL|OL|LI|BLOCKQUOTE|PRE|TABLE)$/i.test(kid.tagName)
  );

const QUOTE_FROM_ATTR = "data-rte-quote-from";

/** Put the quoted blocks back, including list kind and heading level. */
const unwrapRichTextQuote = (quote) => {
  if (!quote?.parentNode) return null;
  const parent = quote.parentNode;
  const doc = quote.ownerDocument;
  if (!quoteHoldsBlocks(quote)) {
    const from = String(quote.getAttribute(QUOTE_FROM_ATTR) || "p").toLowerCase();
    if (from === "li") {
      const kind = listKindOf(quote) || "disc";
      const spec = RTE_LIST_KINDS.find((item) => item.id === kind) || RTE_LIST_KINDS[0];
      const list = doc.createElement(spec.tag);
      stampListKind(list, spec.id);
      const li = doc.createElement("li");
      while (quote.firstChild) li.appendChild(quote.firstChild);
      copyBlockPresentation(quote, li);
      ensureEmptyBlockHasBr(li);
      list.appendChild(li);
      quote.replaceWith(list);
      mergeAdjacentSameLists(list);
      return li;
    }
    const tag = /^(p|h[1-6])$/.test(from) ? from : "p";
    const neo = replaceElementPreserveChildren(quote, tag);
    copyElementAttributes(quote, neo, [QUOTE_FROM_ATTR, RTE_QUOTE_STYLE_ATTR, RTE_QUOTE_COLOR_ATTR]);
    return neo;
  }
  let last = null;
  [...quote.childNodes].forEach((kid) => {
    if (
      kid.nodeType === 3 &&
      String(kid.textContent ?? "").replace(/\u00a0/g, " ").trim() === ""
    ) {
      return;
    }
    parent.insertBefore(kid, quote);
    if (kid.nodeType === 1) last = kid;
  });
  quote.remove();
  let node = parent.firstElementChild;
  while (node && node.parentNode === parent) {
    if (/^(UL|OL)$/i.test(node.tagName)) mergeAdjacentSameLists(node);
    node = node.nextElementSibling;
  }
  return last;
};

const wrapBlocksInQuote = (blocks) => {
  const live = (blocks || []).filter((block) => block?.parentNode);
  if (!live.length) return null;
  const doc = live[0].ownerDocument;
  const pieces = [];
  let index = 0;
  while (index < live.length) {
    const block = live[index];
    if (!block.parentNode) {
      index += 1;
      continue;
    }
    if (block.tagName === "LI") {
      const run = [block];
      let nextIndex = index + 1;
      while (
        nextIndex < live.length &&
        live[nextIndex].tagName === "LI" &&
        live[nextIndex].parentElement === block.parentElement &&
        live[nextIndex].parentNode
      ) {
        run.push(live[nextIndex]);
        nextIndex += 1;
      }
      const extracted = detachListRun(run);
      if (extracted) pieces.push(extracted);
      index = nextIndex;
      continue;
    }
    pieces.push(block);
    index += 1;
  }
  if (!pieces.length) return null;
  const quote = doc.createElement("blockquote");
  pieces[0].parentNode.insertBefore(quote, pieces[0]);
  pieces.forEach((piece) => quote.appendChild(piece));
  return quote;
};

const toggleRichTextQuote = (root) => {
  if (!root || typeof window === "undefined") return false;
  const sel = window.getSelection();
  if (!sel?.rangeCount || !root.contains(sel.anchorNode)) return false;
  const range = sel.getRangeAt(0);
  const focus = sel.focusNode ?? sel.anchorNode;
  const quoteOf = (node) => findBlockquoteAncestor(node, root);
  const finish = (el) => {
    root.querySelectorAll("blockquote").forEach((quote) => {
      if (!root.contains(quote)) return;
      const text = String(quote.textContent ?? "").replace(/\u00a0/g, " ").trim();
      const hasMedia = quote.querySelector("img, table, video, iframe, ul, ol, pre");
      if (!text && !hasMedia) quote.remove();
    });
    if (el && root.contains(el)) placeCaretAtBlockEnd(el);
    return true;
  };

  if (!range.collapsed) {
    const blocks = collectIntersectingStructuralBlocks(range, root, { includePre: true }).filter(
      (block) => {
        if (!block || !root.contains(block) || block.closest(".rte-textbox")) return false;
        if (block.tagName !== "PRE" && block.closest("pre")) return false;
        return true;
      }
    );
    if (blocks.length) {
      const quotes = blocks.map((block) => quoteOf(block));
      const shared = quotes[0];
      if (shared && quotes.every((quote) => quote === shared)) {
        return finish(unwrapRichTextQuote(shared));
      }
      const outside = blocks.filter((block) => !quoteOf(block));
      return finish(wrapBlocksInQuote(outside.length ? outside : blocks));
    }
  }

  const existing = quoteOf(focus);
  if (existing) return finish(unwrapRichTextQuote(existing));
  const block = findStructuralBlockAncestor(focus, root);
  if (!block || block === root) return false;
  if (block.closest?.(".rte-textbox")) return false;
  if (block.tagName !== "PRE" && block.closest?.("pre")) return false;
  return finish(wrapBlocksInQuote([block]));
};

const backspaceAtListItemStart = (li) => {
  if (outdentListItem(li)) {
    placeCaretAtBlockStart(li);
    return true;
  }
  return clearListItem(li);
};

const deleteAtListItemEnd = (li) => {
  const nextLi = siblingListItem(li, "next");
  if (nextLi) return mergeNodeInto(li, nextLi);
  const list = li.parentElement;
  const after = list?.nextElementSibling;
  if (!after) return false;
  if (/^(P|DIV|H[1-6]|BLOCKQUOTE)$/i.test(after.tagName)) {
    return mergeNodeInto(li, after);
  }
  if (/^(UL|OL)$/i.test(after.tagName)) {
    const first = [...after.children].find((child) => child.tagName === "LI");
    if (!first) return false;
    const merged = mergeNodeInto(li, first);
    removeListIfEmpty(after);
    return merged;
  }
  return false;
};

const splitListItemOnEnter = (li, range) => {
  if (!li) return false;
  if (range && !range.collapsed) {
    try {
      range.deleteContents();
    } catch {
      return false;
    }
  }
  const live = window.getSelection()?.rangeCount
    ? window.getSelection().getRangeAt(0)
    : range;
  if (listItemVisuallyEmpty(li)) return exitListItemToParagraph(li);

  const doc = li.ownerDocument;
  const newLi = doc.createElement("li");
  copyBlockPresentation(li, newLi);
  if (live && !caretAtBlockEdge(li, live, "end")) {
    try {
      const tail = document.createRange();
      tail.selectNodeContents(li);
      tail.setStart(live.endContainer, live.endOffset);
      newLi.appendChild(tail.extractContents());
    } catch {
      return false;
    }
  }
  ensureEmptyBlockHasBr(newLi);
  ensureEmptyBlockHasBr(li);
  li.after(newLi);
  placeCaretAtBlockStart(newLi);
  return true;
};

/**
 * Enter / Backspace / Delete inside lists. The browser's default split and
 * outdent often drops the caret at the start of the editing host.
 */
const handleRichTextListKey = (event, root) => {
  if (!root || event.altKey || event.ctrlKey || event.metaKey) return false;
  const isTab = event.key === "Tab";
  if (!isTab && event.shiftKey) return false;
  if (!isTab && event.key !== "Enter" && event.key !== "Backspace" && event.key !== "Delete") {
    return false;
  }
  const sel = typeof window !== "undefined" ? window.getSelection() : null;
  if (!sel?.rangeCount || !sel.isCollapsed) return false;
  const focus = sel.focusNode;
  if (!focus || (focus !== root && !root.contains(focus))) return false;
  if (findPreAncestor(focus, root) || findTableAncestor(focus, root)) return false;

  const range = sel.getRangeAt(0);
  const li = findEnclosingListItem(focus, root);

  if (isTab) {
    if (!li) return false;
    if (event.shiftKey) {
      if (outdentListItem(li)) {
        placeCaretAtBlockStart(li);
        return true;
      }
      return clearListItem(li);
    }
    indentListItem(li);
    return true;
  }

  if (event.key === "Enter") {
    if (!li) return false;
    return splitListItemOnEnter(li, range);
  }

  if (event.key === "Backspace") {
    if (li && caretAtBlockEdge(li, range, "start")) return backspaceAtListItemStart(li);
    if (li) return false;
    let el = focus.nodeType === 1 ? focus : focus.parentElement;
    let block = null;
    while (el && el !== root) {
      if (/^(P|DIV|H[1-6]|BLOCKQUOTE)$/i.test(el.tagName)) {
        block = el;
        break;
      }
      el = el.parentElement;
    }
    if (!block || !caretAtBlockEdge(block, range, "start")) return false;
    const prev = block.previousElementSibling;
    if (prev && /^(UL|OL)$/i.test(prev.tagName)) {
      const last = [...prev.children].reverse().find((child) => child.tagName === "LI");
      if (!last) return false;
      return mergeNodeInto(last, block);
    }
    return joinPlainBlockWithPrevious(block);
  }

  if (event.key === "Delete") {
    if (!li || !caretAtBlockEdge(li, range, "end")) return false;
    return deleteAtListItemEnd(li);
  }
  return false;
};

const markdownContextBlocked = (node, root) => {
  let el = node?.nodeType === 1 ? node : node?.parentElement;
  while (el && el !== root) {
    const tag = el.tagName;
    if (tag === "PRE" || tag === "CODE") return true;
    if (el.classList?.contains("rte-math") || el.classList?.contains("rte-math-block")) return true;
    el = el.parentElement;
  }
  return false;
};

const findMarkdownBlock = (node, root) => {
  let el = node?.nodeType === 1 ? node : node?.parentElement;
  while (el && el !== root) {
    if (/^(P|DIV|LI|BLOCKQUOTE|H[1-6])$/i.test(el.tagName)) return el;
    el = el.parentElement;
  }
  return null;
};

const placeCaretAfterNode = (node) => {
  if (!node?.parentNode || typeof window === "undefined") return;
  const sel = window.getSelection();
  if (!sel) return;
  const range = document.createRange();
  range.setStartAfter(node);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
};

const safeMarkdownUrl = (raw) => {
  const trimmed = String(raw || "").trim();
  if (!trimmed || /[\s<>]/.test(trimmed)) return null;
  if (/^(javascript|data|vbscript):/i.test(trimmed)) return null;
  if (/^(https?:|mailto:|tel:)/i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("/") || trimmed.startsWith("./") || trimmed.startsWith("../")) return trimmed;
  return `https://${trimmed}`;
};

const paintMathFormula = (el, latex, display) => {
  el.setAttribute("data-rte-math", latex);
  el.setAttribute("contenteditable", "false");
  if (display) el.setAttribute("data-rte-math-display", "1");
  try {
    if (typeof katex !== "undefined" && katex && typeof katex.render === "function") {
      katex.render(latex, el, { throwOnError: false, displayMode: !!display });
      return;
    }
  } catch {
    /* keep the source visible */
  }
  el.textContent = latex;
};

const turnBlockIntoList = (block, ordered) => {
  const doc = block.ownerDocument;
  const list = doc.createElement(ordered ? "ol" : "ul");
  const li = doc.createElement("li");
  copyBlockPresentation(block, li);
  while (block.firstChild) li.appendChild(block.firstChild);
  ensureEmptyBlockHasBr(li);
  list.appendChild(li);
  block.replaceWith(list);
  placeCaretAtBlockStart(li);
};

const applyMarkdownBlock = (block, kind) => {
  if (!block?.parentNode) return;
  if (kind === "ul" || kind === "ol") {
    turnBlockIntoList(block, kind === "ol");
    return;
  }
  const tag = kind === "quote" ? "blockquote" : kind;
  const neo = block.tagName.toLowerCase() === tag ? block : replaceElementPreserveChildren(block, tag);
  if (/^h[1-6]$/.test(tag)) releaseInheritedHeadingBold(neo);
  ensureEmptyBlockHasBr(neo);
  placeCaretAtBlockStart(neo);
};

const formatMarkdownSlice = (textNode, start, end, command, fallbackTag) => {
  const doc = textNode.ownerDocument;
  const mid = textNode.splitText(start);
  const tail = mid.splitText(end - start);
  if (!textNode.data) textNode.remove();
  if (tail && !tail.data) tail.remove();
  const sel = window.getSelection();
  if (!sel) return mid;
  const range = doc.createRange();
  range.selectNodeContents(mid);
  sel.removeAllRanges();
  sel.addRange(range);
  let wrapped = false;
  try {
    document.execCommand("styleWithCSS", false, false);
  } catch {
    /* older engines */
  }
  try {
    wrapped = document.execCommand(command, false, null);
  } catch {
    wrapped = false;
  }
  const parent = mid.parentElement;
  const used =
    wrapped &&
    parent &&
    parent !== mid &&
    /^(B|STRONG|I|EM|S|STRIKE|DEL|CODE)$/i.test(parent.tagName);
  const el = used ? parent : doc.createElement(fallbackTag);
  if (!used) {
    mid.parentNode.insertBefore(el, mid);
    el.appendChild(mid);
  }
  placeCaretAfterNode(el);
  return el;
};

const replaceSliceWithNode = (textNode, start, end, node) => {
  const mid = textNode.splitText(start);
  const tail = mid.splitText(end - start);
  mid.parentNode.insertBefore(node, mid);
  mid.remove();
  if (!textNode.data) textNode.remove();
  if (tail && !tail.data) tail.remove();
  placeCaretAfterNode(node);
  return node;
};

/**
 * Typing shortcuts that call the same formatting the toolbar uses.
 * Block markers convert on Space. Inline markers convert on the closing character.
 */
const tryRichTextMarkdownShortcut = (event, root) => {
  if (!root || event.isComposing) return false;
  if (event.ctrlKey || event.metaKey || event.altKey) return false;
  const key = event.key;
  if (!key) return false;

  const sel = typeof window !== "undefined" ? window.getSelection() : null;
  if (!sel?.rangeCount || !sel.isCollapsed) return false;
  const range = sel.getRangeAt(0);
  const focus = range.startContainer;
  if (!focus || (focus !== root && !root.contains(focus))) return false;
  if (findPreAncestor(focus, root)) return false;

  if (key === "Backspace" && !event.shiftKey) {
    const mathBlock = findMarkdownBlock(focus, root);
    if (mathBlock && caretAtBlockEdge(mathBlock, range, "start")) {
      const prev = mathBlock.previousElementSibling;
      if (prev?.classList?.contains("rte-math-block")) {
        prev.remove();
        placeCaretAtBlockStart(mathBlock);
        return true;
      }
    }
    let previous = null;
    if (focus.nodeType === 3 && range.startOffset === 0) previous = focus.previousSibling;
    else if (focus.nodeType === 1) previous = focus.childNodes[range.startOffset - 1];
    while (previous?.nodeType === 3 && !previous.data) previous = previous.previousSibling;
    if (previous?.nodeType === 1 && previous.classList?.contains("rte-math")) {
      previous.remove();
      return true;
    }
    return false;
  }

  if (key.length !== 1 || markdownContextBlocked(focus, root)) return false;

  const block = findMarkdownBlock(focus, root);
  if (!block || (findTableAncestor(focus, root) && key === " ")) return false;

  if (key === " " && !event.shiftKey && block && block.tagName !== "LI") {
    let before = "";
    try {
      const pre = document.createRange();
      pre.selectNodeContents(block);
      pre.setEnd(range.startContainer, range.startOffset);
      before = pre.toString().replace(/\u00a0/g, " ");
    } catch {
      return false;
    }
    let kind = null;
    const heading = before.match(/^\s*(#{1,6})$/);
    if (heading) kind = `h${heading[1].length}`;
    else if (/^\s*>$/.test(before)) kind = "quote";
    else if (/^\s*[-*]$/.test(before)) kind = "ul";
    else if (/^\s*\d+[.)]$/.test(before)) kind = "ol";
    if (!kind) return false;
    if (kind === "quote" && block.tagName === "BLOCKQUOTE") return false;
    try {
      const pre = document.createRange();
      pre.selectNodeContents(block);
      pre.setEnd(range.startContainer, range.startOffset);
      pre.deleteContents();
    } catch {
      return false;
    }
    if (!hasVisibleTextContent(block) && !block.querySelector("br, img")) {
      ensureEmptyBlockHasBr(block);
    }
    applyMarkdownBlock(block, kind);
    return true;
  }

  if (range.startContainer.nodeType !== 3) return false;
  const textNode = range.startContainer;
  const offset = range.startOffset;
  const before = textNode.data.slice(0, offset).replace(/\u00a0/g, " ");

  const rules = [
    { re: /(^|[\s([(])\*\*([^*\n]+)\*$/, open: 2, close: 1, command: "bold", tag: "strong" },
    { re: /(^|[\s([(])\*([^*\n]+)$/, open: 1, close: 0, command: "italic", tag: "em" },
    { re: /(^|[\s([(])_([^_\n]+)$/, open: 1, close: 0, command: "italic", tag: "em" },
    { re: /(^|[\s([(])~~([^~\n]+)~$/, open: 2, close: 1, command: "strikeThrough", tag: "s" },
    { re: /(^|[\s([(])`([^`\n]+)$/, open: 1, close: 0, tag: "code" },
    { re: /(^|[^$])\$\$([^$\n]+)\$$/, open: 2, close: 1, math: "block" },
    { re: /(^|[^$])\$([^$\n]+)$/, open: 1, close: 0, math: "inline" },
    { re: /!\[([^\]\n]*)\]\(([^)\s]+)$/, image: true },
    { re: /(^|[\s([(])\[([^\]\n]+)\]\(([^)\s]+)$/, link: true },
  ];

  const markerKey = (item) => {
    if (item.image || item.link) return ")";
    if (item.math) return "$";
    if (item.tag === "code") return "`";
    if (item.command === "strikeThrough") return "~";
    if (item.command === "bold") return "*";
    if (item.re.source.includes("_([^_")) return "_";
    if (item.command === "italic") return "*";
    return "";
  };
  const rule = rules.find((item) => markerKey(item) === key && item.re.test(before));
  if (!rule) return false;
  const match = before.match(rule.re);
  if (!match) return false;

  if (rule.image || rule.link) {
    const url = safeMarkdownUrl(rule.image ? match[2] : match[3]);
    if (!url) return false;
    const lead = rule.link && match[1] ? match[1].length : 0;
    const consumed = match[0].length - lead;
    const start = offset - consumed;
    if (start < 0) return false;
    const doc = textNode.ownerDocument;
    if (rule.image) {
      const img = doc.createElement("img");
      img.src = url;
      img.alt = match[1] || "";
      replaceSliceWithNode(textNode, start, offset, img);
      return true;
    }
    const anchor = doc.createElement("a");
    anchor.href = url;
    anchor.textContent = match[2];
    replaceSliceWithNode(textNode, start, offset, anchor);
    return true;
  }

  const lead = match[1] ? match[1].length : 0;
  const consumed = match[0].length - lead;
  const start = offset - consumed;
  if (start < 0) return false;
  const innerStart = start + rule.open;
  const innerEnd = offset - rule.close;
  if (innerEnd < innerStart) return false;
  const inner = textNode.data.slice(innerStart, innerEnd);
  if (rule.close) textNode.deleteData(innerEnd, rule.close);
  textNode.deleteData(start, rule.open);

  if (rule.math) {
    const doc = textNode.ownerDocument;
    const sliceStart = start;
    const sliceEnd = start + inner.length;
    const wholeBlock =
      rule.math === "block" &&
      block &&
      block.tagName !== "LI" &&
      block.textContent.replace(/\u00a0/g, " ") === inner;
    const display = !!wholeBlock;
    const host = doc.createElement(display ? "div" : "span");
    host.className = display ? "rte-math-block" : "rte-math";
    paintMathFormula(host, inner, display);
    if (wholeBlock && block.parentNode) {
      block.replaceWith(host);
      const next = doc.createElement("p");
      next.appendChild(doc.createElement("br"));
      host.after(next);
      placeCaretAtBlockStart(next);
      return true;
    }
    replaceSliceWithNode(textNode, sliceStart, sliceEnd, host);
    return true;
  }

  if (rule.tag === "code") {
    const doc = textNode.ownerDocument;
    const code = doc.createElement("code");
    code.textContent = inner;
    replaceSliceWithNode(textNode, start, start + inner.length, code);
    return true;
  }

  formatMarkdownSlice(textNode, start, start + inner.length, rule.command, rule.tag);
  return true;
};

const isEmptyParagraphOrPlainDiv = (el) => {
  if (!el || el.nodeType !== 1) return false;
  const tag = el.tagName;
  if (tag !== "P" && tag !== "DIV") return false;
  if (tag === "DIV") {
    if (el.className && String(el.className).trim()) return false;
    if (
      el.querySelector(
        "ul, ol, blockquote, h1, h2, h3, h4, h5, h6, pre, table, div, section"
      )
    ) {
      return false;
    }
  }
  if (hasVisibleTextContent(el)) return false;
  if (el.querySelector?.(RTE_NONEMPTY_EMBED_SELECTOR)) return false;
  return true;
};

/** Font, alignment, indent, and spacing — not heading font-size. */
const copyParagraphChromeFromHeading = (from, to) => {
  if (!from || !to?.style) return;
  if (from.style?.fontFamily) to.style.fontFamily = from.style.fontFamily;
  if (from.style?.textAlign) to.style.textAlign = from.style.textAlign;
  const indent = getBlockIndentLevel(from);
  if (indent) setBlockIndentLevel(to, indent);
  const mb = from.getAttribute?.(RTE_MB_ATTR);
  if (mb === "0") applyBlockBottomMargin(to, false);
  else if (mb === "1") applyBlockBottomMargin(to, true);
  const spacing = getBlockLineSpacingValue(from);
  if (spacing) applyBlockLineSpacing(to, spacing);
  cleanupEmptyStyleAttr(to);
};

/**
 * Enter in a topic heading. The browser's default split sometimes inserts the
 * new block, or drops the caret, at the start of the editor instead of under
 * the heading. Returns true when Enter was handled.
 */
const exitHeadingOnEnter = (heading) => {
  if (!heading?.parentNode || typeof window === "undefined") return false;
  const sel = window.getSelection();
  if (!sel?.rangeCount) return false;
  const doc = heading.ownerDocument;
  const inside = (node) => node === heading || heading.contains(node);

  const openParagraphAfter = () => {
    const next = heading.nextElementSibling;
    if (isEmptyParagraphOrPlainDiv(next)) {
      const target =
        next.tagName === "DIV" ? replaceElementPreserveChildren(next, "p") : next;
      ensureEmptyBlockHasBr(target);
      placeCaretAtBlockStart(target);
      return;
    }
    const paragraph = doc.createElement("p");
    paragraph.appendChild(doc.createElement("br"));
    copyParagraphChromeFromHeading(heading, paragraph);
    heading.after(paragraph);
    placeCaretAtBlockStart(paragraph);
  };

  const openParagraphBefore = () => {
    const paragraph = doc.createElement("p");
    paragraph.appendChild(doc.createElement("br"));
    copyParagraphChromeFromHeading(heading, paragraph);
    heading.before(paragraph);
    placeCaretAtBlockStart(paragraph);
  };

  let range = sel.getRangeAt(0);

  // Caret on the parent, in the gap before or after the heading element.
  if (range.collapsed && range.startContainer.nodeType === 1) {
    const parent = range.startContainer;
    if (parent.childNodes[range.startOffset - 1] === heading) {
      openParagraphAfter();
      return true;
    }
    if (parent.childNodes[range.startOffset] === heading) {
      openParagraphBefore();
      return true;
    }
  }

  // Whitespace text node sitting directly after the heading.
  if (range.collapsed && range.startContainer.nodeType === 3) {
    const textNode = range.startContainer;
    const onlySpace = !String(textNode.textContent || "")
      .replace(/\u00a0/g, " ")
      .trim();
    if (onlySpace && textNode.previousSibling === heading) {
      openParagraphAfter();
      return true;
    }
  }

  if (!inside(range.startContainer) || !inside(range.endContainer)) return false;

  if (!range.collapsed) {
    range.deleteContents();
    if (!sel.rangeCount || !inside(sel.getRangeAt(0).startContainer)) {
      ensureEmptyBlockHasBr(heading);
      openParagraphAfter();
      return true;
    }
    range = sel.getRangeAt(0);
  }

  let atStart = false;
  let atEnd = false;
  try {
    const before = document.createRange();
    before.selectNodeContents(heading);
    before.setEnd(range.startContainer, range.startOffset);
    atStart = !rangeHasVisibleContent(before);

    const after = document.createRange();
    after.selectNodeContents(heading);
    after.setStart(range.endContainer, range.endOffset);
    atEnd = !rangeHasVisibleContent(after);
  } catch {
    openParagraphAfter();
    return true;
  }

  if (atEnd) {
    try {
      const tail = document.createRange();
      tail.selectNodeContents(heading);
      tail.setStart(range.endContainer, range.endOffset);
      tail.deleteContents();
    } catch {
      /* leave the heading text in place */
    }
    ensureEmptyBlockHasBr(heading);
    openParagraphAfter();
    return true;
  }

  if (atStart) {
    openParagraphBefore();
    return true;
  }

  const nextHeading = doc.createElement(heading.tagName.toLowerCase());
  [...heading.attributes].forEach((attr) => {
    const name = attr.name.toLowerCase();
    if (name === "id" || name === "data-guides-title") return;
    nextHeading.setAttribute(attr.name, attr.value);
  });
  try {
    const tail = document.createRange();
    tail.selectNodeContents(heading);
    tail.setStart(range.endContainer, range.endOffset);
    nextHeading.appendChild(tail.extractContents());
  } catch {
    return false;
  }
  ensureEmptyBlockHasBr(nextHeading);
  ensureEmptyBlockHasBr(heading);
  heading.after(nextHeading);
  placeCaretAtBlockStart(nextHeading);
  return true;
};

/** Snapshot destination-block styles so pasted lines can match them. */
const captureBlockPasteStyles = (block) => {
  if (!block || block.nodeType !== 1) return null;
  return {
    tag: block.tagName.toLowerCase(),
    fontFamily: block.style?.fontFamily?.trim() || "",
    fontSize: block.style?.fontSize?.trim() || "",
    lineSpacing: getBlockLineSpacingValue(block),
    mbAttr: block.getAttribute?.(RTE_MB_ATTR) || "",
    textAlign: block.style?.textAlign?.trim() || "",
    indent: getBlockIndentLevel(block),
  };
};

const applyBlockPasteStyles = (el, snap) => {
  if (!el?.style || !snap) return;
  if (snap.fontFamily) el.style.fontFamily = snap.fontFamily;
  else el.style.removeProperty("font-family");

  if (snap.fontSize) {
    applyFontSizeAndLineHeight(el, snap.fontSize);
  } else {
    el.style.removeProperty("font-size");
  }

  if (snap.lineSpacing) {
    applyBlockLineSpacing(el, snap.lineSpacing);
  } else if (!snap.fontSize) {
    clearBlockLineSpacing(el);
  }

  if (snap.mbAttr === "0") applyBlockBottomMargin(el, false);
  else if (snap.mbAttr === "1") applyBlockBottomMargin(el, true);
  else clearBlockBottomMarginOverride(el);

  if (snap.textAlign) el.style.textAlign = snap.textAlign;
  else {
    el.style.removeProperty("text-align");
    el.removeAttribute("align");
  }

  setBlockIndentLevel(el, snap.indent || 0);
  cleanupEmptyStyleAttr(el);
};

const splitClipboardPlainTextLines = (raw) =>
  String(raw ?? "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n");

const clipboardBlockPlainText = (el) =>
  String(el?.innerText ?? el?.textContent ?? "")
    .replace(/\u00a0/g, " ")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/\n+/g, " ")
    .trim();

/**
 * Turn clipboard HTML into structured paste blocks so headings / lists survive
 * while foreign inline styles are discarded.
 */
const extractPasteBlocksFromHtmlRoot = (root) => {
  if (!root) return [];
  const out = [];

  const pushParagraph = (text) => {
    out.push({ kind: "p", text: String(text ?? "") });
  };

  const visit = (el) => {
    if (!el || el.nodeType !== 1) return;
    const tag = el.tagName.toLowerCase();

    if (/^h[1-6]$/.test(tag)) {
      out.push({
        kind: "heading",
        level: Number(tag.charAt(1)),
        text: clipboardBlockPlainText(el),
      });
      return;
    }
    if (tag === "ul" || tag === "ol") {
      const items = [...el.querySelectorAll(":scope > li")].map((li) =>
        clipboardBlockPlainText(li)
      );
      if (items.length) {
        out.push({ kind: "list", ordered: tag === "ol", items });
      }
      return;
    }
    if (tag === "li") {
      out.push({
        kind: "list",
        ordered: false,
        items: [clipboardBlockPlainText(el)],
      });
      return;
    }
    if (tag === "blockquote") {
      out.push({ kind: "blockquote", text: clipboardBlockPlainText(el) });
      return;
    }
    if (tag === "p" || tag === "pre") {
      pushParagraph(clipboardBlockPlainText(el));
      return;
    }
    if (tag === "br") {
      pushParagraph("");
      return;
    }
    if (tag === "style" || tag === "script" || tag === "meta" || tag === "link" || tag === "title" || tag === "head") {
      return;
    }
    if (tag === "table") {
      const table = readClipboardTable(el);
      if (table) out.push(table);
      return;
    }

    const children = [...el.children];
    if (!children.length) {
      const t = clipboardBlockPlainText(el);
      if (t) pushParagraph(t);
      return;
    }
    children.forEach(visit);
  };

  [...root.childNodes].forEach((node) => {
    if (node.nodeType === 3) {
      const t = String(node.textContent || "")
        .replace(/\u00a0/g, " ")
        .trim();
      if (t) pushParagraph(t);
    } else if (node.nodeType === 1) {
      visit(node);
    }
  });

  while (out.length && out[0].kind === "p" && !out[0].text) out.shift();
  while (out.length && out[out.length - 1].kind === "p" && !out[out.length - 1].text)
    out.pop();
  return out;
};

const clipboardCellText = (cell) => {
  if (!cell) return "";
  const clone = cell.cloneNode(true);
  clone.querySelectorAll("table").forEach((nested) => nested.remove());
  clone.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));
  clone.querySelectorAll("p, div, li, h1, h2, h3, h4, h5, h6").forEach((block) => {
    block.appendChild(block.ownerDocument.createTextNode("\n"));
  });
  return String(clone.textContent || "")
    .replace(/\u00a0/g, " ")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const clipboardSpanCount = (raw, max) => {
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n) || n <= 1) return 0;
  return Math.min(max, n);
};

/** Keep a pasted grid: rows, header cells, and spans. Nested tables stay as cell text. */
const readClipboardTable = (tableEl) => {
  if (!tableEl) return null;
  const rows = [];
  [...tableEl.querySelectorAll("tr")].forEach((tr) => {
    if (tr.closest("table") !== tableEl) return;
    const cells = [];
    [...tr.children].forEach((cell) => {
      if (cell.tagName !== "TD" && cell.tagName !== "TH") return;
      if (cell.closest("table") !== tableEl) return;
      cells.push({
        header: cell.tagName === "TH",
        text: clipboardCellText(cell),
        colspan: clipboardSpanCount(cell.getAttribute("colspan"), 24),
        rowspan: clipboardSpanCount(cell.getAttribute("rowspan"), 24),
      });
    });
    if (cells.length) rows.push(cells);
  });
  if (!rows.length) return null;
  return { kind: "table", rows };
};

const plainTextToTableBlock = (plain) => {
  const lines = splitClipboardPlainTextLines(plain).filter((line) => line.length > 0);
  if (!lines.length || !lines.some((line) => line.includes("\t"))) return null;
  const rows = lines.map((line) => line.split("\t").map((text) => ({
    header: false,
    text: text.trim(),
    colspan: 0,
    rowspan: 0,
  })));
  if (!rows.some((row) => row.length > 1)) return null;
  return { kind: "table", rows };
};

const parseClipboardPasteBlocks = (html, plain) => {
  if (html && typeof DOMParser !== "undefined") {
    try {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const blocks = extractPasteBlocksFromHtmlRoot(doc.body);
      if (blocks.some((block) => block.kind === "table")) return blocks;
      if (blocks.length) {
        const grid = blocks.every((block) => block.kind === "p")
          ? plainTextToTableBlock(plain)
          : null;
        return grid ? [grid] : blocks;
      }
    } catch {
      /* fall through to plain text */
    }
  }
  const grid = plainTextToTableBlock(plain);
  if (grid) return [grid];
  return splitClipboardPlainTextLines(plain).map((text) => ({
    kind: "p",
    text,
  }));
};

const isVisuallyEmptyPasteTarget = (block) => {
  if (!block) return true;
  const text = String(block.innerText ?? block.textContent ?? "")
    .replace(/\u00a0/g, " ")
    .trim();
  if (text) return false;
  return !block.querySelector?.("img, table, video, iframe");
};

/** Build clean DOM for one paste block; applies destination styles (headers keep tags). */
const createDomForPasteBlock = (block, styleSnap, doc = document) => {
  if (block.kind === "heading") {
    const level = Math.min(6, Math.max(1, Number(block.level) || 1));
    const el = doc.createElement(`h${level}`);
    if (block.text) el.textContent = block.text;
    else el.appendChild(doc.createElement("br"));
    if (styleSnap) {
      // Keep heading tags for our CSS sizes; still carry font/spacing/margins.
      const headingSnap = { ...styleSnap, fontSize: "" };
      applyBlockPasteStyles(el, headingSnap);
    }
    return el;
  }
  if (block.kind === "list") {
    const list = doc.createElement(block.ordered ? "ol" : "ul");
    (block.items || []).forEach((text) => {
      const li = doc.createElement("li");
      if (text) li.textContent = text;
      else li.appendChild(doc.createElement("br"));
      if (styleSnap) applyBlockPasteStyles(li, styleSnap);
      list.appendChild(li);
    });
    return list;
  }
  if (block.kind === "blockquote") {
    const el = doc.createElement("blockquote");
    if (block.text) el.textContent = block.text;
    else el.appendChild(doc.createElement("br"));
    if (styleSnap) applyBlockPasteStyles(el, styleSnap);
    return el;
  }
  if (block.kind === "table") {
    const table = doc.createElement("table");
    const headerRow = (block.rows?.[0] || []).some((cell) => cell.header);
    table.className = headerRow
      ? "rte-table rte-table-style-header"
      : "rte-table rte-table-style-grid";
    const tbody = doc.createElement("tbody");
    (block.rows || []).forEach((row) => {
      const tr = doc.createElement("tr");
      row.forEach((cell) => {
        const td = doc.createElement(cell.header ? "th" : "td");
        if (cell.colspan > 1) td.colSpan = cell.colspan;
        if (cell.rowspan > 1) td.rowSpan = cell.rowspan;
        const lines = String(cell.text || "").split("\n");
        if (!lines.some((line) => line.length)) {
          td.appendChild(doc.createElement("br"));
        } else {
          lines.forEach((line, index) => {
            if (index) td.appendChild(doc.createElement("br"));
            if (line) td.appendChild(doc.createTextNode(line));
          });
        }
        tr.appendChild(td);
      });
      tbody.appendChild(tr);
    });
    table.appendChild(tbody);
    return table;
  }
  const el = doc.createElement("p");
  if (block.text) el.textContent = block.text;
  else el.appendChild(doc.createElement("br"));
  if (styleSnap) applyBlockPasteStyles(el, styleSnap);
  return el;
};

const setCellPlainText = (cell, text) => {
  if (!cell) return;
  const doc = cell.ownerDocument;
  while (cell.firstChild) cell.removeChild(cell.firstChild);
  const lines = String(text ?? "").split("\n");
  if (!lines.some((line) => line.length)) {
    cell.appendChild(doc.createElement("br"));
    return;
  }
  lines.forEach((line, index) => {
    if (index) cell.appendChild(doc.createElement("br"));
    if (line) cell.appendChild(doc.createTextNode(line));
  });
};

const insertPlainTextAtRange = (range, text) => {
  range.deleteContents();
  const doc = range.startContainer.ownerDocument || document;
  const lines = String(text ?? "").split("\n");
  const frag = doc.createDocumentFragment();
  let last = null;
  lines.forEach((line, index) => {
    if (index) last = frag.appendChild(doc.createElement("br"));
    if (line) last = frag.appendChild(doc.createTextNode(line));
  });
  if (!last) last = frag.appendChild(doc.createElement("br"));
  range.insertNode(frag);
  const live = doc.getSelection?.() || window.getSelection();
  if (!live) return;
  const caret = doc.createRange();
  caret.setStartAfter(last);
  caret.collapse(true);
  live.removeAllRanges();
  live.addRange(caret);
};

const directTableGrid = (table) =>
  tableRowElements(table)
    .filter((row) => row.closest("table") === table)
    .map((row) => tableRowCells(row).filter((cell) => cell.closest("table") === table));

const matrixFromSelectedCells = (table, selectedCells) => {
  if (!selectedCells?.length) return null;
  const grid = directTableGrid(table);
  const selected = new Set(selectedCells);
  let minR = Infinity;
  let maxR = -1;
  let minC = Infinity;
  let maxC = -1;
  grid.forEach((row, rowIndex) => {
    row.forEach((cell, colIndex) => {
      if (!selected.has(cell)) return;
      minR = Math.min(minR, rowIndex);
      maxR = Math.max(maxR, rowIndex);
      minC = Math.min(minC, colIndex);
      maxC = Math.max(maxC, colIndex);
    });
  });
  if (maxR < 0) return null;
  const matrix = [];
  for (let rowIndex = minR; rowIndex <= maxR; rowIndex += 1) {
    const slice = [];
    for (let colIndex = minC; colIndex <= maxC; colIndex += 1) {
      const cell = grid[rowIndex]?.[colIndex];
      if (!cell || !selected.has(cell)) return null;
      slice.push(cell);
    }
    matrix.push(slice);
  }
  return matrix;
};

const cellsTouchedByRange = (table, range) => {
  if (!table || !range) return [];
  const cells = [];
  directTableGrid(table).forEach((row) => {
    row.forEach((cell) => {
      if (range.collapsed) {
        if (range.startContainer === cell || cell.contains(range.startContainer)) cells.push(cell);
        return;
      }
      try {
        if (range.intersectsNode(cell)) cells.push(cell);
      } catch {
        /* detached nodes */
      }
    });
  });
  return cells;
};

const clipboardGridMatches = (rows, matrix) => {
  if (!matrix?.length || !rows?.length || matrix.length !== rows.length) return false;
  const width = rows[0].length;
  if (!width) return false;
  if (!rows.every((row) => row.length === width)) return false;
  if (rows.some((row) => row.some((cell) => cell.colspan || cell.rowspan))) return false;
  return matrix.every((row) => row.length === width);
};

const rangeStaysInsideCell = (range, cell) => {
  if (!range || !cell) return false;
  const startOk = range.startContainer === cell || cell.contains(range.startContainer);
  const endOk = range.endContainer === cell || cell.contains(range.endContainer);
  return startOk && endOk;
};

const insertPastedTableBelow = (table, grid) => {
  const host = table.closest("figure.rte-table-figure") || table;
  const node = createDomForPasteBlock(grid, null, table.ownerDocument || document);
  host.after(node);
  const next = node.nextElementSibling;
  if (!next || !/^(P|H[1-6]|UL|OL|BLOCKQUOTE|PRE|TABLE|FIGURE)$/i.test(next.tagName)) {
    const trail = node.ownerDocument.createElement("p");
    trail.appendChild(node.ownerDocument.createElement("br"));
    node.after(trail);
  }
  const cells = node.querySelectorAll("td, th");
  const caretCell = cells[cells.length - 1];
  if (!caretCell) return node;
  const live = window.getSelection();
  if (!live) return node;
  const caret = node.ownerDocument.createRange();
  caret.selectNodeContents(caretCell);
  caret.collapse(false);
  live.removeAllRanges();
  live.addRange(caret);
  return node;
};

/** Clear font-size / line-height so a new size is not blocked by nested spans or tall list boxes. */
const clearFontSizeStylesInTree = (rootEl) => {
  if (!rootEl || rootEl.nodeType !== 1) return;
  const nodes = [rootEl, ...rootEl.querySelectorAll("[style], span, font")];
  const seen = new Set();
  nodes.forEach((el) => {
    if (seen.has(el) || el.nodeType !== 1) return;
    seen.add(el);
    if (el !== rootEl && isFontSizeExemptElement(el)) return;
    if (el.style) {
      el.style.removeProperty("font-size");
      el.style.removeProperty("line-height");
      const raw = el.getAttribute("style");
      if (raw != null && String(raw).trim() === "") el.removeAttribute("style");
    }
    if (el.tagName === "FONT" && el.hasAttribute("size")) {
      el.removeAttribute("size");
    }
  });
  // Unwrap empty size-only spans (deepest first)
  const spans = [...rootEl.querySelectorAll("span")].reverse();
  spans.forEach((span) => {
    if (isFontSizeExemptElement(span)) return;
    if (isBareStyleSpan(span)) unwrapInlineElementKeepChildren(span);
  });
  if (isBareStyleSpan(rootEl)) unwrapInlineElementKeepChildren(rootEl);
};

/** Clear font-family (and legacy &lt;font face&gt;) in a subtree. */
const clearFontFamilyStylesInTree = (rootEl) => {
  if (!rootEl || rootEl.nodeType !== 1) return;
  const nodes = [rootEl, ...rootEl.querySelectorAll("[style], span, font")];
  const seen = new Set();
  nodes.forEach((el) => {
    if (seen.has(el) || el.nodeType !== 1) return;
    seen.add(el);
    if (isInsideFlowDiagramBody(el)) return;
    if (el.style) {
      el.style.removeProperty("font-family");
      cleanupEmptyStyleAttr(el);
    }
    if (el.tagName === "FONT" && el.hasAttribute("face")) {
      el.removeAttribute("face");
    }
  });
  const spans = [...rootEl.querySelectorAll("span")].reverse();
  spans.forEach((span) => {
    if (isBareStyleSpan(span)) unwrapInlineElementKeepChildren(span);
  });
  if (isBareStyleSpan(rootEl)) unwrapInlineElementKeepChildren(rootEl);
};

/**
 * If a block keeps an inline font-size while children use another size, line boxes stay tall.
 * Move the block size onto a single inner span so partial re-sizing does not leave spacing.
 */
const flattenBlockFontSizeToInnerSpan = (block) => {
  if (!block?.style?.fontSize) return;
  const size = block.style.fontSize;
  block.style.removeProperty("font-size");
  block.style.removeProperty("line-height");
  const raw = block.getAttribute("style");
  if (raw != null && String(raw).trim() === "") block.removeAttribute("style");

  const only =
    block.childNodes.length === 1 &&
    block.firstChild.nodeType === 1 &&
    block.firstChild.tagName === "SPAN";
  if (only && !block.firstChild.style.fontSize) {
    applyFontSizeAndLineHeight(block.firstChild, size);
    return;
  }
  if (
    only &&
    block.firstChild.style.fontSize &&
    block.firstChild.style.fontSize === size
  ) {
    if (!block.firstChild.style.lineHeight) {
      block.firstChild.style.lineHeight = getEffectiveLineSpacing(block);
    }
    return;
  }

  const wrap = block.ownerDocument.createElement("span");
  applyFontSizeAndLineHeight(wrap, size);
  while (block.firstChild) wrap.appendChild(block.firstChild);
  block.appendChild(wrap);
};

/**
 * Drops wrapper tags iteratively (&lt;font&gt;, inline styles via &lt;span&gt;, etc.).
 */
const unwrapDeepByTagNames = (root, tagNames) => {
  tagNames.forEach((t) => {
    const tn = t.toUpperCase();
    let el;
    while ((el = root.getElementsByTagName(tn)[0]))
      unwrapInlineElementKeepChildren(el);
  });
};

const stripProblematicInlineStyles = (root) => {
  if (!root || root.nodeType !== 1) return;
  const nodes = [
    root,
    ...root.querySelectorAll(
      "[style], [align], [data-rte-line-spacing], [data-rte-mb], [data-rte-vspace]"
    ),
  ];
  const seen = new Set();
  nodes.forEach((el) => {
    if (seen.has(el) || el.nodeType !== 1) return;
    seen.add(el);
    if (el.hasAttribute("align")) el.removeAttribute("align");
    if (el.hasAttribute(RTE_LINE_SPACING_ATTR))
      el.removeAttribute(RTE_LINE_SPACING_ATTR);
    if (el.hasAttribute(RTE_MB_ATTR)) el.removeAttribute(RTE_MB_ATTR);
    if (el.hasAttribute("data-rte-vspace")) el.removeAttribute("data-rte-vspace");
    const s = el.style;
    if (!s) return;
    [
      "color",
      "font-size",
      "font-family",
      "font-weight",
      "font-style",
      "text-decoration",
      "text-decoration-line",
      "text-transform",
      "background-color",
      "background",
      "text-align",
      "vertical-align",
      "line-height",
      "letter-spacing",
      "padding",
      "padding-left",
      "padding-right",
      "padding-top",
      "padding-bottom",
      "margin",
      "margin-left",
      "margin-right",
      "margin-top",
      "margin-bottom",
    ].forEach((prop) => s.removeProperty(prop));
    const raw = el.getAttribute("style");
    if (raw != null && String(raw).trim() === "") el.removeAttribute("style");
  });
};

/**
 * Turn ul/ol/li into plain paragraphs (drops bullets / numbers).
 */
const unwrapListsToParagraphs = (root) => {
  if (!root?.ownerDocument) return;
  const doc = root.ownerDocument;

  const processList = (list) => {
    const parent = list.parentNode;
    if (!parent) return;
    [...list.childNodes].forEach((child) => {
      if (child.nodeType === 1 && child.tagName === "LI") {
        const p = doc.createElement("p");
        while (child.firstChild) p.appendChild(child.firstChild);
        parent.insertBefore(p, list);
      } else if (
        child.nodeType === 3 &&
        String(child.textContent ?? "").trim() !== ""
      ) {
        const p = doc.createElement("p");
        p.appendChild(child);
        parent.insertBefore(p, list);
      } else if (child.nodeType === 1) {
        parent.insertBefore(child, list);
      }
    });
    parent.removeChild(list);
  };

  if (/^(UL|OL)$/i.test(root.tagName)) {
    processList(root);
    return;
  }

  let guard = 0;
  while (guard++ < 40) {
    const lists = [...root.querySelectorAll("ul, ol")];
    if (!lists.length) break;
    const innermost =
      lists.find((l) => !l.querySelector("ul, ol")) || lists[lists.length - 1];
    processList(innermost);
  }
};

const parkRteFlowFigures = (root) => {
  if (!root?.querySelectorAll || !root.ownerDocument) return [];
  const parked = [];
  [...root.querySelectorAll("figure.rte-flow, figure.rte-image, figure.rte-table-figure")].forEach((fig) => {
    const marker = root.ownerDocument.createComment("rte-flow");
    fig.replaceWith(marker);
    parked.push({ marker, fig });
  });
  return parked;
};

const restoreRteFlowFigures = (parked) => {
  parked.forEach(({ marker, fig }) => {
    if (marker.parentNode) marker.replaceWith(fig);
  });
};

const IMAGE_UPLOAD_MAX_BYTES = 5 * 1024 * 1024;
const IMAGE_UPLOAD_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
]);

const buildImageFigure = (doc, src, alt) => {
  const fig = doc.createElement("figure");
  fig.className = "rte-image";
  fig.setAttribute("contenteditable", "false");
  fig.draggable = true;
  const img = doc.createElement("img");
  img.src = src;
  img.alt = alt || "";
  img.draggable = false;
  fig.appendChild(img);
  return fig;
};

const RTE_IMAGE_H_ATTR = "data-rte-image-h";
const RTE_IMAGE_MIN_H = 320;

const imageSlotWidth = (img) => {
  const fig = img.closest?.("figure.rte-image");
  const host = fig?.parentElement;
  if (!host || typeof window === "undefined") return 0;
  const hostWidth = host.clientWidth;
  if (!hostWidth) return 0;
  const figStyle = fig ? window.getComputedStyle(fig) : null;
  const extra = figStyle
    ? (parseFloat(figStyle.paddingLeft) || 0)
      + (parseFloat(figStyle.paddingRight) || 0)
      + (parseFloat(figStyle.borderLeftWidth) || 0)
      + (parseFloat(figStyle.borderRightWidth) || 0)
    : 0;
  return Math.max(0, hostWidth - extra);
};

/** Height of the whole image at the column width, ignoring a temporary scaled height. */
const imageFullHeight = (img) => {
  if (!img) return 0;
  const naturalW = img.naturalWidth || 0;
  const naturalH = img.naturalHeight || 0;
  if (naturalW > 0 && naturalH > 0) {
    const slot = imageSlotWidth(img);
    const width = slot > 0 ? Math.min(slot, naturalW) : (img.clientWidth || naturalW);
    if (width > 0) return (naturalH * width) / naturalW;
  }
  return img.getBoundingClientRect().height || 0;
};

const clearImageScale = (fig) => {
  if (!fig) return;
  const frame = fig.querySelector(":scope > .rte-image-frame");
  const img = frame?.querySelector("img") || fig.querySelector(":scope > img");
  if (frame && img) frame.replaceWith(img);
  if (img) {
    img.style.removeProperty("height");
    img.style.removeProperty("width");
    if (!img.getAttribute("style")) img.removeAttribute("style");
  }
  fig.classList.remove("rte-image-min");
  fig.removeAttribute(RTE_IMAGE_H_ATTR);
  fig.style.removeProperty("--rte-image-h");
  if (!fig.getAttribute("style")) fig.removeAttribute("style");
};

/** Scale the whole image to `px` tall. At 320px it stays centered; full height clears the scale. */
const applyImageFrameHeight = (fig, px) => {
  const img = fig?.querySelector?.("img");
  if (!fig || !img) return;
  const full = imageFullHeight(img);
  const requested = Math.round(Number(px) || 0);
  if (!requested || full <= RTE_IMAGE_MIN_H + 1 || requested >= full - 1) {
    clearImageScale(fig);
    return;
  }
  const frame = fig.querySelector(":scope > .rte-image-frame");
  if (frame) frame.replaceWith(img);
  const height = Math.max(RTE_IMAGE_MIN_H, Math.min(requested, Math.round(full)));
  img.style.height = `${height}px`;
  img.style.width = "auto";
  fig.style.setProperty("--rte-image-h", `${height}px`);
  fig.setAttribute(RTE_IMAGE_H_ATTR, String(height));
  fig.classList.toggle("rte-image-min", height <= RTE_IMAGE_MIN_H);
};

/** Wrap loose images in a figure and place that figure after its paragraph. */
const ensureEditorImageContainers = (root) => {
  if (!root?.querySelectorAll || !root.ownerDocument) return false;
  let changed = false;
  [...root.querySelectorAll("img")].forEach((img) => {
    if (!root.contains(img)) return;
    if (img.closest("figure.rte-image, figure.rte-flow")) return;
    const block = img.closest("p, h1, h2, h3, h4, h5, h6, li, blockquote");
    const inTable = !!img.closest("td, th");
    const fig = root.ownerDocument.createElement("figure");
    fig.className = "rte-image";
    fig.setAttribute("contenteditable", "false");
    fig.draggable = true;
    img.draggable = false;
    img.replaceWith(fig);
    fig.appendChild(img);
    changed = true;
    if (inTable || !block || !root.contains(block) || !block.contains(fig)) return;
    const list = block.tagName === "LI" ? block.closest("ul, ol") : null;
    const anchor = list && root.contains(list) ? list : block;
    if (anchor.parentNode) anchor.after(fig);
    const text = String(block.textContent || "").replace(/\u00a0/g, " ").trim();
    if (!text && block.isConnected && !block.querySelector("img, br, table, ul, ol, figure")) {
      const sel = typeof window !== "undefined" ? window.getSelection() : null;
      const keepCaret = !!(sel?.rangeCount && block.contains(sel.anchorNode));
      block.remove();
      if (keepCaret && fig.isConnected) {
        let next = fig.nextElementSibling;
        if (!next || !/^(P|H1|H2|H3|H4|H5|H6)$/.test(next.tagName)) {
          next = root.ownerDocument.createElement("p");
          next.appendChild(root.ownerDocument.createElement("br"));
          fig.after(next);
        }
        placeCaretAtBlockStart(next);
      }
    }
  });
  [...root.querySelectorAll("figure.rte-image, figure.rte-flow, figure.rte-table-figure")].forEach((fig) => {
    if (!root.contains(fig) || fig.closest("td, th, .rte-textbox")) return;
    const parent = fig.parentElement;
    if (!parent || parent === root || parent.classList.contains("rte-image-row")) return;
    const block = parent.closest("p, h1, h2, h3, h4, h5, h6, li, blockquote");
    if (!block || !root.contains(block)) return;
    const list = block.tagName === "LI" ? block.closest("ul, ol") : null;
    const anchor = list && root.contains(list) ? list : block;
    if (!anchor.parentNode || !anchor.contains(fig)) return;
    anchor.after(fig);
    changed = true;
  });
  if (normalizeImageRows(root)) changed = true;
  return changed;
};

const imageRowFigures = (row) =>
  [...(row?.children || [])].filter((el) => el.matches?.("figure.rte-image"));

const unwrapImageRowIfSparse = (row) => {
  if (!row?.classList?.contains("rte-image-row") || !row.parentNode) return;
  const images = imageRowFigures(row);
  if (images.length >= 2) return;
  images.forEach((fig) => row.parentNode.insertBefore(fig, row));
  row.remove();
};

const normalizeImageRows = (root) => {
  if (!root?.querySelectorAll) return false;
  let changed = false;
  [...root.querySelectorAll(".rte-image-row")].forEach((row) => {
    if (!root.contains(row)) return;
    const parent = row.parentElement;
    const block = parent?.closest?.("p, h1, h2, h3, h4, h5, h6, li, blockquote");
    if (block && root.contains(block) && block.contains(row)) {
      const list = block.tagName === "LI" ? block.closest("ul, ol") : null;
      const anchor = list && root.contains(list) ? list : block;
      if (anchor.parentNode) {
        anchor.after(row);
        changed = true;
      }
    }
    const before = row.isConnected;
    unwrapImageRowIfSparse(row);
    if (before && !row.isConnected) changed = true;
  });
  return changed;
};

/** Image under the pointer, and which half of it the pointer is on. */
const imageBesideTarget = (root, x, y, dragging) => {
  if (!root || !dragging?.classList?.contains("rte-image")) return null;
  let hit = null;
  root.querySelectorAll("figure.rte-image").forEach((fig) => {
    if (!root.contains(fig) || fig === dragging) return;
    const rect = fig.getBoundingClientRect();
    if (rect.width < 8 || rect.height < 8) return;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) return;
    hit = fig;
  });
  if (!hit) return null;
  const rect = hit.getBoundingClientRect();
  const side = x < rect.left + rect.width / 2 ? "left" : "right";
  const row = hit.closest(".rte-image-row");
  const sameRow = !!(row && row.contains(dragging));
  const count = row ? imageRowFigures(row).length : 1;
  if (count >= 2 && !sameRow) return null;
  return { fig: hit, side };
};

const placeImageBeside = (dragging, target, side) => {
  if (!dragging || !target || dragging === target || !target.parentNode) return false;
  const doc = dragging.ownerDocument;
  const oldRow = dragging.closest(".rte-image-row");
  const targetRow = target.closest(".rte-image-row");
  if (oldRow && oldRow === targetRow) {
    if (side === "left") oldRow.insertBefore(dragging, target);
    else target.after(dragging);
    return true;
  }
  if (oldRow) {
    dragging.remove();
    unwrapImageRowIfSparse(oldRow);
  }
  let row = target.closest(".rte-image-row");
  if (row && imageRowFigures(row).length >= 2) {
    row.after(dragging);
    return true;
  }
  if (!row) {
    row = doc.createElement("div");
    row.className = "rte-image-row";
    row.setAttribute("contenteditable", "false");
    target.replaceWith(row);
    row.appendChild(target);
  }
  if (side === "left") row.insertBefore(dragging, target);
  else target.after(dragging);
  return true;
};

const blockUnderMediaPoint = (root, x, y, dragging) => {
  if (!root) return null;
  const nodes = [...root.querySelectorAll("p, h1, h2, h3, h4, h5, h6")];
  let match = null;
  let matchScore = Infinity;
  nodes.forEach((el) => {
    if (!root.contains(el) || el.closest("table, pre, figure")) return;
    if (dragging?.contains?.(el)) return;
    const rect = el.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) return;
    if (x < rect.left - 24 || x > rect.right + 24) return;
    if (y < rect.top - 6 || y > rect.bottom + 22) return;
    const score = (el.tagName === "P" ? 0 : 40) + Math.abs(y - rect.bottom);
    if (score < matchScore) {
      match = el;
      matchScore = score;
    }
  });
  if (match) return match;
  let above = null;
  nodes.forEach((el) => {
    if (el.tagName !== "P" || !root.contains(el) || el.closest("table, pre, figure")) return;
    if (dragging?.contains?.(el)) return;
    const rect = el.getBoundingClientRect();
    if (rect.bottom > y + 8) return;
    if (x < rect.left - 24 || x > rect.right + 24) return;
    if (!above || rect.bottom > above.getBoundingClientRect().bottom) above = el;
  });
  return above;
};

/**
 * Reset rich text to baseline: headings/quotes/code → paragraph, unwrap lists,
 * unwrap formatting (bold/italic/underline/strike/sup/sub/links/spans),
 * strip color/size/highlight/align. Pass asEditorRoot=false for a selection fragment.
 */
const normalizeEditorFormattingToBaseline = (root, { asEditorRoot = true } = {}) => {
  if (!root?.ownerDocument) return;
  const parked = parkRteFlowFigures(root);
  try {
  normalizeEditorFormattingBody(root, asEditorRoot);
  } finally {
    restoreRteFlowFigures(parked);
  }
};

const normalizeEditorFormattingBody = (root, asEditorRoot) => {
  root.querySelectorAll("h1,h2,h3,h4,h5,h6,blockquote,pre").forEach((h) =>
    replaceElementPreserveChildren(h, "p")
  );

  unwrapListsToParagraphs(root);

  unwrapDeepByTagNames(root, ["FONT"]);

  unwrapDeepByTagNames(root, [
    "SMALL",
    "BIG",
    "STRIKE",
    "S",
    "U",
    "STRONG",
    "B",
    "EM",
    "I",
    "SUP",
    "SUB",
    "A",
    "CODE",
  ]);

  if (asEditorRoot) {
    [...root.children].forEach((ch) => {
      const tag = ch.tagName;
      if (!/^DIV|SECTION$/i.test(tag)) return;
      if (ch.querySelector("ul,ol,blockquote,h1,h2,h3,h4,h5,h6,pre,table")) return;
      replaceElementPreserveChildren(ch, "p");
    });
  }

  stripProblematicInlineStyles(root);

  unwrapDeepByTagNames(root, ["SPAN"]);
  unwrapDeepByTagNames(root, ["FONT"]);

  stripProblematicInlineStyles(root);

  if (root.nodeType === 1) clearBlockLayoutStyles(root);

  if (!asEditorRoot) return;

  [...root.childNodes].forEach((cn) => {
    if (
      cn.nodeType === Node.TEXT_NODE &&
      String(cn.textContent ?? "").trim() !== ""
    ) {
      const p = root.ownerDocument.createElement("p");
      root.insertBefore(p, cn);
      p.appendChild(cn);
    }
  });

  [...root.childNodes].forEach((cn) => {
    if (
      cn.nodeType === Node.TEXT_NODE &&
      String((cn.textContent ?? "").trim()) === ""
    ) {
      root.removeChild(cn);
    }
  });
};

const selectionHasSupOrSub = (sel, root, tagName) => {
  if (!sel?.rangeCount || !root) return false;
  const tag = String(tagName).toLowerCase();
  const range = sel.getRangeAt(0);
  const startEl =
    range.startContainer.nodeType === Node.ELEMENT_NODE
      ? range.startContainer
      : range.startContainer.parentElement;
  let el = startEl;
  while (el && el !== root) {
    if (el.nodeType === 1 && el.tagName.toLowerCase() === tag) return true;
    el = el.parentElement;
  }
  if (range.collapsed) return false;
  try {
    const nodes = root.querySelectorAll(tag);
    for (let i = 0; i < nodes.length; i += 1) {
      if (range.intersectsNode(nodes[i])) return true;
    }
  } catch {
    /* detached */
  }
  return false;
};

const STRIKE_TAGS = new Set(["s", "strike", "del"]);

const elementLooksStruck = (el) => {
  if (!el || el.nodeType !== 1) return false;
  if (STRIKE_TAGS.has(el.tagName.toLowerCase())) return true;
  const inline = `${el.style?.textDecorationLine || ""} ${
    el.style?.textDecoration || ""
  } ${el.getAttribute("style") || ""}`;
  return /\bline-through\b/i.test(inline);
};

const selectionHasStrike = (sel, root) => {
  if (!root || !sel?.rangeCount) return false;
  const range = sel.getRangeAt(0);
  let el =
    range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
      ? range.commonAncestorContainer
      : range.commonAncestorContainer.parentElement;
  while (el && el !== root) {
    if (elementLooksStruck(el)) return true;
    el = el.parentElement;
  }
  if (range.collapsed) return false;
  try {
    const nodes = root.querySelectorAll("s, strike, del, [style*='line-through']");
    for (let i = 0; i < nodes.length; i += 1) {
      if (range.intersectsNode(nodes[i])) return true;
    }
  } catch {
    /* detached */
  }
  return false;
};

const stripInlineLineThrough = (el) => {
  if (!el?.style) return;
  const kept = String(el.style.textDecorationLine || "")
    .split(/\s+/)
    .filter((part) => part && !/^line-through$/i.test(part));
  if (kept.length) {
    el.style.textDecorationLine = kept.join(" ");
  } else {
    el.style.removeProperty("text-decoration-line");
    el.style.removeProperty("text-decoration");
  }
  el.style.removeProperty("text-decoration-color");
  el.style.removeProperty("text-decoration-thickness");
  const raw = el.getAttribute("style") || "";
  if (/\bline-through\b/i.test(raw)) {
    el.setAttribute(
      "style",
      raw
        .replace(/text-decoration(?:-line|-color|-thickness)?\s*:[^;]*;?/gi, "")
        .replace(/^\s*;\s*|\s*;\s*$/g, "")
        .replace(/\s*;\s*;/g, ";")
        .trim()
    );
    if (kept.length) el.style.textDecorationLine = kept.join(" ");
  }
  if (!(el.getAttribute("style") || "").trim()) el.removeAttribute("style");
  if (isBareStyleSpan(el)) unwrapInlineElementKeepChildren(el);
};

const collectSelectedTextNodes = (range, root) => {
  if (!range || range.collapsed || !root) return [];
  if (range.commonAncestorContainer.nodeType === Node.TEXT_NODE) {
    return root.contains(range.commonAncestorContainer)
      ? [range.commonAncestorContainer]
      : [];
  }
  const texts = [];
  const walker = document.createTreeWalker(
    range.commonAncestorContainer,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode(node) {
        if (!root.contains(node) || !node.nodeValue) {
          return NodeFilter.FILTER_REJECT;
        }
        try {
          if (!range.intersectsNode(node)) return NodeFilter.FILTER_REJECT;
        } catch {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    }
  );
  let node = walker.nextNode();
  while (node) {
    texts.push(node);
    node = walker.nextNode();
  }
  return texts;
};

const sliceTextNodeToRange = (text, range) => {
  let start = 0;
  let end = text.nodeValue.length;
  if (text === range.startContainer) start = range.startOffset;
  if (text === range.endContainer) end = range.endOffset;
  start = Math.max(0, Math.min(start, text.nodeValue.length));
  end = Math.max(start, Math.min(end, text.nodeValue.length));
  if (start === end) return null;
  if (end < text.nodeValue.length) text.splitText(end);
  if (start > 0) return text.splitText(start);
  return text;
};

const wrapRangeInInlineTag = (range, root, tagName, skipTags = null) => {
  const tag = String(tagName).toLowerCase();
  const skip = skipTags instanceof Set ? skipTags : new Set([tag]);
  const wraps = [];
  collectSelectedTextNodes(range, root).forEach((text) => {
    if (text.parentElement === root && !String(text.nodeValue).trim()) return;
    let ancestor = text.parentElement;
    let skipNode = false;
    while (ancestor && ancestor !== root) {
      const name = ancestor.tagName.toLowerCase();
      if (skip.has(name) || (tag === "code" && name === "pre")) {
        skipNode = true;
        break;
      }
      ancestor = ancestor.parentElement;
    }
    if (skipNode) return;
    const piece = sliceTextNodeToRange(text, range);
    if (!piece?.parentNode) return;
    const wrap = document.createElement(tag);
    piece.parentNode.insertBefore(wrap, piece);
    wrap.appendChild(piece);
    wraps.push(wrap);
  });
  return wraps;
};

const wrapRangeInStrike = (range, root) =>
  wrapRangeInInlineTag(range, root, "s", STRIKE_TAGS);

const wrapRangeInStyledSpans = (range, root, applyToSpan, shouldSkipText) => {
  const wraps = [];
  collectSelectedTextNodes(range, root).forEach((text) => {
    if (text.parentElement === root && !String(text.nodeValue).trim()) return;
    if (isInsideFlowDiagramBody(text.parentElement) || isReferenceChromeNode(text)) return;
    if (typeof shouldSkipText === "function" && shouldSkipText(text)) return;
    const piece = sliceTextNodeToRange(text, range);
    if (!piece?.parentNode) return;
    const wrap = document.createElement("span");
    applyToSpan(wrap);
    piece.parentNode.insertBefore(wrap, piece);
    wrap.appendChild(piece);
    wraps.push(wrap);
  });
  return wraps;
};

const restoreSelectionAroundWraps = (sel, wraps) => {
  if (!sel || !wraps.length) return;
  const nr = document.createRange();
  nr.setStart(wraps[0], 0);
  const last = wraps[wraps.length - 1];
  nr.setEnd(last, last.childNodes.length);
  sel.removeAllRanges();
  sel.addRange(nr);
};

const isInlineCodeEl = (el) =>
  el &&
  el.nodeType === 1 &&
  el.tagName.toLowerCase() === "code" &&
  !el.closest("pre");

const selectionHasInlineCode = (sel, root) => {
  if (!root || !sel?.rangeCount) return false;
  const range = sel.getRangeAt(0);
  let el =
    range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
      ? range.commonAncestorContainer
      : range.commonAncestorContainer.parentElement;
  while (el && el !== root) {
    if (isInlineCodeEl(el)) return true;
    el = el.parentElement;
  }
  if (range.collapsed) return false;
  try {
    const nodes = root.querySelectorAll("code");
    for (let i = 0; i < nodes.length; i += 1) {
      if (isInlineCodeEl(nodes[i]) && range.intersectsNode(nodes[i])) return true;
    }
  } catch {
    /* detached */
  }
  return false;
};

const unwrapInlineCodeInSelection = (root, sel) => {
  if (!root || !sel?.rangeCount) return;
  const range = sel.getRangeAt(0);
  const toUnwrap = [];

  let el =
    range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
      ? range.commonAncestorContainer
      : range.commonAncestorContainer.parentElement;
  while (el && el !== root) {
    if (isInlineCodeEl(el)) toUnwrap.push(el);
    el = el.parentElement;
  }

  if (!range.collapsed) {
    root.querySelectorAll("code").forEach((node) => {
      try {
        if (isInlineCodeEl(node) && range.intersectsNode(node) && !toUnwrap.includes(node)) {
          toUnwrap.push(node);
        }
      } catch {
        /* detached */
      }
    });
  }

  toUnwrap.forEach((node) => unwrapInlineElementKeepChildren(node));
};

const unwrapStrikeInSelection = (root, sel) => {
  if (!root || !sel?.rangeCount) return;
  const range = sel.getRangeAt(0);
  const toClear = [];

  let el =
    range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
      ? range.commonAncestorContainer
      : range.commonAncestorContainer.parentElement;
  while (el && el !== root) {
    if (elementLooksStruck(el)) toClear.push(el);
    el = el.parentElement;
  }

  if (!range.collapsed) {
    root.querySelectorAll("s, strike, del, [style*='line-through']").forEach((node) => {
      try {
        if (range.intersectsNode(node) && !toClear.includes(node)) {
          toClear.push(node);
        }
      } catch {
        /* detached */
      }
    });
  }

  toClear.forEach((node) => {
    if (STRIKE_TAGS.has(node.tagName.toLowerCase())) {
      unwrapInlineElementKeepChildren(node);
    } else {
      stripInlineLineThrough(node);
    }
  });
};

const unwrapSupSubInSelection = (root, sel, tagName) => {
  if (!root || !sel?.rangeCount) return;
  const tag = String(tagName).toLowerCase();
  const range = sel.getRangeAt(0);
  const toUnwrap = [];

  let el =
    range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
      ? range.commonAncestorContainer
      : range.commonAncestorContainer.parentElement;
  while (el && el !== root) {
    if (el.nodeType === 1 && el.tagName.toLowerCase() === tag) {
      toUnwrap.push(el);
      break;
    }
    el = el.parentElement;
  }

  if (!range.collapsed) {
    root.querySelectorAll(tag).forEach((node) => {
      try {
        if (range.intersectsNode(node) && !toUnwrap.includes(node)) {
          toUnwrap.push(node);
        }
      } catch {
        /* detached */
      }
    });
  }

  toUnwrap.forEach((node) => unwrapInlineElementKeepChildren(node));
};

// Lightweight rich text editor for HTML content (used for introductions and helper text)
function RteCaptionMenu({ mode, node, toggleClassName, onApply, onRemove, variant = "light", size }) {
  const alignName = useId();
  const sizeName = useId();
  const customId = useId();
  const settings = readCaptionSettings(node);
  const kinds = mode === "table"
    ? [["table", "Table"]]
    : [["figure", "Figure"], ["image", "Image"], ["slides", "Slides"]];
  const kind = settings.kind;
  const abbrWord = CAPTION_LABELS[kind]?.abbr || "Fig.";
  const [customDraft, setCustomDraft] = useState("");
  const kindPickedRef = useRef(false);
  const editorRoot = node?.closest?.(".rte-editable-root") || node?.closest?.("[contenteditable='true']");
  const customLabels = customCaptionLabels(editorRoot);
  if (
    settings.custom &&
    !customLabels.some((label) => label.toLowerCase() === settings.custom.toLowerCase())
  ) {
    customLabels.push(settings.custom);
  }
  const apply = (patch) => {
    if (!node) return;
    onApply(node, {
      kind: settings.kind,
      abbr: settings.abbr,
      align: settings.align,
      size: settings.size,
      custom: settings.custom,
      ...patch,
    });
  };
  const commitCustom = () => {
    const text = customDraft.trim();
    if (!text) return;
    if (text.toLowerCase() === String(settings.custom).toLowerCase() && settings.kind === "custom") {
      setCustomDraft("");
      return;
    }
    apply({ kind: "custom", custom: text });
    setCustomDraft("");
  };
  return (
    <Dropdown
      align="end"
      autoClose="outside"
      onToggle={(open) => {
        if (!open) {
          if (!kindPickedRef.current) commitCustom();
          return;
        }
        kindPickedRef.current = false;
        if (!node || settings.has) return;
        onApply(node, {
          kind: defaultCaptionKind(node),
          abbr: false,
          align: "center",
          size: "14px",
          custom: "",
        });
      }}
    >
      <Dropdown.Toggle
        variant={variant}
        size={size}
        className={toggleClassName}
        title="Caption"
        aria-label="Caption"
      >
        <i className="bi bi-card-text"></i>
      </Dropdown.Toggle>
      <Dropdown.Menu className="shadow-sm rte-caption-menu" onMouseDown={(event) => event.stopPropagation()}>
        {kinds.map(([id, label]) => (
          <Dropdown.Item
            key={id}
            as="button"
            type="button"
            active={settings.has && settings.kind === id}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              kindPickedRef.current = true;
              apply({ kind: id });
            }}
          >
            {label}
          </Dropdown.Item>
        ))}
        {customLabels.map((label) => (
          <Dropdown.Item
            key={`custom:${label.toLowerCase()}`}
            as="button"
            type="button"
            active={
              settings.has &&
              settings.kind === "custom" &&
              settings.custom.toLowerCase() === label.toLowerCase()
            }
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              kindPickedRef.current = true;
              apply({ kind: "custom", custom: label });
            }}
          >
            {label}
          </Dropdown.Item>
        ))}
        <div className="rte-caption-custom">
          <label className="rte-caption-menu-label" htmlFor={customId}>
            Your label
          </label>
          <input
            id={customId}
            type="text"
            className={`rte-caption-custom-input${settings.kind === "custom" ? " is-active" : ""}`}
            value={customDraft}
            placeholder="Type your own"
            aria-label="Custom caption label"
            onChange={(event) => setCustomDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                kindPickedRef.current = false;
                commitCustom();
              }
            }}
            onBlur={(event) => {
              const next = event.relatedTarget;
              const menu = event.currentTarget.closest(".rte-caption-menu");
              if (next && menu?.contains(next)) return;
              if (!kindPickedRef.current) commitCustom();
            }}
          />
        </div>
        {kind !== "custom" ? (
          <div className="rte-caption-switch">
            <Form.Check
              type="switch"
              id={`${customId}-abbr`}
              label={`Abbreviation (${abbrWord})`}
              checked={!!settings.abbr}
              onChange={(event) => apply({ abbr: event.target.checked })}
            />
          </div>
        ) : null}
        <div className="dropdown-divider" />
        <div className="rte-caption-menu-label">Size</div>
        <div className="rte-caption-choice">
          <div className="radio-inputs" role="radiogroup" aria-label="Caption size">
            {CAPTION_SIZES.map((opt) => (
              <label key={opt.id} className="radio">
                <input
                  type="radio"
                  name={sizeName}
                  value={opt.id}
                  checked={settings.size === opt.id}
                  aria-label={`${opt.label} ${opt.id}`}
                  onChange={() => apply({ size: opt.id })}
                />
                <span className="name flex-column py-1 lh-sm">
                  <span>{opt.label}</span>
                  <span className="fw-normal" style={{ fontSize: "0.7rem" }}>{opt.id}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
        <div className="dropdown-divider" />
        <div className="rte-caption-menu-label">Position</div>
        <div className="rte-caption-choice">
          <div className="radio-inputs" role="radiogroup" aria-label="Caption position">
            {ALIGN_OPTIONS.filter((opt) => opt.value !== "justify").map((opt) => (
              <label key={opt.value} className="radio">
                <input
                  type="radio"
                  name={alignName}
                  value={opt.value}
                  checked={settings.align === opt.value}
                  aria-label={opt.label}
                  onChange={() => apply({ align: opt.value })}
                />
                <span className="name" title={opt.label}>
                  <i className={`bi ${opt.icon}`} aria-hidden="true"></i>
                </span>
              </label>
            ))}
          </div>
        </div>
        {settings.has ? (
          <>
            <Dropdown.Divider />
            <Dropdown.Item
              as="button"
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onRemove(node)}
            >
              Remove caption
            </Dropdown.Item>
          </>
        ) : null}
      </Dropdown.Menu>
    </Dropdown>
  );
}

/** Visible page inside the scrolling document, in viewport coordinates. */
const readDocumentChromeClip = (shell) => {
  if (!shell) return null;
  const scroller = shell.closest(".rte-document-body");
  if (!scroller) return null;
  const page = shell.querySelector(".rte-document-page") || shell;
  const scrollerRect = scroller.getBoundingClientRect();
  const pageRect = page.getBoundingClientRect();
  const top = Math.max(Math.ceil(scrollerRect.top), Math.ceil(pageRect.top));
  const left = Math.max(Math.ceil(scrollerRect.left), Math.ceil(pageRect.left));
  const right = Math.min(Math.floor(scrollerRect.right), Math.floor(pageRect.right));
  const bottom = Math.min(Math.floor(scrollerRect.bottom), Math.floor(pageRect.bottom));
  if (right - left < 8 || bottom - top < 8) return null;
  return { top, left, right, bottom };
};

const clipContainsBox = (clip, top, left, width, height) => {
  if (!clip) return true;
  return (
    top + height > clip.top &&
    top < clip.bottom &&
    left + width > clip.left &&
    left < clip.right
  );
};

/** Latest diagram artwork, keyed by embed id. Undo restores document HTML, then paints these back. */
const captureFlowDiagramBodies = (root, cache) => {
  if (!root?.querySelectorAll || !cache) return;
  root.querySelectorAll("figure.rte-flow").forEach((fig) => {
    const id = fig.getAttribute("data-rte-flow-id");
    if (!id) return;
    const body = figureBodyHtml(fig).trim();
    if (!/<svg\b/i.test(body)) return;
    cache.set(String(id), body);
  });
};

/**
 * Document history ignores editor chrome and flow-diagram artwork.
 * Inserting, moving, deleting, or captioning a diagram still changes this HTML.
 */
const historyHtmlFrom = (html, cache) => {
  const host = document.createElement("div");
  host.innerHTML = html || "";
  host.querySelectorAll(".rte-table-sel, .rte-table-sel-preview").forEach((el) => {
    el.classList.remove("rte-table-sel", "rte-table-sel-preview");
    if (!String(el.getAttribute("class") || "").trim()) el.removeAttribute("class");
  });
  host.querySelectorAll(".rte-code-gutter").forEach((gutter) => gutter.remove());
  captureFlowDiagramBodies(host, cache);
  // Hover chrome. Real edits are still captured; these attributes are put back on restore.
  host.querySelectorAll("[draggable], [contenteditable]").forEach((el) => {
    el.removeAttribute("draggable");
    el.removeAttribute("contenteditable");
  });
  host.querySelectorAll("figure.rte-flow").forEach((fig) => {
    fig.classList.remove("rte-flow-missing");
    fig.removeAttribute("data-rte-flow-rev");
    if (!String(fig.getAttribute("class") || "").trim()) fig.removeAttribute("class");
    setFigureBody(fig, "");
  });
  return host.innerHTML;
};

const rehydrateFlowDiagramBodies = (root, cache) => {
  if (!root?.querySelectorAll || !cache) return;
  root.querySelectorAll("figure.rte-flow").forEach((fig) => {
    const id = fig.getAttribute("data-rte-flow-id");
    const svg = id ? cache.get(String(id)) : "";
    if (!svg || figureBodyHtml(fig) === svg) return;
    setFigureBody(fig, svg);
  });
};

/** Flow-diagram artwork and editor chrome are not document edits. */
const isIgnoredHistoryMutation = (mutation) => {
  if (mutation.type === "attributes") {
    const name = mutation.attributeName;
    if (name === "draggable" || name === "contenteditable" || name === "data-rte-flow-rev") return true;
  }
  const node = mutation.target;
  const el = node?.nodeType === 1 ? node : node?.parentElement;
  if (!el?.closest) return false;
  if (el.closest(".rte-code-gutter")) return true;
  const flow = el.closest("figure.rte-flow");
  if (!flow) return false;
  return !el.closest("figcaption, .rte-caption");
};

function RteChromeClip({ clip, children }) {
  if (!clip) return children;
  const width = clip.right - clip.left;
  const height = clip.bottom - clip.top;
  if (width < 8 || height < 8) return children;
  return (
    <div
      className="rte-document-chrome-clip"
      style={{ top: clip.top, left: clip.left, width, height }}
    >
      {children}
    </div>
  );
}

const RichTextEditor = ({
  value,
  onChange,
  placeholder,
  variant = "default",
  prepareDomBeforeEmit,
  documentHeader = null,
  references = null,
}) => {
  const editorRef = useRef(null);
  const editorShellRef = useRef(null);
  const activeTableRef = useRef(null);
  const tableSelectionRef = useRef(null);
  const tableSelectionRestoreRef = useRef(null);
  const keepTableBandPopRef = useRef(false);
  const closeTableBandPopRef = useRef(() => {});
  const tableBandPopRef = useRef(false);
  const tableBandBtnRef = useRef(null);
  const tableHoverTimerRef = useRef(null);
  const tableHoverKeyRef = useRef("");
  const tableHoverIntentRef = useRef(null);
  const applyTableHoverRef = useRef(() => {});
  const editingTableRef = useRef(null);
  const tableEditStickyRef = useRef(false);
  const tableEditChromeSigRef = useRef("");
  const lastValueFromParentRef = useRef(undefined);
  /** Prevents stale controlled `value` from reverting the DOM right after toolbar / execCommand edits. */
  const lastEmittedHtmlRef = useRef(null);
  /** HTML strings emitted since the parent last matched the DOM. Older echoes must not replace a newer edit. */
  const emittedSinceHydrateRef = useRef([]);
  const inputNormalizeRafRef = useRef(0);
  /** Last `value` applied from props (or echoed from our emit). Used to detect lagging parent props. */
  const lastHydratedPropRef = useRef(undefined);
  const linkRangeRef = useRef(null);
  const selectionRangeRef = useRef(null);
  /** Toolbar clicks move focus before the command runs; don't let that overwrite the caret. */
  const toolbarSelectionLockRef = useRef(false);
  const syncFromEditorRef = useRef(null);
  const lastEditTargetRef = useRef("body"); // "title" | "body" — toolbar must not steal title edits
  const linkHoverHideTimerRef = useRef(null);
  const [blockType, setBlockType] = useState("Paragraph");
  const [headingValue, setHeadingValue] = useState("p");
  const [caretInspect, setCaretInspect] = useState(() => ({
    ...CARET_INSPECTOR_EMPTY,
  }));
  const caretInspectRef = useRef(CARET_INSPECTOR_EMPTY);
  const commitCaretInspect = (next) => {
    const resolved = typeof next === "function" ? next(caretInspectRef.current) : next;
    const prev = caretInspectRef.current;
    const same = Object.keys(CARET_INSPECTOR_EMPTY).every((key) => prev[key] === resolved[key]);
    if (!resolved || same) return;
    caretInspectRef.current = resolved;
    setCaretInspect(resolved);
  };
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkLabel, setLinkLabel] = useState("");
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [flowModalOpen, setFlowModalOpen] = useState(false);
  const [flowEmbedId, setFlowEmbedId] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [imageAlt, setImageAlt] = useState("");
  const [imageUpload, setImageUpload] = useState(null);
  const [imageUploadError, setImageUploadError] = useState("");
  const imageFileRef = useRef(null);
  const mediaDragRef = useRef(null);
  const tableDragActiveRef = useRef(false);
  const tableDragDroppedRef = useRef(false);
  const tableDragListenersRef = useRef(null);
  const mediaDropBlockRef = useRef(null);
  const imageBesideDropRef = useRef(null);
  const mediaFigRef = useRef(null);
  const mediaChromeSigRef = useRef("");
  const [mediaChrome, setMediaChrome] = useState(null);
  const imageResizeSigRef = useRef("");
  const imageResizeDragRef = useRef(null);
  const imageResizeListenersRef = useRef(null);
  const [imageResizeBar, setImageResizeBar] = useState(null);
  const [captionRev, setCaptionRev] = useState(0);
  const [mediaDropLine, setMediaDropLine] = useState(null);
  const [hoveredLinkEl, setHoveredLinkEl] = useState(null);
  const [selectionMenuPos, setSelectionMenuPos] = useState(null); // { top, left, placement }
  const [referenceUiOpen, setReferenceUiOpen] = useState(false);
  const [referenceSlot, setReferenceSlot] = useState(null);
  const [headingSwitches, setHeadingSwitches] = useState([]);
  const [chromeClip, setChromeClip] = useState(null);
  const chromeClipSigRef = useRef("");
  const [textboxChrome, setTextboxChrome] = useState(null);
  const [quoteChrome, setQuoteChrome] = useState(null);
  const textboxNodeRef = useRef(null);
  const quoteNodeRef = useRef(null);
  const hoveredTextboxRef = useRef(null);
  const hoveredQuoteRef = useRef(null);
  const headingChromeSigRef = useRef("");
  const textboxChromeSigRef = useRef("");
  const quoteChromeSigRef = useRef("");
  const refreshChromeRef = useRef(() => {});
  const refreshSelectionMenuRef = useRef(() => {});
  const isPointerSelectingRef = useRef(false);
  const selectionMenuRafRef = useRef(0);
  const [tableGridHover, setTableGridHover] = useState({ rows: 0, cols: 0 });
  const [tableGridDragging, setTableGridDragging] = useState(false);
  const [tableMenuOpen, setTableMenuOpen] = useState(false);
  const [tableStyleId, setTableStyleId] = useState("grid");
  const [tableColGutter, setTableColGutter] = useState(null);
  const [tableRowGutter, setTableRowGutter] = useState(null);
  const [tableSelection, setTableSelection] = useState(null); // { type: 'row'|'col', index }
  const [tableHoverSel, setTableHoverSel] = useState(null); // preview while near edge
  const [tableDelCol, setTableDelCol] = useState(null);
  const [tableDelRow, setTableDelRow] = useState(null);
  const [tableBandPop, setTableBandPop] = useState(false);
  const [tableStyleRev, setTableStyleRev] = useState(0);
  const [tableEditChrome, setTableEditChrome] = useState(null);
  const [tableMoveHandle, setTableMoveHandle] = useState(null);
  const [colResizeHandle, setColResizeHandle] = useState(null);
  tableSelectionRef.current = tableSelection;
  tableBandPopRef.current = tableBandPop;
  closeTableBandPopRef.current = () => {
    setTableBandPop((open) => (open ? false : open));
  };
  const tableMoveRef = useRef(null);
  const tableMoveSigRef = useRef("");
  const colResizeTimerRef = useRef(null);
  const colResizeIntentRef = useRef(null);
  const colResizeReadyRef = useRef(null);
  const colResizeTableRef = useRef(null);
  const colResizeDragRef = useRef(null);
  const colResizeListenersRef = useRef(null);
  const colResizeFollowRef = useRef(() => {});
  const tableGridPendingRef = useRef(null);
  const tableGridDraggingRef = useRef(false);
  const tableColDelHideTimerRef = useRef(null);
  const tableRowDelHideTimerRef = useRef(null);
  const historyRef = useRef({ stack: [], index: -1 });
  const isRestoringHistoryRef = useRef(false);
  const typingHistoryTimerRef = useRef(null);
  const historyCommitRef = useRef(() => {});
  /** Latest SVG for each embedded diagram. Document undo does not rewind these. */
  const flowSvgCacheRef = useRef(new Map());
  const [historyTick, setHistoryTick] = useState(0);

  const HISTORY_MAX = 80;
  const TYPING_HISTORY_MS = 450;

  const readSnapshotHtml = () => {
    const root = editorRef.current;
    if (!root) return "";
    return historyHtmlFrom(root.innerHTML, flowSvgCacheRef.current);
  };

  const bumpHistoryUi = () => setHistoryTick((t) => t + 1);

  const commitHistorySnapshot = (htmlArg) => {
    if (isRestoringHistoryRef.current) return;
    const html = historyHtmlFrom(
      htmlArg != null ? htmlArg : editorRef.current?.innerHTML || "",
      flowSvgCacheRef.current
    );
    const hist = historyRef.current;
    if (hist.index >= 0 && hist.stack[hist.index] === html) return;
    const next = hist.stack.slice(0, Math.max(0, hist.index + 1));
    next.push(html);
    while (next.length > HISTORY_MAX) next.shift();
    hist.stack = next;
    hist.index = next.length - 1;
    bumpHistoryUi();
  };
  historyCommitRef.current = () => {
    if (isRestoringHistoryRef.current) return;
    commitHistorySnapshot();
  };

  const resetHistoryFromHtml = (html) => {
    const normalized = historyHtmlFrom(html ?? "", flowSvgCacheRef.current);
    historyRef.current = { stack: [normalized], index: 0 };
    bumpHistoryUi();
  };

  const clearTypingHistoryTimer = () => {
    if (typingHistoryTimerRef.current != null) {
      clearTimeout(typingHistoryTimerRef.current);
      typingHistoryTimerRef.current = null;
    }
  };

  const scheduleTypingHistory = () => {
    clearTypingHistoryTimer();
    typingHistoryTimerRef.current = window.setTimeout(() => {
      typingHistoryTimerRef.current = null;
      commitHistorySnapshot();
    }, TYPING_HISTORY_MS);
  };

  useEffect(() => {
    const root = editorRef.current;
    if (!root || typeof MutationObserver === "undefined") return undefined;
    let timer = 0;
    const dragInProgress = () =>
      colResizeDragRef.current ||
      imageResizeDragRef.current ||
      mediaDragRef.current ||
      tableDragActiveRef.current;
    const observer = new MutationObserver((mutations) => {
      if (isRestoringHistoryRef.current || dragInProgress()) return;
      if (!mutations.some((mutation) => !isIgnoredHistoryMutation(mutation))) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        timer = 0;
        if (isRestoringHistoryRef.current || dragInProgress()) return;
        historyCommitRef.current();
      }, TYPING_HISTORY_MS);
    });
    observer.observe(root, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
    });
    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, []);

  const restoreHistoryAt = (index) => {
    const hist = historyRef.current;
    const root = editorRef.current;
    if (!root || index < 0 || index >= hist.stack.length) return;
    clearTypingHistoryTimer();
    isRestoringHistoryRef.current = true;
    hist.index = index;
    clearTableSelection();
    captureFlowDiagramBodies(root, flowSvgCacheRef.current);
    root.innerHTML = hist.stack[index];
    rehydrateFlowDiagramBodies(root, flowSvgCacheRef.current);
    renumberCaptions(root);
    root.querySelectorAll("figure.rte-flow, figure.rte-image, figure.rte-table-figure").forEach((fig) => {
      fig.setAttribute("contenteditable", "false");
      if (!fig.classList.contains("rte-table-figure")) fig.draggable = true;
    });
    refreshCodeBlockLineNumbers(root);
    const html = root.innerHTML;
    noteLocalEmit(html);
    lastValueFromParentRef.current = html;
    lastHydratedPropRef.current = html;
    onChange(html);
    updateBlockTypeFromSelection();
    refreshRteDocBaseVar(root);
    isRestoringHistoryRef.current = false;
    bumpHistoryUi();
    toolbarSelectionLockRef.current = false;
    focusEditor();
  };

  const performUndo = () => {
    toolbarSelectionLockRef.current = false;
    const hist = historyRef.current;
    if (hist.index < 0 || !editorRef.current) return;
    clearTypingHistoryTimer();
    const current = readSnapshotHtml();
    // Pending edit is not on the stack yet. Record it so redo can bring it back.
    if (current !== hist.stack[hist.index]) {
      commitHistorySnapshot(current);
      const next = historyRef.current;
      if (next.index > 0) restoreHistoryAt(next.index - 1);
      return;
    }
    if (hist.index <= 0) return;
    restoreHistoryAt(hist.index - 1);
  };

  const performRedo = () => {
    toolbarSelectionLockRef.current = false;
    const hist = historyRef.current;
    if (hist.index < 0) return;
    clearTypingHistoryTimer();
    const current = readSnapshotHtml();
    if (current !== hist.stack[hist.index]) {
      commitHistorySnapshot(current);
      return;
    }
    if (hist.index >= hist.stack.length - 1) return;
    restoreHistoryAt(hist.index + 1);
  };

  const noteLocalEmit = (html) => {
    lastEmittedHtmlRef.current = html;
    const pending = emittedSinceHydrateRef.current;
    if (pending[pending.length - 1] !== html) pending.push(html);
    if (pending.length > 12) pending.splice(0, pending.length - 12);
  };

  const syncFromEditor = () => {
    if (!editorRef.current) return;
    if (inputNormalizeRafRef.current) {
      cancelAnimationFrame(inputNormalizeRafRef.current);
      inputNormalizeRafRef.current = 0;
    }
    normalizeDivBlocksToParagraphs(editorRef.current);
    refreshCodeBlockLineNumbers(editorRef.current);
    const savedSel = tableSelection;
    const table = activeTableRef.current;
    clearTableSelectionDom(editorRef.current);
    try {
      prepareDomBeforeEmit?.(editorRef.current);
    } catch {
      /* ignore prepare errors */
    }
    ensureEditorImageContainers(editorRef.current);
    renumberCaptions(editorRef.current);
    const html = editorRef.current.innerHTML;
    noteLocalEmit(html);
    lastValueFromParentRef.current = html;
    onChange(html);
    if (savedSel && table && editorRef.current.contains(table)) {
      paintTableSelection(table, savedSel, false);
      positionTableDeleteForSelection(table, savedSel);
    }
    updateBlockTypeFromSelection();
    refreshRteDocBaseVar(editorRef.current);
    if (toolbarSelectionLockRef.current) {
      toolbarSelectionLockRef.current = false;
      stashEditorSelectionRange();
    }
    if (!isRestoringHistoryRef.current) {
      clearTypingHistoryTimer();
      commitHistorySnapshot(html);
    }
  };
  syncFromEditorRef.current = syncFromEditor;

  const applyEditorMutation = (mutator, options = {}) => {
    const root = editorRef.current;
    if (!root || typeof mutator !== "function") return;
    if (options.focus !== false) {
      root.focus({ preventScroll: true });
      restoreEditorSelectionRange();
    }
    const before = root.innerHTML;
    mutator(root);
    if (options.focus === false && root.innerHTML === before) return;
    syncFromEditor();
  };

  useEffect(() => {
    const onFlowUpdated = (event) => {
      const root = editorRef.current;
      const { id, svg } = event.detail || {};
      if (!root || id == null || !svg) return;
      let changed = false;
      root.querySelectorAll("figure.rte-flow").forEach((fig) => {
        if (fig.getAttribute("data-rte-flow-id") !== String(id)) return;
        if (figureBodyHtml(fig) === svg) return;
        setFigureBody(fig, svg);
        changed = true;
      });
      if (changed) syncFromEditorRef.current?.();
    };
    const onFlowEdit = (event) => {
      const root = editorRef.current;
      const id = event.detail?.id;
      if (!root || id == null || !(event.target instanceof Node) || !root.contains(event.target)) return;
      setFlowEmbedId(String(id));
      setFlowModalOpen(true);
    };
    window.addEventListener("rte-flow-updated", onFlowUpdated);
    document.addEventListener("rte-flow-edit", onFlowEdit);
    return () => {
      window.removeEventListener("rte-flow-updated", onFlowUpdated);
      document.removeEventListener("rte-flow-edit", onFlowEdit);
    };
  }, []);

  // Controlled `value`: never clobber freshly edited DOM while the parent prop is still one tick behind.
  useLayoutEffect(() => {
    if (!editorRef.current) {
      tableSelectionRestoreRef.current = null;
      return;
    }

    const incoming = value ?? "";
    const dom = editorRef.current.innerHTML;

    if (incoming === dom) {
      tableSelectionRestoreRef.current = null;
      lastHydratedPropRef.current = incoming;
      lastEmittedHtmlRef.current = null;
      emittedSinceHydrateRef.current = [];
      lastValueFromParentRef.current = incoming;
      refreshRteDocBaseVar(editorRef.current);
      refreshCodeBlockLineNumbers(editorRef.current);
      if (ensureEditorImageContainers(editorRef.current)) {
        syncFromEditor();
      } else if (historyRef.current.index < 0) {
        resetHistoryFromHtml(incoming);
      }
      return;
    }

    const awaitingEcho =
      lastEmittedHtmlRef.current != null &&
      dom === lastEmittedHtmlRef.current &&
      incoming !== dom &&
      incoming === lastHydratedPropRef.current;

    if (awaitingEcho) {
      tableSelectionRestoreRef.current = null;
      refreshRteDocBaseVar(editorRef.current);
      refreshCodeBlockLineNumbers(editorRef.current);
      return;
    }

    // A newer local edit (join / split / typing) is already in the DOM. An older
    // parent value — the paste, or a rewrite of it — must not put that edit back.
    const localEditAhead =
      lastEmittedHtmlRef.current != null &&
      dom === lastEmittedHtmlRef.current &&
      incoming !== dom &&
      emittedSinceHydrateRef.current.length > 1 &&
      !isRestoringHistoryRef.current;

    if (localEditAhead) {
      tableSelectionRestoreRef.current = null;
      refreshRteDocBaseVar(editorRef.current);
      refreshCodeBlockLineNumbers(editorRef.current);
      return;
    }

    // Parent rewrote our latest emit (e.g. title placeholder sync) — apply HTML
    // but keep the undo/redo stack intact.
    const parentRewroteOurEmit =
      lastEmittedHtmlRef.current != null &&
      dom === lastEmittedHtmlRef.current &&
      incoming !== dom &&
      !isRestoringHistoryRef.current;

    if (parentRewroteOurEmit) {
      replaceEditorHtmlKeepCaret(editorRef.current, incoming);
      restoreTableBandAfterHydrate();
      refreshCodeBlockLineNumbers(editorRef.current);
      lastEmittedHtmlRef.current = editorRef.current.innerHTML;
      emittedSinceHydrateRef.current = [];
      lastHydratedPropRef.current = incoming;
      lastValueFromParentRef.current = incoming;
      updateBlockTypeFromSelection();
      refreshRteDocBaseVar(editorRef.current);
      return;
    }

    lastEmittedHtmlRef.current = null;
    emittedSinceHydrateRef.current = [];

    if (incoming !== dom) {
      replaceEditorHtmlKeepCaret(editorRef.current, incoming);
    }
    restoreTableBandAfterHydrate();
    refreshCodeBlockLineNumbers(editorRef.current);
    lastEmittedHtmlRef.current = editorRef.current.innerHTML;
    lastHydratedPropRef.current = incoming;
    lastValueFromParentRef.current = incoming;
    updateBlockTypeFromSelection();
    refreshRteDocBaseVar(editorRef.current);
    if (!isRestoringHistoryRef.current) {
      resetHistoryFromHtml(incoming);
    }
  }, [value]);

  const refreshEditorChrome = () => {
    const root = editorRef.current;
    if (!root || typeof window === "undefined") {
      if (headingChromeSigRef.current) {
        headingChromeSigRef.current = "";
        setHeadingSwitches([]);
      }
      if (textboxChromeSigRef.current) {
        textboxChromeSigRef.current = "";
        textboxNodeRef.current = null;
        setTextboxChrome(null);
      }
      if (quoteChromeSigRef.current) {
        quoteChromeSigRef.current = "";
        quoteNodeRef.current = null;
        setQuoteChrome(null);
      }
      if (mediaChromeSigRef.current) {
        mediaChromeSigRef.current = "";
        setMediaChrome(null);
      }
      if (imageResizeSigRef.current) {
        imageResizeSigRef.current = "";
        setImageResizeBar(null);
      }
      if (tableEditChromeSigRef.current) {
        tableEditChromeSigRef.current = "";
        editingTableRef.current = null;
        tableEditStickyRef.current = false;
        setTableEditChrome(null);
      }
      return;
    }

    const shellRect = editorShellRef.current?.getBoundingClientRect?.() || null;
    const clip = readDocumentChromeClip(editorShellRef.current);
    const clipSig = clip ? `${clip.top}:${clip.left}:${clip.right}:${clip.bottom}` : "";
    if (clipSig !== chromeClipSigRef.current) {
      chromeClipSigRef.current = clipSig;
      setChromeClip(clip);
    }
    colResizeFollowRef.current();
    const visibleInShell = (rect) => {
      if (clip) return clipContainsBox(clip, rect.top, rect.left, rect.width, rect.height);
      if (!shellRect) return true;
      return rect.bottom > shellRect.top + 4 && rect.top < shellRect.bottom - 4;
    };
    const placeBar = (rect, barW, barH, preferAbove) => {
      const above = preferAbove || rect.height < 48;
      let top = Math.round(above ? rect.top - barH - 4 : rect.top + 4);
      if (clip && top < clip.top) top = Math.round(rect.top + 4);
      if (!clip) top = Math.max(4, top);
      const viewW = document.documentElement?.clientWidth || window.innerWidth;
      let rightEdge = Math.round(rect.right - 4);
      if (clip) {
        rightEdge = Math.min(rightEdge, clip.right - 4);
        rightEdge = Math.max(rightEdge, clip.left + Math.min(barW, clip.right - clip.left - 8));
      } else {
        rightEdge = Math.min(viewW - 8, Math.max(rightEdge, Math.min(188, viewW - 16)));
      }
      const right = Math.max(8, viewW - rightEdge);
      if (clip && !clipContainsBox(clip, top, rightEdge - barW, barW, barH)) return null;
      return { top, right };
    };
    const measured = [];
    root.querySelectorAll("h1,h2,h3,h4,h5,h6").forEach((el) => {
      if (!root.contains(el) || el.closest("[data-guides-title]")) return;
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return;
      if (!visibleInShell(rect)) return;
      const btnTop = Math.round(rect.top);
      const btnLeft = Math.round(rect.left) - 40;
      if (clip && !clipContainsBox(clip, btnTop, btnLeft, 40, 30)) return;
      measured.push({
        el,
        top: Math.round(rect.top),
        left: Math.round(rect.left),
        level: Number(el.tagName.slice(1)) || 1,
      });
    });
    const headingSig = measured
      .map((item) => `${item.level}:${item.top}:${item.left}`)
      .join("|");
    if (headingSig !== headingChromeSigRef.current) {
      headingChromeSigRef.current = headingSig;
      setHeadingSwitches(
        measured.map((item, index) => ({
          key: index,
          top: item.top,
          left: item.left,
          level: item.level,
        }))
      );
    }

    const hovered = hoveredTextboxRef.current;
    let box = hovered && root.contains(hovered) ? hovered : null;
    const menuOpen = !!document.querySelector(
      ".rte-textbox-chrome:not(.rte-quote-chrome) .dropdown-menu.show"
    );
    if (
      !box &&
      menuOpen &&
      textboxNodeRef.current &&
      root.contains(textboxNodeRef.current)
    ) {
      box = textboxNodeRef.current;
    }
    textboxNodeRef.current = box;
    const hideTextboxChrome = () => {
      textboxChromeSigRef.current = "";
      setTextboxChrome(null);
    };
    const textboxHidden =
      !box ||
      (() => {
        const rect = box.getBoundingClientRect();
        return (
          (rect.width === 0 && rect.height === 0) ||
          (shellRect && (rect.bottom < shellRect.top || rect.top > shellRect.bottom))
        );
      })();
    if (textboxHidden) {
      hideTextboxChrome();
    } else {
      const rect = box.getBoundingClientRect();
      const pad = box.getAttribute(RTE_TB_PAD) || "2";
      const border = box.getAttribute(RTE_TB_BORDER) || "1";
      const color = box.getAttribute(RTE_TB_COLOR) || "gray";
      const placed = placeBar(rect, 168, 34, rect.height < 48);
      if (!placed) {
        hideTextboxChrome();
      } else {
      const { top, right } = placed;
      const sig = `${top}:${right}:${pad}:${border}:${color}`;
      if (sig !== textboxChromeSigRef.current) {
        textboxChromeSigRef.current = sig;
        setTextboxChrome({ top, right, pad, border, color });
      }
      }
    }

    const hoveredQuote = hoveredQuoteRef.current;
    let quote = hoveredQuote && root.contains(hoveredQuote) ? hoveredQuote : null;
    const quoteMenuOpen = !!document.querySelector(
      ".rte-quote-chrome .dropdown-menu.show"
    );
    if (
      !quote &&
      quoteMenuOpen &&
      quoteNodeRef.current &&
      root.contains(quoteNodeRef.current)
    ) {
      quote = quoteNodeRef.current;
    }
    quoteNodeRef.current = quote;
    const hideQuoteChrome = () => {
      quoteChromeSigRef.current = "";
      setQuoteChrome(null);
    };
    const quoteHidden =
      !quote ||
      (() => {
        const rect = quote.getBoundingClientRect();
        return (
          (rect.width === 0 && rect.height === 0) ||
          (shellRect && (rect.bottom < shellRect.top || rect.top > shellRect.bottom))
        );
      })();
    if (quoteHidden) {
      hideQuoteChrome();
    } else {
      const rect = quote.getBoundingClientRect();
      const style = quote.getAttribute(RTE_QUOTE_STYLE_ATTR) || "classic";
      const color = quote.getAttribute(RTE_QUOTE_COLOR_ATTR) || "gray";
      const placed = placeBar(rect, 140, 34, rect.height < 48);
      if (!placed) {
        hideQuoteChrome();
      } else {
      const { top, right } = placed;
      const sig = `${top}:${right}:${style}:${color}`;
      if (sig !== quoteChromeSigRef.current) {
        quoteChromeSigRef.current = sig;
        setQuoteChrome({ top, right, style, color });
      }
      }
    }

    const liveSel = window.getSelection();
    const selInEditor = !!(liveSel?.focusNode && root.contains(liveSel.focusNode));
    const selTable = selInEditor ? findTableAncestor(liveSel.focusNode, root) : null;
    let editTable = selTable;
    if (
      !editTable &&
      tableEditStickyRef.current &&
      editingTableRef.current &&
      root.contains(editingTableRef.current)
    ) {
      editTable = editingTableRef.current;
    }
    if (editTable && (mediaDragRef.current === editTable || mediaDragRef.current?.contains?.(editTable))) {
      editTable = null;
    }
    editingTableRef.current = editTable;
    if (!editTable) {
      if (selInEditor) tableEditStickyRef.current = false;
      if (tableEditChromeSigRef.current) {
        tableEditChromeSigRef.current = "";
        setTableEditChrome(null);
      }
    } else {
      const rect = editTable.getBoundingClientRect();
      const hidden =
        (rect.width === 0 && rect.height === 0) ||
        (shellRect && (rect.bottom < shellRect.top || rect.top > shellRect.bottom));
      if (hidden) {
        if (tableEditChromeSigRef.current) {
          tableEditChromeSigRef.current = "";
          setTableEditChrome(null);
        }
      } else {
        const bg = editTable.getAttribute(RTE_TABLE_BG_ATTR) || "";
        const perimeter = editTable.getAttribute(RTE_TABLE_PERIM_ATTR) || "1";
        const inside = editTable.getAttribute(RTE_TABLE_INSIDE_ATTR) || "1";
        const perimColor = editTable.getAttribute(RTE_TABLE_PERIM_COLOR_ATTR) || "";
        const inColor = editTable.getAttribute(RTE_TABLE_INSIDE_COLOR_ATTR) || "";
        const placed = placeBar(rect, 240, 44, true);
        if (!placed) {
          if (tableEditChromeSigRef.current) {
            tableEditChromeSigRef.current = "";
            setTableEditChrome(null);
          }
        } else {
        const { top, right } = placed;
        const tableSig = `${top}:${right}:${bg}:${perimeter}:${inside}:${perimColor}:${inColor}`;
        if (tableSig !== tableEditChromeSigRef.current) {
          tableEditChromeSigRef.current = tableSig;
          setTableEditChrome({ top, right, bg, perimeter, inside, perimColor, inColor });
        }
        }
      }
    }

    const hideImageResize = () => {
      if (!imageResizeSigRef.current) return;
      imageResizeSigRef.current = "";
      setImageResizeBar(null);
    };
    const placeImageResize = (fig) => {
      if (!fig?.classList?.contains("rte-image") || !root.contains(fig) || mediaDragRef.current) {
        hideImageResize();
        return;
      }
      const img = fig.querySelector("img");
      if (!img) {
        hideImageResize();
        return;
      }
      const full = imageFullHeight(img);
      if (!(img.naturalWidth > 0) || full <= RTE_IMAGE_MIN_H + 1) {
        if (!img.complete) {
          img.addEventListener("load", () => refreshChromeRef.current(), { once: true });
        }
        hideImageResize();
        return;
      }
      const rect = img.getBoundingClientRect();
      const barW = 56;
      const barH = 18;
      const top = Math.round(rect.bottom - barH / 2);
      const left = Math.round(rect.left + (rect.width - barW) / 2);
      const offscreen =
        rect.width < 8 ||
        (shellRect && (rect.bottom < shellRect.top + 4 || rect.top > shellRect.bottom - 4)) ||
        (clip && !clipContainsBox(clip, top, left, barW, barH));
      if (offscreen) {
        hideImageResize();
        return;
      }
      const sig = `${top}:${left}`;
      if (sig !== imageResizeSigRef.current) {
        imageResizeSigRef.current = sig;
        setImageResizeBar({ top, left });
      }
    };

    const resizeFig = imageResizeDragRef.current?.fig;
    const mediaFig = (resizeFig && root.contains(resizeFig) ? resizeFig : null) || mediaFigRef.current;
    if (!mediaFig || !root.contains(mediaFig) || mediaDragRef.current) {
      if (mediaChromeSigRef.current) {
        mediaChromeSigRef.current = "";
        setMediaChrome(null);
      }
      hideImageResize();
      return;
    }
    const mediaRect = mediaFig.getBoundingClientRect();
    if (
      (mediaRect.width === 0 && mediaRect.height === 0) ||
      (shellRect && (mediaRect.bottom < shellRect.top || mediaRect.top > shellRect.bottom))
    ) {
      if (mediaChromeSigRef.current) {
        mediaChromeSigRef.current = "";
        setMediaChrome(null);
      }
      hideImageResize();
      return;
    }
    const isFlow = mediaFig.classList.contains("rte-flow");
    const barW = isFlow ? 124 : 84;
    const placed = placeBar(mediaRect, barW, 34, false);
    if (!placed) {
      if (mediaChromeSigRef.current) {
        mediaChromeSigRef.current = "";
        setMediaChrome(null);
      }
      placeImageResize(mediaFig);
      return;
    }
    const { top, right } = placed;
    const mediaSig = `${top}:${right}:${isFlow ? 1 : 0}`;
    if (mediaSig !== mediaChromeSigRef.current) {
      mediaChromeSigRef.current = mediaSig;
      setMediaChrome({ top, right, flow: isFlow });
    }
    placeImageResize(mediaFig);
  };
  refreshChromeRef.current = refreshEditorChrome;

  useLayoutEffect(() => {
    refreshChromeRef.current();
  });

  const handleInput = (e) => {
    const inputRoot = e.currentTarget;
    if (e.nativeEvent?.isComposing) {
      const html = inputRoot.innerHTML;
      noteLocalEmit(html);
      lastValueFromParentRef.current = html;
      onChange(html);
      return;
    }
    // Publish the browser's edit first. Rewriting the DOM inside this event
    // makes Chrome undo the keystroke and throw the caret.
    renumberCaptions(inputRoot);
    const htmlNow = inputRoot.innerHTML;
    noteLocalEmit(htmlNow);
    lastValueFromParentRef.current = htmlNow;
    onChange(htmlNow);
    updateBlockTypeFromSelection();
    refreshRteDocBaseVar(inputRoot);
    if (!isRestoringHistoryRef.current) {
      bumpHistoryUi();
      scheduleTypingHistory();
    }
    refreshChromeRef.current();

    if (inputNormalizeRafRef.current) {
      cancelAnimationFrame(inputNormalizeRafRef.current);
    }
    inputNormalizeRafRef.current = requestAnimationFrame(() => {
      inputNormalizeRafRef.current = 0;
      if (!inputRoot.isConnected) return;
      const htmlBefore = inputRoot.innerHTML;
      let liveRange = null;
      const liveSel = window.getSelection();
      if (liveSel?.rangeCount && inputRoot.contains(liveSel.anchorNode)) {
        liveRange = liveSel.getRangeAt(0).cloneRange();
      }
      const inputCaret = captureLeafBlockCaret(inputRoot);
      normalizeDivBlocksToParagraphs(inputRoot);
      try {
        prepareDomBeforeEmit?.(inputRoot);
      } catch {
        /* ignore prepare errors */
      }
      ensureEditorImageContainers(inputRoot);
      renumberCaptions(inputRoot);
      refreshCodeBlockLineNumbers(inputRoot);
      if (inputRoot.innerHTML === htmlBefore) return;
      const selNow = window.getSelection();
      const caretStillInside =
        selNow?.rangeCount &&
        inputRoot.contains(selNow.anchorNode) &&
        inputRoot.contains(selNow.focusNode);
      if (!caretStillInside) {
        const liveStillThere =
          liveRange &&
          inputRoot.contains(liveRange.startContainer) &&
          inputRoot.contains(liveRange.endContainer);
        if (liveStillThere && selNow) {
          selNow.removeAllRanges();
          selNow.addRange(liveRange);
        } else if (inputCaret) {
          restoreLeafBlockCaret(inputRoot, inputCaret);
        }
      }
      const savedSel = tableSelection;
      const table = activeTableRef.current;
      clearTableSelectionDom(inputRoot);
      const html = inputRoot.innerHTML;
      noteLocalEmit(html);
      lastValueFromParentRef.current = html;
      onChange(html);
      if (savedSel && table && inputRoot.contains(table)) {
        paintTableSelection(table, savedSel, false);
        positionTableDeleteForSelection(table, savedSel);
      }
      updateBlockTypeFromSelection();
      refreshRteDocBaseVar(inputRoot);
      if (!isRestoringHistoryRef.current) scheduleTypingHistory();
      refreshChromeRef.current();
    });
  };

  /**
   * Paste keeps heading / list structure from the clipboard, strips foreign
   * inline styles, and stamps destination-block styles (font, spacing, margins).
   */
  const handlePaste = (e) => {
    if (!editorRef.current || typeof document === "undefined") return;
    const capText = e.target?.closest?.(".rte-caption-text");
    if (capText && editorRef.current.contains(capText)) {
      e.preventDefault();
      const plain = (e.clipboardData?.getData("text/plain") || "").replace(/\s*\n\s*/g, " ").trim();
      if (plain) document.execCommand("insertText", false, plain);
      return;
    }
    const root = editorRef.current;
    const cd = e.clipboardData;
    if (!cd) return;

    const plain = cd.getData("text/plain");
    const html = cd.getData("text/html");
    if ((plain == null || plain === "") && !html) return;

    e.preventDefault();
    focusEditor();

    const sel = window.getSelection();
    if (!sel) return;
    if (!sel.rangeCount || !root.contains(sel.anchorNode)) {
      const r = document.createRange();
      r.selectNodeContents(root);
      r.collapse(false);
      sel.removeAllRanges();
      sel.addRange(r);
    }

    let range = sel.getRangeAt(0);
    const focusNode = sel.focusNode ?? range.startContainer;
    let targetBlock = findClosestStyleBlock(focusNode, root);
    const styleSnap = captureBlockPasteStyles(targetBlock);
    const inPre = Boolean(findPreAncestor(focusNode, root));
    const hostTable = findTableAncestor(focusNode, root);
    const inTable = Boolean(hostTable);

    const pasteBlocks = parseClipboardPasteBlocks(html, plain || "");
    const pastedTable = pasteBlocks.find((block) => block.kind === "table") || null;
    const onlyPastedTable =
      !!pastedTable &&
      pasteBlocks.filter((block) => block.kind === "table").length === 1 &&
      pasteBlocks.every(
        (block) =>
          block.kind === "table" ||
          (block.kind === "p" && !String(block.text || "").trim())
      );

    if (inTable && onlyPastedTable && !inPre) {
      const band =
        tableSelectionRef.current && activeTableRef.current === hostTable
          ? tableSelectionRef.current
          : null;
      let matrix = null;
      if (band?.type === "row") {
        const row = directTableGrid(hostTable)[band.index];
        matrix = row?.length ? [row] : null;
      } else if (band?.type === "col") {
        const col = cellsForTableBand(hostTable, band);
        matrix = col.length ? col.map((cell) => [cell]) : null;
      } else {
        matrix = matrixFromSelectedCells(hostTable, cellsTouchedByRange(hostTable, range));
      }
      const rows = pastedTable.rows || [];
      if (clipboardGridMatches(rows, matrix)) {
        const singleCell = matrix.length === 1 && matrix[0].length === 1;
        const cell = matrix[0][0];
        if (singleCell && !band && rangeStaysInsideCell(range, cell)) {
          insertPlainTextAtRange(range, rows[0][0].text);
        } else {
          matrix.forEach((row, rowIndex) => {
            row.forEach((target, colIndex) => {
              setCellPlainText(target, rows[rowIndex][colIndex].text);
            });
          });
          const last = matrix[matrix.length - 1][matrix[matrix.length - 1].length - 1];
          const live = window.getSelection();
          if (live && last) {
            const caret = document.createRange();
            caret.selectNodeContents(last);
            caret.collapse(false);
            live.removeAllRanges();
            live.addRange(caret);
          }
        }
      } else {
        insertPastedTableBelow(hostTable, pastedTable);
      }
      syncFromEditor();
      return;
    }

    if (!range.collapsed) {
      range.deleteContents();
      range = sel.rangeCount ? sel.getRangeAt(0) : range;
    }

    if (inPre) {
      document.execCommand("insertText", false, plain || "");
      if (targetBlock) applyBlockPasteStyles(targetBlock, styleSnap);
      syncFromEditor();
      return;
    }

    if (inTable) {
      if (!pastedTable) {
        document.execCommand("insertText", false, plain || "");
        const block = findClosestStyleBlock(
          window.getSelection()?.focusNode,
          root
        );
        if (block && styleSnap) applyBlockPasteStyles(block, styleSnap);
        syncFromEditor();
        return;
      }
      targetBlock = hostTable || targetBlock;
    }

    if (!pasteBlocks.length) return;

    const hasStructure = pasteBlocks.some(
      (b) =>
        b.kind === "heading" ||
        b.kind === "list" ||
        b.kind === "blockquote" ||
        b.kind === "table"
    );
    const onlyPlainParagraphs =
      !hasStructure && pasteBlocks.every((b) => b.kind === "p");

    // Single plain line into current block
    if (onlyPlainParagraphs && pasteBlocks.length === 1) {
      document.execCommand("insertText", false, pasteBlocks[0].text || "");
      const block = findClosestStyleBlock(
        window.getSelection()?.focusNode,
        root
      );
      if (block && styleSnap) applyBlockPasteStyles(block, styleSnap);
      normalizeDivBlocksToParagraphs(root);
      syncFromEditor();
      return;
    }

    // Multi plain lines with no structure: first line in-place, rest as siblings
    if (onlyPlainParagraphs && !hasStructure) {
      document.execCommand("insertText", false, pasteBlocks[0].text || "");
      let block =
        findClosestStyleBlock(window.getSelection()?.focusNode, root) ||
        targetBlock;
      if (block && styleSnap) applyBlockPasteStyles(block, styleSnap);

      const parentList =
        block?.tagName === "LI" ? block.closest?.("ul, ol") : null;
      pasteBlocks.slice(1).forEach((pb) => {
        if (!block?.parentNode) return;
        let neo;
        if (parentList && block.tagName === "LI") {
          neo = document.createElement("li");
          if (pb.text) neo.appendChild(document.createTextNode(pb.text));
          else neo.appendChild(document.createElement("br"));
          parentList.insertBefore(neo, block.nextSibling);
        } else {
          neo = document.createElement("p");
          if (pb.text) neo.appendChild(document.createTextNode(pb.text));
          else neo.appendChild(document.createElement("br"));
          block.parentNode.insertBefore(neo, block.nextSibling);
        }
        if (styleSnap) applyBlockPasteStyles(neo, styleSnap);
        block = neo;
      });

      if (block) {
        const live = window.getSelection();
        live?.removeAllRanges();
        const nr = document.createRange();
        nr.selectNodeContents(block);
        nr.collapse(false);
        live?.addRange(nr);
      }
      normalizeDivBlocksToParagraphs(root);
      syncFromEditor();
      return;
    }

    // Structured paste (headers / lists / mixed): insert clean nodes with our styles
    const nodes = pasteBlocks.map((b) =>
      createDomForPasteBlock(b, styleSnap, document)
    );
    if (!nodes.length) return;

    const insertAfter = (refNode, node) => {
      if (!refNode?.parentNode) return node;
      refNode.parentNode.insertBefore(node, refNode.nextSibling);
      return node;
    };

    let anchor = targetBlock;
    const emptyTarget = isVisuallyEmptyPasteTarget(targetBlock);
    const inListItem = targetBlock?.tagName === "LI";

    // Pasting a list into a list item: merge items into the same list
    if (
      inListItem &&
      pasteBlocks.length === 1 &&
      pasteBlocks[0].kind === "list"
    ) {
      const list = targetBlock.closest("ul, ol");
      const items = pasteBlocks[0].items || [];
      if (list && items.length) {
        if (emptyTarget) {
          if (items[0]) targetBlock.textContent = items[0];
          else {
            targetBlock.innerHTML = "";
            targetBlock.appendChild(document.createElement("br"));
          }
          if (styleSnap) applyBlockPasteStyles(targetBlock, styleSnap);
          let prev = targetBlock;
          items.slice(1).forEach((text) => {
            const li = document.createElement("li");
            if (text) li.textContent = text;
            else li.appendChild(document.createElement("br"));
            if (styleSnap) applyBlockPasteStyles(li, styleSnap);
            list.insertBefore(li, prev.nextSibling);
            prev = li;
          });
          const live = window.getSelection();
          live?.removeAllRanges();
          const nr = document.createRange();
          nr.selectNodeContents(prev);
          nr.collapse(false);
          live?.addRange(nr);
        } else {
          let prev = targetBlock;
          items.forEach((text) => {
            const li = document.createElement("li");
            if (text) li.textContent = text;
            else li.appendChild(document.createElement("br"));
            if (styleSnap) applyBlockPasteStyles(li, styleSnap);
            list.insertBefore(li, prev.nextSibling);
            prev = li;
          });
          const live = window.getSelection();
          live?.removeAllRanges();
          const nr = document.createRange();
          nr.selectNodeContents(prev);
          nr.collapse(false);
          live?.addRange(nr);
        }
        normalizeDivBlocksToParagraphs(root);
        syncFromEditor();
        return;
      }
    }

    let lastInserted = null;
    if (emptyTarget && targetBlock && targetBlock !== root) {
      // Replace empty paragraph / heading placeholder with the first pasted node
      const first = nodes[0];
      targetBlock.parentNode.replaceChild(first, targetBlock);
      lastInserted = first;
      nodes.slice(1).forEach((node) => {
        lastInserted = insertAfter(lastInserted, node);
      });
    } else if (
      targetBlock &&
      pasteBlocks[0].kind === "p" &&
      !/^H[1-6]|LI|BLOCKQUOTE|PRE$/i.test(targetBlock.tagName)
    ) {
      // First paragraph merges into the current text block; rest follow after
      document.execCommand("insertText", false, pasteBlocks[0].text || "");
      let block =
        findClosestStyleBlock(window.getSelection()?.focusNode, root) ||
        targetBlock;
      if (block && styleSnap) applyBlockPasteStyles(block, styleSnap);
      lastInserted = block;
      nodes.slice(1).forEach((node) => {
        lastInserted = insertAfter(lastInserted, node);
      });
    } else if (targetBlock && targetBlock.parentNode) {
      // Insert structured blocks after the current block
      lastInserted = targetBlock;
      nodes.forEach((node) => {
        lastInserted = insertAfter(lastInserted, node);
      });
    } else {
      nodes.forEach((node) => root.appendChild(node));
      lastInserted = nodes[nodes.length - 1];
    }

    nodes.forEach((node) => {
      if (node.tagName !== "TABLE" || !node.parentNode) return;
      const list = node.closest("ul, ol");
      if (list?.parentNode) {
        list.after(node);
        if (![...list.children].some((child) => child.tagName === "LI")) list.remove();
      }
      const next = node.nextElementSibling;
      if (!next || !/^(P|H[1-6]|UL|OL|BLOCKQUOTE|PRE|TABLE)$/i.test(next.tagName)) {
        const trail = document.createElement("p");
        trail.appendChild(document.createElement("br"));
        node.after(trail);
      }
    });

    if (lastInserted) {
      const live = window.getSelection();
      live?.removeAllRanges();
      const cells =
        lastInserted.tagName === "TABLE" ? lastInserted.querySelectorAll("td, th") : null;
      const caretTarget =
        cells && cells.length
          ? cells[cells.length - 1]
          : lastInserted.tagName === "UL" || lastInserted.tagName === "OL"
            ? lastInserted.lastElementChild || lastInserted
            : lastInserted;
      const nr = document.createRange();
      nr.selectNodeContents(caretTarget);
      nr.collapse(false);
      live?.addRange(nr);
    }

    normalizeDivBlocksToParagraphs(root);
    syncFromEditor();
  };

  const getGuideTitleEl = () =>
    editorShellRef.current?.querySelector?.("[data-guides-title='1']") ||
    editorShellRef.current?.querySelector?.(`#${RTE_GUIDE_TITLE_ID}`) ||
    null;

  const selectionIsInGuideTitle = () => {
    const titleEl = getGuideTitleEl();
    if (!titleEl || typeof window === "undefined") return false;
    const sel = window.getSelection();
    const node = sel?.focusNode || sel?.anchorNode;
    return Boolean(node && (titleEl === node || titleEl.contains(node)));
  };

  const isEditingGuideTitle = () =>
    lastEditTargetRef.current === "title" || selectionIsInGuideTitle();

  const applyHeadingToGuideTitle = (vRaw) => {
    const titleEl = getGuideTitleEl();
    if (!titleEl) return false;
    const desired = String(vRaw ?? "").toLowerCase();
    // Title stays a heading; never demote it to a paragraph in the body.
    if (!/^h[1-6]$/.test(desired)) return true;
    const n = Number(desired.slice(1));
    const wrap = titleEl.closest(".guides-title-editor") || titleEl;
    wrap.dispatchEvent(
      new CustomEvent("guide-title-apply-heading", {
        bubbles: true,
        detail: { level: n },
      })
    );
    if (unwrapLeadingEmptyHeadings(editorRef.current)) {
      syncFromEditor();
    }
    setHeadingValue(desired);
    setBlockType(desired.toUpperCase());
    lastEditTargetRef.current = "title";
    requestAnimationFrame(() => {
      getGuideTitleEl()?.focus();
    });
    return true;
  };

  const focusEditor = () => {
    ensureDefaultParagraphSeparator();
    const root = editorRef.current;
    if (!root) return;
    root.focus({ preventScroll: true });
    restoreEditorSelectionRange();
  };

  const stashEditorSelectionRange = () => {
    if (typeof window === "undefined" || !editorRef.current) return;
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;
    const range = sel.getRangeAt(0);
    if (!editorRef.current.contains(range.commonAncestorContainer)) return;
    selectionRangeRef.current = range.cloneRange();
  };

  const restoreEditorSelectionRange = () => {
    if (typeof window === "undefined" || !editorRef.current) return false;
    const saved = selectionRangeRef.current;
    if (!saved) return false;
    const root = editorRef.current;
    const sc = saved.startContainer;
    const ec = saved.endContainer;
    if (!root.contains(sc) || !root.contains(ec)) return false;
    const sel = window.getSelection();
    if (!sel) return false;
    sel.removeAllRanges();
    sel.addRange(saved.cloneRange());
    return true;
  };

  const hideSelectionMenu = () => {
    if (selectionMenuRafRef.current) {
      cancelAnimationFrame(selectionMenuRafRef.current);
      selectionMenuRafRef.current = 0;
    }
    setSelectionMenuPos(null);
  };

  const isRteToolbarDropdownTarget = (target) =>
    !!target?.closest?.(
      ".rte-toolbar .dropdown, .rte-menu, .rte-toolbar .dropdown-toggle, .dropdown-menu.show"
    );

  const isRteToolbarDropdownOpen = () =>
    !!document.querySelector(
      ".rte-toolbar .dropdown-menu.show, .rte-menu .dropdown-menu.show"
    );

  const refreshSelectionMenu = () => {
    if (typeof window === "undefined" || !editorRef.current) {
      setSelectionMenuPos(null);
      return;
    }
    if (
      isPointerSelectingRef.current ||
      linkModalOpen ||
      imageModalOpen ||
      referenceUiOpen ||
      isRteToolbarDropdownOpen()
    ) {
      setSelectionMenuPos(null);
      return;
    }
    const root = editorRef.current;
    const sel = window.getSelection();
    if (
      !sel?.rangeCount ||
      sel.isCollapsed ||
      !sel.anchorNode ||
      !sel.focusNode ||
      !root.contains(sel.anchorNode) ||
      !root.contains(sel.focusNode)
    ) {
      setSelectionMenuPos(null);
      return;
    }
    const text = String(sel.toString() || "")
      .replace(/\u00a0/g, " ")
      .trim();
    if (!text) {
      setSelectionMenuPos(null);
      return;
    }

    const range = sel.getRangeAt(0);
    let rect = range.getBoundingClientRect();
    if (!rect || (rect.width === 0 && rect.height === 0)) {
      const rects = range.getClientRects();
      if (!rects?.length) {
        setSelectionMenuPos(null);
        return;
      }
      rect = rects[0];
    }
    const clip = readDocumentChromeClip(editorShellRef.current);
    if (clip && !clipContainsBox(clip, rect.top, rect.left, Math.max(rect.width, 1), rect.height)) {
      setSelectionMenuPos(null);
      return;
    }
    stashEditorSelectionRange();
    const menuApproxWidth = references ? 560 : 510;
    const popH = 44;
    const gap = 8;
    if (clip) {
      const roomAbove = rect.top - popH - gap >= clip.top;
      const roomBelow = rect.bottom + popH + gap <= clip.bottom;
      const placement = !roomAbove && roomBelow ? "below" : "above";
      const half = Math.min(menuApproxWidth, Math.max(80, clip.right - clip.left - 16)) / 2;
      const left = Math.min(
        Math.max(rect.left + rect.width / 2, clip.left + half),
        clip.right - half
      );
      const top = placement === "below" ? rect.bottom : rect.top;
      setSelectionMenuPos({ top, left, placement });
      return;
    }
    const pad = 8;
    const left = Math.min(
      Math.max(rect.left + rect.width / 2, pad + menuApproxWidth / 2),
      window.innerWidth - pad - menuApproxWidth / 2
    );
    const top = Math.max(rect.top, pad + 36);
    setSelectionMenuPos({ top, left, placement: "above" });
  };

  const scheduleSelectionMenuRefresh = () => {
    if (selectionMenuRafRef.current) {
      cancelAnimationFrame(selectionMenuRafRef.current);
    }
    selectionMenuRafRef.current = requestAnimationFrame(() => {
      selectionMenuRafRef.current = 0;
      refreshSelectionMenuRef.current();
    });
  };
  refreshSelectionMenuRef.current = refreshSelectionMenu;

  const applyStrikeThrough = () => {
    if (typeof document === "undefined" || !editorRef.current) return;
    focusEditor();
    restoreEditorSelectionRange();
    const root = editorRef.current;
    const sel = window.getSelection();
    if (!sel?.rangeCount) return;
    const range = sel.getRangeAt(0);
    if (!root.contains(range.commonAncestorContainer)) return;

    if (selectionHasStrike(sel, root)) {
      unwrapStrikeInSelection(root, sel);
      stashEditorSelectionRange();
      syncFromEditor();
      return;
    }

    if (range.collapsed) {
      try {
        document.execCommand("styleWithCSS", false, false);
      } catch {
        /* older engines */
      }
      document.execCommand("strikeThrough", false, null);
      stashEditorSelectionRange();
      syncFromEditor();
      return;
    }

    const wraps = wrapRangeInStrike(range, root);
    if (wraps.length) {
      const nr = document.createRange();
      nr.setStart(wraps[0], 0);
      const last = wraps[wraps.length - 1];
      nr.setEnd(last, last.childNodes.length);
      sel.removeAllRanges();
      sel.addRange(nr);
    }
    stashEditorSelectionRange();
    syncFromEditor();
  };

  const applySelectionMenuFormat = (command) => {
    if (typeof document === "undefined") return;
    if (command === "strikeThrough") {
      applyStrikeThrough();
      scheduleSelectionMenuRefresh();
      return;
    }
    focusEditor();
    restoreEditorSelectionRange();
    document.execCommand(command, false, null);
    stashEditorSelectionRange();
    syncFromEditor();
    scheduleSelectionMenuRefresh();
  };

  const applySelectionMenuHeading = (level) => {
    const current = String(caretInspect.blockShort || "").toLowerCase();
    applyHeadingValue(current === level ? "p" : level);
    scheduleSelectionMenuRefresh();
  };

  const switchHeadingLevel = (top, left, tagLower) => {
    const root = editorRef.current;
    const tag = String(tagLower || "").toLowerCase();
    if (!root || !/^h[1-6]$/.test(tag)) return;
    let el = null;
    root.querySelectorAll("h1,h2,h3,h4,h5,h6").forEach((node) => {
      if (el || !root.contains(node) || node.closest("[data-guides-title]")) return;
      const rect = node.getBoundingClientRect();
      if (Math.round(rect.top) === top && Math.round(rect.left) === left) el = node;
    });
    if (!el || el.tagName.toLowerCase() === tag) return;
    const neo = replaceElementPreserveChildren(el, tag);
    [...el.attributes].forEach((attr) => {
      neo.setAttribute(attr.name, attr.value);
    });
    releaseInheritedHeadingBold(neo);
    syncFromEditor();
    refreshChromeRef.current();
  };

  const applySelectionTextbox = () => {
    if (typeof document === "undefined" || !editorRef.current) return;
    focusEditor();
    restoreEditorSelectionRange();
    const root = editorRef.current;
    const sel = window.getSelection();
    if (!sel?.rangeCount || sel.isCollapsed) return;
    const range = sel.getRangeAt(0);
    if (!root.contains(range.commonAncestorContainer)) return;

    const startBlock = findStructuralBlockAncestor(range.startContainer, root);
    const endBlock = findStructuralBlockAncestor(range.endContainer, root);
    const crosses =
      selectionCrossesBlocks(range) ||
      !!(startBlock && endBlock && startBlock !== endBlock);
    const inTable = !!findTableAncestor(range.commonAncestorContainer, root);
    const listHost =
      range.commonAncestorContainer.nodeType === 1
        ? range.commonAncestorContainer
        : range.commonAncestorContainer.parentElement;
    const listEl = listHost?.closest?.("ul, ol, li");
    const inList = !!(listEl && root.contains(listEl));
    if ((inTable || inList) && crosses) return;

    const contents = range.extractContents();
    if (!contents?.childNodes?.length) return;
    const box = document.createElement(crosses ? "div" : "span");
    box.className = RTE_TEXTBOX_CLASS;
    box.setAttribute(RTE_TB_PAD, "2");
    box.setAttribute(RTE_TB_BORDER, "1");
    box.setAttribute(RTE_TB_COLOR, "gray");
    box.appendChild(contents);
    range.insertNode(box);
    if (box.tagName === "DIV") liftBlockTextbox(box, root);

    const caret = document.createRange();
    caret.selectNodeContents(box);
    caret.collapse(false);
    sel.removeAllRanges();
    sel.addRange(caret);
    stashEditorSelectionRange();
    hoveredTextboxRef.current = box;
    textboxChromeSigRef.current = "";
    syncFromEditor();
    scheduleSelectionMenuRefresh();
    refreshChromeRef.current();
  };

  const setTextboxSetting = (attr, value) => {
    const root = editorRef.current;
    const box = textboxNodeRef.current;
    if (!root || !box || !root.contains(box)) return;
    box.setAttribute(attr, value);
    textboxChromeSigRef.current = "";
    syncFromEditor();
    refreshChromeRef.current();
  };

  const removeTextboxKeepText = () => {
    const root = editorRef.current;
    const box = textboxNodeRef.current;
    if (!root || !box || !root.contains(box) || !box.parentNode) return;
    const parent = box.parentNode;
    const first = box.firstChild;
    const after = box.nextSibling;
    while (box.firstChild) parent.insertBefore(box.firstChild, box);
    box.remove();
    const sel = window.getSelection();
    if (sel) {
      const range = document.createRange();
      if (first?.parentNode) {
        range.setStart(first, 0);
        range.collapse(true);
      } else if (after) {
        range.setStartBefore(after);
        range.collapse(true);
      } else {
        range.selectNodeContents(parent);
        range.collapse(false);
      }
      sel.removeAllRanges();
      sel.addRange(range);
    }
    hoveredTextboxRef.current = null;
    textboxNodeRef.current = null;
    textboxChromeSigRef.current = "";
    setTextboxChrome(null);
    stashEditorSelectionRange();
    syncFromEditor();
    refreshChromeRef.current();
  };

  const setQuoteSetting = (attr, value) => {
    const root = editorRef.current;
    const quote = quoteNodeRef.current;
    if (!root || !quote || !root.contains(quote)) return;
    quote.setAttribute(attr, value);
    quoteChromeSigRef.current = "";
    syncFromEditor();
    refreshChromeRef.current();
  };

  const removeQuoteKeepText = () => {
    const root = editorRef.current;
    const quote = quoteNodeRef.current;
    if (!root || !quote || !root.contains(quote)) return;
    const restored = unwrapRichTextQuote(quote);
    hoveredQuoteRef.current = null;
    quoteNodeRef.current = null;
    quoteChromeSigRef.current = "";
    setQuoteChrome(null);
    if (restored) placeCaretAtBlockEnd(restored);
    stashEditorSelectionRange();
    syncFromEditor();
    refreshChromeRef.current();
  };

  const applyListKind = (kind) => {
    if (!editorRef.current || typeof document === "undefined") return;
    if (!RTE_LIST_KINDS.some((item) => item.id === kind)) return;
    focusEditor();
    restoreEditorSelectionRange();
    const root = editorRef.current;
    const sel = window.getSelection();
    if (!sel?.rangeCount || !root.contains(sel.anchorNode)) return;
    const range = sel.getRangeAt(0);
    let blocks = range.collapsed
      ? (() => {
          const block = findClosestStyleBlock(sel.focusNode, root);
          return block ? [block] : [];
        })()
      : collectIntersectingStructuralBlocks(range, root, { includePre: false });
    blocks = blocks.filter((block) => {
      if (!block || !root.contains(block) || block.closest("pre, table")) return false;
      if (block.tagName === "DIV" && String(block.className || "").trim()) return false;
      return true;
    });

    const retagTree = (list) => {
      let top = list;
      while (
        top?.parentElement?.tagName === "LI" &&
        /^(UL|OL)$/i.test(top.parentElement.parentElement?.tagName)
      ) {
        top = top.parentElement.parentElement;
      }
      const stamped = stampListKind(top, kind);
      stamped?.querySelectorAll("ul, ol").forEach((sub) => stampListKind(sub, kind));
      return stamped;
    };

    if (range.collapsed) {
      const li = findEnclosingListItem(sel.focusNode, root);
      if (li) {
        const list = li.parentElement;
        if (listKindOf(list) === kind) clearListItem(li);
        else {
          retagTree(list);
          if (root.contains(li)) placeCaretAtBlockStart(li);
        }
        syncFromEditor();
        return;
      }
    }

    const listItems = blocks.filter((block) => block.tagName === "LI");
    if (listItems.length && blocks.every((block) => block.tagName === "LI")) {
      listItems.forEach((li) => {
        if (li.parentElement) retagTree(li.parentElement);
      });
      syncFromEditor();
      return;
    }

    const fresh = blocks.filter((block) => block.tagName !== "LI" && !block.closest("li"));
    if (!fresh.length) return;
    const spec = RTE_LIST_KINDS.find((item) => item.id === kind);
    const list = root.ownerDocument.createElement(spec.tag);
    list.setAttribute(RTE_LIST_KIND_ATTR, kind);
    fresh[0].before(list);
    let caretLi = null;
    fresh.forEach((block) => {
      if (!block.isConnected) return;
      const item = root.ownerDocument.createElement("li");
      copyBlockPresentation(block, item);
      while (block.firstChild) item.appendChild(block.firstChild);
      ensureEmptyBlockHasBr(item);
      list.appendChild(item);
      block.remove();
      caretLi = item;
    });
    if (caretLi) placeCaretAtBlockEnd(caretLi);
    syncFromEditor();
  };

  const applyFormat = (command, valueArg = null) => {
    if (typeof document === "undefined") return;
    if (command === "insertUnorderedList") {
      applyListKind("disc");
      return;
    }
    if (command === "insertOrderedList") {
      applyListKind("decimal");
      return;
    }
    if (command === "undo") {
      performUndo();
      return;
    }
    if (command === "redo") {
      performRedo();
      return;
    }
    if (command === "superscript" || command === "subscript") {
      applyToggleSupSub(command);
      return;
    }
    if (command === "strikeThrough") {
      applyStrikeThrough();
      return;
    }
    focusEditor();
    const root = editorRef.current;
    const sel = typeof window !== "undefined" ? window.getSelection() : null;
    if (!root || !sel?.rangeCount || !root.contains(sel.anchorNode)) {
      toolbarSelectionLockRef.current = false;
      return;
    }
    const bookmark = captureLeafBlockCaret(root);
    document.execCommand(command, false, valueArg);
    if (bookmark) restoreLeafBlockCaret(root, bookmark);
    syncFromEditor();
  };

  const applyToggleSupSub = (command) => {
    if (typeof document === "undefined" || !editorRef.current) return;
    focusEditor();
    restoreEditorSelectionRange();
    const root = editorRef.current;
    const sel = window.getSelection();
    if (!sel?.rangeCount) return;
    const range = sel.getRangeAt(0);
    if (!root.contains(range.commonAncestorContainer)) return;

    const tag = command === "superscript" ? "sup" : "sub";
    const other = tag === "sup" ? "sub" : "sup";
    const isOn = selectionHasSupOrSub(sel, root, tag);

    // Super and sub are mutually exclusive
    unwrapSupSubInSelection(root, sel, other);

    if (isOn) {
      unwrapSupSubInSelection(root, sel, tag);
      syncFromEditor();
      return;
    }

    // Re-read range after possible unwrap of the other tag
    const live = sel.rangeCount ? sel.getRangeAt(0) : range;
    if (live.collapsed) {
      document.execCommand(command, false, null);
      syncFromEditor();
      return;
    }

    const wrap = document.createElement(tag);
    wrap.appendChild(live.extractContents());
    live.insertNode(wrap);
    sel.removeAllRanges();
    const nr = document.createRange();
    nr.selectNodeContents(wrap);
    sel.addRange(nr);
    syncFromEditor();
  };

  const collectIntersectingListItems = (sel, root) => {
    if (!sel?.rangeCount || !root) return [];
    const range = sel.getRangeAt(0);
    if (range.collapsed) {
      let el =
        sel.focusNode?.nodeType === Node.ELEMENT_NODE
          ? sel.focusNode
          : sel.focusNode?.parentElement;
      while (el && el !== root) {
        if (el.nodeType === 1 && el.tagName === "LI") return [el];
        el = el.parentElement;
      }
      return [];
    }
    const out = [];
    root.querySelectorAll("li").forEach((li) => {
      try {
        if (range.intersectsNode(li)) out.push(li);
      } catch {
        /* detached */
      }
    });
    return out;
  };

  const findClosestStyleBlock = (node, root) => {
    let el =
      node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement;
    while (el && el !== root) {
      if (el.nodeType === 1) {
        const tag = el.tagName.toLowerCase();
        if (
          tag === "li" ||
          tag === "p" ||
          tag === "div" ||
          tag === "blockquote" ||
          tag === "pre" ||
          /^h[1-6]$/.test(tag)
        ) {
          return el;
        }
      }
      el = el.parentElement;
    }
    return null;
  };

  const wrapSelectionStyles = (styles) => {
    if (typeof window === "undefined" || !editorRef.current) return;
    focusEditor();
    restoreEditorSelectionRange();
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;
    const range = sel.getRangeAt(0);
    const root = editorRef.current;
    if (!root.contains(range.commonAncestorContainer)) return;

    // Caret only: apply styles to the surrounding list item / block
    if (range.collapsed) {
      const block = findClosestStyleBlock(sel.focusNode, root);
      if (!block) return;
      Object.assign(block.style, styles);
      syncFromEditor();
      return;
    }

    // Multi-item list selection: style each <li> (avoids breaking the list DOM)
    const listItems = collectIntersectingListItems(sel, root);
    if (listItems.length > 1) {
      listItems.forEach((li) => Object.assign(li.style, styles));
      syncFromEditor();
      return;
    }

    const wraps = wrapRangeInStyledSpans(range, root, (span) => {
      Object.assign(span.style, styles);
    });
    restoreSelectionAroundWraps(sel, wraps);
    syncFromEditor();
  };

  const applyFontSizePx = (sizeRaw) => {
    const size = String(sizeRaw ?? "").trim();
    const root = editorRef.current;
    if (!size || !root) return;
    focusEditor();

    if (selectionCoversEntireEditor(root, selectionRangeRef.current)) {
      applyFontSizeEverywhere(root, size);
      syncFromEditor();
      return;
    }

    normalizeDivBlocksToParagraphs(root);

    const pinTableHeaderFontSizes = (scope) => {
      if (!scope?.querySelectorAll || typeof window === "undefined" || !window.getComputedStyle) return;
      const cells = [...scope.querySelectorAll("th")];
      scope.querySelectorAll("table.rte-table-style-header").forEach((table) => {
        const row = table.querySelector("tr");
        row?.querySelectorAll(":scope > td").forEach((td) => cells.push(td));
      });
      cells.forEach((cell) => {
        if (!root.contains(cell) || !cell.style || cell.style.fontSize) return;
        const fs = window.getComputedStyle(cell).fontSize;
        if (fs && fs !== "0px") cell.style.fontSize = fs;
      });
    };

    const isBodySizeTarget = (el) => {
      if (!el || el.nodeType !== 1 || !root.contains(el)) return false;
      if (isInsideFlowDiagramBody(el) || isFontSizeExemptElement(el)) return false;
      if (el.closest("figcaption, .rte-caption, h1, h2, h3, h4, h5, h6, pre")) return false;
      const tag = el.tagName;
      if (tag === "P" || tag === "LI" || tag === "BLOCKQUOTE") return true;
      if (tag === "TD") return true;
      if (tag === "DIV") {
        if (isSkippableFontChrome(el)) return false;
        if (el.closest("td")) return true;
        if (el.className && String(el.className).trim()) return false;
        if (el.closest("figure, .rte-textbox")) return false;
        if (el.querySelector("p, div, h1, h2, h3, h4, h5, h6, ul, ol, blockquote, table, pre")) {
          return false;
        }
        return true;
      }
      return false;
    };

    pinTableHeaderFontSizes(root);

    const targets = [];
    root.querySelectorAll("p, li, blockquote, td, div").forEach((el) => {
      if (isBodySizeTarget(el)) targets.push(el);
    });

    targets.forEach((block) => {
      clearFontSizeStylesInTree(block);
      applyFontSizeAndLineHeight(block, size);
      block.querySelectorAll("div, span, font, b, strong, i, em, u, s, a, p, li").forEach((child) => {
        if (!isBodySizeTarget(child) && child.tagName !== "SPAN" && child.tagName !== "FONT"
          && !/^(B|STRONG|I|EM|U|S|A)$/i.test(child.tagName)) return;
        if (isFontSizeExemptElement(child) || isSkippableFontChrome(child)) return;
        if (child.classList?.contains("rte-cite-marker") || child.closest?.(".rte-cite-marker")) {
          child.style.setProperty("font-size", `calc(${size} * 0.75)`, "important");
          return;
        }
        applyFontSizeAndLineHeight(child, size);
      });
      const list = block.parentElement;
      if (list && /^(UL|OL)$/i.test(list.tagName)) {
        list.style.removeProperty("line-height");
        list.style.removeProperty("font-size");
        cleanupEmptyStyleAttr(list);
      }
    });
    root.querySelectorAll("[data-rte-generated='bibliography'], [data-rte-generated='bibliography'] *").forEach((el) => {
      if (!el?.style || isHeadingOrTitleElement(el) || isSkippableFontChrome(el)) return;
      el.style.setProperty("font-size", size, "important");
    });
    lockFlowDiagramTypography(root);
    syncFromEditor();
  };

  const transformSelectedTextPreserveMarkup = (textTransformFn) => {
    if (typeof window === "undefined" || !editorRef.current) return;
    focusEditor();
    restoreEditorSelectionRange();
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;
    const range = sel.getRangeAt(0);
    if (range.collapsed || !editorRef.current.contains(range.commonAncestorContainer)) return;

    const fragment = range.extractContents();
    const walker = document.createTreeWalker(fragment, NodeFilter.SHOW_TEXT, null);
    let tn;
    const batch = [];
    while ((tn = walker.nextNode())) batch.push(tn);
    batch.forEach((node) => {
      node.textContent = textTransformFn(node.textContent);
    });

    const firstInserted = fragment.firstChild;
    const lastInserted = fragment.lastChild;

    range.insertNode(fragment);

    sel.removeAllRanges();
    if (firstInserted && lastInserted) {
      const nr = document.createRange();
      nr.setStartBefore(firstInserted);
      nr.setEndAfter(lastInserted);
      sel.addRange(nr);
    }
    syncFromEditor();
  };

  const deriveHeadingTagFromDom = () => {
    const node = resolveFocusAncestor();
    let el =
      node && node.nodeType === 1 ? node : node?.parentElement;
    if (!el || !editorRef.current.contains(el)) return "p";

    while (el && el !== editorRef.current) {
      if (el.nodeType === 1) {
        const tag = el.tagName.toLowerCase();
        if (tag === "h1" || tag === "h2" || tag === "h3" || tag === "h4" || tag === "h5" || tag === "h6") {
          return tag;
        }
        if (
          tag === "ol" ||
          tag === "ul" ||
          tag === "li" ||
          tag === "blockquote" ||
          tag === "pre"
        ) {
          return null;
        }
        if (tag === "p" || tag === "div") return "p";
      }
      el = el.parentNode;
    }
    return "p";
  };

  const resolveFocusAncestor = () => {
    if (typeof window === "undefined" || !editorRef.current) return null;
    const sel = window.getSelection();
    if (!sel || !sel.focusNode) return null;
    const node = sel.focusNode;
    if (!editorRef.current.contains(node)) return null;
    return node.nodeType === 1 ? node : node.parentElement;
  };

  const clearTableGuides = () => {
    setTableColGutter(null);
    setTableRowGutter(null);
  };

  const clearTableSelectionDom = (root = editorRef.current) => {
    if (!root?.querySelectorAll) return;
    root
      .querySelectorAll(".rte-table-sel, .rte-table-sel-preview")
      .forEach((el) => {
        el.classList.remove("rte-table-sel", "rte-table-sel-preview");
      });
  };

  const paintTableSelection = (table, selection, preview = false) => {
    if (!table) return;
    const cls = preview ? "rte-table-sel-preview" : "rte-table-sel";
    const other = preview ? "rte-table-sel" : "rte-table-sel-preview";
    table.querySelectorAll(`.${cls}, .${other}`).forEach((el) => {
      el.classList.remove("rte-table-sel", "rte-table-sel-preview");
    });
    if (!selection) return;
    const rows = [...table.querySelectorAll("tr")];
    if (selection.type === "row") {
      const row = rows[selection.index];
      row
        ?.querySelectorAll(":scope > th, :scope > td")
        .forEach((c) => c.classList.add(cls));
    } else if (selection.type === "col") {
      rows.forEach((row) => {
        const cells = row.querySelectorAll(":scope > th, :scope > td");
        if (cells[selection.index]) cells[selection.index].classList.add(cls);
      });
    }
  };

  const positionTableDeleteForSelection = (table, selection) => {
    if (!table || !selection) {
      setTableDelCol(null);
      setTableDelRow(null);
      return;
    }
    if (selection.type === "col") {
      setTableDelRow(null);
      showTableColDelete(table, selection.index);
    } else {
      setTableDelCol(null);
      showTableRowDelete(table, selection.index);
    }
  };

  const clearTableSelection = () => {
    clearTableSelectionDom();
    tableSelectionRef.current = null;
    setTableSelection(null);
    setTableHoverSel(null);
    setTableDelCol(null);
    setTableDelRow(null);
    if (!keepTableBandPopRef.current) setTableBandPop(false);
  };

  const selectTableRowOrCol = (table, type, index) => {
    if (!table) return;
    activeTableRef.current = table;
    const next = { type, index };
    tableSelectionRef.current = next;
    setTableHoverSel(null);
    setTableSelection(next);
    if (!keepTableBandPopRef.current) setTableBandPop(false);
    paintTableSelection(table, next, false);
    positionTableDeleteForSelection(table, next);
  };

  const restoreTableBandAfterHydrate = () => {
    const pending = tableSelectionRestoreRef.current;
    if (pending) keepTableBandPopRef.current = true;
    clearTableSelection();
    if (!pending) return;
    tableSelectionRestoreRef.current = null;
    const fresh = Date.now() - pending.at < 1500;
    const table = editorRef.current?.querySelectorAll("table")[pending.tableIndex];
    if (fresh && table) selectTableRowOrCol(table, pending.type, pending.index);
    keepTableBandPopRef.current = false;
  };

  const cancelTableColDelHide = () => {
    if (tableColDelHideTimerRef.current != null) {
      clearTimeout(tableColDelHideTimerRef.current);
      tableColDelHideTimerRef.current = null;
    }
  };

  const scheduleTableColDelHide = () => {
    cancelTableColDelHide();
    tableColDelHideTimerRef.current = window.setTimeout(() => {
      tableColDelHideTimerRef.current = null;
      setTableDelCol(null);
    }, TABLE_FLOAT_DEL_HIDE_MS);
  };

  const cancelTableRowDelHide = () => {
    if (tableRowDelHideTimerRef.current != null) {
      clearTimeout(tableRowDelHideTimerRef.current);
      tableRowDelHideTimerRef.current = null;
    }
  };

  const scheduleTableRowDelHide = () => {
    cancelTableRowDelHide();
    tableRowDelHideTimerRef.current = window.setTimeout(() => {
      tableRowDelHideTimerRef.current = null;
      setTableDelRow(null);
    }, TABLE_FLOAT_DEL_HIDE_MS);
  };

  const refreshActiveTableFromSelection = () => {
    const root = editorRef.current;
    if (!root || typeof window === "undefined") {
      activeTableRef.current = null;
      return;
    }
    const sel = window.getSelection();
    const table =
      sel?.focusNode && root.contains(sel.focusNode)
        ? findTableAncestor(sel.focusNode, root)
        : null;
    activeTableRef.current = table;
  };

  const shellOrigin = () => wrapPaddingEdgeOrigin(editorShellRef.current);

  const cancelColResizeTimer = () => {
    if (colResizeTimerRef.current != null) {
      window.clearTimeout(colResizeTimerRef.current);
      colResizeTimerRef.current = null;
    }
  };

  const stopColResizeDrag = () => {
    const listeners = colResizeListenersRef.current;
    colResizeListenersRef.current = null;
    if (listeners) {
      document.removeEventListener("mousemove", listeners.move);
      document.removeEventListener("mouseup", listeners.up);
    }
    document.documentElement.classList.remove("rte-col-resizing");
  };

  const hideColResize = () => {
    if (colResizeDragRef.current) return;
    cancelColResizeTimer();
    colResizeIntentRef.current = null;
    colResizeReadyRef.current = null;
    colResizeTableRef.current = null;
    setColResizeHandle((prev) => (prev ? null : prev));
  };

  const placeColResizeHandle = (table, colIndex) => {
    const shell = editorShellRef.current;
    const cell = rowCells(table.querySelector("tr"))[colIndex];
    if (!shell || !cell) return;
    const origin = shellOrigin();
    const trect = table.getBoundingClientRect();
    const lineLeft = cell.getBoundingClientRect().right - origin.left;
    const lineTop = trect.top - origin.top + TABLE_EDGE_PX;
    const lineHeight = Math.max(0, trect.height - TABLE_EDGE_PX);
    colResizeTableRef.current = table;
    setColResizeHandle((prev) => {
      if (
        prev &&
        prev.colIndex === colIndex &&
        prev.lineLeft === lineLeft &&
        prev.lineTop === lineTop &&
        prev.lineHeight === lineHeight
      ) {
        return prev;
      }
      return { colIndex, lineLeft, lineTop, lineHeight };
    });
  };

  const armColResize = (table, hit) => {
    if (
      colResizeReadyRef.current?.key === hit.key &&
      colResizeTableRef.current === table
    ) {
      return;
    }
    if (
      colResizeTimerRef.current != null &&
      colResizeIntentRef.current?.key === hit.key &&
      colResizeIntentRef.current?.table === table
    ) {
      return;
    }
    if (colResizeReadyRef.current) {
      colResizeReadyRef.current = null;
      setColResizeHandle(null);
    }
    cancelColResizeTimer();
    colResizeReadyRef.current = null;
    colResizeIntentRef.current = { key: hit.key, table, colIndex: hit.colIndex };
    colResizeTimerRef.current = window.setTimeout(() => {
      colResizeTimerRef.current = null;
      const pending = colResizeIntentRef.current;
      if (!pending || pending.key !== hit.key || pending.table !== table) return;
      if (!editorRef.current?.contains(pending.table)) return;
      colResizeReadyRef.current = pending;
      placeColResizeHandle(pending.table, pending.colIndex);
    }, TABLE_COL_RESIZE_MS);
  };

  const startColResize = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const ready = colResizeReadyRef.current;
    const table = colResizeTableRef.current;
    if (!ready || !table || !editorRef.current?.contains(table)) return;
    const widths = measureColumnWidths(table);
    if (widths.length < 2 || ready.colIndex < 0 || ready.colIndex >= widths.length - 1) return;
    stopColResizeDrag();
    colResizeDragRef.current = {
      table,
      colIndex: ready.colIndex,
      startX: event.clientX,
      widths,
    };
    document.documentElement.classList.add("rte-col-resizing");
    const move = (ev) => {
      const active = colResizeDragRef.current;
      if (!active || !editorRef.current?.contains(active.table)) return;
      ev.preventDefault();
      const pair = active.widths[active.colIndex] + active.widths[active.colIndex + 1];
      const min = Math.min(TABLE_COL_MIN_PX, pair / 2);
      const nextLeft = Math.max(
        min,
        Math.min(pair - min, active.widths[active.colIndex] + (ev.clientX - active.startX))
      );
      const next = active.widths.slice();
      next[active.colIndex] = nextLeft;
      next[active.colIndex + 1] = pair - nextLeft;
      applyColumnWidths(active.table, next);
      placeColResizeHandle(active.table, active.colIndex);
    };
    const up = () => {
      stopColResizeDrag();
      colResizeDragRef.current = null;
      syncFromEditor();
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
    colResizeListenersRef.current = { move, up };
  };

  colResizeFollowRef.current = () => {
    const ready = colResizeReadyRef.current;
    const table = colResizeTableRef.current;
    if (!ready || !table || colResizeDragRef.current) return;
    if (!editorRef.current?.contains(table)) {
      hideColResize();
      return;
    }
    placeColResizeHandle(table, ready.colIndex);
  };

  const updateTableColGuide = (table, insertAfterCol) => {
    const shell = editorShellRef.current;
    if (!shell || !table) return;
    setTableRowGutter(null);
    const shownKey = tableHoverKeyRef.current;
    requestAnimationFrame(() => {
      if (tableHoverKeyRef.current !== shownKey) return;
      const firstRow = table.querySelector("tr");
      if (!firstRow) return;
      const cells = [...firstRow.querySelectorAll("th, td")];
      const origin = shellOrigin();
      const trect = table.getBoundingClientRect();
      let lineLeft;
      if (insertAfterCol < 0) {
        const cell = cells[0];
        if (!cell) return;
        lineLeft = cell.getBoundingClientRect().left - origin.left;
      } else {
        const cell = cells[insertAfterCol];
        if (!cell) return;
        lineLeft = cell.getBoundingClientRect().right - origin.left;
      }
      activeTableRef.current = table;
      setTableColGutter({
        insertAfterCol,
        lineLeft,
        lineTop: trect.top - origin.top,
        lineHeight: trect.height,
      });
    });
  };

  const updateTableRowGuide = (table, mode, rowIndex) => {
    const shell = editorShellRef.current;
    if (!shell || !table) return;
    setTableColGutter(null);
    const shownKey = tableHoverKeyRef.current;
    requestAnimationFrame(() => {
      if (tableHoverKeyRef.current !== shownKey) return;
      const rows = [...table.querySelectorAll("tr")];
      const origin = shellOrigin();
      const trect = table.getBoundingClientRect();
      // Span only the table's border box (not the editor/shell)
      const rowLineLeft = trect.left - origin.left;
      const rowLineWidth = Math.max(0, trect.width);
      if (mode === "afterLast") {
        const last = rows[rows.length - 1];
        if (!last) return;
        activeTableRef.current = table;
        setTableRowGutter({
          mode: "afterLast",
          lineTop: last.getBoundingClientRect().bottom - origin.top,
          rowLineLeft,
          rowLineWidth,
        });
      } else {
        const row = rows[rowIndex];
        if (!row) return;
        activeTableRef.current = table;
        setTableRowGutter({
          mode: "beforeRow",
          rowIndex,
          lineTop: row.getBoundingClientRect().top - origin.top,
          rowLineLeft,
          rowLineWidth,
        });
      }
    });
  };

  const showTableColDelete = (table, colIndex) => {
    const shell = editorShellRef.current;
    if (!shell || !table) return;
    const firstRow = table.querySelector("tr");
    const cell = firstRow?.querySelectorAll("th, td")?.[colIndex];
    if (!cell) return;
    cancelTableColDelHide();
    const origin = shellOrigin();
    const rect = cell.getBoundingClientRect();
    activeTableRef.current = table;
    setTableDelCol({
      colIndex,
      left: rect.left - origin.left + rect.width / 2,
      top: rect.top - origin.top,
    });
  };

  const showTableRowDelete = (table, rowIndex) => {
    const shell = editorShellRef.current;
    if (!shell || !table || rowIndex < 0) return;
    const row = table.querySelectorAll("tr")[rowIndex];
    const cell = row?.querySelector("th, td");
    if (!cell) return;
    cancelTableRowDelHide();
    const origin = shellOrigin();
    const rect = cell.getBoundingClientRect();
    activeTableRef.current = table;
    setTableDelRow({
      rowIndex,
      left: rect.left - origin.left,
      top: rect.top - origin.top + rect.height / 2,
    });
  };

  const cancelTableHoverTimer = () => {
    if (tableHoverTimerRef.current != null) {
      window.clearTimeout(tableHoverTimerRef.current);
      tableHoverTimerRef.current = null;
    }
  };

  const hideShownTableHover = () => {
    tableHoverKeyRef.current = "";
    clearTableGuides();
    setTableHoverSel(null);
    const saved = tableSelectionRef.current;
    const table = activeTableRef.current;
    if (saved && table && editorRef.current?.contains(table)) {
      paintTableSelection(table, saved, false);
    } else {
      clearTableSelectionDom();
    }
  };

  const dismissTableHover = () => {
    const pending = tableHoverTimerRef.current != null;
    cancelTableHoverTimer();
    tableHoverIntentRef.current = null;
    if (tableHoverKeyRef.current) hideShownTableHover();
    else if (!pending) return;
  };

  applyTableHoverRef.current = (intent) => {
    const { table, gutter, preview } = intent || {};
    if (!table || !editorRef.current?.contains(table)) return;
    if (gutter?.kind === "col") updateTableColGuide(table, gutter.insertAfterCol);
    else if (gutter?.kind === "row") updateTableRowGuide(table, gutter.mode, gutter.rowIndex);
    else clearTableGuides();

    if (preview) {
      setTableHoverSel(preview);
      const saved = tableSelectionRef.current;
      if (
        saved &&
        activeTableRef.current === table &&
        saved.type === preview.type &&
        saved.index === preview.index
      ) {
        paintTableSelection(table, saved, false);
      } else {
        paintTableSelection(table, preview, true);
      }
      return;
    }
    setTableHoverSel(null);
    const saved = tableSelectionRef.current;
    if (saved && activeTableRef.current === table) {
      paintTableSelection(table, saved, false);
    } else {
      table
        .querySelectorAll(".rte-table-sel-preview")
        .forEach((el) => el.classList.remove("rte-table-sel-preview"));
    }
  };

  const armTableHover = (intent) => {
    if (
      tableHoverTimerRef.current != null &&
      tableHoverIntentRef.current?.key === intent.key
    ) {
      return;
    }
    cancelTableHoverTimer();
    tableHoverIntentRef.current = intent;
    tableHoverTimerRef.current = window.setTimeout(() => {
      tableHoverTimerRef.current = null;
      const pending = tableHoverIntentRef.current;
      if (!pending || pending.key !== intent.key) return;
      if (!editorRef.current?.contains(pending.table)) return;
      tableHoverKeyRef.current = pending.key;
      applyTableHoverRef.current(pending);
    }, TABLE_HOVER_SHOW_MS);
  };

  const handleEditorTableMouseMove = (e) => {
    const root = editorRef.current;
    const shell = editorShellRef.current;
    if (!root || !shell) return;
    const target = e.target;
    if (!(target instanceof Element)) return;
    if (colResizeDragRef.current) return;

    if (target.closest(".rte-table-chrome")) return;

    const table = target.closest("table");
    if (!table || !root.contains(table)) {
      dismissTableHover();
      hideColResize();
      return;
    }

    const cell = target.closest("td, th");
    if (!cell || !table.contains(cell)) return;

    const row = cell.parentElement;
    const rows = [...table.querySelectorAll("tr")];
    const rowIndex = rows.indexOf(row);
    const cells = rowCells(row);
    const colIndex = cells.indexOf(cell);
    if (rowIndex < 0 || colIndex < 0) return;

    const cellRect = cell.getBoundingClientRect();
    const onColumnTop = rowIndex === 0 && e.clientY - cellRect.top <= TABLE_EDGE_PX;
    if (onColumnTop) {
      if (colResizeTimerRef.current || colResizeReadyRef.current) hideColResize();
    } else {
      const divider = columnDividerAtPoint(cell, colIndex, cells.length, e.clientX);
      if (divider) {
        dismissTableHover();
        armColResize(table, divider);
        return;
      }
      if (colResizeTimerRef.current || colResizeReadyRef.current) hideColResize();
    }

    const intent = buildTableHoverIntent(table, e, cell, rowIndex, colIndex, rows, cells);
    if (!intent) {
      dismissTableHover();
      return;
    }
    if (intent.key === tableHoverKeyRef.current) {
      cancelTableHoverTimer();
      tableHoverIntentRef.current = null;
      return;
    }
    if (tableHoverKeyRef.current) hideShownTableHover();
    armTableHover(intent);
  };

  const handleEditorTableMouseDown = (e) => {
    const root = editorRef.current;
    if (!root) return;
    const target = e.target;
    if (!(target instanceof Element)) return;
    if (target.closest("figcaption.rte-caption")) {
      const cap = target.closest("figcaption.rte-caption");
      const text = cap?.querySelector(".rte-caption-text");
      const fig = cap?.closest("figure");
      if (fig) fig.draggable = false;
      if (text) {
        e.preventDefault();
        text.focus();
        const sel = window.getSelection();
        const hit = document.caretRangeFromPoint?.(e.clientX, e.clientY);
        const hitInside = hit && (hit.startContainer === text || text.contains(hit.startContainer));
        if (sel) {
          const range = document.createRange();
          if (hitInside) {
            sel.removeAllRanges();
            sel.addRange(hit);
          } else {
            range.selectNodeContents(text);
            range.collapse(!text.textContent);
            sel.removeAllRanges();
            sel.addRange(range);
          }
        }
      }
      return;
    }
    const mediaFig = target.closest("figure.rte-image, figure.rte-flow");
    if (mediaFig && root.contains(mediaFig)) {
      mediaFig.draggable = true;
      mediaFig.setAttribute("contenteditable", "false");
      return;
    }
    if (target.closest(".rte-table-chrome")) return;

    if (colResizeTimerRef.current || colResizeReadyRef.current) hideColResize();

    const table = target.closest("table");
    if (!table || !root.contains(table)) {
      if (tableSelection) clearTableSelection();
      return;
    }

    const cell = target.closest("td, th");
    if (!cell || !table.contains(cell)) return;

    const row = cell.parentElement;
    const rows = [...table.querySelectorAll("tr")];
    const rowIndex = rows.indexOf(row);
    const cells = [...row.querySelectorAll(":scope > th, :scope > td")];
    const colIndex = cells.indexOf(cell);
    if (rowIndex < 0 || colIndex < 0) return;

    const rect = cell.getBoundingClientRect();
    const nearLeft = e.clientX - rect.left <= TABLE_EDGE_PX;
    const nearTop = e.clientY - rect.top <= TABLE_EDGE_PX;
    const isFirstCol = colIndex === 0;

    if (nearLeft && isFirstCol) {
      e.preventDefault();
      cancelTableHoverTimer();
      tableHoverIntentRef.current = null;
      selectTableRowOrCol(table, "row", rowIndex);
      return;
    }
    if (nearTop && rowIndex === 0) {
      e.preventDefault();
      cancelTableHoverTimer();
      tableHoverIntentRef.current = null;
      selectTableRowOrCol(table, "col", colIndex);
      return;
    }

    // Click inside cell content: clear row/col selection so editing can proceed
    cancelTableHoverTimer();
    tableHoverIntentRef.current = null;
    if (tableHoverKeyRef.current) {
      tableHoverKeyRef.current = "";
      clearTableGuides();
      setTableHoverSel(null);
      clearTableSelectionDom();
    }
    if (tableSelection) clearTableSelection();
  };

  const handleEditorShellMouseLeave = (e) => {
    const next = e.relatedTarget;
    if (next instanceof Node && editorShellRef.current?.contains(next)) return;
    if (next instanceof Element && next.closest?.(".rte-table-chrome, .rte-table-edit-chrome, .rte-table-band-pop")) {
      return;
    }
    dismissTableHover();
    hideColResize();
    hideTableMoveHandle();
  };

  const applyHighlightColor = (color) => {
    if (typeof document === "undefined") return;
    focusEditor();
    restoreEditorSelectionRange();
    try {
      document.execCommand("styleWithCSS", false, true);
    } catch {
      /* older engines */
    }
    const ok = document.execCommand("hiliteColor", false, color);
    if (!ok) document.execCommand("backColor", false, color);
    stashEditorSelectionRange();
    syncFromEditor();
  };

  const clearHighlightColor = () => {
    if (typeof document === "undefined") return;
    focusEditor();
    restoreEditorSelectionRange();
    try {
      document.execCommand("styleWithCSS", false, true);
    } catch {
      /* older engines */
    }
    document.execCommand("hiliteColor", false, "transparent");
    stashEditorSelectionRange();
    syncFromEditor();
  };

  const applySelectionMenuHighlight = (color) => {
    applyHighlightColor(color);
    scheduleSelectionMenuRefresh();
  };

  const insertTableAtSelection = (rows, cols, styleId = tableStyleId) => {
    if (!editorRef.current || typeof document === "undefined") return;
    const r = Math.max(1, rows);
    const c = Math.max(1, cols);
    focusEditor();
    restoreEditorSelectionRange();
    document.execCommand(
      "insertHTML",
      false,
      buildRteTableHtml(r, c, styleId)
    );
    setTableGridHover({ rows: 0, cols: 0 });
    setTableGridDragging(false);
    tableGridDraggingRef.current = false;
    tableGridPendingRef.current = null;
    setTableMenuOpen(false);
    syncFromEditor();
    requestAnimationFrame(() => refreshActiveTableFromSelection());
  };

  const insertTableColumnAtGuide = () => {
    const table = activeTableRef.current;
    if (!table || !editorRef.current?.contains(table) || !tableColGutter) return;
    const insertAfter = tableColGutter.insertAfterCol;
    const sized = tableColumnsSized(table);
    const before = sized ? measureColumnWidths(table) : null;
    const rows = table.querySelectorAll("tr");
    rows.forEach((tr) => {
      const cells = [...tr.querySelectorAll(":scope > th, :scope > td")];
      const neo = document.createElement(
        cells[0] && cells[0].tagName === "TH" && !tr.querySelector("td")
          ? "th"
          : "td"
      );
      neo.innerHTML = "<br>";
      if (insertAfter < 0) {
        tr.insertBefore(neo, cells[0] || null);
      } else {
        const ref = cells[insertAfter];
        if (ref?.nextSibling) tr.insertBefore(neo, ref.nextSibling);
        else tr.appendChild(neo);
      }
    });
    if (before) applyColumnWidths(table, widthsWithInsertedColumn(before, Math.max(0, insertAfter + 1)));
    clearTableGuides();
    syncFromEditor();
  };

  const insertTableRowAtGuide = () => {
    const table = activeTableRef.current;
    if (!table || !editorRef.current?.contains(table) || !tableRowGutter) return;
    const body = table.tBodies[0] || table;
    const rows = [...table.querySelectorAll("tr")];
    const cols =
      rows[0]?.querySelectorAll(":scope > th, :scope > td").length || 1;
    const tr = document.createElement("tr");
    for (let i = 0; i < cols; i += 1) {
      const td = document.createElement("td");
      td.innerHTML = "<br>";
      tr.appendChild(td);
    }
    if (tableRowGutter.mode === "afterLast") {
      body.appendChild(tr);
    } else {
      const ref = rows[tableRowGutter.rowIndex];
      if (ref) body.insertBefore(tr, ref);
      else body.appendChild(tr);
    }
    clearTableGuides();
    syncFromEditor();
  };

  const addTableRow = () => {
    const table = activeTableRef.current;
    if (!table || !editorRef.current?.contains(table)) return;
    const body = table.tBodies[0] || table;
    const cols =
      body.rows[0]?.cells?.length ||
      table.querySelector("tr")?.children?.length ||
      1;
    const tr = document.createElement("tr");
    for (let i = 0; i < cols; i += 1) {
      const td = document.createElement("td");
      td.innerHTML = "<br>";
      tr.appendChild(td);
    }
    body.appendChild(tr);
    syncFromEditor();
  };

  const addTableColumn = () => {
    const table = activeTableRef.current;
    if (!table || !editorRef.current?.contains(table)) return;
    const sized = tableColumnsSized(table);
    const before = sized ? measureColumnWidths(table) : null;
    const rows = table.querySelectorAll("tr");
    rows.forEach((tr) => {
      const cell = document.createElement(
        tr.querySelector("th") && !tr.querySelector("td") ? "th" : "td"
      );
      cell.innerHTML = "<br>";
      tr.appendChild(cell);
    });
    if (before) applyColumnWidths(table, widthsWithInsertedColumn(before, before.length));
    syncFromEditor();
  };

  const deleteTableColumnAt = (colIndex) => {
    const table = activeTableRef.current;
    if (!table || !editorRef.current?.contains(table)) return;
    const rows = table.querySelectorAll("tr");
    const colCount =
      rows[0]?.querySelectorAll(":scope > th, :scope > td").length || 0;
    if (colCount <= 1) return;
    const sized = tableColumnsSized(table);
    const before = sized ? measureColumnWidths(table) : null;
    rows.forEach((tr) => {
      const cells = tr.querySelectorAll(":scope > th, :scope > td");
      if (cells[colIndex]) cells[colIndex].remove();
    });
    if (before) applyColumnWidths(table, before.filter((_, index) => index !== colIndex));
    clearTableSelection();
    clearTableGuides();
    syncFromEditor();
  };

  const deleteTableRowAt = (rowIndex) => {
    const table = activeTableRef.current;
    if (!table || !editorRef.current?.contains(table)) return;
    const rows = table.querySelectorAll("tr");
    if (rows.length <= 1) return;
    if (rows[rowIndex]) rows[rowIndex].remove();
    clearTableSelection();
    clearTableGuides();
    syncFromEditor();
  };

  const mutateTable = (table, fn) => {
    const root = editorRef.current;
    if (!root || !table || !root.contains(table)) return;
    const selection = tableSelectionRef.current;
    if (selection) {
      tableSelectionRestoreRef.current = {
        tableIndex: [...root.querySelectorAll("table")].indexOf(table),
        type: selection.type,
        index: selection.index,
        at: Date.now(),
      };
    }
    fn(table);
    syncFromEditor();
    setTableStyleRev((n) => n + 1);
  };

  const applySelectedBandColor = (color) => {
    const table = activeTableRef.current;
    const selection = tableSelectionRef.current;
    if (!table || !selection) return;
    mutateTable(table, () => applyBandColor(table, selection, color));
  };

  const applySelectedBandEdge = (target, width) => {
    const table = activeTableRef.current;
    const selection = tableSelectionRef.current;
    if (!table || !selection) return;
    mutateTable(table, () => applyBandTargetWidth(table, selection, target, width));
  };

  const applySelectedBandEdgeColor = (color) => {
    const table = activeTableRef.current;
    const selection = tableSelectionRef.current;
    if (!table || !selection) return;
    mutateTable(table, () => applyBandAllBorderColor(table, selection, color));
  };

  const applySelectedBandSideColor = (target, color) => {
    const table = activeTableRef.current;
    const selection = tableSelectionRef.current;
    if (!table || !selection) return;
    mutateTable(table, () => applyBandTargetColor(table, selection, target, color));
  };

  const applyEditingTableBg = (color) => {
    const table = editingTableRef.current;
    if (!table || !editorRef.current?.contains(table)) return;
    tableEditStickyRef.current = true;
    mutateTable(table, () => paintTableFill(table, color));
  };

  const applyEditingTableBorder = (attr, width) => {
    const table = editingTableRef.current;
    if (!table || !editorRef.current?.contains(table)) return;
    tableEditStickyRef.current = true;
    mutateTable(table, () => {
      if (width == null || width === "") table.removeAttribute(attr);
      else table.setAttribute(attr, width);
    });
  };

  const applyEditingTableBorderColor = (kind, color) => {
    const table = editingTableRef.current;
    if (!table || !editorRef.current?.contains(table)) return;
    tableEditStickyRef.current = true;
    mutateTable(table, () => applyTableBorderColor(table, kind, color));
  };

  const resetSelectedBand = () => {
    const table = activeTableRef.current;
    const selection = tableSelectionRef.current;
    if (!table || !selection) return;
    mutateTable(table, () => resetBandToDefault(table, selection));
  };

  const resetEditingTable = () => {
    const table = editingTableRef.current || activeTableRef.current;
    if (!table || !editorRef.current?.contains(table)) return;
    tableEditStickyRef.current = true;
    mutateTable(table, () => resetTableToDefault(table));
  };

  const flushCaretInspector = () => {
    const root = editorRef.current;
    if (typeof window === "undefined" || !root) {
      commitCaretInspect({ ...CARET_INSPECTOR_EMPTY });
      return;
    }
    const sel = window.getSelection();
    if (!sel?.focusNode || !root.contains(sel.focusNode)) {
      commitCaretInspect({ ...CARET_INSPECTOR_EMPTY });
      return;
    }

    const range = sel.rangeCount ? sel.getRangeAt(0) : null;
    const selected = Boolean(range && !range.collapsed);
    const targets = resolveSelectionTargets(sel, root);
    if (!targets.length) {
      commitCaretInspect({ ...CARET_INSPECTOR_EMPTY });
      return;
    }

    const blockShort = compactUniqueLabels(
      targets.map((t) => inspectBlockForNode(t, root).short)
    );
    const blockLong = compactUniqueLabels(
      targets.map((t) => inspectBlockForNode(t, root).long)
    );
    const fontSize = compactUniqueLabels(
      targets.map((t) => window.getComputedStyle(t).fontSize || "—")
    );
    const fontFamilyKeys = targets.map((t) => getExplicitFontFamily(t, root));
    const uniqueFontKeys = [
      ...new Set(fontFamilyKeys.map((k) => normalizeFontFamilyKey(k))),
    ];
    let fontFamilyValue = "";
    let fontFamily = "Default";
    if (uniqueFontKeys.length === 1) {
      fontFamilyValue =
        fontFamilyKeys.find(
          (k) => normalizeFontFamilyKey(k) === uniqueFontKeys[0]
        ) || "";
      const matched = matchFontFamilyOption(fontFamilyValue);
      fontFamily = matched?.label || (fontFamilyValue ? "Custom" : "Default");
      fontFamilyValue =
        matched && matched.value !== FONT_FAMILY_DEFAULT
          ? matched.value
          : fontFamilyValue;
    } else if (uniqueFontKeys.length > 1) {
      fontFamily = "—";
      fontFamilyValue = "";
    }
    const colorLabel = compactUniqueLabels(
      targets.map((t) => cssColorToHexLabel(window.getComputedStyle(t).color))
    );
    const caseLabel = compactUniqueLabels(
      targets.map((t) =>
        cssTextTransformToCaseKey(window.getComputedStyle(t).textTransform)
      )
    );
    const align = compactUniqueLabels(
      targets.map((t) => cssTextAlignToKey(window.getComputedStyle(t).textAlign)),
      "left"
    );

    let bold = false;
    let italic = false;
    let underline = false;
    let strikeThrough = false;
    let superscript = false;
    let subscript = false;
    let inLink = false;
    let inQuote = false;
    let inCode = false;
    let inTable = false;
    try {
      const ae = document.activeElement;
      const edFocused =
        ae === root ||
        (typeof Node !== "undefined" &&
          ae instanceof Node &&
          root.contains(ae));
      if (edFocused) {
        if (selected) {
          const boldVals = targets.map((t) =>
            computedWeightIsBold(window.getComputedStyle(t).fontWeight)
          );
          const italicVals = targets.map((t) =>
            /(italic|oblique)/i.test(window.getComputedStyle(t).fontStyle)
          );
          const underlineVals = targets.map((t) =>
            /\bunderline\b/i.test(window.getComputedStyle(t).textDecorationLine)
          );
          const strikeVals = targets.map((t) =>
            /\bline-through\b/i.test(
              window.getComputedStyle(t).textDecorationLine
            )
          );
          bold = boldVals.some(Boolean);
          italic = italicVals.some(Boolean);
          underline = underlineVals.some(Boolean);
          strikeThrough = strikeVals.some(Boolean);
          superscript = targets.some((t) => {
            let el = t;
            while (el && el !== root) {
              if (el.nodeType === 1 && el.tagName.toLowerCase() === "sup") {
                return true;
              }
              el = el.parentElement;
            }
            return false;
          });
          subscript = targets.some((t) => {
            let el = t;
            while (el && el !== root) {
              if (el.nodeType === 1 && el.tagName.toLowerCase() === "sub") {
                return true;
              }
              el = el.parentElement;
            }
            return false;
          });
        } else {
          bold = document.queryCommandState("bold");
          italic = document.queryCommandState("italic");
          underline = document.queryCommandState("underline");
          strikeThrough = document.queryCommandState("strikeThrough");
          superscript = document.queryCommandState("superscript");
          subscript = document.queryCommandState("subscript");
        }
      }
    } catch {
      /* queryCommandState throws when not focused in some hosts */
    }

    inLink = selectionIntersectsLink(sel, root);
    inQuote = targets.some((t) => {
      let el = t;
      while (el && el !== root) {
        if (el.nodeType === 1 && el.tagName.toLowerCase() === "blockquote") {
          return true;
        }
        el = el.parentElement;
      }
      return false;
    });
    inCode = targets.some((t) => {
      let el = t;
      while (el && el !== root) {
        if (el.nodeType === 1) {
          const tag = el.tagName.toLowerCase();
          if (tag === "pre" || tag === "code") return true;
        }
        el = el.parentElement;
      }
      return false;
    });
    inTable = Boolean(findTableAncestor(sel.focusNode, root));

    let layoutBlocks = [];
    if (range?.collapsed) {
      const block = findClosestStyleBlock(range.startContainer, root);
      layoutBlocks = block ? [block] : [];
    } else if (range) {
      layoutBlocks = collectIntersectingStructuralBlocks(range, root, {
        includePre: false,
      });
      if (!layoutBlocks.length) {
        const block = findClosestStyleBlock(range.startContainer, root);
        layoutBlocks = block ? [block] : [];
      }
    }
    const hasBottomMargin = layoutBlocks.length
      ? layoutBlocks.some((b) => blockHasBottomMargin(b))
      : false;
    const spacingVals = [
      ...new Set(
        layoutBlocks.map((b) => getBlockLineSpacingValue(b)).filter(Boolean)
      ),
    ];
    const lineSpacing = spacingVals.length === 1 ? spacingVals[0] : "";
    const listKinds = targets.map((target) => {
      const li = findEnclosingListItem(target, root);
      return li ? listKindOf(li.parentElement) : "";
    });
    const listKind = listKinds.every((item) => item === listKinds[0]) ? listKinds[0] : "";

    commitCaretInspect({
      active: true,
      selected,
      blockShort,
      blockLong,
      fontSize,
      fontFamily,
      fontFamilyValue,
      colorLabel,
      caseLabel,
      bold,
      italic,
      underline,
      strikeThrough,
      superscript,
      subscript,
      inLink,
      inQuote,
      inCode,
      inTable,
      align: ALIGN_OPTIONS.some((o) => o.value === align) ? align : "left",
      hasBottomMargin,
      lineSpacing,
      listKind,
    });

    refreshActiveTableFromSelection();
  };

  const updateBlockTypeFromSelection = () => {
    if (typeof window === "undefined" || !editorRef.current) {
      commitCaretInspect({ ...CARET_INSPECTOR_EMPTY });
      return;
    }
    const sel = window.getSelection();
    if (!sel || !sel.focusNode) {
      if (isRteToolbarDropdownOpen()) return;
      commitCaretInspect({ ...CARET_INSPECTOR_EMPTY });
      return;
    }
    const node = sel.focusNode;
    if (!editorRef.current.contains(node)) {
      if (isRteToolbarDropdownOpen() || isRteToolbarDropdownTarget(document.activeElement)) {
        return;
      }
      commitCaretInspect({ ...CARET_INSPECTOR_EMPTY });
      return;
    }

    let el = node.nodeType === 1 ? node : node.parentNode;
    let found = "Paragraph";

    while (el && el !== editorRef.current) {
      if (el.nodeType === 1) {
        const tag = el.tagName.toLowerCase();
        const hLabels = {
          h1: "H1",
          h2: "H2",
          h3: "H3",
          h4: "H4",
          h5: "H5",
          h6: "H6",
        };
        if (hLabels[tag]) {
          found = hLabels[tag];
          break;
        }
        if (tag === "blockquote") {
          found = "Quote";
          break;
        }
        if (tag === "pre" || tag === "code") {
          found = "Code";
          break;
        }
        if (tag === "li") {
          const parentTag = el.parentElement
            ? el.parentElement.tagName.toLowerCase()
            : "";
          if (parentTag === "ol") {
            found = "# list";
          } else if (parentTag === "ul") {
            found = "B list";
          }
          break;
        }
        if (tag === "p" || tag === "div") {
          found = "P";
          break;
        }
      }
      el = el.parentNode;
    }

    setBlockType(found);

    const hTag = deriveHeadingTagFromDom();
    if (hTag) setHeadingValue(hTag);

    flushCaretInspector();
  };

  useEffect(() => {
    let trimmingReferences = false;
    const handleSelectionChange = () => {
      if (!editorRef.current) return;
      if (trimmingReferences) return;
      if (toolbarSelectionLockRef.current) return;
      trimmingReferences = true;
      try {
        trimSelectionAroundReferences(editorRef.current);
      } finally {
        trimmingReferences = false;
      }
      const sel = window.getSelection();
      const titleEl = getGuideTitleEl();
      if (titleEl && sel?.focusNode && (titleEl === sel.focusNode || titleEl.contains(sel.focusNode))) {
        lastEditTargetRef.current = "title";
        const tag = titleEl.tagName.toLowerCase();
        setHeadingValue(tag);
        setBlockType(tag.toUpperCase());
        if (!isPointerSelectingRef.current) hideSelectionMenu();
        return;
      }
      if (!sel || !sel.focusNode) {
        if (!isPointerSelectingRef.current) hideSelectionMenu();
        refreshChromeRef.current();
        return;
      }
      if (!editorRef.current.contains(sel.focusNode)) {
        if (!isPointerSelectingRef.current) hideSelectionMenu();
        refreshChromeRef.current();
        return;
      }
      lastEditTargetRef.current = "body";
      stashEditorSelectionRange();
      updateBlockTypeFromSelection();
      refreshChromeRef.current();
      if (!isPointerSelectingRef.current) scheduleSelectionMenuRefresh();
    };

    const handlePointerDown = (e) => {
      if (!e.target?.closest?.(".rte-table-band-pop, .rte-table-band-btn")) {
        closeTableBandPopRef.current();
      }
      if (
        !e.target?.closest?.(".rte-table-edit-chrome") &&
        !(tableEditStickyRef.current && e.target?.closest?.(".dropdown-menu"))
      ) {
        tableEditStickyRef.current = false;
      }
      const titleEl = getGuideTitleEl();
      if (titleEl && (titleEl === e.target || titleEl.contains(e.target))) {
        lastEditTargetRef.current = "title";
      }
      const root = editorRef.current;
      if (!root) return;
      const inSelectionPopover = !!e.target?.closest?.(".rte-selection-popover");
      const toolbarHit = e.target?.closest?.(
        ".rte-toolbar, .rte-menu, .rte-link-popover, .rte-picker-dropdown-menu"
      );
      if (inSelectionPopover || toolbarHit) {
        const sel = window.getSelection();
        if (
          sel?.rangeCount &&
          root.contains(sel.getRangeAt(0).commonAncestorContainer)
        ) {
          selectionRangeRef.current = sel.getRangeAt(0).cloneRange();
        }
        toolbarSelectionLockRef.current = true;
        // The popover unmounts if hidden here, so its button click never runs.
        if (!inSelectionPopover) hideSelectionMenu();
        return;
      }
      toolbarSelectionLockRef.current = false;
      if (root.contains(e.target)) {
        lastEditTargetRef.current = "body";
      }
      if (isRteToolbarDropdownTarget(e.target)) {
        hideSelectionMenu();
        return;
      }
      if (root.contains(e.target)) {
        isPointerSelectingRef.current = true;
        hideSelectionMenu();
        return;
      }
      // Click outside editor (and not on the selection menu) closes it
      if (!e.target?.closest?.(".rte-selection-popover")) {
        hideSelectionMenu();
      }
    };

    const handleFocusIn = (e) => {
      const titleEl = getGuideTitleEl();
      if (titleEl && (titleEl === e.target || titleEl.contains(e.target))) {
        lastEditTargetRef.current = "title";
        const tag = titleEl.tagName.toLowerCase();
        setHeadingValue(tag);
        setBlockType(tag.toUpperCase());
        return;
      }
      if (editorRef.current?.contains(e.target)) {
        lastEditTargetRef.current = "body";
      }
    };

    const handleDropdownShow = (e) => {
      if (isRteToolbarDropdownTarget(e.target)) hideSelectionMenu();
    };

    const handlePointerUp = (e) => {
      if (isRteToolbarDropdownTarget(e.target) || isRteToolbarDropdownOpen()) {
        isPointerSelectingRef.current = false;
        hideSelectionMenu();
        return;
      }
      if (!isPointerSelectingRef.current) {
        scheduleSelectionMenuRefresh();
        return;
      }
      isPointerSelectingRef.current = false;
      scheduleSelectionMenuRefresh();
    };

    const handleScroll = () => {
      refreshChromeRef.current();
      refreshSelectionMenuRef.current();
    };

    if (typeof document !== "undefined") {
      document.addEventListener("selectionchange", handleSelectionChange);
      document.addEventListener("pointerdown", handlePointerDown, true);
      document.addEventListener("pointerup", handlePointerUp, true);
      document.addEventListener("focusin", handleFocusIn);
      document.addEventListener("show.bs.dropdown", handleDropdownShow, true);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("scroll", handleScroll, true);
      window.addEventListener("resize", handleScroll);
    }

    return () => {
      if (typeof document !== "undefined") {
        document.removeEventListener("selectionchange", handleSelectionChange);
        document.removeEventListener("pointerdown", handlePointerDown, true);
        document.removeEventListener("pointerup", handlePointerUp, true);
        document.removeEventListener("focusin", handleFocusIn);
        document.removeEventListener("show.bs.dropdown", handleDropdownShow, true);
      }
      if (typeof window !== "undefined") {
        window.removeEventListener("scroll", handleScroll, true);
        window.removeEventListener("resize", handleScroll);
      }
      if (selectionMenuRafRef.current) {
        cancelAnimationFrame(selectionMenuRafRef.current);
        selectionMenuRafRef.current = 0;
      }
      if (linkHoverHideTimerRef.current != null) {
        window.clearTimeout(linkHoverHideTimerRef.current);
        linkHoverHideTimerRef.current = null;
      }
      if (tableColDelHideTimerRef.current != null) {
        clearTimeout(tableColDelHideTimerRef.current);
        tableColDelHideTimerRef.current = null;
      }
      if (tableRowDelHideTimerRef.current != null) {
        clearTimeout(tableRowDelHideTimerRef.current);
        tableRowDelHideTimerRef.current = null;
      }
      if (tableHoverTimerRef.current != null) {
        clearTimeout(tableHoverTimerRef.current);
        tableHoverTimerRef.current = null;
      }
      cancelColResizeTimer();
      stopColResizeDrag();
      colResizeDragRef.current = null;
      if (typingHistoryTimerRef.current != null) {
        clearTimeout(typingHistoryTimerRef.current);
        typingHistoryTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const root = editorRef.current;
    if (!root || typeof ResizeObserver === "undefined") return undefined;

    let raf = 0;
    const schedule = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        raf = 0;
        refreshCodeBlockLineNumbers(root);
      });
    };

    const ro = new ResizeObserver(schedule);
    ro.observe(root);

    const observePres = () => {
      root.querySelectorAll("pre").forEach((pre) => {
        try {
          ro.observe(pre);
        } catch {
          /* already observing */
        }
      });
    };
    observePres();

    const mo = new MutationObserver((mutations) => {
      let addedPre = false;
      mutations.forEach((m) => {
        m.addedNodes.forEach((n) => {
          if (n.nodeType !== 1) return;
          if (n.tagName === "PRE" || n.querySelector?.("pre")) addedPre = true;
        });
      });
      if (addedPre) observePres();
    });
    mo.observe(root, { childList: true, subtree: true });

    return () => {
      if (raf) cancelAnimationFrame(raf);
      ro.disconnect();
      mo.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!tableGridDragging) return undefined;
    const onUp = () => {
      const pending = tableGridPendingRef.current;
      tableGridPendingRef.current = null;
      tableGridDraggingRef.current = false;
      setTableGridDragging(false);
      if (pending?.rows > 0 && pending?.cols > 0) {
        insertTableAtSelection(
          pending.rows,
          pending.cols,
          pending.styleId || tableStyleId
        );
      }
    };
    document.addEventListener("mouseup", onUp);
    return () => document.removeEventListener("mouseup", onUp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableGridDragging]);

  useEffect(() => {
    ensureDefaultParagraphSeparator();
    // Sync block type when external value changes
    updateBlockTypeFromSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    ensureDefaultParagraphSeparator();
  }, []);

  const applyStructuralHeading = (tagLower, sel) => {
    if (!editorRef.current || typeof document === "undefined") return false;
    const root = editorRef.current;
    const anchor = sel?.anchorNode ?? null;
    if (!anchor || !root.contains(anchor)) return false;

    const focusNode = sel.focusNode != null ? sel.focusNode : anchor;
    const block = findStructuralBlockAncestor(focusNode, root);
    const fmtBlock = tagLower === "p" ? "P" : tagLower.toUpperCase();
    const desired = String(tagLower).toLowerCase();

    if (block && block !== root) {
      const from = block.tagName.toLowerCase();
      if (/^li$/i.test(from)) {
        return Boolean(liftListItemOut(block, desired));
      }
      if (from !== desired) {
        const neo = replaceElementPreserveChildren(block, desired);
        if (/^h[1-6]$/.test(desired)) releaseInheritedHeadingBold(neo);
      }
      return true;
    }

    document.execCommand("formatBlock", false, fmtBlock);
    if (/^h[1-6]$/.test(desired)) {
      const anchor = window.getSelection()?.anchorNode;
      const host = anchor?.nodeType === 1 ? anchor : anchor?.parentElement;
      const heading = host?.closest?.("h1, h2, h3, h4, h5, h6");
      if (heading && root.contains(heading)) releaseInheritedHeadingBold(heading);
    }
    return true;
  };

  const applyHeadingValue = (vRaw) => {
    const v = String(vRaw ?? "");
    if (!v) return;

    if (isEditingGuideTitle()) {
      applyHeadingToGuideTitle(v);
      toolbarSelectionLockRef.current = false;
      return;
    }

    focusEditor();

    let sel =
      typeof window !== "undefined" ? window.getSelection() : null;
    if (
      !sel?.rangeCount ||
      !editorRef.current?.contains(sel.anchorNode)
    ) {
      toolbarSelectionLockRef.current = false;
      return;
    }

    if (typeof window !== "undefined" && editorRef.current) {
      sel = window.getSelection();
      const root = editorRef.current;
      const desired = String(v).toLowerCase();
      const range =
        sel?.rangeCount && root.contains(sel.getRangeAt(0).commonAncestorContainer)
          ? sel.getRangeAt(0)
          : null;

      const bookmark = captureLeafBlockCaret(root);
      const plain = root.innerText;

      if (range && !range.collapsed) {
        const blocks = collectIntersectingStructuralBlocks(range, root, {
          includePre: true,
        });
        if (blocks.length >= 1) {
          blocks.forEach((block) => {
            const from = block.tagName.toLowerCase();
            if (from === desired) return;
            if (/^li$/i.test(from)) {
              const neo = liftListItemOut(block, desired);
              if (/^h[1-6]$/.test(desired)) releaseInheritedHeadingBold(neo);
              return;
            }
            const neo = replaceElementPreserveChildren(block, desired);
            if (/^h[1-6]$/.test(desired)) releaseInheritedHeadingBold(neo);
          });
          normalizeDivBlocksToParagraphs(root);
          if (bookmark && root.innerText === plain) restoreLeafBlockCaret(root, bookmark);
          syncFromEditor();
          setHeadingValue(v);
          return;
        }
      }

      applyStructuralHeading(v, sel);
      normalizeDivBlocksToParagraphs(editorRef.current);
      if (bookmark && editorRef.current && editorRef.current.innerText === plain) {
        restoreLeafBlockCaret(editorRef.current, bookmark);
      }
    }

    syncFromEditor();
    setHeadingValue(v);
  };

  const openFlowModal = () => {
    if (typeof window === "undefined" || !editorRef.current) return;
    stashEditorSelectionRange();
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    setFlowEmbedId(null);
    setFlowModalOpen(true);
  };

  const applyFlowDiagram = (record, svg, options = {}) => {
    const root = editorRef.current;
    if (!root || !record?.id || !svg || typeof document === "undefined") return;
    const existing = flowEmbedId
      ? [...root.querySelectorAll("figure.rte-flow")].find(
          (fig) => fig.getAttribute("data-rte-flow-id") === String(flowEmbedId)
        )
      : null;
    if (existing) {
      existing.setAttribute("data-rte-flow-id", String(record.id));
      setFigureBody(existing, svg);
      syncFromEditor();
    } else {
      focusEditor();
      restoreEditorSelectionRange();
      const sel = window.getSelection();
      if (!sel) return;
      if (!sel.rangeCount || !root.contains(sel.anchorNode)) {
        const range = document.createRange();
        range.selectNodeContents(root);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
      }
      const fig = document.createElement("figure");
      fig.className = "rte-flow";
      fig.contentEditable = "false";
      fig.draggable = true;
      fig.setAttribute("data-rte-flow-id", String(record.id));
      fig.innerHTML = svg;
      const anchor = currentMediaBlock(root);
      if (anchor && root.contains(anchor)) anchor.after(fig);
      else root.appendChild(fig);
      placeFigureUnderBlock(root, fig, anchor, { addParagraph: true });
      syncFromEditor();
    }
    if (options.close !== false) {
      setFlowModalOpen(false);
      setFlowEmbedId(null);
    } else {
      setFlowEmbedId(String(record.id));
    }
  };

  const openImageModal = () => {
    if (typeof window === "undefined" || !editorRef.current) return;
    stashEditorSelectionRange();
    setImageUrl("");
    setImageAlt("");
    setImageUpload(null);
    setImageUploadError("");
    setImageModalOpen(true);
  };

  const placeFigureUnderBlock = (root, fig, block, { addParagraph = false } = {}) => {
    const oldRow = fig.classList?.contains("rte-image") ? fig.closest(".rte-image-row") : null;
    if (block && block.parentNode && root.contains(block) && !fig.contains(block)) {
      const list = block.tagName === "LI" ? block.closest("ul, ol") : null;
      const anchor = list && root.contains(list) ? list : block;
      anchor.after(fig);
    } else if (fig.parentNode !== root) {
      root.appendChild(fig);
    }
    if (oldRow && !oldRow.contains(fig)) unwrapImageRowIfSparse(oldRow);
    if (addParagraph) {
      const next = fig.nextElementSibling;
      if (!next || !/^(P|H1|H2|H3|H4|H5|H6)$/.test(next.tagName)) {
        const p = document.createElement("p");
        p.appendChild(document.createElement("br"));
        fig.after(p);
      }
      placeCaretAtBlockStart(fig.nextElementSibling);
    }
  };

  const currentMediaBlock = (root) => {
    const sel = window.getSelection();
    const node = sel?.rangeCount ? sel.anchorNode : null;
    if (!node || !root.contains(node)) return null;
    return (
      (node.nodeType === 1 ? node : node.parentElement)?.closest?.(
        "p, h1, h2, h3, h4, h5, h6, li, blockquote"
      ) || null
    );
  };

  const insertImageFigure = (src, alt, block) => {
    const root = editorRef.current;
    if (!root || !src) return;
    const fig = buildImageFigure(root.ownerDocument, src, alt);
    const anchor = block || currentMediaBlock(root);
    if (!fig.parentNode) {
      if (anchor && root.contains(anchor)) anchor.after(fig);
      else root.appendChild(fig);
    }
    placeFigureUnderBlock(root, fig, anchor, { addParagraph: true });
    syncFromEditor();
  };

  const readImageFile = (file) =>
    new Promise((resolve, reject) => {
      if (!file || !IMAGE_UPLOAD_TYPES.has(file.type)) {
        reject(new Error("Use a PNG, JPEG, GIF, or WebP image."));
        return;
      }
      if (file.size > IMAGE_UPLOAD_MAX_BYTES) {
        reject(new Error("Image must be 5 MB or smaller."));
        return;
      }
      const reader = new FileReader();
      reader.onload = () => resolve({ name: file.name, src: String(reader.result || "") });
      reader.onerror = () => reject(new Error("Could not read that image."));
      reader.readAsDataURL(file);
    });

  const handleImageFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    readImageFile(file)
      .then((upload) => {
        setImageUpload(upload);
        setImageUploadError("");
        setImageUrl("");
      })
      .catch((err) => {
        setImageUpload(null);
        setImageUploadError(err.message || "Could not read that image.");
      });
  };

  const applyImageFromModal = () => {
    const uploaded = imageUpload?.src || "";
    const urlRaw = imageUrl.trim();
    let src = uploaded;
    if (!src && urlRaw) {
      if (/^(javascript|data|vbscript):/i.test(urlRaw) && !/^data:image\/(png|jpeg|gif|webp);/i.test(urlRaw)) {
        return;
      }
      src = /^(https?:|data:image\/)/i.test(urlRaw) ? urlRaw : `https://${urlRaw}`;
    }
    if (!src || !editorRef.current) return;
    focusEditor();
    restoreEditorSelectionRange();
    insertImageFigure(src, imageAlt.trim());
    setImageModalOpen(false);
    setImageUrl("");
    setImageAlt("");
    setImageUpload(null);
    setImageUploadError("");
  };

  const removeMediaFigure = () => {
    const root = editorRef.current;
    const fig = mediaFigRef.current;
    if (!root || !fig || !root.contains(fig)) return;
    const row = fig.closest(".rte-image-row");
    fig.remove();
    if (row) unwrapImageRowIfSparse(row);
    mediaFigRef.current = null;
    mediaChromeSigRef.current = "";
    setMediaChrome(null);
    syncFromEditor();
  };

  const editMediaFlow = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const fig = mediaFigRef.current;
    const id = fig?.getAttribute("data-rte-flow-id");
    if (!fig || !id) return;
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    setFlowEmbedId(String(id));
    setFlowModalOpen(true);
  };

  const hideMediaChrome = () => {
    mediaFigRef.current = null;
    mediaChromeSigRef.current = "";
    setMediaChrome(null);
    imageResizeSigRef.current = "";
    setImageResizeBar(null);
  };

  const applyCaptionTo = (node, settings) => {
    const root = editorRef.current;
    if (!root || !node || !root.contains(node) || !settings) return;
    applyCaptionSettings(node, settings);
    renumberCaptions(root);
    setCaptionRev((n) => n + 1);
    syncFromEditor();
  };

  const removeCaptionFrom = (node) => {
    const root = editorRef.current;
    if (!root || !node || !root.contains(node)) return;
    removeCaption(node);
    renumberCaptions(root);
    setCaptionRev((n) => n + 1);
    syncFromEditor();
  };

  const clearMediaDrag = () => {
    const node = mediaDragRef.current;
    node?.classList?.remove("rte-table-dragging");
    mediaDragRef.current = null;
    mediaDropBlockRef.current = null;
    imageBesideDropRef.current = null;
    setMediaDropLine(null);
  };

  const stopTableDragListeners = () => {
    const listeners = tableDragListenersRef.current;
    if (!listeners) return;
    document.removeEventListener("dragover", listeners.over, true);
    document.removeEventListener("drop", listeners.drop, true);
    tableDragListenersRef.current = null;
  };

  const commitTableDrag = () => {
    if (!tableDragActiveRef.current) return;
    tableDragActiveRef.current = false;
    stopTableDragListeners();
    const dragging = mediaDragRef.current;
    const block = mediaDropBlockRef.current;
    const dropped = tableDragDroppedRef.current;
    tableDragDroppedRef.current = false;
    dragging?.classList?.remove("rte-table-dragging");
    const root = editorRef.current;
    if (
      dropped &&
      dragging &&
      root?.contains(dragging) &&
      block &&
      root.contains(block) &&
      !dragging.contains(block)
    ) {
      placeFigureUnderBlock(root, dragging, block);
      mediaDragRef.current = null;
      mediaDropBlockRef.current = null;
      setMediaDropLine(null);
      syncFromEditor();
      return;
    }
    clearMediaDrag();
  };

  const hideTableMoveHandle = () => {
    tableMoveRef.current = null;
    tableMoveSigRef.current = "";
    setTableMoveHandle(null);
  };

  const showTableMoveHandle = (table) => {
    const shell = editorShellRef.current;
    if (!shell || !table) return;
    const origin = wrapPaddingEdgeOrigin(shell);
    const rect = table.getBoundingClientRect();
    const top = Math.round(rect.top - origin.top);
    const left = Math.round(rect.left - origin.left);
    const sig = `${top}:${left}`;
    tableMoveRef.current = table;
    if (tableMoveSigRef.current === sig) return;
    tableMoveSigRef.current = sig;
    setTableMoveHandle({ top, left });
  };

  const stopImageResizeDrag = () => {
    const listeners = imageResizeListenersRef.current;
    imageResizeListenersRef.current = null;
    if (listeners) {
      document.removeEventListener("mousemove", listeners.move);
      document.removeEventListener("mouseup", listeners.up);
    }
    document.documentElement.classList.remove("rte-image-resizing");
    const drag = imageResizeDragRef.current;
    imageResizeDragRef.current = null;
    if (drag?.fig) drag.fig.draggable = true;
  };

  const startImageResize = (event) => {
    if (event.button !== 0) return;
    const fig = imageResizeDragRef.current?.fig || mediaFigRef.current;
    const img = fig?.querySelector?.("img");
    const root = editorRef.current;
    if (!fig || !img || !root?.contains(fig) || !fig.classList.contains("rte-image")) return;
    event.preventDefault();
    event.stopPropagation();
    stopImageResizeDrag();
    const startHeight = img.getBoundingClientRect().height;
    const full = imageFullHeight(img);
    if (full <= RTE_IMAGE_MIN_H + 1) return;
    fig.draggable = false;
    imageResizeDragRef.current = { fig, startY: event.clientY, startHeight, full };
    document.documentElement.classList.add("rte-image-resizing");
    const move = (moveEvent) => {
      const drag = imageResizeDragRef.current;
      if (!drag?.fig) return;
      moveEvent.preventDefault();
      const next = drag.startHeight + (moveEvent.clientY - drag.startY);
      applyImageFrameHeight(drag.fig, next);
      refreshChromeRef.current();
    };
    const up = (upEvent) => {
      const drag = imageResizeDragRef.current;
      stopImageResizeDrag();
      if (drag?.fig && root.contains(drag.fig)) {
        const over = typeof document.elementFromPoint === "function"
          ? document.elementFromPoint(upEvent.clientX, upEvent.clientY)
          : null;
        const stillOver =
          over instanceof Element &&
          (drag.fig === over ||
            drag.fig.contains(over) ||
            over.closest?.(".rte-image-resize, .rte-media-chrome"));
        if (!stillOver) {
          mediaFigRef.current = null;
          mediaChromeSigRef.current = "";
          setMediaChrome(null);
          imageResizeSigRef.current = "";
          setImageResizeBar(null);
        }
        syncFromEditor();
      }
      refreshChromeRef.current();
    };
    imageResizeListenersRef.current = { move, up };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  };

  const handleEditorDragStart = (event) => {
    if (imageResizeDragRef.current) {
      event.preventDefault();
      return;
    }
    const root = editorRef.current;
    const fig = event.target?.closest?.("figure.rte-image, figure.rte-flow");
    if (!root || !fig || !root.contains(fig)) return;
    if (event.target?.closest?.("figcaption")) {
      event.preventDefault();
      return;
    }
    fig.draggable = true;
    mediaDragRef.current = fig;
    mediaFigRef.current = null;
    mediaChromeSigRef.current = "";
    setMediaChrome(null);
    imageResizeSigRef.current = "";
    setImageResizeBar(null);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", "rte-media");
    }
    event.stopPropagation();
  };

  const handleEditorDragOver = (event) => {
    const root = editorRef.current;
    if (!root) return;
    const dragging = mediaDragRef.current;
    const fileDrop = !dragging && [...(event.dataTransfer?.types || [])].includes("Files");
    if (!dragging && !fileDrop) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = dragging ? "move" : "copy";
    const beside = imageBesideTarget(root, event.clientX, event.clientY, dragging);
    imageBesideDropRef.current = beside;
    if (beside) {
      const rect = beside.fig.getBoundingClientRect();
      const top = Math.round(rect.top);
      const left = Math.round(beside.side === "left" ? rect.left : rect.right);
      const height = Math.round(rect.height);
      mediaDropBlockRef.current = null;
      setMediaDropLine((prev) =>
        prev && prev.vertical && prev.top === top && prev.left === left && prev.height === height
          ? prev
          : { top, left, width: 3, height, vertical: true }
      );
      return;
    }
    const block = blockUnderMediaPoint(root, event.clientX, event.clientY, dragging);
    mediaDropBlockRef.current = block;
    if (!block) {
      setMediaDropLine(null);
      return;
    }
    const rect = block.getBoundingClientRect();
    const top = Math.round(rect.bottom);
    const left = Math.round(rect.left);
    const width = Math.round(rect.width);
    setMediaDropLine((prev) =>
      prev && prev.top === top && prev.left === left && prev.width === width
        ? prev
        : { top, left, width }
    );
  };

  const handleEditorDrop = (event) => {
    const root = editorRef.current;
    if (!root) return;
    if (tableDragActiveRef.current) {
      event.preventDefault();
      return;
    }
    const dragging = mediaDragRef.current;
    const beside = imageBesideDropRef.current
      || imageBesideTarget(root, event.clientX, event.clientY, dragging);
    if (dragging && beside?.fig && root.contains(beside.fig) && root.contains(dragging)) {
      event.preventDefault();
      placeImageBeside(dragging, beside.fig, beside.side);
      clearMediaDrag();
      syncFromEditor();
      return;
    }
    const block = mediaDropBlockRef.current || blockUnderMediaPoint(root, event.clientX, event.clientY, dragging);
    if (dragging && root.contains(dragging)) {
      event.preventDefault();
      if (block && !dragging.contains(block)) placeFigureUnderBlock(root, dragging, block);
      clearMediaDrag();
      syncFromEditor();
      return;
    }
    const file = [...(event.dataTransfer?.files || [])].find((item) =>
      IMAGE_UPLOAD_TYPES.has(item.type)
    );
    if (!file) {
      clearMediaDrag();
      return;
    }
    event.preventDefault();
    clearMediaDrag();
    readImageFile(file)
      .then((upload) => insertImageFigure(upload.src, "", block))
      .catch(() => {});
  };

  const handleEditorDragEnd = () => {
    if (tableDragActiveRef.current) {
      commitTableDrag();
      return;
    }
    clearMediaDrag();
  };

  const clearContent = () => {
    if (!editorRef.current) return;
    editorRef.current.innerHTML = "";
    editorRef.current.style.removeProperty(RTE_DOC_BASE_VAR);
    setHeadingValue("p");
    setBlockType("Paragraph");
    clearTableSelection();
    syncFromEditor();
  };

  const collectBlocksForLayoutFromRange = (range, root) => {
    if (!range || !root) return [];

    const listItemsFromRange = () => {
      if (range.collapsed) {
        let el =
          range.startContainer?.nodeType === Node.ELEMENT_NODE
            ? range.startContainer
            : range.startContainer?.parentElement;
        while (el && el !== root) {
          if (el.nodeType === 1 && el.tagName === "LI") return [el];
          el = el.parentElement;
        }
        return [];
      }
      const out = [];
      root.querySelectorAll("li").forEach((li) => {
        if (rangeMeaningfullyIntersectsBlock(range, li)) out.push(li);
      });
      return out;
    };

    if (range.collapsed) {
      const listItems = listItemsFromRange();
      if (listItems.length) return listItems;
      const block = findClosestStyleBlock(range.startContainer, root);
      return block ? [block] : [];
    }

    // Multi-block selection: every intersecting leaf block (paragraphs + list items).
    const blocks = collectIntersectingStructuralBlocks(range, root, {
      includePre: false,
    });
    if (blocks.length) return blocks;

    const listItems = listItemsFromRange();
    if (listItems.length) return listItems;
    const block = findClosestStyleBlock(range.startContainer, root);
    return block ? [block] : [];
  };

  /** Read-only: does not restore/mutate the live selection (safe during render). */
  const peekBlocksForLayout = () => {
    if (!editorRef.current || typeof window === "undefined") return [];
    const root = editorRef.current;
    const saved = selectionRangeRef.current;
    if (
      saved &&
      root.contains(saved.startContainer) &&
      root.contains(saved.endContainer)
    ) {
      return collectBlocksForLayoutFromRange(saved, root);
    }
    const sel = window.getSelection();
    if (
      !sel?.rangeCount ||
      !root.contains(sel.getRangeAt(0).commonAncestorContainer)
    ) {
      return [];
    }
    return collectBlocksForLayoutFromRange(sel.getRangeAt(0), root);
  };

  const collectBlocksForLayout = () => {
    if (!editorRef.current || typeof window === "undefined") return [];
    const root = editorRef.current;
    restoreEditorSelectionRange();
    const sel = window.getSelection();
    if (
      !sel?.rangeCount ||
      !root.contains(sel.getRangeAt(0).commonAncestorContainer)
    ) {
      return [];
    }
    return collectBlocksForLayoutFromRange(sel.getRangeAt(0), root);
  };

  const applyLineSpacingToSelection = (value) => {
    if (!editorRef.current) return;
    focusEditor();
    restoreEditorSelectionRange();
    const blocks = collectBlocksForLayout();
    if (!blocks.length) return;
    const v = String(value ?? "").trim();
    blocks.forEach((block) => {
      if (!v || v === "clear") clearBlockLineSpacing(block);
      else applyBlockLineSpacing(block, v);
    });
    syncFromEditor();
  };

  const applyBottomMarginToSelection = (enabled) => {
    if (!editorRef.current) return;
    focusEditor();
    restoreEditorSelectionRange();
    const blocks = collectBlocksForLayout();
    if (!blocks.length) return;
    blocks.forEach((block) => applyBlockBottomMargin(block, enabled));
    syncFromEditor();
    commitCaretInspect((prev) => ({ ...prev, hasBottomMargin: Boolean(enabled) }));
  };

  const applyRemoveFormat = () => {
    if (!editorRef.current || typeof document === "undefined") return;
    focusEditor();
    restoreEditorSelectionRange();
    const root = editorRef.current;
    const sel = window.getSelection();

    // Only clear when there is a real text selection (not a caret / empty click).
    if (
      !sel?.rangeCount ||
      !root.contains(sel.getRangeAt(0).commonAncestorContainer)
    ) {
      return;
    }
    const range = sel.getRangeAt(0);
    if (range.collapsed) return;

    // Selection entirely inside the topic/article title — leave it alone.
    const startBlock = findClosestStyleBlock(range.startContainer, root);
    const endBlock = findClosestStyleBlock(range.endContainer, root);
    if (
      isProtectedTitleHeading(startBlock, root) &&
      isProtectedTitleHeading(endBlock, root)
    ) {
      return;
    }

    // Snapshot current DOM (incl. uncommitted typing) so clear can be undone.
    clearTypingHistoryTimer();
    commitHistorySnapshot();

    const finish = () => {
      setHoveredLinkEl(null);
      syncFromEditor();
      updateBlockTypeFromSelection();
    };

    const resetBlockFully = (block) => {
      if (!block || !root.contains(block)) return;
      // Never demote / wipe the document title heading
      if (isProtectedTitleHeading(block, root)) return block;
      let target = block;
      const tag = block.tagName;
      if (/^H[1-6]$/i.test(tag) || /^(BLOCKQUOTE|PRE)$/i.test(tag)) {
        target = replaceElementPreserveChildren(block, "p");
      }
      clearFontFamilyStylesInTree(target);
      normalizeEditorFormattingToBaseline(target, { asEditorRoot: false });
      clearBlockLayoutStyles(target);
      return target;
    };

    const rangeCoversBlockContent = (rangeArg, block) => {
      const blockText = normalizeVisibleText(
        block.innerText || block.textContent
      );
      const rangeText = normalizeVisibleText(rangeArg.toString());
      if (blockText && rangeText && blockText === rangeText) return true;
      const br = document.createRange();
      try {
        br.selectNodeContents(block);
        return (
          rangeArg.compareBoundaryPoints(Range.START_TO_START, br) <= 0 &&
          rangeArg.compareBoundaryPoints(Range.END_TO_END, br) >= 0
        );
      } catch {
        return false;
      }
    };

    const collectIntersectingStyleBlocks = (rangeArg) => {
      const out = [];
      const seen = new Set();
      root
        .querySelectorAll("p, div, li, blockquote, pre, h1, h2, h3, h4, h5, h6")
        .forEach((el) => {
          if (seen.has(el)) return;
          if (el.tagName !== "PRE" && el.closest?.("pre")) return;
          if (isProtectedTitleHeading(el, root)) return;
          try {
            if (!rangeArg.intersectsNode(el)) return;
          } catch {
            return;
          }
          const nested = el.querySelector(
            "p, div, li, blockquote, pre, h1, h2, h3, h4, h5, h6"
          );
          if (nested && el.tagName !== "LI") return;
          seen.add(el);
          out.push(el);
        });
      return out;
    };

    // Whole-editor selection → reset each non-title block (keep title heading)
    const full = document.createRange();
    full.selectNodeContents(root);
    const coversAll =
      range.compareBoundaryPoints(Range.START_TO_START, full) <= 0 &&
      range.compareBoundaryPoints(Range.END_TO_END, full) >= 0;
    if (coversAll) {
      collectIntersectingStyleBlocks(range).forEach((b) => resetBlockFully(b));
      // Also clear root-level inline wrappers that aren't the title
      [...root.children].forEach((ch) => {
        if (isProtectedTitleHeading(ch, root)) return;
        if (/^(P|DIV|LI|BLOCKQUOTE|PRE|H[1-6])$/i.test(ch.tagName)) return;
        if (ch.querySelector?.("h1, h2, h3, h4, h5, h6, p, li, ul, ol")) return;
        normalizeEditorFormattingToBaseline(ch, { asEditorRoot: false });
      });
      finish();
      return;
    }

    // One or more fully highlighted lines/blocks → reset each (skip title)
    const blocks = collectIntersectingStyleBlocks(range);
    const fullyCovered = blocks.filter((b) => rangeCoversBlockContent(range, b));
    if (blocks.length > 0 && fullyCovered.length === blocks.length) {
      fullyCovered.forEach((b) => resetBlockFully(b));
      finish();
      return;
    }

    // Partial selection: never pull the title into the cleared fragment
    const titleEl =
      root.querySelector(`#${RTE_GUIDE_TITLE_ID}`) ||
      root.querySelector("[data-guides-title='1']");
    if (titleEl && isProtectedTitleHeading(titleEl, root)) {
      try {
        if (range.intersectsNode(titleEl)) {
          const titleRange = document.createRange();
          titleRange.selectNode(titleEl);
          if (range.compareBoundaryPoints(Range.START_TO_START, titleRange) < 0) {
            range.setStartAfter(titleEl);
          }
          if (
            range.collapsed ||
            range.compareBoundaryPoints(Range.START_TO_END, range) >= 0
          ) {
            return;
          }
        }
      } catch {
        /* ignore */
      }
    }

    const frag = range.extractContents();
    const holder = document.createElement("div");
    holder.appendChild(frag);
    clearFontFamilyStylesInTree(holder);
    normalizeEditorFormattingToBaseline(holder, { asEditorRoot: false });

    const insertFrag = document.createDocumentFragment();
    while (holder.firstChild) insertFrag.appendChild(holder.firstChild);
    const first = insertFrag.firstChild;
    const last = insertFrag.lastChild;
    range.insertNode(insertFrag);

    blocks.forEach((b) => {
      if (root.contains(b) && !isProtectedTitleHeading(b, root)) {
        clearFontFamilyStylesInTree(b);
        normalizeBlockFontMetrics(b);
      }
    });

    sel.removeAllRanges();
    if (first && last) {
      const nr = document.createRange();
      nr.setStartBefore(first);
      nr.setEndAfter(last);
      sel.addRange(nr);
    }
    finish();
  };

  const applyFontFamilyValue = (familyRaw) => {
    if (!editorRef.current) return;
    focusEditor();
    restoreEditorSelectionRange();
    const root = editorRef.current;
    const raw = String(familyRaw ?? "").trim();
    const clear = !raw || raw === FONT_FAMILY_DEFAULT;
    const family = clear ? "" : raw;

    const selectionSpansEditor = () => {
      const live = window.getSelection();
      const liveRange = live?.rangeCount ? live.getRangeAt(0) : null;
      const saved = selectionRangeRef.current;
      const range = liveRange && !liveRange.collapsed ? liveRange : saved;
      if (!range || range.collapsed) return false;
      const ancestor = range.commonAncestorContainer;
      const el = ancestor?.nodeType === 1 ? ancestor : ancestor?.parentElement;
      return el === root;
    };

    if (selectionCoversEntireEditor(root, selectionRangeRef.current) || selectionSpansEditor()) {
      const title = editorShellRef.current?.querySelector?.("[data-guides-title='1']");
      applyFontFamilyEverywhere(root, family, title);
      syncFromEditor();
      return;
    }

    const sel = window.getSelection();
    if (!sel?.rangeCount) return;
    const range = sel.getRangeAt(0);
    if (!root.contains(range.commonAncestorContainer)) return;

    const applyToBlock = (block) => {
      if (!block) return;
      clearFontFamilyStylesInTree(block);
      paintFontFamilyOn(block, family);
      const list = block.closest?.("ul, ol");
      if (list) paintFontFamilyOn(list, family);
      block.querySelectorAll(
        "p, li, ul, ol, span, font, b, strong, i, em, u, s, a, figcaption, .rte-caption, .rte-caption-text, .rte-caption-label, .rte-cite, .rte-cite *, .rte-cite-marker, .rte-cite-marker *"
      ).forEach((el) => paintFontFamilyOn(el, family));
    };

    const rangeCoversBlock = (block) => rangeCoversEditableBlock(range, block);

    if (range.collapsed) {
      const block = findClosestStyleBlock(sel.focusNode, root);
      if (!block) return;
      applyToBlock(block);
      syncFromEditor();
      return;
    }

    const listItems = collectIntersectingListItems(sel, root);
    const structural = collectIntersectingStructuralBlocks(range, root, { includePre: true });
    const selectionStaysInLists = listItems.length > 0 && structural.every(
      (block) => block.tagName === "LI" || block.closest("li, ul, ol")
    );
    const blocks = selectionStaysInLists ? listItems : structural;

    const paintTableFontFamily = (table) => {
      clearFontPropertyInTree(table, "font-family");
      [table, ...table.querySelectorAll("td, th, p, div, li, span, font, b, strong, i, em, u, s, a")].forEach((el) => {
        if (!el?.style || isSkippableFontChrome(el)) return;
        if (family) el.style.fontFamily = family;
        else {
          el.style.removeProperty("font-family");
          cleanupEmptyStyleAttr(el);
        }
      });
    };

    const flowBlocks = [...root.querySelectorAll("p, li, h1, h2, h3, h4, h5, h6, blockquote, pre, div")]
      .filter((el) => root.contains(el)
        && !el.closest("table, .rte-code-gutter, .rte-caption-catalog, figure.rte-flow, figure.rte-image, [data-rte-generated='bibliography']")
        && normalizeVisibleText(el.innerText));
    const coversDocumentFlow = flowBlocks.length > 0 && flowBlocks.every(rangeCoversBlock);

    if ((blocks.length && blocks.every(rangeCoversBlock)) || coversDocumentFlow) {
      const refs = referenceNodesForFont(root, range, coversDocumentFlow);
      (coversDocumentFlow ? flowBlocks : blocks).forEach(applyToBlock);
      root.querySelectorAll("table").forEach((table) => {
        if (!root.contains(table)) return;
        const ancestor = range.commonAncestorContainer?.nodeType === 1
          ? range.commonAncestorContainer
          : range.commonAncestorContainer?.parentElement;
        let hit = rangeCoversNodeContents(range, table);
        if (!hit) {
          try { hit = range.intersectsNode(table); } catch { hit = false; }
        }
        if (!hit && coversDocumentFlow && ancestor?.contains?.(table)) hit = true;
        if (hit) paintTableFontFamily(table);
      });
      refs.forEach((el) => paintFontFamilyOn(el, family));
      lockFlowDiagramTypography(root);
      syncFromEditor();
      return;
    }

    const appliedFamily = clear
      ? window.getComputedStyle(root).fontFamily
      : family;
    const refs = referenceNodesForFont(root, range, false);
    const wraps = wrapRangeInStyledSpans(range, root, (span) => {
      span.style.setProperty("font-family", appliedFamily, "important");
    });
    restoreSelectionAroundWraps(sel, wraps);
    refs.forEach((el) => paintFontFamilyOn(el, family));
    lockFlowDiagramTypography(root);
    syncFromEditor();
  };

  const applyUnlinkOnly = () => {
    if (typeof document === "undefined") return;
    focusEditor();
    restoreEditorSelectionRange();
    const root = editorRef.current;
    const sel = window.getSelection();
    const anchor =
      sel?.focusNode && root
        ? findLinkAncestor(sel.focusNode, root)
        : null;
    if (anchor) {
      unwrapAnchorElement(anchor);
    } else {
      document.execCommand("unlink", false, null);
    }
    setHoveredLinkEl(null);
    syncFromEditor();
  };

  const removeHoveredLink = () => {
    if (!hoveredLinkEl || !editorRef.current?.contains(hoveredLinkEl)) {
      setHoveredLinkEl(null);
      return;
    }
    unwrapAnchorElement(hoveredLinkEl);
    setHoveredLinkEl(null);
    syncFromEditor();
  };

  const clearLinkHoverHideTimer = () => {
    if (linkHoverHideTimerRef.current != null) {
      window.clearTimeout(linkHoverHideTimerRef.current);
      linkHoverHideTimerRef.current = null;
    }
  };

  const scheduleLinkHoverHide = () => {
    clearLinkHoverHideTimer();
    linkHoverHideTimerRef.current = window.setTimeout(() => {
      setHoveredLinkEl(null);
      linkHoverHideTimerRef.current = null;
    }, 180);
  };

  const handleEditorMouseOver = (e) => {
    const root = editorRef.current;
    if (!root) return;
    const hoveredTable = e.target?.closest?.("table")
      || e.target?.closest?.("figure.rte-table-figure")?.querySelector("table");
    if (hoveredTable && root.contains(hoveredTable)) showTableMoveHandle(hoveredTable);
    const fig = e.target?.closest?.("figure.rte-image, figure.rte-flow");
    if (fig && root.contains(fig)) {
      if (!imageResizeDragRef.current) fig.draggable = true;
      fig.setAttribute("contenteditable", "false");
      if (mediaFigRef.current !== fig) {
        mediaFigRef.current = fig;
        mediaChromeSigRef.current = "";
        refreshChromeRef.current();
      }
    }
    const box = e.target?.closest?.(`.${RTE_TEXTBOX_CLASS}`);
    if (box && root.contains(box) && hoveredTextboxRef.current !== box) {
      hoveredTextboxRef.current = box;
      if (hoveredQuoteRef.current) {
        hoveredQuoteRef.current = null;
        quoteChromeSigRef.current = "";
      }
      refreshChromeRef.current();
    }
    const quote = e.target?.closest?.("blockquote");
    if (
      quote &&
      root.contains(quote) &&
      !e.target?.closest?.(`.${RTE_TEXTBOX_CLASS}`) &&
      hoveredQuoteRef.current !== quote
    ) {
      hoveredQuoteRef.current = quote;
      refreshChromeRef.current();
    }
    const anchor = e.target?.closest?.("a");
    if (anchor && root.contains(anchor)) {
      clearLinkHoverHideTimer();
      setHoveredLinkEl(anchor);
      return;
    }
    if (hoveredLinkEl) {
      scheduleLinkHoverHide();
    }
  };

  const handleEditorMouseOut = (e) => {
    const root = editorRef.current;
    const related = e.relatedTarget;
    const fig = mediaFigRef.current;
    if (
      !imageResizeDragRef.current &&
      fig &&
      root?.contains(fig) &&
      (e.target === fig || fig.contains(e.target)) &&
      !(
        related &&
        (fig === related ||
          fig.contains(related) ||
          related.closest?.(".rte-media-chrome, .rte-image-resize"))
      )
    ) {
      mediaFigRef.current = null;
      mediaChromeSigRef.current = "";
      setMediaChrome(null);
      imageResizeSigRef.current = "";
      setImageResizeBar(null);
    }
    const box = hoveredTextboxRef.current;
    if (
      box &&
      root?.contains(box) &&
      (e.target === box || box.contains(e.target)) &&
      !(
        related &&
        (box.contains(related) ||
          related.closest?.(".rte-textbox-chrome:not(.rte-quote-chrome)"))
      )
    ) {
      hoveredTextboxRef.current = null;
      refreshChromeRef.current();
    }
    const hoveredTable = tableMoveRef.current;
    const tableWrap = hoveredTable?.closest?.("figure.rte-table-figure");
    if (
      hoveredTable &&
      root?.contains(hoveredTable) &&
      (e.target === hoveredTable || hoveredTable.contains(e.target) || (tableWrap && (e.target === tableWrap || tableWrap.contains(e.target)))) &&
      !(
        related &&
        (hoveredTable.contains(related) ||
          tableWrap?.contains(related) ||
          related.closest?.(".rte-table-hover-actions, .rte-caption-menu"))
      ) &&
      !tableDragActiveRef.current &&
      !document.querySelector(".rte-caption-menu.show")
    ) {
      hideTableMoveHandle();
    }
    const quote = hoveredQuoteRef.current;
    if (
      quote &&
      root?.contains(quote) &&
      (e.target === quote || quote.contains(e.target)) &&
      !(
        related &&
        (quote.contains(related) || related.closest?.(".rte-quote-chrome"))
      )
    ) {
      hoveredQuoteRef.current = null;
      refreshChromeRef.current();
    }
    if (!root || !hoveredLinkEl) return;
    if (
      related &&
      (hoveredLinkEl.contains(related) ||
        related.closest?.(".rte-link-popover"))
    ) {
      return;
    }
    scheduleLinkHoverHide();
  };

  const applyToggleBlockTag = (tagLower) => {
    if (!editorRef.current || typeof document === "undefined") return;
    focusEditor();
    restoreEditorSelectionRange();
    const sel = window.getSelection();
    if (!sel) return;

    const root = editorRef.current;
    const focusNode = sel.focusNode ?? sel.anchorNode;
    if (!focusNode || !root.contains(focusNode)) return;

    if (tagLower === "blockquote") {
      toggleRichTextQuote(root);
      syncFromEditor();
      return;
    }

    const placeCaretIn = (el) => {
      if (!el) return;
      sel.removeAllRanges();
      const nr = document.createRange();
      nr.selectNodeContents(el);
      nr.collapse(false);
      sel.addRange(nr);
    };

    const scrubBlockStyles = (el) => {
      if (!el?.style) return;
      el.style.removeProperty("text-align");
      el.removeAttribute("align");
      const raw = el.getAttribute("style");
      if (raw != null && String(raw).trim() === "") el.removeAttribute("style");
    };

    const dropEmptyQuoteCodeShells = () => {
      root.querySelectorAll("blockquote, pre").forEach((el) => {
        if (!root.contains(el)) return;
        const text = String(el.textContent ?? "")
          .replace(/\u00a0/g, " ")
          .trim();
        const hasMedia = el.querySelector("img, table, video, iframe");
        if (!text && !hasMedia) el.remove();
      });
    };

    const range =
      sel.rangeCount && root.contains(sel.getRangeAt(0).commonAncestorContainer)
        ? sel.getRangeAt(0)
        : null;

    // Multi-paragraph selection: wrap all selected blocks into one quote/code
    if (range && !range.collapsed) {
      const blocks = collectIntersectingStructuralBlocks(range, root, {
        includePre: true,
      }).filter((b) => !/^li$/i.test(b.tagName));

      if (blocks.length > 1) {
        const outerOf = (b) =>
          tagLower === "blockquote"
            ? findBlockquoteAncestor(b, root)
            : findPreAncestor(b, root);
        const outers = blocks.map(outerOf);
        const sharedOuter = outers[0];
        const allInSame =
          sharedOuter &&
          sharedOuter.tagName.toLowerCase() === tagLower &&
          outers.every((o) => o === sharedOuter);

        if (allInSame) {
          const last = unwrapStructuralBlockToParagraphs(sharedOuter);
          if (last) {
            scrubBlockStyles(last);
            placeCaretIn(last);
          }
          dropEmptyQuoteCodeShells();
          syncFromEditor();
          return;
        }

        const wrapper = document.createElement(tagLower);
        const first = blocks[0];
        first.parentNode.insertBefore(wrapper, first);

        if (tagLower === "pre") {
          const texts = blocks.map((b) =>
            String(b.innerText ?? b.textContent ?? "").replace(/\n+$/, "")
          );
          blocks.forEach((b) => b.remove());
          wrapper.textContent = texts.join("\n");
        } else {
          blocks.forEach((block) => {
            wrapper.appendChild(block);
          });
        }

        scrubBlockStyles(wrapper);
        placeCaretIn(wrapper);
        dropEmptyQuoteCodeShells();
        syncFromEditor();
        return;
      }
    }

    // Prefer the actual quote/code wrapper (not an inner <p>/<div> from soft breaks)
    let block =
      tagLower === "blockquote"
        ? findBlockquoteAncestor(focusNode, root)
        : tagLower === "pre"
          ? findPreAncestor(focusNode, root)
          : null;
    if (!block) {
      block = findStructuralBlockAncestor(focusNode, root);
    }
    const current = block?.tagName?.toLowerCase?.() ?? "";

    // Turn OFF quote/code: replace the wrapper itself with a clean paragraph
    // (avoids leaving an empty blockquote after text was moved out)
    if (current === tagLower && block && block !== root) {
      const neo = unwrapStructuralBlockToParagraphs(block);
      if (neo) scrubBlockStyles(neo);
      placeCaretIn(neo);
      dropEmptyQuoteCodeShells();
      syncFromEditor();
      return;
    }

    if (block && block !== root && !/^li$/i.test(current)) {
      // Don't nest a new quote inside an existing one — upgrade the outer wrapper
      const outerQuote =
        tagLower === "blockquote"
          ? findBlockquoteAncestor(block.parentElement, root)
          : null;
      const outerPre =
        tagLower === "pre" ? findPreAncestor(block.parentElement, root) : null;
      const target = outerQuote || outerPre || block;
      if (
        (outerQuote || outerPre) &&
        target.tagName.toLowerCase() === tagLower
      ) {
        const neo = unwrapStructuralBlockToParagraphs(target);
        if (neo) scrubBlockStyles(neo);
        placeCaretIn(neo);
      } else {
        const neo = replaceElementPreserveChildren(target, tagLower);
        scrubBlockStyles(neo);
        placeCaretIn(neo);
      }
    } else {
      document.execCommand("formatBlock", false, tagLower.toUpperCase());
    }

    syncFromEditor();
  };

  const applyToggleInlineCode = () => {
    if (!editorRef.current || typeof document === "undefined") return;
    focusEditor();
    restoreEditorSelectionRange();
    const root = editorRef.current;
    const sel = window.getSelection();
    if (!sel?.rangeCount) return;
    const range = sel.getRangeAt(0);
    if (!root.contains(range.commonAncestorContainer)) return;

    if (selectionHasInlineCode(sel, root)) {
      unwrapInlineCodeInSelection(root, sel);
      stashEditorSelectionRange();
      syncFromEditor();
      return;
    }

    if (range.collapsed) {
      const code = document.createElement("code");
      code.appendChild(document.createTextNode("\u200b"));
      range.insertNode(code);
      const nr = document.createRange();
      nr.setStart(code.firstChild, 1);
      nr.collapse(true);
      sel.removeAllRanges();
      sel.addRange(nr);
    } else {
      const wraps = wrapRangeInInlineTag(range, root, "code");
      if (wraps.length) {
        const nr = document.createRange();
        nr.setStart(wraps[0], 0);
        const last = wraps[wraps.length - 1];
        nr.setEnd(last, last.childNodes.length);
        sel.removeAllRanges();
        sel.addRange(nr);
      }
    }
    stashEditorSelectionRange();
    syncFromEditor();
  };

  const openLinkModal = () => {
    if (typeof window === "undefined" || !editorRef.current) return;

    linkRangeRef.current = null;

    let initialUrl = "";
    let initialLabel = "";
    const sel = window.getSelection();

    const inside =
      sel?.rangeCount &&
      editorRef.current.contains(sel.anchorNode) &&
      editorRef.current.contains(sel.focusNode);

    if (inside) {
      const range = sel.getRangeAt(0);
      linkRangeRef.current = range.cloneRange();

      const linkEl = findLinkAncestor(sel.anchorNode, editorRef.current);
      if (linkEl) {
        initialUrl = linkEl.getAttribute("href") || "";
      }
      if (!range.collapsed) initialLabel = range.toString();
    }

    setLinkUrl(initialUrl);
    setLinkLabel(initialLabel);
    setLinkModalOpen(true);
  };

  const handleLinkToolbarClick = () => {
    if (caretInspect.inLink) {
      applyUnlinkOnly();
      return;
    }
    openLinkModal();
  };

  // Stack RTE child modals above a parent modal (Bootstrap does not support nested modals natively)
  const stackNestedModal = (modalSelector) => {
    const openModals = document.querySelectorAll(".modal.show").length;
    if (openModals === 0) return;

    setTimeout(() => {
      const backdrops = document.querySelectorAll(".modal-backdrop:not(.stacked)");
      if (backdrops.length === 0) return;

      const latestBackdrop = backdrops[backdrops.length - 1];
      const targetModal = document.querySelector(modalSelector);

      // BS 5.3 defaults: backdrop 1050, modal 1055 — bump each stacked layer by 10
      latestBackdrop.style.zIndex = String(1050 + 10 * openModals);
      if (targetModal) {
        targetModal.style.zIndex = String(1055 + 10 * openModals);
      }
      latestBackdrop.classList.add("stacked");
    }, 0);
  };

  const handleNestedModalExited = () => {
    if (document.querySelectorAll(".modal.show").length > 0) {
      document.body.classList.add("modal-open");
    }
  };

  const handleLinkModalShow = () => stackNestedModal(".modal.rte-link-modal");
  const handleImageModalShow = () => stackNestedModal(".modal.rte-image-modal");

  const applyLinkFromModal = () => {
    const urlRaw = linkUrl.trim();
    if (!urlRaw || !editorRef.current) return;
    const trimmed =
      /^https?:\/\//i.test(urlRaw) ||
      /^mailto:/i.test(urlRaw) ||
      /^tel:/i.test(urlRaw)
        ? urlRaw
        : `https://${urlRaw}`;

    const label = linkLabel.trim();

    editorRef.current.focus();

    let range = linkRangeRef.current;
    let sel = window.getSelection();
    sel.removeAllRanges();

    const savedOk =
      range &&
      editorRef.current.contains(range.commonAncestorContainer);

    if (savedOk && !range.collapsed) {
      sel.addRange(range.cloneRange());
      document.execCommand("createLink", false, trimmed);
    } else if (savedOk && range.collapsed) {
      const r = range.cloneRange();
      sel.addRange(r);
      const caret = sel.getRangeAt(0);
      const a = document.createElement("a");
      a.href = trimmed;
      a.textContent = label || trimmed || "Link";
      caret.insertNode(a);
      const nr = document.createRange();
      nr.setStartAfter(a);
      nr.collapse(true);
      sel.removeAllRanges();
      sel.addRange(nr);
    } else {
      if (!sel.rangeCount || !editorRef.current.contains(sel.anchorNode)) {
        sel.selectAllChildren(editorRef.current);
        sel.collapseToEnd();
      }
      range = sel.getRangeAt(0);
      if (range.collapsed) {
        const a = document.createElement("a");
        a.href = trimmed;
        a.textContent = label || trimmed || "Link";
        range.insertNode(a);
        const nr = document.createRange();
        nr.setStartAfter(a);
        nr.collapse(true);
        sel.removeAllRanges();
        sel.addRange(nr);
      } else {
        document.execCommand("createLink", false, trimmed);
      }
    }

    linkRangeRef.current = null;
    syncFromEditor();
    setLinkModalOpen(false);
    setLinkUrl("");
    setLinkLabel("");
  };

  const onPredefinedTextColor = (hex) => {
    if (!hex || typeof document === "undefined") return;
    applyFormat("foreColor", hex);
  };

  const onCustomTextColorPick = (e) => {
    const hex = e.target.value;
    if (!hex || typeof document === "undefined") return;
    applyFormat("foreColor", hex);
  };

  const isEmpty =
    !value ||
    value.replace(/<br\s*\/?>/gi, "").replace(/&nbsp;/g, "").trim() === "";

  const headingSelectDisabled =
    blockType === "Bulleted list" || blockType === "Numbered list";

  const contextTargetLabel = caretInspect.selected ? "Selection" : "Caret";
  const blockContextTitle = caretInspect.active
    ? `${contextTargetLabel} block: ${caretInspect.blockLong} (${caretInspect.blockShort})`
    : "Block (focus editor)";

  const sizeContextTitle = caretInspect.active
    ? `${contextTargetLabel} size: ${caretInspect.fontSize}`
    : "Font size (focus editor)";
  const fontFamilyContextTitle = caretInspect.active
    ? `${contextTargetLabel} font: ${caretInspect.fontFamily}`
    : "Font (focus editor)";

  const colorContextTitle = caretInspect.active
    ? `${contextTargetLabel} color: ${caretInspect.colorLabel}`
    : "Color (focus editor)";

  const caseContextTitle = caretInspect.active
    ? `${contextTargetLabel} case: ${caseKeyToTitle(caretInspect.caseLabel)}`
    : "Case (focus editor)";

  const histState = historyRef.current;
  const snapshotNow =
    historyTick >= 0 && editorRef.current ? readSnapshotHtml() : "";
  const pendingDocumentEdit =
    histState.index >= 0 && snapshotNow !== histState.stack[histState.index];
  const canUndo = histState.index > 0 || pendingDocumentEdit;
  const canRedo =
    !pendingDocumentEdit &&
    histState.index >= 0 &&
    histState.index < histState.stack.length - 1;

  const currentLineSpacing = caretInspect.active
    ? (caretInspect.lineSpacing || "")
    : (() => {
        if (typeof window === "undefined" || !editorRef.current) return "";
        try {
          const blocks = peekBlocksForLayout();
          if (!blocks.length) {
            const sel = window.getSelection();
            const focus = sel?.focusNode;
            if (!focus || !editorRef.current.contains(focus)) return "";
            const block = findClosestStyleBlock(focus, editorRef.current);
            return getBlockLineSpacingValue(block);
          }
          const values = [
            ...new Set(blocks.map((b) => getBlockLineSpacingValue(b)).filter(Boolean)),
          ];
          return values.length === 1 ? values[0] : "";
        } catch {
          return "";
        }
      })();

  const currentHasBottomMargin = caretInspect.active
    ? Boolean(caretInspect.hasBottomMargin)
    : (() => {
        if (typeof window === "undefined" || !editorRef.current) return false;
        try {
          const blocks = peekBlocksForLayout();
          if (!blocks.length) {
            const sel = window.getSelection();
            const focus = sel?.focusNode;
            if (!focus || !editorRef.current.contains(focus)) return false;
            const block = findClosestStyleBlock(focus, editorRef.current);
            return blockHasBottomMargin(block);
          }
          return blocks.some((b) => blockHasBottomMargin(b));
        } catch {
          return false;
        }
      })();

  const handleEditorKeyDown = (e) => {
    const capText = e.target?.closest?.(".rte-caption-text");
    if (capText && editorRef.current?.contains(capText) && !(e.ctrlKey || e.metaKey)) {
      if (e.key === "Enter" || ((e.key === "Backspace" || e.key === "Delete") && !capText.textContent)) {
        e.preventDefault();
      }
      return;
    }
    const mod = e.ctrlKey || e.metaKey;
    if (mod) {
      const key = String(e.key || "").toLowerCase();
      if (key === "z" && !e.shiftKey) {
        e.preventDefault();
        performUndo();
        return;
      }
      if (key === "y" || (key === "z" && e.shiftKey)) {
        e.preventDefault();
        performRedo();
        return;
      }
    }

    const root = editorRef.current;
    if (!root) return;
    toolbarSelectionLockRef.current = false;

    if (tryRichTextMarkdownShortcut(e, root)) {
      e.preventDefault();
      syncFromEditor();
      return;
    }

    if (handleRichTextListKey(e, root)) {
      e.preventDefault();
      syncFromEditor();
      return;
    }

    // Tab / Shift+Tab: indent like list padding (2rem steps)
    if (e.key === "Tab" && !mod && !e.altKey) {
      const sel = window.getSelection();
      if (!sel?.rangeCount) return;
      const focus = sel.focusNode;
      if (!focus || !root.contains(focus)) return;
      if (findPreAncestor(focus, root)) return;

      e.preventDefault();
      const delta = e.shiftKey ? -1 : 1;
      const listItems = collectIntersectingListItems(sel, root);
      const blocks =
        listItems.length > 0
          ? listItems
          : (() => {
              const range = sel.getRangeAt(0);
              if (range.collapsed) {
                const block = findClosestStyleBlock(focus, root);
                return block ? [block] : [];
              }
              const out = [];
              const seen = new Set();
              root
                .querySelectorAll("p, div, li, blockquote, h1, h2, h3, h4, h5, h6")
                .forEach((el) => {
                  if (seen.has(el)) return;
                  if (el.closest("pre")) return;
                  try {
                    if (!range.intersectsNode(el)) return;
                  } catch {
                    return;
                  }
                  // Prefer leaf blocks (skip wrappers that only contain other blocks)
                  const nested = el.querySelector(
                    "p, div, li, blockquote, h1, h2, h3, h4, h5, h6"
                  );
                  if (nested && el.tagName !== "LI") return;
                  seen.add(el);
                  out.push(el);
                });
              if (!out.length) {
                const block = findClosestStyleBlock(focus, root);
                if (block) out.push(block);
              }
              return out;
            })();
      if (!blocks.length) return;
      blocks.forEach((block) => {
        setBlockIndentLevel(block, getBlockIndentLevel(block) + delta);
      });
      syncFromEditor();
      return;
    }

    // Inside a quotation: Enter adds a line break in the same block (no new quote / gap)
    if (
      e.key === "Enter" &&
      !e.shiftKey &&
      !e.ctrlKey &&
      !e.metaKey &&
      !e.altKey
    ) {
      const sel = window.getSelection();
      if (!sel?.rangeCount) return;
      const focus = sel.focusNode;
      if (!focus || (focus !== root && !root.contains(focus))) return;
      if (findBlockquoteAncestor(focus, root)) {
        if (findTableAncestor(focus, root)) return;
        e.preventDefault();
        document.execCommand("insertLineBreak");
        syncFromEditor();
        return;
      }

      // Headings: don't use the browser split. It sometimes inserts the new
      // paragraph, or moves the caret, to the start of the document.
      if (!findTableAncestor(focus, root) && !findPreAncestor(focus, root)) {
        const headingBlock = (() => {
          const block = findClosestStyleBlock(focus, root);
          if (block && /^H[1-6]$/i.test(block.tagName)) return block;
          if (!sel.isCollapsed || !sel.rangeCount) return null;
          const range = sel.getRangeAt(0);
          const container = range.startContainer;
          if (!container || (container !== root && !root.contains(container))) return null;
          if (container.nodeType === 1) {
            const prev = container.childNodes[range.startOffset - 1];
            if (prev?.nodeType === 1 && /^H[1-6]$/i.test(prev.tagName)) return prev;
          }
          if (container.nodeType === 3) {
            const prev = container.previousSibling;
            const onlySpace = !String(container.textContent || "")
              .replace(/\u00a0/g, " ")
              .trim();
            if (onlySpace && prev?.nodeType === 1 && /^H[1-6]$/i.test(prev.tagName)) {
              return prev;
            }
          }
          return null;
        })();
        if (headingBlock && exitHeadingOnEnter(headingBlock)) {
          e.preventDefault();
          syncFromEditor();
          return;
        }

        // Paragraphs: split here so Enter keeps the new line and the caret.
        // The browser split is often undone, and the caret jumps to another block.
        if (sel.isCollapsed) {
          const flowBlock = findClosestStyleBlock(focus, root);
          if (flowBlock && splitPlainBlockOnEnter(flowBlock, sel.getRangeAt(0))) {
            e.preventDefault();
            syncFromEditor();
            return;
          }
        }
      }

      // After the browser splits the block, drop inherited large-size struts so a
      // small / normal paragraph keeps its own top/bottom spacing.
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (!editorRef.current) return;
          const liveRoot = editorRef.current;
          const liveSel = window.getSelection();
          const liveFocus = liveSel?.focusNode;
          if (!liveFocus || !liveRoot.contains(liveFocus)) return;
          if (findPreAncestor(liveFocus, liveRoot)) return;
          const block = findClosestStyleBlock(liveFocus, liveRoot);
          if (!block) return;
          const prev = block.previousElementSibling;
          const bookmark = captureLeafBlockCaret(liveRoot);
          let liveRange = null;
          if (liveSel.rangeCount && liveRoot.contains(liveSel.anchorNode)) {
            liveRange = liveSel.getRangeAt(0).cloneRange();
          }
          const plain = liveRoot.innerText;
          let changed = false;
          [prev, block].forEach((b) => {
            if (!b || !liveRoot.contains(b)) return;
            if (!/^(P|DIV|LI|H[1-6]|BLOCKQUOTE)$/i.test(b.tagName)) return;
            const before = `${b.getAttribute("style") || ""}||${b.innerHTML}`;
            normalizeBlockFontMetrics(b);
            const after = `${b.getAttribute("style") || ""}||${b.innerHTML}`;
            if (before !== after) changed = true;
          });
          const selAfter = window.getSelection();
          const caretStillInside =
            selAfter?.rangeCount &&
            liveRoot.contains(selAfter.anchorNode) &&
            liveRoot.contains(selAfter.focusNode);
          if (!caretStillInside && liveRoot.innerText === plain) {
            const liveStillThere =
              liveRange &&
              liveRoot.contains(liveRange.startContainer) &&
              liveRoot.contains(liveRange.endContainer);
            if (liveStillThere && selAfter) {
              selAfter.removeAllRanges();
              selAfter.addRange(liveRange);
            } else if (bookmark) {
              restoreLeafBlockCaret(liveRoot, bookmark);
            }
          }
          if (changed) {
            refreshCodeBlockLineNumbers(liveRoot);
            const html = liveRoot.innerHTML;
            noteLocalEmit(html);
            lastValueFromParentRef.current = html;
            onChange(html);
            scheduleTypingHistory();
          }
        });
      });
    }
  };

  const toggleStateLabel = (on) => (on ? "on" : "off");
  const boldContextTitle = caretInspect.active
    ? `Bold: ${toggleStateLabel(caretInspect.bold)}`
    : "Bold (focus editor)";
  const italicContextTitle = caretInspect.active
    ? `Italic: ${toggleStateLabel(caretInspect.italic)}`
    : "Italic (focus editor)";
  const underlineContextTitle = caretInspect.active
    ? `Underline: ${toggleStateLabel(caretInspect.underline)}`
    : "Underline (focus editor)";
  const strikeContextTitle = caretInspect.active
    ? `Strikethrough: ${toggleStateLabel(caretInspect.strikeThrough)}`
    : "Strikethrough (focus editor)";

  const isHeadingBlockShort = /^H[1-6]$/i.test(
    String(caretInspect.blockShort ?? "")
  );
  const blockToggleDisplay = (() => {
    if (!caretInspect.active) return "H";
    if (isHeadingBlockShort) return caretInspect.blockShort;
    if (/^h[1-6]$/i.test(headingValue)) {
      return HEADING_OPTIONS.find((o) => o.value === headingValue)?.label ?? "H";
    }
    return "H";
  })();
  const blockContextShort = isHeadingBlockShort
    ? caretInspect.blockShort
    : "H";
  const blockContextLong = isHeadingBlockShort
    ? caretInspect.blockLong
    : "Paragraph";
  const currentAlignOpt =
    ALIGN_OPTIONS.find((o) => o.value === caretInspect.align) || ALIGN_OPTIONS[0];

  /**
   * "At caret" panel scoped to the control that opened the menu (block / size / color / case only).
   */
  const renderRtePickerContextHeader = (scope) => (
    <>
      <Dropdown.Header className="py-2 small bg-light">
        <div
          className="text-uppercase text-muted mb-2"
          style={{ fontSize: "0.62rem", letterSpacing: "0.05em" }}
        >
          {scope === "block" &&
            (caretInspect.selected ? "Block in selection" : "Block at caret")}
          {scope === "font" &&
            (caretInspect.selected ? "Font in selection" : "Font at caret")}
          {scope === "size" &&
            (caretInspect.selected ? "Size in selection" : "Size at caret")}
          {scope === "color" &&
            (caretInspect.selected ? "Color in selection" : "Color at caret")}
          {scope === "case" &&
            (caretInspect.selected ? "Case in selection" : "Case at caret")}
        </div>
        {scope === "block" && (
          <div className="d-flex flex-row align-items-center gap-1">
            <span className="badge bg-white text-dark border font-monospace px-2 py-1 align-self-start">
              {caretInspect.active ? blockContextShort : "—"}
            </span>
            <div
              className="text-muted small mb-0 lh-sm"
              style={{ fontSize: "0.72rem" }}
            >
              {caretInspect.active ? blockContextLong : "—"}
            </div>
          </div>
        )}
        {scope === "font" && (
          <div className="d-flex flex-row align-items-center gap-1">
            <span
              className="badge bg-white text-dark border px-2 py-1 align-self-start"
              style={{
                fontFamily:
                  caretInspect.fontFamilyValue ||
                  "inherit",
                maxWidth: "9rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {caretInspect.active ? caretInspect.fontFamily : "—"}
            </span>
            <div
              className="text-muted mb-0 lh-sm"
              style={{ fontSize: "0.72rem" }}
            >
              {caretInspect.selected ? "Font in selection" : "Font at caret"}
            </div>
          </div>
        )}
        {scope === "size" && (
          <div className="d-flex flex-row align-items-center gap-1">
            <span className="badge bg-white text-dark border font-monospace px-2 py-1 align-self-start fs-6">
              {caretInspect.active ? caretInspect.fontSize : "—"}
            </span>
            <div
              className="text-muted mb-0 lh-sm"
              style={{ fontSize: "0.72rem" }}
            >
              {caretInspect.selected ? "Size in selection" : "Size at caret"}
            </div>
          </div>
        )}
        {scope === "color" && (
          <div className="d-flex flex-wrap align-items-center gap-2">
            {caretInspect.active &&
            /^#([0-9A-F]{3}|[0-9A-F]{6})$/i.test(caretInspect.colorLabel) ? (
              <span
                className="rte-picker-header-swatch rounded-1 flex-shrink-0"
                style={{ backgroundColor: caretInspect.colorLabel }}
                aria-hidden
              />
            ) : null}
            <span className="badge bg-white text-dark border font-monospace">
              {caretInspect.active ? caretInspect.colorLabel : "—"}
            </span>
          </div>
        )}
        {scope === "case" && (
          <div className="d-flex flex-row align-items-center gap-1">
            <span className="badge bg-white text-dark border px-2 py-1 align-self-start d-inline-flex align-items-center">
              {caretInspect.active
                ? renderCaseLabel(caretInspect.caseLabel)
                : "—"}
            </span>
            <div
              className="text-muted mb-0 lh-sm"
              style={{ fontSize: "0.72rem" }}
            >
              {caretInspect.selected ? "Case in selection" : "Case at caret"}
            </div>
          </div>
        )}
      </Dropdown.Header>
      <Dropdown.Divider className="m-0" />
    </>
  );

  const toClipStyle = (style) => {
    if (!chromeClip || !style) return style;
    const next = { ...style };
    if (typeof style.top === "number") next.top = style.top - chromeClip.top;
    if (typeof style.left === "number") next.left = style.left - chromeClip.left;
    if (typeof style.right === "number") {
      const rightEdge = window.innerWidth - style.right;
      next.right = chromeClip.right - rightEdge;
    }
    return next;
  };

  const popoverHeadingLevels = useMemo(
    () => selectionPopoverHeadingLevels(value),
    [value]
  );
  const popoverListSpec = useMemo(() => {
    const kind = selectionPopoverListKind(value);
    return RTE_LIST_KINDS.find((item) => item.id === kind) || RTE_LIST_KINDS[0];
  }, [value]);

  return (
    <div className={variant === "document" ? "rte-document" : "bg-light rounded-3 p-1"}>
      <div className={variant === "document" ? "rte-document-toolbar px-1 py-2" : "px-2 py-1"}>
        <div className="rte-toolbar d-flex flex-wrap align-items-center w-100">
        {/* 1 · History */}
        <div className="rte-toolbar-group">
          <RteTip text="Undo (Ctrl+Z)">
            <span className="d-inline-flex">
              <Button
                variant="light-lite"
                size="sm"
                className="rte-tb-btn"
                aria-label="Undo (Ctrl+Z)"
                disabled={!canUndo}
                onMouseDown={(event) => event.preventDefault()}
                onClick={performUndo}
              >
                <i className="bi bi-arrow-90deg-left"></i>
              </Button>
            </span>
          </RteTip>
          <RteTip text="Redo (Ctrl+Y)">
            <span className="d-inline-flex">
              <Button
                variant="light-lite"
                size="sm"
                className="rte-tb-btn"
                aria-label="Redo (Ctrl+Y)"
                disabled={!canRedo}
                onMouseDown={(event) => event.preventDefault()}
                onClick={performRedo}
              >
                <i className="bi bi-arrow-90deg-right"></i>
              </Button>
            </span>
          </RteTip>
        </div>

        <div className="rte-toolbar-sep" aria-hidden />

        {/* 2 · Blocks */}
        <div className="rte-toolbar-group">
          {/* Font family */}
          <Dropdown
            align="start"
            onSelect={(eventKey) => {
              if (eventKey == null) return;
              applyFontFamilyValue(String(eventKey));
            }}
            className="rte-menu"
          >
            <RteTip text={fontFamilyContextTitle}>
            <Dropdown.Toggle
              variant="light-lite"
              size="sm"
              id="rte-font-family-dropdown"
              className="rte-tb-btn rte-tb-dd no-arrow"
              aria-label="Font"
              style={{ width: "4.5rem" }}
            >
              <span
                className="small text-truncate"
                style={{
                  maxWidth: "4.5rem",
                  fontFamily: caretInspect.fontFamilyValue || "inherit",
                }}
              >
                {caretInspect.active ? caretInspect.fontFamily : "Default"}
              </span>
            </Dropdown.Toggle>
            </RteTip>
            <Dropdown.Menu className="rte-picker-dropdown-menu shadow-sm">
              {renderRtePickerContextHeader("font")}
              {FONT_FAMILY_OPTIONS.map((opt) => (
                <Dropdown.Item
                  key={opt.label}
                  eventKey={opt.value}
                  as="button"
                  type="button"
                  active={
                    caretInspect.active
                      ? opt.value === FONT_FAMILY_DEFAULT
                        ? !caretInspect.fontFamilyValue
                        : normalizeFontFamilyKey(caretInspect.fontFamilyValue) ===
                          normalizeFontFamilyKey(opt.value)
                      : opt.value === FONT_FAMILY_DEFAULT
                  }
                  style={
                    opt.value !== FONT_FAMILY_DEFAULT
                      ? { fontFamily: opt.value }
                      : undefined
                  }
                >
                  {opt.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          {/* Font size */}
          <Dropdown
            align="start"
            onSelect={(eventKey) => {
              const k = String(eventKey ?? "");
              if (k) applyFontSizePx(k);
            }}
            className="rte-menu"
          >
            <RteTip text={sizeContextTitle}>
            <Dropdown.Toggle
              variant="light-lite"
              size="sm"
              id="rte-font-size-dropdown"
              className="rte-tb-btn rte-tb-dd no-arrow"
              aria-label="Font size"
              style={{ width: "1.75rem" }}
            >
              <span className="font-monospace small">
                {caretInspect.active && caretInspect.fontSize !== "—"
                  ? String(caretInspect.fontSize).replace("px", "")
                  : "Size"}
              </span>
            </Dropdown.Toggle>
            </RteTip>
            <Dropdown.Menu className="rte-picker-dropdown-menu shadow-sm">
              {renderRtePickerContextHeader("size")}
              {FONT_SIZE_OPTIONS.map((opt) => (
                <Dropdown.Item
                  key={opt.value}
                  eventKey={opt.value}
                  as="button"
                  type="button"
                >
                  {opt.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          {/* Heading */}
          <Dropdown
            align="start"
            onSelect={(eventKey) => {
              const k = String(eventKey ?? "");
              if (!k || headingSelectDisabled) return;
              const targetingTitle = isEditingGuideTitle();
              const current = targetingTitle
                ? (getGuideTitleEl()?.tagName.toLowerCase() || "h1")
                : deriveHeadingTagFromDom();
              // Clicking the active heading turns it back into a paragraph
              if (/^h[1-6]$/i.test(k) && current === k) {
                if (targetingTitle) return;
                applyHeadingValue("p");
                return;
              }
              applyHeadingValue(k);
            }}
            className="rte-menu"
          >
            <RteTip
              text={
                headingSelectDisabled
                  ? "Headings apply to paragraphs; exit the list first"
                  : blockContextTitle
              }
            >
            <span className="d-inline-flex">
            <Dropdown.Toggle
              variant="light-lite"
              size="sm"
              id="rte-block-dropdown"
              disabled={headingSelectDisabled}
              className="rte-tb-btn rte-tb-dd no-arrow"
              aria-label="Heading"
              style={{ width: "1.75rem" }}
            >
              <span className="font-monospace fw-semibold">
                {blockToggleDisplay}
              </span>
            </Dropdown.Toggle>
            </span>
            </RteTip>
            <Dropdown.Menu className="rte-picker-dropdown-menu shadow-sm">
              {renderRtePickerContextHeader("block")}
              <Dropdown.Header className="py-1 small fw-normal text-muted bg-transparent">
                Heading
              </Dropdown.Header>
              {HEADING_OPTIONS.map((opt) => (
                <Dropdown.Item
                  key={opt.value}
                  eventKey={opt.value}
                  as="button"
                  type="button"
                  active={opt.value === headingValue}
                >
                  {opt.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          
         
         
        </div>

        <div className="rte-toolbar-sep" aria-hidden />

        {/* 3 · Inline */}
        <div className="rte-toolbar-group">
          {/* Bold */}
          <RteTip text={boldContextTitle}>
            <Button
              variant="light-lite"
              size="sm"
              className="rte-tb-btn"
              active={caretInspect.bold}
              aria-label={boldContextTitle}
              onClick={() => applyFormat("bold")}
            >
              <i className="bi bi-type-bold"></i>
            </Button>
          </RteTip>

          {/* Italic */}
          <RteTip text={italicContextTitle}>
            <Button
              variant="light-lite"
              size="sm"
              className="rte-tb-btn"
              active={caretInspect.italic}
              aria-label={italicContextTitle}
              onClick={() => applyFormat("italic")}
            >
              <i className="bi bi-type-italic"></i>
            </Button>
          </RteTip>

          {/* Strike through */}
          <RteTip text={strikeContextTitle}>
            <Button
              variant="light-lite"
              size="sm"
              className="rte-tb-btn"
              aria-label={strikeContextTitle}
              active={caretInspect.strikeThrough}
              onClick={() => applyFormat("strikeThrough")}
            >
              <i className="bi bi-type-strikethrough"></i>
            </Button>
          </RteTip> 

          {/* Underline */}
          <RteTip text={underlineContextTitle}>
            <Button
              variant="light-lite"
              size="sm"
              className="rte-tb-btn"
              active={caretInspect.underline}
              aria-label={underlineContextTitle}
              onClick={() => applyFormat("underline")}
            >
              <i className="bi bi-type-underline"></i>
            </Button>
          </RteTip>

          {/* Text case */}
          <Dropdown
            className="rte-menu"
            onSelect={(eventKey) => {
              const k = String(eventKey ?? "");
              if (k === "uppercase") {
                wrapSelectionStyles({ textTransform: "uppercase" });
              } else if (k === "lowercase") {
                wrapSelectionStyles({ textTransform: "lowercase" });
              } else if (k === "removecase") {
                wrapSelectionStyles({ textTransform: "none" });
              } else if (k === "titlecase") {
                transformSelectedTextPreserveMarkup(TITLE_CASE_TRANSFORM);
              }
            }}
            align="start"
          >
            <RteTip text={caseContextTitle}>
            <Dropdown.Toggle
              variant="light-lite"
              size="sm"
              id="rte-text-case-dropdown"
              className="rte-tb-btn no-arrow"
              aria-label="Text case"
            >
              {caretInspect.active
                ? renderCaseLabel(caretInspect.caseLabel)
                : <span className="font-monospace small fw-semibold">Tt</span>}
            </Dropdown.Toggle>
            </RteTip>
            <Dropdown.Menu className="rte-case-dropdown-menu rte-picker-dropdown-menu shadow-sm">
              {renderRtePickerContextHeader("case")}
              <Dropdown.Item eventKey="uppercase" as="button" type="button" className="py-2">
                <span className="font-monospace fw-semibold me-2">ABC</span>
                Uppercase
              </Dropdown.Item>
              <Dropdown.Item eventKey="titlecase" as="button" type="button" className="py-2">
                <span className="font-monospace fw-semibold me-2">AbC</span>
                Title Case
              </Dropdown.Item>
              <Dropdown.Item eventKey="lowercase" as="button" type="button" className="py-2">
                <span className="font-monospace fw-semibold me-2">abc</span>
                Lowercase
              </Dropdown.Item>
              <Dropdown.Divider className="my-1" />
              <Dropdown.Item eventKey="removecase" as="button" type="button" className="py-2">
                Remove Case
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          {/* Lists */}
          <Dropdown
            align="start"
            onSelect={(eventKey) => {
              const k = String(eventKey ?? "");
              if (RTE_LIST_KINDS.some((item) => item.id === k)) applyListKind(k);
            }}
            className="rte-menu"
          >
            <RteTip text="Lists">
            <Dropdown.Toggle
              variant="light-lite"
              size="sm"
              id="rte-list-dropdown"
              className="rte-tb-btn rte-tb-dd no-arrow"
              aria-label="Bulleted or numbered list"
            >
              <i className="bi bi-list-ul"></i>
            </Dropdown.Toggle>
            </RteTip>
            <Dropdown.Menu className="shadow-sm">
              {RTE_LIST_KINDS.map((item) => (
                <Dropdown.Item
                  key={item.id}
                  eventKey={item.id}
                  as="button"
                  type="button"
                  active={caretInspect.listKind === item.id}
                >
                  {item.icon ? (
                    <i className={`bi ${item.icon} me-2`}></i>
                  ) : (
                    <span className="font-monospace me-2">a.</span>
                  )}
                  {item.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          {/* Text color */}
          <Dropdown
            onSelect={(eventKey) => {
              if (eventKey != null && eventKey !== "") {
                onPredefinedTextColor(String(eventKey));
              }
            }}
            align="start"
            className="rte-menu"
          >
            <RteTip text={colorContextTitle}>
            <Dropdown.Toggle
              variant="light-lite"
              size="sm"
              id="rte-text-color-preset-dropdown"
              className="rte-tb-btn no-arrow"
              aria-label="Text color"
            >
              <span className="d-inline-flex flex-column align-items-center lh-1">
                <span className="fw-semibold" style={{ fontSize: "0.8rem" }}>A</span>
                <span
                  className="rte-tb-color-bar rounded-pill"
                  style={{
                    backgroundColor:
                      caretInspect.active &&
                      /^#([0-9A-F]{3}|[0-9A-F]{6})$/i.test(caretInspect.colorLabel)
                        ? caretInspect.colorLabel
                        : "var(--gray-700)",
                  }}
                />
              </span>
            </Dropdown.Toggle>
            </RteTip>
            <Dropdown.Menu className="rte-color-dropdown-menu shadow-sm">
              <div className="rte-color-swatch-grid p-1" role="listbox" aria-label="Text colors">
                {TEXT_COLOR_OPTIONS.map((opt) => (
                  <RteTip key={opt.value} text={opt.label}>
                    <Dropdown.Item
                      eventKey={opt.value}
                      as="button"
                      type="button"
                      role="option"
                      aria-label={opt.label}
                      className={`rte-color-swatch border-0 rounded-1 ${
                        opt.lightSwatch ? "rte-color-swatch-light-border" : ""
                      }`}
                      style={{ backgroundColor: opt.value }}
                    />
                  </RteTip>
                ))}
              </div>
            </Dropdown.Menu>
          </Dropdown>

          {/* Highlight */}
          <Dropdown
            onSelect={(eventKey) => {
              const k = String(eventKey ?? "");
              if (k === "clear") clearHighlightColor();
              else if (k) applyHighlightColor(k);
            }}
            align="start"
            className="rte-menu"
          >
            <RteTip text="Highlight">
            <Dropdown.Toggle
              variant="light-lite"
              size="sm"
              id="rte-highlight-dropdown"
              className="rte-tb-btn no-arrow"
              aria-label="Highlight color"
            >
              <i className="bi bi-highlighter"></i>
            </Dropdown.Toggle>
            </RteTip>
            <Dropdown.Menu className="rte-color-dropdown-menu shadow-sm">
              <div className="d-flex justify-content-between gap-1 mb-1 p-1">
                {HIGHLIGHT_COLOR_OPTIONS.map((opt) => (
                  <RteTip key={opt.value} text={opt.label}>
                    <button
                      type="button"
                      aria-label={opt.label}
                      className={`rte-highlight-swatch rte-hl-${opt.token} border-0 rounded-1`}
                      onClick={() => applyHighlightColor(opt.apply)}
                    />
                  </RteTip>
                ))}
              </div>
              <Dropdown.Divider className="my-1" />
              <Dropdown.Item
                eventKey="clear"
                as="button"
                type="button"
                className="small py-1"
              >
                Remove highlight
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>

        
        <div className="rte-toolbar-sep" aria-hidden />

        {/* 4 · Super / Sub */}
        <div className="rte-toolbar-group">
          
          {/* Align */}
          <div className="rte-align-expanded">
            {ALIGN_OPTIONS.map((opt) => (
              <RteTip key={opt.value} text={opt.label}>
                <Button
                  variant="light-lite"
                  size="sm"
                  className="rte-tb-btn"
                  active={caretInspect.align === opt.value}
                  aria-label={opt.label}
                  onClick={() => applyFormat(opt.command)}
                >
                  <i className={`bi ${opt.icon}`}></i>
                </Button>
              </RteTip>
            ))}
          </div>

          {/* Align */}
          <Dropdown
            align="start"
            className="rte-menu rte-align-collapsed"
            onSelect={(eventKey) => {
              const opt = ALIGN_OPTIONS.find((o) => o.value === String(eventKey ?? ""));
              if (opt) applyFormat(opt.command);
            }}
          >
            <RteTip text={currentAlignOpt.label}>
              <Dropdown.Toggle
                variant="light-lite"
                size="sm"
                id="rte-align-dropdown"
                className="rte-tb-btn rte-tb-dd no-arrow"
                aria-label={currentAlignOpt.label}
              >
                <i className={`bi ${currentAlignOpt.icon}`} aria-hidden />
              </Dropdown.Toggle>
            </RteTip>
            <Dropdown.Menu className="shadow-sm">
              {ALIGN_OPTIONS.map((opt) => (
                <Dropdown.Item
                  key={opt.value}
                  eventKey={opt.value}
                  as="button"
                  type="button"
                  active={caretInspect.align === opt.value}
                >
                  <i className={`bi ${opt.icon} me-2`} aria-hidden />
                  {opt.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          {/* Line spacing */}
          <Dropdown
            align="start"
            className="rte-menu"
            onToggle={(next) => {
              if (!next) return;
              stashEditorSelectionRange();
              const root = editorRef.current;
              const saved = selectionRangeRef.current;
              if (!root || !saved) return;
              const blocks = collectBlocksForLayoutFromRange(saved, root);
              if (!blocks.length) return;
              const hasBottomMargin = blocks.some((b) => blockHasBottomMargin(b));
              const spacingVals = [
                ...new Set(
                  blocks.map((b) => getBlockLineSpacingValue(b)).filter(Boolean)
                ),
              ];
              commitCaretInspect((prev) => ({
                ...prev,
                active: true,
                selected: !saved.collapsed,
                hasBottomMargin,
                lineSpacing: spacingVals.length === 1 ? spacingVals[0] : "",
              }));
            }}
            onSelect={(eventKey) => {
              const k = String(eventKey ?? "");
              if (k === "clear") applyLineSpacingToSelection("clear");
              else if (k === "mb-toggle") {
                applyBottomMarginToSelection(!currentHasBottomMargin);
              } else if (isRteLineSpacingValue(k)) applyLineSpacingToSelection(k);
            }}
          >
            <RteTip text="Line spacing">
            <Dropdown.Toggle
              variant="light-lite"
              size="sm"
              id="rte-line-spacing-dropdown"
              className="rte-tb-btn rte-tb-dd no-arrow"
              aria-label="Line spacing"
            >
              <i className="bi bi-distribute-vertical" aria-hidden />
            </Dropdown.Toggle>
            </RteTip>
            <Dropdown.Menu className="rte-picker-dropdown-menu shadow-sm">
              <Dropdown.Header className="py-1 small fw-normal text-muted bg-transparent">
                Line spacing
              </Dropdown.Header>
              {RTE_LINE_SPACING_OPTIONS.map((n) => (
                <Dropdown.Item
                  key={n}
                  eventKey={n}
                  as="button"
                  type="button"
                  active={currentLineSpacing === n}
                >
                  {n}
                </Dropdown.Item>
              ))}
              <Dropdown.Divider className="my-1" />
              <Dropdown.Item eventKey="clear" as="button" type="button">
                Default ({RTE_TEXT_LINE_HEIGHT})
              </Dropdown.Item>
              <Dropdown.Divider className="my-1" />
              <Dropdown.Item
                eventKey="mb-toggle"
                as="button"
                type="button"
                active={!currentHasBottomMargin && caretInspect.active}
              >
                {currentHasBottomMargin
                  ? "Remove bottom margin"
                  : "Add bottom margin"}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          {/* Superscript */}
          <RteTip text={caretInspect.superscript ? "Remove superscript" : "Superscript"}>
            <Button
              variant="light-lite"
              size="sm"
              className="rte-tb-btn"
              active={caretInspect.superscript}
              aria-label={caretInspect.superscript ? "Remove superscript" : "Superscript"}
              onClick={() => applyFormat("superscript")}
            >
              <i className="bi bi-superscript"></i>
            </Button>
          </RteTip>

          {/* Subscript */}
          <RteTip text={caretInspect.subscript ? "Remove subscript" : "Subscript"}>
            <Button
              variant="light-lite"
              size="sm"
              className="rte-tb-btn"
              active={caretInspect.subscript}
              aria-label={caretInspect.subscript ? "Remove subscript" : "Subscript"}
              onClick={() => applyFormat("subscript")}
            >
              <i className="bi bi-subscript"></i>
            </Button>
          </RteTip>
        </div>

        <div className="rte-toolbar-sep" aria-hidden />

        <div className="rte-toolbar-group">
          {/* Link */}
          <RteTip
            text={
              caretInspect.inLink
                ? "Remove link"
                : "Insert link — select text first, or paste URL and label below"
            }
          >
          <Button
            variant="light-lite"
            size="sm"
            className="rte-tb-btn"
            active={caretInspect.inLink}
            aria-label={
              caretInspect.inLink
                ? "Remove link"
                : "Insert link — select text first, or paste URL and label below"
            }
            onClick={handleLinkToolbarClick}
          >
            {caretInspect.inLink ? (
              <span className="position-relative d-inline-flex lh-1" aria-hidden="true">
                <i className="bi bi-link"></i>
                <i className="bi bi-slash position-absolute top-50 start-50 translate-middle"></i>
              </span>
            ) : (
              <i className="bi bi-link-45deg"></i>
            )}
          </Button>
          </RteTip>

          {/* Quotation */}
          <RteTip text={caretInspect.inQuote ? "Remove quotation" : "Quotation block"}>
              <Button
                variant="light-lite"
                size="sm"
                className="rte-tb-btn"
                active={caretInspect.inQuote}
                aria-label={caretInspect.inQuote ? "Remove quotation" : "Quotation block"}
                onClick={() => applyToggleBlockTag("blockquote")}
              >
                <i className="bi bi-quote"></i>
              </Button>
            </RteTip>

          {/* Code */}
          <Dropdown
            align="start"
            className="rte-menu"
            onSelect={(eventKey) => {
              const k = String(eventKey ?? "");
              if (k === "block") applyToggleBlockTag("pre");
              else if (k === "inline") applyToggleInlineCode();
            }}
          >
            <RteTip text="Code">
              <Dropdown.Toggle
                variant="light-lite"
                size="sm"
                id="rte-code-dropdown"
                className="rte-tb-btn rte-tb-dd no-arrow"
                active={caretInspect.inCode}
                aria-label="Code"
              >
                <i className={`bi ${caretInspect.inCode ? "bi-braces" : "bi-code"}`} aria-hidden />
              </Dropdown.Toggle>
            </RteTip>
            
            <Dropdown.Menu className="shadow-sm">
              <Dropdown.Item eventKey="block" as="button" type="button" active={caretInspect.inCode}>
                <i className="bi bi-code-square me-2" aria-hidden />
                {caretInspect.inCode ? "Remove code block" : "Code block"}
              </Dropdown.Item>
              <Dropdown.Item eventKey="inline" as="button" type="button">
                <i className="bi bi-code-slash me-2" aria-hidden />
                Inline code
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>

        <div className="rte-toolbar-sep" aria-hidden />
        
        {/* 6 · Insert */}
        <div className="rte-toolbar-group">
          {/* Image */}
          <RteTip text="Insert image">
          <Button
            variant="light-lite"
            size="sm"
            className="rte-tb-btn rte-tb-add gap-1"
            aria-label="Insert image"
            onClick={openImageModal}
          >
            <span className="position-relative d-inline-flex lh-1" aria-hidden>
              <i className="bi bi-image"></i>
              <i
                className="bi bi-plus-circle-fill position-absolute"
                style={{ fontSize: "0.55rem", right: "-0.35rem", top: "-0.2rem" }}
              ></i>
            </span>
          </Button>
          </RteTip>

          {/* Table */}
          <Dropdown
            align="start"
            className="rte-menu"
            show={tableMenuOpen}
            onToggle={(next) => {
              setTableMenuOpen(next);
              if (!next) {
                setTableGridHover({ rows: 0, cols: 0 });
                setTableGridDragging(false);
                tableGridDraggingRef.current = false;
                tableGridPendingRef.current = null;
              } else {
                stashEditorSelectionRange();
                setTableGridHover({ rows: 1, cols: 1 });
              }
            }}
          >
            <RteTip text="Insert table">
            <Dropdown.Toggle
              variant="light-lite"
              size="sm"
              id="rte-table-dropdown"
              className="rte-tb-btn no-arrow"
              aria-label="Insert table"
            >
              <i className="bi bi-table"></i>
            </Dropdown.Toggle>
            </RteTip>
            <Dropdown.Menu>
              <Dropdown.Header className="px-2 pb-2 pt-1 bg-light rounded-top-2">
              <div className="small text-muted mb-1">Style</div>
              <div className="rte-table-style-options">
                {TABLE_STYLE_OPTIONS.map((style) => (
                  <button
                    key={style.id}
                    type="button"
                    className={`rte-table-style-option${
                      tableStyleId === style.id ? " is-active" : ""
                    }`}
                    aria-label={style.label}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setTableStyleId(style.id);
                    }}
                  >
                    <span
                      className={`rte-table-style-preview rte-table-style-preview-${style.id}`}
                      aria-hidden
                    >
                      <span />
                      <span />
                      <span />
                      <span />
                      <span />
                      <span />
                    </span>
                    <span className="rte-table-style-label">{style.label}</span>
                  </button>
                ))}
              </div>
              </Dropdown.Header>
              <Dropdown.Divider className="my-0" />
              <div className="px-2 py-1">
              <div className="small text-muted mb-2">
                {tableGridHover.rows > 0
                  ? `${tableGridHover.rows} × ${tableGridHover.cols}`
                  : "Select size"}
              </div>
              <div
                className="rte-table-size-grid"
                onMouseLeave={() => {
                  if (!tableGridDragging) setTableGridHover({ rows: 0, cols: 0 });
                }}
              >
                {Array.from({ length: TABLE_GRID_MAX }, (_, rowIdx) => (
                  <div key={`tr-${rowIdx}`} className="rte-table-size-row">
                    {Array.from({ length: TABLE_GRID_MAX }, (_, colIdx) => {
                      const r = rowIdx + 1;
                      const c = colIdx + 1;
                      const active =
                        tableGridHover.rows >= r && tableGridHover.cols >= c;
                      return (
                        <button
                          key={`tc-${rowIdx}-${colIdx}`}
                          type="button"
                          className={`rte-table-size-cell${
                            active ? " is-active" : ""
                          }`}
                          aria-label={`${r} by ${c}`}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            tableGridDraggingRef.current = true;
                            setTableGridDragging(true);
                            setTableGridHover({ rows: r, cols: c });
                            tableGridPendingRef.current = {
                              rows: r,
                              cols: c,
                              styleId: tableStyleId,
                            };
                          }}
                          onMouseEnter={() => {
                            setTableGridHover({ rows: r, cols: c });
                            if (tableGridDraggingRef.current) {
                              tableGridPendingRef.current = {
                                rows: r,
                                cols: c,
                                styleId: tableStyleId,
                              };
                            }
                          }}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
              </div>
            </Dropdown.Menu>
          </Dropdown>

          <RteTip text="Flow diagram">
            <Button
              variant="light-lite"
              size="sm"
              className="rte-tb-btn"
              aria-label="Flow diagram"
              onClick={openFlowModal}
            >
              <i className="bi bi-diagram-3"></i>
            </Button>
          </RteTip>

          {/* Clear formatting */}
          <RteTip text="Clear formatting">
            <Button
              variant="light-lite"
              size="sm"
              className="rte-tb-btn"
              aria-label="Clear formatting"
              onClick={applyRemoveFormat}
            >
              <i className="bi bi-eraser"></i>
            </Button>
          </RteTip>
        </div>






        <div className={`${variant === "document" ? "d-none" : "ms-auto"}`}>
          <RteTip text="Clear all content">
            <Button
              variant="light-lite"
              size="sm"
              className="rte-tb-btn"
              aria-label="Clear all content"
              onClick={clearContent}
            >
              <i className="bi bi-trash"></i>
            </Button>
          </RteTip>
        </div>
        </div>

      </div>
      <div
        className={
          variant === "document"
            ? "rte-document-body rich-text-editor-select-hover"
            : "bg-white rounded-3 p-1 rich-text-editor-select-hover"
        }
      >
        <div className="d-flex align-items-start">
          <div className={variant === "document" ? "d-none" : "px-2 pt-2"}>
            <span className="visually-hidden">Current block type</span>
          </div>
          <div
            ref={editorShellRef}
            className="position-relative flex-grow-1 rte-editor-shell"
            onMouseLeave={handleEditorShellMouseLeave}
          >
            {variant === "document" ? (
              <div className="rte-document-page eval-rich-content">
                {documentHeader}
                <div
                  ref={editorRef}
                  className="p-2 flex-grow-1 eval-rich-content rte-editable-root"
                  style={{
                    minHeight: "100%",
                    outline: "none",
                    cursor: "text",
                    color: "var(--gray-800)",
                  }}
                  contentEditable
                  suppressContentEditableWarning
                  onInput={handleInput}
                  onPaste={handlePaste}
                  onKeyDown={handleEditorKeyDown}
                  onKeyUp={(e) => {
                    if (e.key === "Enter" || e.key === "Backspace" || e.key === "Delete") {
                      refreshCodeBlockLineNumbers(e.currentTarget);
                    }
                  }}
                  onMouseOver={handleEditorMouseOver}
                  onMouseOut={handleEditorMouseOut}
                  onMouseMove={handleEditorTableMouseMove}
                  onMouseDown={handleEditorTableMouseDown}
                  onDragStart={handleEditorDragStart}
                  onDragOver={handleEditorDragOver}
                  onDrop={handleEditorDrop}
                  onDragEnd={handleEditorDragEnd}
                />
              </div>
            ) : (
            <div
              ref={editorRef}
              className="p-2 flex-grow-1 eval-rich-content rte-editable-root"
              style={{
                minHeight: "80px",
                outline: "none",
                cursor: "text",
                color: "var(--gray-800)",
              }}
              contentEditable
              suppressContentEditableWarning
              onInput={handleInput}
              onPaste={handlePaste}
              onKeyDown={handleEditorKeyDown}
              onKeyUp={(e) => {
                if (e.key === "Enter" || e.key === "Backspace" || e.key === "Delete") {
                  refreshCodeBlockLineNumbers(e.currentTarget);
                }
              }}
              onMouseOver={handleEditorMouseOver}
              onMouseOut={handleEditorMouseOut}
              onMouseMove={handleEditorTableMouseMove}
              onMouseDown={handleEditorTableMouseDown}
              onDragStart={handleEditorDragStart}
              onDragOver={handleEditorDragOver}
              onDrop={handleEditorDrop}
              onDragEnd={handleEditorDragEnd}
            />
            )}
            <div
              className="rte-table-chrome"
              aria-hidden={
                !tableColGutter &&
                !tableRowGutter &&
                !tableDelCol &&
                !tableDelRow &&
                !tableMoveHandle &&
                !colResizeHandle
              }
            >
              {tableMoveHandle && (
                <div
                  className="rte-table-hover-actions"
                  style={{ top: tableMoveHandle.top, left: tableMoveHandle.left }}
                  onMouseDown={(event) => {
                    if (event.target.closest?.(".rte-table-move")) return;
                    event.preventDefault();
                  }}
                >
                <RteTip text="Drag table">
                  <button
                    type="button"
                    draggable
                    aria-label="Drag table"
                    className="rte-table-move"
                    onDragStart={(event) => {
                      const table = tableMoveRef.current;
                      const root = editorRef.current;
                      if (!table || !root?.contains(table)) return;
                      const node = table.closest("figure.rte-table-figure") || table;
                      stopTableDragListeners();
                      tableDragActiveRef.current = true;
                      tableDragDroppedRef.current = false;
                      node.classList.add("rte-table-dragging");
                      mediaDragRef.current = node;
                      event.dataTransfer.effectAllowed = "move";
                      event.dataTransfer.setData("text/plain", "rte-table");
                      const track = (clientX, clientY) => {
                        const block = blockUnderMediaPoint(root, clientX, clientY, node);
                        mediaDropBlockRef.current = block;
                        if (!block) {
                          setMediaDropLine(null);
                          return;
                        }
                        const rect = block.getBoundingClientRect();
                        const top = Math.round(rect.bottom);
                        const left = Math.round(rect.left);
                        const width = Math.round(rect.width);
                        setMediaDropLine((prev) =>
                          prev && prev.top === top && prev.left === left && prev.width === width
                            ? prev
                            : { top, left, width }
                        );
                      };
                      const over = (dragEvent) => {
                        if (!tableDragActiveRef.current) return;
                        dragEvent.preventDefault();
                        dragEvent.dataTransfer.dropEffect = "move";
                        track(dragEvent.clientX, dragEvent.clientY);
                      };
                      const drop = (dragEvent) => {
                        if (!tableDragActiveRef.current) return;
                        dragEvent.preventDefault();
                        dragEvent.stopPropagation();
                        tableDragDroppedRef.current = true;
                        track(dragEvent.clientX, dragEvent.clientY);
                      };
                      document.addEventListener("dragover", over, true);
                      document.addEventListener("drop", drop, true);
                      tableDragListenersRef.current = { over, drop };
                    }}
                    onDragEnd={() => {
                      commitTableDrag();
                      hideTableMoveHandle();
                    }}
                  >
                    <i className="bi bi-grip-vertical"></i>
                  </button>
                </RteTip>
                </div>
              )}
              {colResizeHandle && (
                <div
                  className="rte-table-col-resize"
                  role="separator"
                  aria-orientation="vertical"
                  aria-label="Resize column"
                  style={{
                    left: colResizeHandle.lineLeft,
                    top: colResizeHandle.lineTop,
                    height: colResizeHandle.lineHeight,
                  }}
                  onMouseDown={startColResize}
                  onMouseLeave={(event) => {
                    if (colResizeDragRef.current) return;
                    const next = event.relatedTarget;
                    const table = colResizeTableRef.current;
                    const ready = colResizeReadyRef.current;
                    if (next instanceof Element && table && ready) {
                      const nextCell = next.closest("td, th");
                      if (nextCell && table.contains(nextCell)) {
                        const nextCells = rowCells(nextCell.parentElement);
                        const nextIndex = nextCells.indexOf(nextCell);
                        const hit = columnDividerAtPoint(
                          nextCell,
                          nextIndex,
                          nextCells.length,
                          event.clientX
                        );
                        if (hit && hit.colIndex === ready.colIndex) return;
                      }
                    }
                    hideColResize();
                  }}
                />
              )}
              {tableColGutter && (
                <>
                  <div
                    className="rte-table-guide-col"
                    style={{
                      left: tableColGutter.lineLeft - 1,
                      top: tableColGutter.lineTop,
                      height: tableColGutter.lineHeight,
                    }}
                  />
                  <RteTip text="Add column">
                  <button
                    type="button"
                    aria-label="Add column"
                    className="rte-table-guide-plus"
                    style={{
                      left: tableColGutter.lineLeft - 11,
                      top: tableColGutter.lineTop - 11,
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={insertTableColumnAtGuide}
                  >
                    <i className="bi bi-plus-lg"></i>
                  </button>
                  </RteTip>
                </>
              )}
              {tableRowGutter && (
                <>
                  <div
                    className="rte-table-guide-row"
                    style={{
                      left: tableRowGutter.rowLineLeft,
                      width: tableRowGutter.rowLineWidth,
                      top: tableRowGutter.lineTop - 1,
                    }}
                  />
                  <RteTip text="Add row">
                  <button
                    type="button"
                    aria-label="Add row"
                    className="rte-table-guide-plus"
                    style={{
                      left: tableRowGutter.rowLineLeft - 11,
                      top: tableRowGutter.lineTop - 11,
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={insertTableRowAtGuide}
                  >
                    <i className="bi bi-plus-lg"></i>
                  </button>
                  </RteTip>
                </>
              )}
              {tableSelection?.type === "col" && tableDelCol && (
                <div
                  className="rte-table-band-actions is-col"
                  style={{
                    left: tableDelCol.left,
                    top: tableDelCol.top - 6,
                  }}
                >
                  <RteTip text="Delete column">
                    <button
                      type="button"
                      aria-label="Delete column"
                      className="rte-table-del-btn"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => deleteTableColumnAt(tableDelCol.colIndex)}
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </RteTip>
                  <span ref={tableBandBtnRef} className="rte-table-band-btn">
                    <RteTip text="Column color and borders">
                      <button
                        type="button"
                        aria-label="Column color and borders"
                        className="rte-table-del-btn"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => setTableBandPop((open) => !open)}
                      >
                        <i className="bi bi-palette"></i>
                      </button>
                    </RteTip>
                  </span>
                </div>
              )}
              {tableSelection?.type === "row" && tableDelRow && (
                <div
                  className="rte-table-band-actions is-row"
                  style={{
                    left: tableDelRow.left - 6,
                    top: tableDelRow.top,
                  }}
                >
                  <RteTip text="Delete row">
                    <button
                      type="button"
                      aria-label="Delete row"
                      className="rte-table-del-btn"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => deleteTableRowAt(tableDelRow.rowIndex)}
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </RteTip>
                  <span ref={tableBandBtnRef} className="rte-table-band-btn">
                    <RteTip text="Row color and borders">
                      <button
                        type="button"
                        aria-label="Row color and borders"
                        className="rte-table-del-btn"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => setTableBandPop((open) => !open)}
                      >
                        <i className="bi bi-palette"></i>
                      </button>
                    </RteTip>
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
        {isEmpty && placeholder && (
          <div
            className={
              variant === "document"
                ? "px-2 py-1 text-muted small"
                : "px-2 py-1 bg-light small rounded-3"
            }
          >
            {placeholder}
          </div>
        )}
      </div>

      <RteChromeClip clip={chromeClip}>
      {mediaDropLine && (
        <div
          className={`rte-media-drop-line${mediaDropLine.vertical ? " is-vertical" : ""}`}
          style={toClipStyle({
            top: mediaDropLine.top,
            left: mediaDropLine.left,
            width: mediaDropLine.width,
            height: mediaDropLine.height,
          })}
        />
      )}
      {imageResizeBar && (
        <div
          className="rte-image-resize"
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize image"
          style={toClipStyle({ top: imageResizeBar.top, left: imageResizeBar.left })}
          onMouseDown={startImageResize}
          onMouseLeave={(event) => {
            if (imageResizeDragRef.current) return;
            const next = event.relatedTarget;
            const fig = mediaFigRef.current;
            if (
              next instanceof Element &&
              (next.closest?.(".rte-image-resize, .rte-media-chrome") ||
                (fig && (fig === next || fig.contains(next))))
            ) {
              return;
            }
            hideMediaChrome();
          }}
        />
      )}
      {mediaChrome && (
        <div
          className="rte-textbox-chrome rte-media-chrome shadow-sm"
          style={toClipStyle({ top: mediaChrome.top, right: mediaChrome.right })}
          onMouseDown={(event) => {
            if (event.target?.closest?.("input, label, .dropdown-menu")) return;
            event.preventDefault();
          }}
          onMouseLeave={(event) => {
            if (imageResizeDragRef.current) return;
            const next = event.relatedTarget;
            const fig = mediaFigRef.current;
            if (
              next instanceof Element &&
              (event.currentTarget.contains(next) ||
                next.closest?.(".rte-media-chrome, .rte-image-resize, .rte-caption-menu, .tooltip") ||
                (fig && (fig === next || fig.contains(next))))
            ) {
              return;
            }
            if (document.querySelector(".rte-media-chrome .dropdown-menu.show")) return;
            hideMediaChrome();
          }}
        >
          <RteTip text="Remove">
            <Button
              variant="light-lite"
              size="sm"
              className="rte-tb-btn"
              aria-label="Remove"
              onMouseDown={(event) => event.preventDefault()}
              onClick={removeMediaFigure}
            >
              <i className="bi bi-trash"></i>
            </Button>
          </RteTip>
          <RteCaptionMenu
            mode="media"
            node={mediaFigRef.current}
            variant="light-lite"
            size="sm"
            toggleClassName={`rte-tb-btn no-arrow${captionRev >= 0 && readCaptionSettings(mediaFigRef.current).has ? " is-on" : ""}`}
            onApply={applyCaptionTo}
            onRemove={removeCaptionFrom}
          />
          {mediaChrome.flow ? (
            <RteTip text="Edit diagram">
              <Button
                variant="light-lite"
                size="sm"
                className="rte-tb-btn"
                aria-label="Edit diagram"
                onMouseDown={(event) => event.preventDefault()}
                onClick={editMediaFlow}
              >
                <i className="bi bi-pencil"></i>
              </Button>
            </RteTip>
          ) : null}
        </div>
      )}

      {headingSwitches.map((heading) => (
        <div
          key={`rte-heading-${heading.key}`}
          className="rte-heading-switch"
          style={toClipStyle({
            top: heading.top,
            left: chromeClip ? heading.left - 40 : Math.max(4, heading.left - 40),
          })}
          onMouseDown={(e) => e.preventDefault()}
        >
          <Dropdown
            align="start"
            onSelect={(eventKey) =>
            switchHeadingLevel(heading.top, heading.left, eventKey)
          }
          >
            <Dropdown.Toggle
              variant="light-lite"
              size="sm"
              className="rte-heading-switch-toggle no-arrow"
              aria-label="Heading level"
              title="Heading level"
            >
              <span className="font-monospace fw-semibold">{`H${heading.level}`}</span>
            </Dropdown.Toggle>
            <Dropdown.Menu className="shadow-sm">
              {HEADING_OPTIONS.map((opt) => (
                <Dropdown.Item
                  key={opt.value}
                  eventKey={opt.value}
                  as="button"
                  type="button"
                  active={opt.value === `h${heading.level}`}
                >
                  {opt.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      ))}

      {textboxChrome && (
        <div
          className="rte-textbox-chrome shadow-sm"
          style={toClipStyle({ top: textboxChrome.top, right: textboxChrome.right })}
          onMouseDown={(e) => e.preventDefault()}
          onMouseLeave={(e) => {
            const box = textboxNodeRef.current;
            const related = e.relatedTarget;
            if (box && related && (box === related || box.contains(related))) return;
            if (document.querySelector(".rte-textbox-chrome:not(.rte-quote-chrome) .dropdown-menu.show")) return;
            if (hoveredTextboxRef.current) {
              hoveredTextboxRef.current = null;
              refreshChromeRef.current();
            }
          }}
        >
          <Dropdown align="end">
            <Dropdown.Toggle
              variant="light-lite"
              size="sm"
              className="rte-tb-btn"
              aria-label="Box padding"
              title="Padding"
            >
              <i className="bi bi-arrows-angle-expand"></i>
            </Dropdown.Toggle>
            <Dropdown.Menu className="shadow-sm">
              {RTE_TEXTBOX_PADS.map((opt) => (
                <Dropdown.Item
                  key={opt.value}
                  as="button"
                  type="button"
                  active={textboxChrome.pad === opt.value}
                  onClick={() => setTextboxSetting(RTE_TB_PAD, opt.value)}
                >
                  {`Padding: ${opt.label}`}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <Dropdown align="end">
            <Dropdown.Toggle
              variant="light-lite"
              size="sm"
              className="rte-tb-btn"
              aria-label="Box border"
              title="Border"
            >
              <i className="bi bi-square"></i>
            </Dropdown.Toggle>
            <Dropdown.Menu className="shadow-sm">
              {RTE_TEXTBOX_BORDERS.map((opt) => (
                <Dropdown.Item
                  key={opt.value}
                  as="button"
                  type="button"
                  active={textboxChrome.border === opt.value}
                  onClick={() => setTextboxSetting(RTE_TB_BORDER, opt.value)}
                >
                  {`Border: ${opt.label}`}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <Dropdown align="end">
            <Dropdown.Toggle
              variant="light-lite"
              size="sm"
              className="rte-tb-btn"
              aria-label="Box color"
              title="Color"
            >
              <span className={`rte-textbox-color-dot is-${textboxChrome.color}`} />
            </Dropdown.Toggle>
            <Dropdown.Menu className="shadow-sm">
              {RTE_TEXTBOX_COLORS.map((opt) => (
                <Dropdown.Item
                  key={opt.value}
                  as="button"
                  type="button"
                  active={textboxChrome.color === opt.value}
                  onClick={() => setTextboxSetting(RTE_TB_COLOR, opt.value)}
                >
                  <span className={`rte-textbox-color-dot is-${opt.value} me-2`} />
                  {opt.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <RteTip text="Remove box, keep text">
            <Button
              variant="light-lite"
              size="sm"
              className="rte-tb-btn"
              aria-label="Remove text box"
              onMouseDown={(e) => e.preventDefault()}
              onClick={removeTextboxKeepText}
            >
              <i className="bi bi-x-lg"></i>
            </Button>
          </RteTip>
        </div>
      )}

      {quoteChrome && (
        <div
          className="rte-textbox-chrome rte-quote-chrome shadow-sm"
          style={toClipStyle({ top: quoteChrome.top, right: quoteChrome.right })}
          onMouseDown={(e) => e.preventDefault()}
          onMouseLeave={(e) => {
            const quote = quoteNodeRef.current;
            const related = e.relatedTarget;
            if (quote && related && (quote === related || quote.contains(related))) return;
            if (document.querySelector(".rte-quote-chrome .dropdown-menu.show")) return;
            if (hoveredQuoteRef.current) {
              hoveredQuoteRef.current = null;
              refreshChromeRef.current();
            }
          }}
        >
          <Dropdown align="end">
            <Dropdown.Toggle
              variant="light-lite"
              size="sm"
              className="rte-tb-btn"
              aria-label="Quotation design"
              title="Design"
            >
              <i className="bi bi-quote"></i>
            </Dropdown.Toggle>
            <Dropdown.Menu className="shadow-sm">
              {RTE_QUOTE_STYLES.map((opt) => (
                <Dropdown.Item
                  key={opt.value}
                  as="button"
                  type="button"
                  active={quoteChrome.style === opt.value}
                  onClick={() => setQuoteSetting(RTE_QUOTE_STYLE_ATTR, opt.value)}
                >
                  {opt.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <Dropdown align="end">
            <Dropdown.Toggle
              variant="light-lite"
              size="sm"
              className="rte-tb-btn"
              aria-label="Quotation color"
              title="Color"
            >
              <span className={`rte-textbox-color-dot is-${quoteChrome.color}`} />
            </Dropdown.Toggle>
            <Dropdown.Menu className="shadow-sm">
              {RTE_TEXTBOX_COLORS.map((opt) => (
                <Dropdown.Item
                  key={opt.value}
                  as="button"
                  type="button"
                  active={quoteChrome.color === opt.value}
                  onClick={() => setQuoteSetting(RTE_QUOTE_COLOR_ATTR, opt.value)}
                >
                  <span className={`rte-textbox-color-dot is-${opt.value} me-2`} />
                  {opt.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <RteTip text="Remove quotation, keep text">
            <Button
              variant="light-lite"
              size="sm"
              className="rte-tb-btn"
              aria-label="Remove quotation"
              onMouseDown={(e) => e.preventDefault()}
              onClick={removeQuoteKeepText}
            >
              <i className="bi bi-x-lg"></i>
            </Button>
          </RteTip>
        </div>
      )}

      {tableEditChrome && (
        <div
          className="rte-textbox-chrome rte-table-edit-chrome shadow-sm"
          style={toClipStyle({ top: tableEditChrome.top, right: tableEditChrome.right })}
          onMouseDown={(e) => {
            tableEditStickyRef.current = true;
            if (e.target?.closest?.("input, label, .dropdown-menu")) return;
            e.preventDefault();
          }}
          onMouseLeave={(e) => {
            if (e.relatedTarget instanceof Element && e.currentTarget.contains(e.relatedTarget)) return;
            if (document.querySelector(".rte-table-edit-chrome .dropdown-menu.show")) return;
            const root = editorRef.current;
            const sel = window.getSelection();
            const stillEditing =
              sel?.focusNode &&
              root?.contains(sel.focusNode) &&
              findTableAncestor(sel.focusNode, root);
            if (stillEditing) return;
            tableEditStickyRef.current = false;
            editingTableRef.current = null;
            tableEditChromeSigRef.current = "";
            setTableEditChrome(null);
          }}
        >
          <Dropdown align="end" autoClose="outside">
            <Dropdown.Toggle
              variant="light-lite"
              size="sm"
              className="rte-tb-btn"
              aria-label="Table background"
              title="Background"
            >
              <span
                className={`rte-textbox-color-dot${
                  sanitizeBorderColor(tableEditChrome.bg) ? "" : ` is-${tableEditChrome.bg || "none"}`
                }`}
                style={
                  sanitizeBorderColor(tableEditChrome.bg)
                    ? { background: tableEditChrome.bg }
                    : undefined
                }
              />
            </Dropdown.Toggle>
            <Dropdown.Menu className="shadow-sm">
              {RTE_TEXTBOX_COLORS.map((opt) => (
                <Dropdown.Item
                  key={opt.value}
                  as="button"
                  type="button"
                  active={(tableEditChrome.bg || "none") === opt.value}
                  onClick={() => applyEditingTableBg(opt.value)}
                >
                  <span className={`rte-textbox-color-dot is-${opt.value} me-2`} />
                  {opt.label}
                </Dropdown.Item>
              ))}
              <div className="dropdown-divider" />
              <div className="px-2 pb-2 d-flex align-items-center gap-2">
                <span className="rte-band-label mb-0">Custom</span>
                <label className="rte-border-color-custom" title="Choose a fill color">
                  <input
                    type="color"
                    aria-label="Choose table fill color"
                    value={sanitizeBorderColor(tableEditChrome.bg) || "#fff3a3"}
                    onMouseDown={(event) => event.stopPropagation()}
                    onChange={(event) => applyEditingTableBg(event.target.value)}
                  />
                </label>
              </div>
            </Dropdown.Menu>
          </Dropdown>
          <Dropdown align="end" autoClose="outside">
            <Dropdown.Toggle
              variant="light-lite"
              size="sm"
              className="rte-tb-btn"
              aria-label="Outer border"
              title="Outer border"
            >
              <span className="d-inline-flex flex-column align-items-center lh-1">
                <i className="bi bi-border-outer"></i>
                <span
                  className="rte-tb-color-bar rounded-pill"
                  style={{ backgroundColor: tableEditChrome.perimColor || "var(--border-bg)" }}
                />
              </span>
            </Dropdown.Toggle>
            <Dropdown.Menu className="shadow-sm rte-table-border-menu">
              {RTE_TABLE_BORDER_STEPS.map((opt) => (
                <Dropdown.Item
                  key={opt.value}
                  as="button"
                  type="button"
                  active={tableEditChrome.perimeter === opt.value}
                  onClick={() => applyEditingTableBorder(RTE_TABLE_PERIM_ATTR, opt.value)}
                >
                  {`Outer border: ${opt.label}`}
                </Dropdown.Item>
              ))}
              <div className="dropdown-divider" />
              <div className="px-2 pb-2">
                <div className="rte-band-label">Color</div>
                <RteBorderColorChoices
                  value={tableEditChrome.perimColor}
                  ariaLabel="Choose outer border color"
                  onChange={(color) => applyEditingTableBorderColor("perimeter", color)}
                />
              </div>
            </Dropdown.Menu>
          </Dropdown>
          <Dropdown align="end" autoClose="outside">
            <Dropdown.Toggle
              variant="light-lite"
              size="sm"
              className="rte-tb-btn"
              aria-label="Inside borders"
              title="Inside borders"
            >
              <span className="d-inline-flex flex-column align-items-center lh-1">
                <i className="bi bi-border-inner"></i>
                <span
                  className="rte-tb-color-bar rounded-pill"
                  style={{ backgroundColor: tableEditChrome.inColor || "var(--border-bg)" }}
                />
              </span>
            </Dropdown.Toggle>
            <Dropdown.Menu className="shadow-sm rte-table-border-menu">
              {RTE_TABLE_BORDER_STEPS.map((opt) => (
                <Dropdown.Item
                  key={opt.value}
                  as="button"
                  type="button"
                  active={tableEditChrome.inside === opt.value}
                  onClick={() => applyEditingTableBorder(RTE_TABLE_INSIDE_ATTR, opt.value)}
                >
                  {`Inside borders: ${opt.label}`}
                </Dropdown.Item>
              ))}
              <div className="dropdown-divider" />
              <div className="px-2 pb-2">
                <div className="rte-band-label">Color</div>
                <RteBorderColorChoices
                  value={tableEditChrome.inColor}
                  ariaLabel="Choose inside border color"
                  onChange={(color) => applyEditingTableBorderColor("inside", color)}
                />
              </div>
            </Dropdown.Menu>
          </Dropdown>
          <RteCaptionMenu
            mode="table"
            node={editingTableRef.current}
            variant="light-lite"
            size="sm"
            toggleClassName="rte-tb-btn no-arrow"
            onApply={applyCaptionTo}
            onRemove={removeCaptionFrom}
          />
          <RteTip text="Reset table, rows, and columns">
            <Button
              variant="light-lite"
              size="sm"
              className="rte-tb-btn"
              aria-label="Reset table to default"
              onMouseDown={(e) => e.preventDefault()}
              onClick={resetEditingTable}
            >
              <i className="bi bi-arrow-counterclockwise"></i>
            </Button>
          </RteTip>
        </div>
      )}

      {tableBandPop && tableSelection && tableBandBtnRef.current && (
        <Overlay
          show
          target={tableBandBtnRef.current}
          placement={tableSelection.type === "row" ? "right" : "bottom"}
          container={() => document.body}
          popperConfig={RTE_TIP_POPPER_CONFIG}
        >
          {(overlayProps) => {
            const bandTable =
              activeTableRef.current && editorRef.current?.contains(activeTableRef.current)
                ? activeTableRef.current
                : null;
            const bandColor = bandTable ? readBandColor(bandTable, tableSelection) : "";
            void tableStyleRev;
            return (
              <Popover
                {...overlayProps}
                id="rte-table-band-pop"
                className="rte-table-band-pop shadow-sm"
                onMouseDown={(event) => {
                  if (event.target?.closest?.("input[type='color']")) return;
                  event.preventDefault();
                }}
              >
                <Popover.Header className="py-2">
                  {tableSelection.type === "row" ? "Row" : "Column"}
                </Popover.Header>
                <Popover.Body>
                  <div className="rte-band-label">Fill</div>
                  <div className="rte-band-colors">
                    {RTE_TEXTBOX_COLORS.map((opt) => {
                      const active =
                        opt.value === "none" ? bandColor === "" : bandColor === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          className={`rte-band-color${active ? " is-active" : ""}`}
                          aria-label={opt.label}
                          title={opt.label}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => applySelectedBandColor(opt.value)}
                        >
                          <span className={`rte-textbox-color-dot is-${opt.value}`} />
                        </button>
                      );
                    })}
                    <label className="rte-border-color-custom" title="Choose a fill color">
                      <input
                        type="color"
                        aria-label="Choose fill color"
                        value={sanitizeBorderColor(bandColor) || "#fff3a3"}
                        onMouseDown={(event) => event.stopPropagation()}
                        onChange={(event) => applySelectedBandColor(event.target.value)}
                      />
                    </label>
                  </div>
                  <div className="rte-band-label mt-2">Borders</div>
                  {RTE_BAND_BORDER_TARGETS.map((target) => {
                    const width = bandTable
                      ? readBandTargetWidth(bandTable, tableSelection, target.id)
                      : "";
                    const sideColor = bandTable
                      ? readBandTargetColor(bandTable, tableSelection, target.id)
                      : "";
                    return (
                      <div key={target.id} className="rte-band-edge">
                        <div className="rte-band-edge-label">
                          <i className={`bi ${target.icon}`} aria-hidden />
                          {target.label}
                        </div>
                        <div className="rte-band-edge-controls">
                          <div className="rte-band-steps">
                            {RTE_TABLE_BORDER_STEPS.map((step) => (
                              <button
                                key={step.value}
                                type="button"
                                className={`rte-band-step${width === step.value ? " is-active" : ""}`}
                                title={step.label}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => applySelectedBandEdge(target.id, step.value)}
                              >
                                {step.label}
                              </button>
                            ))}
                          </div>
                          <label className="rte-border-color-custom" title={`${target.label} color`}>
                            <input
                              type="color"
                              aria-label={`${target.label} color`}
                              value={sanitizeBorderColor(sideColor) || "#6c757d"}
                              onMouseDown={(event) => event.stopPropagation()}
                              onChange={(event) =>
                                applySelectedBandSideColor(target.id, event.target.value)
                              }
                            />
                          </label>
                        </div>
                      </div>
                    );
                  })}
                  <div className="rte-band-label mt-2">Border color</div>
                  <RteBorderColorChoices
                    value={bandTable ? readBandAllBorderColor(bandTable, tableSelection) : ""}
                    ariaLabel="Choose border color"
                    onChange={(color) => applySelectedBandEdgeColor(color)}
                  />
                  <button
                    type="button"
                    className="rte-band-reset"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={resetSelectedBand}
                  >
                    Reset to default
                  </button>
                </Popover.Body>
              </Popover>
            );
          }}
        </Overlay>
      )}

      {selectionMenuPos && (
        <div
          className={`rte-selection-popover shadow-sm${selectionMenuPos.placement === "below" ? " is-below" : ""}${references ? " has-references" : ""}`}
          role="toolbar"
          aria-label="Selection formatting"
          style={toClipStyle({
            top: selectionMenuPos.top,
            left: selectionMenuPos.left,
          })}
          onMouseDown={(e) => e.preventDefault()}
        >
          <RteTip text="Bold">
            <Button
              variant="light-lite"
              size="sm"
              className="rte-tb-btn"
              active={caretInspect.bold}
              aria-label="Bold"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applySelectionMenuFormat("bold")}
            >
              <i className="bi bi-type-bold"></i>
            </Button>
          </RteTip>
          <RteTip text="Italic">
            <Button
              variant="light-lite"
              size="sm"
              className="rte-tb-btn"
              active={caretInspect.italic}
              aria-label="Italic"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applySelectionMenuFormat("italic")}
            >
              <i className="bi bi-type-italic"></i>
            </Button>
          </RteTip>
          <RteTip text="Underline">
            <Button
              variant="light-lite"
              size="sm"
              className="rte-tb-btn"
              active={caretInspect.underline}
              aria-label="Underline"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applySelectionMenuFormat("underline")}
            >
              <i className="bi bi-type-underline"></i>
            </Button>
          </RteTip>
          <RteTip text="Strikethrough">
            <Button
              variant="light-lite"
              size="sm"
              className="rte-tb-btn"
              active={caretInspect.strikeThrough}
              aria-label="Strikethrough"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applySelectionMenuFormat("strikeThrough")}
            >
              <i className="bi bi-type-strikethrough"></i>
            </Button>
          </RteTip>
          <span className="rte-selection-popover-sep" aria-hidden />
          {popoverHeadingLevels.map((level) => {
            const label = HEADING_OPTIONS.find((opt) => opt.value === level)?.label || level.toUpperCase();
            const headingName = `Heading ${label.slice(1)}`;
            return (
              <RteTip key={level} text={headingName}>
                <Button
                  variant="light-lite"
                  size="sm"
                  className="rte-tb-btn"
                  active={caretInspect.blockShort === label}
                  aria-label={headingName}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => applySelectionMenuHeading(level)}
                >
                  <span className="font-monospace fw-semibold">{label}</span>
                </Button>
              </RteTip>
            );
          })}
          <RteTip text={popoverListSpec.label}>
            <Button
              variant="light-lite"
              size="sm"
              className="rte-tb-btn"
              active={caretInspect.listKind === popoverListSpec.id}
              aria-label={popoverListSpec.label}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                applyListKind(popoverListSpec.id);
                scheduleSelectionMenuRefresh();
              }}
            >
              {popoverListSpec.icon ? (
                <i className={`bi ${popoverListSpec.icon}`}></i>
              ) : (
                <span className="font-monospace fw-semibold">a.</span>
              )}
            </Button>
          </RteTip>
          <RteTip text="Text box">
            <Button
              variant="light-lite"
              size="sm"
              className="rte-tb-btn"
              aria-label="Text box"
              onMouseDown={(e) => e.preventDefault()}
              onClick={applySelectionTextbox}
            >
              <i className="bi bi-textarea"></i>
            </Button>
          </RteTip>
          <span className="rte-selection-popover-sep" aria-hidden />
          {HIGHLIGHT_COLOR_OPTIONS.map((opt) => (
            <RteTip key={opt.value} text={`Highlight ${opt.label}`}>
              <button
                type="button"
                aria-label={`Highlight ${opt.label}`}
                className={`rte-highlight-swatch rte-hl-${opt.token} border-0 rounded-1`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => applySelectionMenuHighlight(opt.apply)}
              />
            </RteTip>
          ))}
          <span className="rte-selection-popover-sep" aria-hidden />
          <RteTip text="Remove highlight">
            <Button
              variant="light-lite"
              size="sm"
              className="rte-tb-btn"
              aria-label="Remove highlight"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                clearHighlightColor();
                scheduleSelectionMenuRefresh();
              }}
            >
              <i className="bi bi-eraser"></i>
            </Button>
          </RteTip>
          {references ? (
            <>
              <span className="rte-selection-popover-sep" aria-hidden />
              <div ref={setReferenceSlot} className="rte-reference-slot" />
            </>
          ) : null}
        </div>
      )}
      </RteChromeClip>

      <Overlay
        target={hoveredLinkEl}
        show={Boolean(hoveredLinkEl)}
        placement="top"
        rootClose={false}
        container={() => document.body}
        popperConfig={RTE_TIP_POPPER_CONFIG}
      >
        {(overlayProps) => (
          <Popover
            {...overlayProps}
            id="rte-link-hover-popover"
            className="rte-link-popover shadow-sm"
            onMouseEnter={clearLinkHoverHideTimer}
            onMouseLeave={scheduleLinkHoverHide}
          >
            <Popover.Body className="p-1 d-flex align-items-center gap-1">
              <RteTip text="Remove link">
              <Button
                variant="outline-danger"
                size="sm"
                className="d-flex align-items-center gap-1 py-0 px-2"
                onMouseDown={(e) => e.preventDefault()}
                onClick={removeHoveredLink}
                aria-label="Remove link"
              >
                <span className="position-relative d-inline-flex lh-1" aria-hidden="true">
                  <i className="bi bi-link"></i>
                  <i className="bi bi-slash position-absolute top-50 start-50 translate-middle"></i>
                </span>
                <span className="small">Remove</span>
              </Button>
              </RteTip>
            </Popover.Body>
          </Popover>
        )}
      </Overlay>

      <Modal
        show={linkModalOpen}
        onHide={() => setLinkModalOpen(false)}
        onShow={handleLinkModalShow}
        onExited={handleNestedModalExited}
        centered
        enforceFocus={false}
        className="rte-link-modal"
      >
        <Modal.Header closeButton className="border-0 py-2">
          <div className="d-flex flex-column align-items-start w-100">
            <Modal.Title className="h6 mb-0">Add link</Modal.Title>
            <div className="text-hl"></div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <FloatingLabel 
              label="URL"
            >
            <Form.Control
              type="text"
              size="sm"
              placeholder="https://example.com"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              autoFocus
            /></FloatingLabel>
          </Form.Group>
          <Form.Group className="mb-0">
            <FloatingLabel 
              label="Display text (optional if text is selected)"
            >
            <Form.Control
              type="text"
              size="sm"
              placeholder="Link label"
              value={linkLabel}
              onChange={(e) => setLinkLabel(e.target.value)}
            /></FloatingLabel>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="py-2 border-0">
          <Button variant="outline-secondary" size="sm" onClick={() => setLinkModalOpen(false)}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={applyLinkFromModal} disabled={!linkUrl.trim()}>
            Insert link
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={imageModalOpen}
        onHide={() => setImageModalOpen(false)}
        onShow={handleImageModalShow}
        onExited={handleNestedModalExited}
        centered
        enforceFocus={false}
        className="rte-image-modal"
      >
        <Modal.Header closeButton className="border-0 py-2">
          <div className="d-flex flex-column align-items-start w-100">
            <Modal.Title className="h6 mb-0">Add image</Modal.Title>
            <div className="text-hl"></div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <input
              ref={imageFileRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              className="d-none"
              onChange={handleImageFile}
            />
            <Button
              type="button"
              variant="outline-secondary"
              size="sm"
              onClick={() => imageFileRef.current?.click()}
            >
              Upload image
            </Button>
            {imageUpload?.name ? (
              <div className="small mt-2">
                {imageUpload.name}
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 ms-2"
                  onClick={() => setImageUpload(null)}
                >
                  Clear
                </button>
              </div>
            ) : null}
            {imageUpload?.src ? (
              <img
                src={imageUpload.src}
                alt=""
                className="d-block mt-2"
                style={{ maxWidth: "100%", maxHeight: "8rem", objectFit: "contain" }}
              />
            ) : null}
            {imageUploadError ? (
              <div className="text-danger small mt-2">{imageUploadError}</div>
            ) : null}
          </Form.Group>
          <Form.Group className="mb-3">
            <FloatingLabel 
              label="Image URL"
            >
            <Form.Control
              type="text"
              size="sm"
              placeholder="https://example.com/image.png"
              value={imageUrl}
              onChange={(e) => {
                setImageUrl(e.target.value);
                if (e.target.value.trim()) setImageUpload(null);
              }}
              autoFocus
            /></FloatingLabel>
          </Form.Group>
          <Form.Group className="mb-0">
            <FloatingLabel 
              label="Alt text (optional)"
            >
            <Form.Control
              type="text"
              size="sm"
              placeholder="Describe the image"
              value={imageAlt}
              onChange={(e) => setImageAlt(e.target.value)}
            /></FloatingLabel>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="py-2 border-0">
          <Button variant="outline-secondary" size="sm" onClick={() => setImageModalOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={applyImageFromModal}
            disabled={!imageUrl.trim() && !imageUpload?.src}
          >
            Insert image
          </Button>
        </Modal.Footer>
      </Modal>

      <FlowDiagramModal
        show={flowModalOpen}
        embedId={flowEmbedId}
        onHide={() => {
          setFlowModalOpen(false);
          setFlowEmbedId(null);
        }}
        onApply={applyFlowDiagram}
      />
      {references ? (
        <Suspense fallback={null}>
          <ReferenceController
            document={references}
            editorRef={editorRef}
            slot={referenceSlot}
            htmlValue={value}
            applyMutation={applyEditorMutation}
            stashSelection={stashEditorSelectionRange}
            onUiOpenChange={setReferenceUiOpen}
          />
        </Suspense>
      ) : null}
    </div>
  );
};

export default RichTextEditor;
